"use client"
import React, { useState, useEffect, useRef } from "react"
import axios from "axios"
import DashNavbar from "./DashNavbar"
import Sidebar from "./Sidebar"
import { Pagination, Modal, Card, Form, InputGroup, Button, Table, Spinner, ButtonGroup, Badge } from "react-bootstrap"
import Swal from "sweetalert2"

// --- Custom CSS ---
const CustomStyles = () => (
  <style>{`
    /* Main Layout */
    .interviewer-feedback-page .main-content { background-color: #f8f9fa; }
    .interviewer-feedback-page.wrapper { display: flex; height: calc(100vh - 60px); }
    .interviewer-feedback-page .main-content { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; }
    
    /* Table & Card Styling */
    .interviewer-feedback-page .card { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; }
    .interviewer-feedback-page .table-responsive { flex-grow: 1; overflow-y: auto; padding: 1.5rem; padding-top: 0; }
    .sub-table-container { padding: 1rem; border-top: 2px solid #0d6efd; background: white; margin: 10px; border-radius: 5px; box-shadow: 0 2px 4px rgba(0,0,0,0.05); }

    /* --- PDF/Form REPLICATION STYLES --- */
    .pdf-viewer-container {
        background-color: #525659; /* Acrobat Reader Grey Background */
        padding: 2rem;
        display: flex;
        justify-content: center;
        min-height: 100%;
    }

    .pdf-paper {
        background-color: white;
        width: 100%;
        max-width: 850px; /* A4 width approx */
        min-height: 1100px;
        padding: 40px 50px;
        box-shadow: 0 0 10px rgba(0,0,0,0.3);
        font-family: "Times New Roman", Times, serif; /* Match PDF Font */
        color: #000;
    }

    /* PDF Header */
    .pdf-header h2 {
        font-size: 24px;
        font-weight: bold;
        margin-bottom: 25px;
        color: #000;
    }
    
    .info-line {
        display: flex;
        margin-bottom: 8px;
        align-items: flex-end;
    }
    .info-label {
        font-weight: bold;
        min-width: 160px;
        font-size: 16px;
    }
    .info-value {
        flex-grow: 1;
        border-bottom: 1px solid #333;
        padding-left: 10px;
        font-size: 16px;
        min-height: 24px;
    }

    /* PDF Sections */
    .section-header {
        font-size: 18px;
        font-weight: bold;
        margin-top: 30px;
        margin-bottom: 10px;
        display: flex;
        align-items: center;
        gap: 10px;
    }
    .section-header i { font-size: 18px; }
    .rating-instruction { font-size: 14px; font-style: italic; margin-bottom: 15px; color: #444; }

    /* PDF Tables */
    .pdf-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
    .pdf-table th {
        text-align: left;
        font-weight: bold;
        padding: 5px 0;
        border-bottom: 1px solid #000; /* Minimal header border */
        font-size: 15px;
    }
    .pdf-table td {
        padding: 10px 5px;
        vertical-align: top;
        font-size: 14px;
        border-bottom: 1px solid #e0e0e0; /* Faint row lines */
        text-align: justify;
    }
    
    .col-competency { width: 25%; font-weight: bold; text-align: justify; }
    .col-desc { width: 40%; text-align: justify; }
    .col-rating { width: 10%; text-align: center !important; }
    .col-comments { width: 25%; text-align: justify; }

    /* Recommendation Section */
    .checkbox-group { display: flex; flex-direction: column; gap: 8px; margin-top: 10px; }
    .checkbox-item { display: flex; align-items: center; gap: 10px; font-size: 15px; }
    .box-icon { font-size: 18px; line-height: 1; }

    .list-section { margin-top: 15px; }
    .list-line { display: flex; margin-bottom: 5px; }
    .list-number { width: 20px; font-weight: bold; }
    .list-content { flex-grow: 1; border-bottom: 1px solid #ccc; min-height: 20px; }

    /* Signature */
    .signature-section { margin-top: 50px; border-top: 1px solid #ccc; padding-top: 10px; display: flex; justify-content: space-between; font-size: 15px; }

    /* For PDF Export specifically */
    .pdf-export-mode {
        padding: 20px !important;
        background: white !important;
    }
  `}</style>
)

const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com"

function InterviewerFeedback() {
  const reportRef = useRef(null)
  
  // Data Stores
  const [mergedEvaluations, setMergedEvaluations] = useState([])
  const [groupedCandidates, setGroupedCandidates] = useState({})
  const [evaluationParameters, setEvaluationParameters] = useState([]) 

  // UI State
  const [searchTerm, setSearchTerm] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [isLoading, setIsLoading] = useState(false)
  const [expandedCandidate, setExpandedCandidate] = useState(null)

  // Modal State
  const [selectedSession, setSelectedSession] = useState(null)
  const [modalViewMode, setModalViewMode] = useState("general")
  const [filteredEvaluationParameters, setFilteredEvaluationParameters] = useState({})

  // --- 1. Load html2pdf script dynamically ---
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js";
    script.async = true;
    document.body.appendChild(script);
    fetchAllData();
    return () => { document.body.removeChild(script); }
  }, [])

  // --- 2. Fetch Data ---
  const fetchAllData = async () => {
    setIsLoading(true)
    try {
      const parametersRes = await axios.get(`${API_BASE_URL}/job-postings/business-028/evaluation-parameters/`)
      const allParametersData = parametersRes.data || []
      const allParametersArray = Array.isArray(allParametersData) ? allParametersData : allParametersData.results || []
      setEvaluationParameters(allParametersArray)

      const [generalRes, paramRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/interview-evaluations/`),
        axios.get(`${API_BASE_URL}/evaluations/`),
      ])

      const generalList = generalRes.data || []
      const safeGeneralList = Array.isArray(generalList) ? generalList : generalList.results || []
      const paramList = paramRes.data || []
      const safeParamList = Array.isArray(paramList) ? paramList : paramList.results || []

      processAndMergeData(safeGeneralList, safeParamList)
    } catch (error) {
      console.error("Error fetching data:", error)
      Swal.fire("Error", "Could not fetch evaluation data.", "error")
    } finally {
      setIsLoading(false)
    }
  }

  // --- 3. Process & Merge Logic ---
  const processAndMergeData = (generalList, paramList) => {
    const sessionMap = new Map()
    const getKey = (item) => {
      const jobId = item.job_posting || item.job_posting_id || item.linked_posting_id || "unknown-job"
      const name = item.candidate_name || "unknown-candidate"
      return `${name}|${jobId}`
    }

    generalList.forEach((item) => {
      if (item.candidate_name === "N/A - Template") return
      const key = getKey(item)
      if (!sessionMap.has(key)) {
        sessionMap.set(key, {
          id: key,
          candidate_name: item.candidate_name,
          candidate_email: item.candidate_email,
          job_title: item.job_title || "N/A",
          job_type: item.job_type,
          job_posting_id: item.job_posting,
          date: item.evaluation_date,
          interview_id: item.id || item.interview_id, 
          general_data: item,
          parameter_data: null,
        })
      } else {
        const session = sessionMap.get(key)
        session.general_data = item
      }
    })

    paramList.forEach((item) => {
      const key = getKey(item)
      if (sessionMap.has(key)) {
        const session = sessionMap.get(key)
        session.parameter_data = item
        if (item.interview) session.interview_id = item.interview
      } else {
        sessionMap.set(key, {
          id: key,
          candidate_name: item.candidate_name,
          candidate_email: item.candidate_email || "N/A",
          job_title: item.job_title || "N/A",
          job_type: item.posting_type || "N/A",
          job_posting_id: item.posting_id || item.job_posting || "N/A",
          date: item.interview_date || item.created_at,
          interview_id: item.interview, 
          general_data: null,
          parameter_data: item,
        })
      }
    })

    const mergedArray = Array.from(sessionMap.values())
    setMergedEvaluations(mergedArray)
    groupDataByCandidate(mergedArray)
  }

  const groupDataByCandidate = (data) => {
    const groups = data.reduce((acc, session) => {
      const name = session.candidate_name
      if (!acc[name]) {
        acc[name] = { candidate_name: name, email: session.candidate_email, sessions: [] }
      }
      acc[name].sessions.push(session)
      return acc
    }, {})
    setGroupedCandidates(groups)
  }

  // --- 4. Filtering & Pagination ---
  const candidateNames = Object.keys(groupedCandidates).filter(
    (name) =>
      name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      groupedCandidates[name].email?.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  const totalPages = Math.ceil(candidateNames.length / itemsPerPage)
  const paginatedNames = candidateNames.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A"
    return new Date(dateStr).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
  }

  const handleViewDetails = (session) => {
    setSelectedSession(session)
    setModalViewMode("general")
    fetchFilteredDataForJob(session)
  }

  const fetchFilteredDataForJob = async (session) => {
    try {
      const jobPostingId = session.job_posting_id
      const interviewId = session.interview_id; 

      let detailedParamData = session.parameter_data || {}; 
      if (interviewId) {
          try {
              const specificUrl = `${API_BASE_URL}/interviews/${interviewId}/submit-evaluation/`
              const response = await axios.get(specificUrl)
              detailedParamData = { ...detailedParamData, ...response.data }
          } catch (err) { console.warn("Could not fetch detailed evaluation:", err) }
      } 

      setSelectedSession((prev) => ({ ...prev, parameter_data: detailedParamData }))

      if (Array.isArray(evaluationParameters) && evaluationParameters.length > 0) {
        const matchedParameters = evaluationParameters.filter(param => param.job_posting === jobPostingId)
        const groupedParams = matchedParameters.reduce((acc, param) => {
          const area = param.area || "GENERAL"
          if (!acc[area]) acc[area] = []
          acc[area].push(param)
          return acc
        }, {})
        setFilteredEvaluationParameters(groupedParams)
      }
    } catch (error) { console.error("Error fetching filtered data:", error) }
  }

  // --- 5. PDF Generation Logic ---
  const handleDownloadPDF = () => {
    if (!reportRef.current) return;
    
    // Check if the global html2pdf library is loaded
    if (typeof window.html2pdf === 'undefined') {
        Swal.fire("Error", "PDF library is still loading. Please try again in a moment.", "info");
        return;
    }

    const element = reportRef.current;
    const fileName = `${selectedSession?.candidate_name || 'Evaluation'}_Report.pdf`;

    const opt = {
      margin: 10,
      filename: fileName,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, letterRendering: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };

    // Run the conversion
    window.html2pdf().set(opt).from(element).save();
  };

  const renderGeneralTable = (details) => {
    if (!details || details.length === 0) return <p className="text-muted">No general questions recorded.</p>
    return (
      <Table bordered hover>
        <thead className="table-light">
          <tr>
            <th style={{ width: "70%" }}>Question</th>
            <th className="text-center">Rating</th>
          </tr>
        </thead>
        <tbody>
          {details.map((d, i) => (
            <tr key={i}>
              <td className="justified-text">{d.question_text}</td>
              <td className="text-center">
                {d.answer_value ? <Badge bg="info">{d.answer_value}/5</Badge> : <span className="text-muted">-</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    )
  }

  const renderParameterPDF = (paramData) => {
    const responses = paramData.responses || []
    
    const renderRow = (param) => {
        const resp = responses.find(r => r.parameter === param.id);
        return (
            <tr key={param.id}>
                <td className="col-competency">{param.parameter_text}</td>
                <td className="col-desc">{param.description}</td>
                <td className="col-rating">
                    {resp?.rating ? <div style={{ display: 'inline-block', border: '1px solid #000', borderRadius: '50%', width: '26px', height: '26px', textAlign: 'center', lineHeight: '24px', fontWeight: 'bold' }}>{resp.rating}</div> : ""}
                </td>
                <td className="col-comments" style={{ fontSize: '13px' }}>{resp?.comments || "No comments"}</td>
            </tr>
        )
    }

    const rec = paramData.overall_recommendation || "";
    const isStrongly = rec.toLowerCase().includes("strongly recommend") || rec === "STRONGLY_RECOMMEND";
    const isRec = !isStrongly && (rec.toLowerCase().includes("recommend") || rec === "RECOMMEND") && !rec.toLowerCase().includes("not");
    const isNeutral = rec.toLowerCase().includes("neutral") || rec.toLowerCase().includes("hold") || rec === "NEUTRAL";
    const isNot = rec.toLowerCase().includes("not recommend") || rec === "DO_NOT_RECOMMEND";

    const strengths = Array.isArray(paramData.strengths) ? paramData.strengths : [paramData.strengths || ""];
    const developments = Array.isArray(paramData.development_areas) ? paramData.development_areas : [paramData.development_areas || ""];

    return (
      <div className="pdf-viewer-container">
        <div className="pdf-paper" ref={reportRef}>
            <div className="pdf-header">
                <h2>Post-Interview Employee Evaluation Form</h2>
                <div className="info-line"><div className="info-label">Candidate Name:</div><div className="info-value">{paramData.candidate_name || selectedSession?.candidate_name}</div></div>
                <div className="info-line"><div className="info-label">Position Applied For:</div><div className="info-value">{selectedSession?.job_title}</div></div>
                <div className="info-line"><div className="info-label">Interview Date:</div><div className="info-value">{formatDate(paramData.display_date || selectedSession?.date)}</div></div>
                <div className="info-line"><div className="info-label">Interviewer(s):</div><div className="info-value">{paramData.interviewer || "N/A"}</div></div>
                <hr style={{ borderTop: "1px solid #ccc", marginTop: "20px" }} />
            </div>

            <div className="section-header">1. Core Competencies</div>
            <table className="pdf-table">
                <thead><tr><th className="col-competency">Competency</th><th className="col-desc">Description</th><th className="col-rating">Rating</th><th className="col-comments">Comments</th></tr></thead>
                <tbody>
                    {filteredEvaluationParameters["CORE_COMPETENCIES"]?.map(renderRow)}
                    {(!filteredEvaluationParameters["CORE_COMPETENCIES"] || filteredEvaluationParameters["CORE_COMPETENCIES"].length === 0) && <tr><td colSpan="4" className="text-center">N/A</td></tr>}
                </tbody>
            </table>

            <div className="section-header">2. Overall Recommendation</div>
            <div className="checkbox-group">
                <div className="checkbox-item"><span className="box-icon">{isStrongly ? "☒" : "☐"}</span> Strongly Recommend</div>
                <div className="checkbox-item"><span className="box-icon">{isRec ? "☒" : "☐"}</span> Recommend</div>
                <div className="checkbox-item"><span className="box-icon">{isNeutral ? "☒" : "☐"}</span> Neutral / Hold</div>
                <div className="checkbox-item"><span className="box-icon">{isNot ? "☒" : "☐"}</span> Do Not Recommend</div>
            </div>

            <div className="list-section">
                <strong>• Top Strengths:</strong>
                {strengths.slice(0, 3).map((s, i) => (<div className="list-line" key={i}><div className="list-number">{i+1}.</div><div className="list-content">{s}</div></div>))}
            </div>

            <div className="list-section">
                <strong>• Key Development Areas:</strong>
                {developments.slice(0, 3).map((d, i) => (<div className="list-line" key={i}><div className="list-number">{i+1}.</div><div className="list-content">{d}</div></div>))}
            </div>

            <div className="list-section">
                <strong>• Final Comments:</strong>
                <div style={{ marginTop: "5px", border: "1px solid #ccc", minHeight: "60px", padding: "10px" }}>{paramData.final_comments}</div>
            </div>

            <div className="signature-section">
                <div>Interviewer’s Signature: ________________________</div>
                <div>Date: <u>{formatDate(new Date())}</u></div>
            </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <CustomStyles />
      <DashNavbar />
      <div className="wrapper d-flex interviewer-feedback-page">
        <Sidebar />
        <div className="main-content flex-grow-1 p-3 p-md-4">
          <Card className="shadow-sm mb-4 h-100">
            <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center border-bottom">
              <h4 className="mb-0 text-primary fw-bold"><i className="bi bi-clipboard-data me-2"></i>Feedback Evaluations</h4>
              <InputGroup style={{ maxWidth: "300px" }}>
                <InputGroup.Text className="bg-light"><i className="bi bi-search"></i></InputGroup.Text>
                <Form.Control type="search" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
              </InputGroup>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="align-middle mb-0">
                  <thead className="table-light">
                    <tr><th className="ps-4">#</th><th>Candidate Name</th><th>Email</th><th className="text-center">Interviews</th><th className="text-center">Action</th></tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <tr><td colSpan="5" className="text-center py-5"><Spinner animation="border" variant="primary" /></td></tr>
                    ) : paginatedNames.map((name, idx) => {
                        const group = groupedCandidates[name]
                        const isExpanded = expandedCandidate === name
                        return (
                          <React.Fragment key={name}>
                            <tr className={isExpanded ? "table-active" : ""}>
                              <td className="ps-4 text-muted">{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                              <td className="fw-bold text-dark">{name}</td>
                              <td className="text-muted">{group.email}</td>
                              <td className="text-center"><Badge bg="secondary" pill>{group.sessions.length}</Badge></td>
                              <td className="text-center"><Button variant={isExpanded ? "primary" : "outline-primary"} size="sm" onClick={() => setExpandedCandidate(isExpanded ? null : name)}>{isExpanded ? <i className="bi bi-chevron-up"></i> : <i className="bi bi-chevron-down"></i>}</Button></td>
                            </tr>
                            {isExpanded && (
                              <tr className="sub-table-row">
                                <td colSpan="5">
                                  <div className="sub-table-container">
                                    <Table className="sub-table mb-0">
                                      <thead><tr><th>Title</th><th>Type</th><th>ID</th><th>Date</th><th>Data</th><th className="text-center">Action</th></tr></thead>
                                      <tbody>
                                        {group.sessions.map((session) => (
                                          <tr key={session.id}>
                                            <td>{session.job_title}</td>
                                            <td><Badge bg="light" text="dark" className="border">{session.job_type || "N/A"}</Badge></td>
                                            <td className="font-monospace text-muted small">{session.job_posting_id}</td>
                                            <td>{formatDate(session.date)}</td>
                                            <td>{session.general_data && <Badge bg="info" className="me-1">General</Badge>}{session.parameter_data && <Badge bg="success">Params</Badge>}</td>
                                            <td className="text-center"><Button variant="outline-dark" size="sm" onClick={() => handleViewDetails(session)}><i className="bi bi-eye me-1"></i> View Report</Button></td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </Table>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        )
                    })}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
            {totalPages > 1 && (
              <Card.Footer className="bg-white d-flex justify-content-end py-3">
                <Pagination>
                  <Pagination.Prev onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} />
                  {[...Array(totalPages)].map((_, i) => (<Pagination.Item key={i + 1} active={i + 1 === currentPage} onClick={() => setCurrentPage(i + 1)}>{i + 1}</Pagination.Item>))}
                  <Pagination.Next onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} />
                </Pagination>
              </Card.Footer>
            )}
          </Card>
        </div>
      </div>

      <Modal show={selectedSession !== null} onHide={() => setSelectedSession(null)} size="xl" centered scrollable>
        <Modal.Header closeButton className="bg-light"><Modal.Title className="h5 text-primary">Evaluation Details</Modal.Title></Modal.Header>
        <Modal.Body className="p-0" style={{ background: modalViewMode === "parameters" ? "#525659" : "white" }}>
          {selectedSession && (
            <div className="d-flex flex-column h-100">
              <div className="px-4 py-2 bg-white sticky-top border-bottom">
                <ButtonGroup className="w-100 shadow-sm">
                  <Button variant={modalViewMode === "general" ? "primary" : "outline-light text-dark border"} onClick={() => setModalViewMode("general")} disabled={!selectedSession.general_data}>General Questions</Button>
                  <Button variant={modalViewMode === "parameters" ? "primary" : "outline-light text-dark border"} onClick={() => setModalViewMode("parameters")} disabled={!selectedSession.parameter_data}>Evaluation Form</Button>
                </ButtonGroup>
              </div>
              <div className="flex-grow-1">
                {modalViewMode === "general" && selectedSession.general_data ? (
                  <div className="p-4" ref={reportRef}>
                    <div className="pdf-export-mode d-none d-print-block"><h3>General Evaluation: {selectedSession.candidate_name}</h3></div>
                    <h5 className="mb-3 text-secondary border-bottom pb-2">General Screening Questions</h5>
                    {renderGeneralTable(selectedSession.general_data.evaluation_details)}
                  </div>
                ) : modalViewMode === "parameters" && selectedSession.parameter_data ? (
                  renderParameterPDF(selectedSession.parameter_data)
                ) : <div className="text-center py-5 text-muted"><p>No data available.</p></div>}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-light">
          <Button variant="secondary" onClick={() => setSelectedSession(null)}>Close</Button>
          <Button variant="primary" onClick={handleDownloadPDF}>
            <i className="bi bi-file-earmark-pdf me-2"></i>Save as PDF
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default InterviewerFeedback


// "use client"
// import React, { useState, useEffect } from "react"
// import axios from "axios"
// import DashNavbar from "./DashNavbar"
// import Sidebar from "./Sidebar"
// import { Pagination, Modal, Card, Form, InputGroup, Button, Table, Spinner, ButtonGroup, Badge } from "react-bootstrap"
// import Swal from "sweetalert2"

// // --- Custom CSS ---
// const CustomStyles = () => (
//   <style>{`
//     /* Main Layout */
//     .interviewer-feedback-page .main-content { background-color: #f8f9fa; }
//     .interviewer-feedback-page.wrapper { display: flex; height: calc(100vh - 60px); }
//     .interviewer-feedback-page .main-content { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; }
    
//     /* Table & Card Styling */
//     .interviewer-feedback-page .card { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; }
//     .interviewer-feedback-page .table-responsive { flex-grow: 1; overflow-y: auto; padding: 1.5rem; padding-top: 0; }
//     .sub-table-container { padding: 1rem; border-top: 2px solid #0d6efd; background: white; margin: 10px; border-radius: 5px; box-shadow: 0 2px 4px rgba(0,0,0,0.05); }

//     /* --- PDF/Form REPLICATION STYLES --- */
//     .pdf-viewer-container {
//         background-color: #525659; /* Acrobat Reader Grey Background */
//         padding: 2rem;
//         display: flex;
//         justify-content: center;
//         min-height: 100%;
//     }

//     .pdf-paper {
//         background-color: white;
//         width: 100%;
//         max-width: 850px; /* A4 width approx */
//         min-height: 1100px;
//         padding: 40px 50px;
//         box-shadow: 0 0 10px rgba(0,0,0,0.3);
//         font-family: "Times New Roman", Times, serif; /* Match PDF Font */
//         color: #000;
//     }

//     /* PDF Header */
//     .pdf-header h2 {
//         font-size: 24px;
//         font-weight: bold;
//         margin-bottom: 25px;
//         color: #000;
//     }
    
//     .info-line {
//         display: flex;
//         margin-bottom: 8px;
//         align-items: flex-end;
//     }
//     .info-label {
//         font-weight: bold;
//         min-width: 160px;
//         font-size: 16px;
//     }
//     .info-value {
//         flex-grow: 1;
//         border-bottom: 1px solid #333;
//         padding-left: 10px;
//         font-size: 16px;
//         min-height: 24px;
//     }

//     /* PDF Sections */
//     .section-header {
//         font-size: 18px;
//         font-weight: bold;
//         margin-top: 30px;
//         margin-bottom: 10px;
//         display: flex;
//         align-items: center;
//         gap: 10px;
//     }
//     .section-header i { font-size: 18px; }
//     .rating-instruction { font-size: 14px; font-style: italic; margin-bottom: 15px; color: #444; }

//     /* PDF Tables */
//     .pdf-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
//     .pdf-table th {
//         text-align: left;
//         font-weight: bold;
//         padding: 5px 0;
//         border-bottom: 1px solid #000; /* Minimal header border */
//         font-size: 15px;
//     }
//     .pdf-table td {
//         padding: 10px 5px;
//         vertical-align: top;
//         font-size: 14px;
//         border-bottom: 1px solid #e0e0e0; /* Faint row lines */
//         text-align: justify; /* Justify all cells by default */
//     }
    
//     /* Column Specific Styles */
//     .col-competency { 
//         width: 25%; 
//         font-weight: bold; 
//         text-align: justify; /* Justify Competency/Parameter Names */
//     }
//     .col-desc { 
//         width: 40%; 
//         text-align: justify; /* Justify Description */
//     }
//     .col-rating { 
//         width: 10%; 
//         text-align: center !important; /* Keep Badge Centered for UX */
//     }
//     .col-comments { 
//         width: 25%; 
//         text-align: justify; /* Justify Comments */
//     }

//     /* Recommendation Section */
//     .checkbox-group { display: flex; flex-direction: column; gap: 8px; margin-top: 10px; }
//     .checkbox-item { display: flex; align-items: center; gap: 10px; font-size: 15px; }
//     .box-icon { font-size: 18px; line-height: 1; }

//     .list-section { margin-top: 15px; }
//     .list-line {
//         display: flex;
//         margin-bottom: 5px;
//     }
//     .list-number { width: 20px; font-weight: bold; }
//     .list-content { flex-grow: 1; border-bottom: 1px solid #ccc; min-height: 20px; }

//     /* Signature */
//     .signature-section { margin-top: 50px; border-top: 1px solid #ccc; padding-top: 10px; display: flex; justify-content: space-between; font-size: 15px; }

//     /* Print Styling */
//     @media print {
//         body * { visibility: hidden; }
//         .modal-content, .modal-content * { visibility: visible; }
//         .modal-content { position: absolute; left: 0; top: 0; width: 100%; box-shadow: none !important; border: none !important; }
//         .pdf-viewer-container { background: white; padding: 0; }
//         .pdf-paper { box-shadow: none; width: 100%; max-width: 100%; padding: 0; }
//         .btn-close, .modal-footer, .modal-header { display: none !important; }
//     }
//   `}</style>
// )

// const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com"

// function InterviewerFeedback() {
//   // Data Stores
//   const [mergedEvaluations, setMergedEvaluations] = useState([])
//   const [groupedCandidates, setGroupedCandidates] = useState({})
//   const [evaluationParameters, setEvaluationParameters] = useState([]) 

//   // UI State
//   const [searchTerm, setSearchTerm] = useState("")
//   const [currentPage, setCurrentPage] = useState(1)
//   const [itemsPerPage, setItemsPerPage] = useState(10)
//   const [isLoading, setIsLoading] = useState(false)
//   const [expandedCandidate, setExpandedCandidate] = useState(null)

//   // Modal State
//   const [selectedSession, setSelectedSession] = useState(null)
//   const [modalViewMode, setModalViewMode] = useState("general") // 'general' or 'parameters'

//   // Filtered Data State
//   const [filteredEvaluationParameters, setFilteredEvaluationParameters] = useState({})

//   // --- 1. Fetch Data ---
//   const fetchAllData = async () => {
//     setIsLoading(true)
//     try {
//       const parametersRes = await axios.get(`${API_BASE_URL}/job-postings/business-028/evaluation-parameters/`)
//       const allParametersData = parametersRes.data || []
//       const allParametersArray = Array.isArray(allParametersData) ? allParametersData : allParametersData.results || []

//       setEvaluationParameters(allParametersArray)

//       const [generalRes, paramRes] = await Promise.all([
//         axios.get(`${API_BASE_URL}/interview-evaluations/`),
//         axios.get(`${API_BASE_URL}/evaluations/`),
//       ])

//       const generalList = generalRes.data || []
//       const safeGeneralList = Array.isArray(generalList) ? generalList : generalList.results || []

//       const paramList = paramRes.data || []
//       const safeParamList = Array.isArray(paramList) ? paramList : paramList.results || []

//       processAndMergeData(safeGeneralList, safeParamList)
//     } catch (error) {
//       console.error("Error fetching data:", error)
//       Swal.fire("Error", "Could not fetch evaluation data. Please check API connections.", "error")
//     } finally {
//       setIsLoading(false)
//     }
//   }

//   useEffect(() => {
//     fetchAllData()
//   }, [])

//   // --- 2. Process & Merge Logic ---
//   const processAndMergeData = (generalList, paramList) => {
//     const sessionMap = new Map()

//     const getKey = (item) => {
//       const jobId = item.job_posting || item.job_posting_id || item.linked_posting_id || "unknown-job"
//       const name = item.candidate_name || "unknown-candidate"
//       return `${name}|${jobId}`
//     }

//     generalList.forEach((item) => {
//       if (item.candidate_name === "N/A - Template") return

//       const key = getKey(item)
//       if (!sessionMap.has(key)) {
//         sessionMap.set(key, {
//           id: key,
//           candidate_name: item.candidate_name,
//           candidate_email: item.candidate_email,
//           job_title: item.job_title || "N/A",
//           job_type: item.job_type,
//           job_posting_id: item.job_posting,
//           date: item.evaluation_date,
//           // Capture Interview ID for General type if available
//           interview_id: item.id || item.interview_id, 
//           general_data: item,
//           parameter_data: null,
//         })
//       } else {
//         const session = sessionMap.get(key)
//         session.general_data = item
//         if (!session.job_type) session.job_type = item.job_type
//         if (!session.job_posting_id) session.job_posting_id = item.job_posting
//       }
//     })

//     paramList.forEach((item) => {
//       const key = getKey(item)

//       if (sessionMap.has(key)) {
//         const session = sessionMap.get(key)
//         session.parameter_data = item
//         // Capture Interview ID from Param type (Preferred)
//         if (item.interview) session.interview_id = item.interview
//         if (item.job_title) session.job_title = item.job_title
//       } else {
//         sessionMap.set(key, {
//           id: key,
//           candidate_name: item.candidate_name,
//           candidate_email: item.candidate_email || "N/A",
//           job_title: item.job_title || "N/A",
//           job_type: item.posting_type || "N/A",
//           job_posting_id: item.posting_id || item.job_posting || "N/A",
//           date: item.interview_date || item.created_at,
//           // Capture Interview ID
//           interview_id: item.interview, 
//           general_data: null,
//           parameter_data: item,
//         })
//       }
//     })

//     const mergedArray = Array.from(sessionMap.values())
//     setMergedEvaluations(mergedArray)
//     groupDataByCandidate(mergedArray)
//   }

//   const groupDataByCandidate = (data) => {
//     const groups = data.reduce((acc, session) => {
//       const name = session.candidate_name
//       if (!acc[name]) {
//         acc[name] = {
//           candidate_name: name,
//           email: session.candidate_email,
//           sessions: [],
//         }
//       }
//       acc[name].sessions.push(session)
//       return acc
//     }, {})
//     setGroupedCandidates(groups)
//   }

//   // --- 3. Filtering & Pagination ---
//   const candidateNames = Object.keys(groupedCandidates).filter(
//     (name) =>
//       name.toLowerCase().includes(searchTerm.toLowerCase()) ||
//       groupedCandidates[name].email?.toLowerCase().includes(searchTerm.toLowerCase()),
//   )

//   const totalPages = Math.ceil(candidateNames.length / itemsPerPage)
//   const paginatedNames = candidateNames.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

//   // --- 4. Helpers ---
//   const formatDate = (dateStr) => {
//     if (!dateStr) return "N/A"
//     return new Date(dateStr).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
//   }

//   const handleViewDetails = (session) => {
//     setSelectedSession(session)
//     setModalViewMode("general")
//     fetchFilteredDataForJob(session)
//   }

//   const fetchFilteredDataForJob = async (session) => {
//     try {
//       const jobPostingId = session.job_posting_id
//       const businessOpportunity = session.general_data?.business_opportunity || null
//       const technicalConsultant = session.general_data?.technical_consultant || null
      
//       // Get the interview ID captured in processAndMergeData
//       const interviewId = session.interview_id; 

//       // 1. Fetch General Data
//       const generalRes = await axios.get(`${API_BASE_URL}/interview-evaluations/`)
//       const generalList = Array.isArray(generalRes.data) ? generalRes.data : generalRes.data.results || []
//       const filteredGeneral = generalList.find((item) => item.job_posting === jobPostingId)

//       // 2. Fetch Detailed Parameter Data using Interview ID
//       // Start with existing parameter_data (which has 'interviewer')
//       let detailedParamData = session.parameter_data || {}; 
      
//       if (interviewId) {
//           try {
//               // API Call to get specific rating/comments
//               const specificUrl = `${API_BASE_URL}/interviews/${interviewId}/submit-evaluation/`
//               console.log("Fetching details from:", specificUrl)
//               const response = await axios.get(specificUrl)
              
//               // Merge existing info (like interviewer name) with new detailed info (like comments/ratings)
//               detailedParamData = { ...detailedParamData, ...response.data }
//           } catch (err) {
//               console.warn("Could not fetch detailed evaluation:", err)
//               // If detailed fetch fails, detailedParamData remains as session.parameter_data
//           }
//       } 

//       setSelectedSession((prev) => ({
//         ...prev,
//         general_data: filteredGeneral,
//         parameter_data: detailedParamData, 
//       }))

//       if (Array.isArray(evaluationParameters) && evaluationParameters.length > 0) {
//         const matchedParameters = evaluationParameters.filter((param) => {
//           const jobMatch = param.job_posting === jobPostingId
//           const businessMatch = businessOpportunity === null || param.business_opportunity === businessOpportunity
//           const consultantMatch = technicalConsultant === null || param.technical_consultant === technicalConsultant
//           return jobMatch && businessMatch && consultantMatch
//         })

//         const groupedParams = matchedParameters.reduce((acc, param) => {
//           const area = param.area || "GENERAL"
//           if (!acc[area]) acc[area] = []
//           acc[area].push(param)
//           return acc
//         }, {})

//         setFilteredEvaluationParameters(groupedParams)
//       }
//     } catch (error) {
//       console.error("Error fetching filtered data:", error)
//     }
//   }

//   // --- Render General Questions (Keeping Bootstrap Table) ---
//   const renderGeneralTable = (details) => {
//     if (!details || details.length === 0) return <p className="text-muted">No general questions recorded.</p>
//     return (
//       <Table bordered hover>
//         <thead className="table-light">
//           <tr>
//             <th style={{ width: "70%" }}>Question</th>
//             <th className="text-center">Rating</th>
//           </tr>
//         </thead>
//         <tbody>
//           {details.map((d, i) => (
//             <tr key={i}>
//               <td className="justified-text">{d.question_text}</td>
//               <td className="text-center">
//                 {d.answer_value ? <Badge bg="info">{d.answer_value}/5</Badge> : <span className="text-muted">-</span>}
//               </td>
//             </tr>
//           ))}
//         </tbody>
//       </Table>
//     )
//   }

//   // --- Render PDF Replica (The core request) ---
//   const renderParameterPDF = (paramData) => {
//     // paramData is a merge of existing session data and new detailed responses
//     const responses = paramData.responses || []
    
//     // Fallback if specific filtering isn't ready
//     const parametersToUse = Object.keys(filteredEvaluationParameters).length > 0 ? filteredEvaluationParameters : {
//         "CORE_COMPETENCIES": [], "BEHAVIORAL": [], "ROLE_FIT": []
//     }

//     // Helper to find the specific response object for a given parameter ID
//     const getResponse = (paramId) => {
//         // We match the 'parameter' field in the response object with the 'id' of the parameter definition
//         return responses.find(r => r.parameter === paramId);
//     }

//     // Helper to render a table row
//     const renderRow = (param) => {
//         const resp = getResponse(param.id);
        
//         // Rating Badge Style
//         const getRatingContent = (rating) => {
//              if (!rating) return "";
//              return (
//                  <div style={{
//                     display: 'inline-block',
//                     border: '1px solid #000',
//                     borderRadius: '50%',
//                     width: '26px', 
//                     height: '26px', 
//                     textAlign: 'center',
//                     lineHeight: '24px',
//                     fontWeight: 'bold'
//                  }}>
//                     {rating}
//                  </div>
//              )
//         }

//         return (
//             <tr key={param.id}>
//                 <td className="col-competency">{param.parameter_text}</td>
//                 <td className="col-desc">{param.description}</td>
//                 {/* Display Rating */}
//                 <td className="col-rating">
//                     {getRatingContent(resp?.rating)}
//                 </td>
//                 {/* Display Comments */}
//                 <td className="col-comments" style={{ fontSize: '13px', fontStyle: resp?.comments ? 'normal' : 'italic' }}>
//                     {resp?.comments || "No comments"}
//                 </td>
//             </tr>
//         )
//     }

//     // Recommendation logic
//     const rec = paramData.overall_recommendation || "";
//     const isStrongly = rec.toLowerCase().includes("strongly recommend") || rec === "STRONGLY_RECOMMEND";
//     const isRec = !isStrongly && (rec.toLowerCase().includes("recommend") || rec === "RECOMMEND") && !rec.toLowerCase().includes("not");
//     const isNeutral = rec.toLowerCase().includes("neutral") || rec.toLowerCase().includes("hold") || rec === "NEUTRAL";
//     const isNot = rec.toLowerCase().includes("not recommend") || rec === "DO_NOT_RECOMMEND";

//     // Arrays for lists
//     const strengths = Array.isArray(paramData.strengths) ? paramData.strengths : [paramData.strengths || ""];
//     const developments = Array.isArray(paramData.development_areas) ? paramData.development_areas : [paramData.development_areas || ""];

//     return (
//       <div className="pdf-viewer-container">
//         <div className="pdf-paper">
            
//             {/* Header */}
//             <div className="pdf-header">
//                 <h2>Post-Interview Employee Evaluation Form</h2>
//                 <div className="info-line">
//                     <div className="info-label">Candidate Name:</div>
//                     <div className="info-value">{paramData.candidate_name || selectedSession?.candidate_name}</div>
//                 </div>
//                 <div className="info-line">
//                     <div className="info-label">Position Applied For:</div>
//                     <div className="info-value">{selectedSession?.job_title}</div>
//                 </div>
//                 <div className="info-line">
//                     <div className="info-label">Department:</div>
//                     <div className="info-value">{paramData.department || "N/A"}</div>
//                 </div>
//                 <div className="info-line">
//                     <div className="info-label">Interview Date:</div>
//                     <div className="info-value">{formatDate(paramData.display_date || selectedSession?.date)}</div>
//                 </div>
//                 <div className="info-line">
//                     <div className="info-label">Interviewer(s):</div>
//                     <div className="info-value">{paramData.interviewer || "N/A"}</div>
//                 </div>
//                 <hr style={{ borderTop: "1px solid #ccc", marginTop: "20px" }} />
//             </div>

//             {/* Section 1: Core Competencies */}
//             <div className="section-header">
//                 <i className="bi bi-search"></i> 1. Core Competencies
//             </div>
//             <div className="rating-instruction">Rate 1 – 5 (1 = Poor, 5 = Excellent) and add comments/evidence:</div>
            
//             <table className="pdf-table">
//                 <thead>
//                     <tr>
//                         <th className="col-competency">Competency</th>
//                         <th className="col-desc">Description</th>
//                         <th className="col-rating">Rating</th>
//                         <th className="col-comments">Comments / Evidence</th>
//                     </tr>
//                 </thead>
//                 <tbody>
//                     {filteredEvaluationParameters["CORE_COMPETENCIES"]?.map(renderRow)}
//                     {(!filteredEvaluationParameters["CORE_COMPETENCIES"] || filteredEvaluationParameters["CORE_COMPETENCIES"].length === 0) && (
//                          <tr><td colSpan="4" className="text-center text-muted">No Core Competencies defined.</td></tr>
//                     )}
//                 </tbody>
//             </table>

//             {/* Section 2: Behavioral */}
//             <div className="section-header">
//                 <i className="bi bi-file-text"></i> 2. Additional Behavioral & Functional Parameters
//             </div>
//             <table className="pdf-table">
//                 <thead>
//                     <tr>
//                         <th className="col-competency">Parameter</th>
//                         <th className="col-desc">Description</th>
//                         <th className="col-rating">Rating</th>
//                         <th className="col-comments">Comments / Evidence</th>
//                     </tr>
//                 </thead>
//                 <tbody>
//                     {filteredEvaluationParameters["BEHAVIORAL"]?.map(renderRow)}
//                     {(!filteredEvaluationParameters["BEHAVIORAL"] || filteredEvaluationParameters["BEHAVIORAL"].length === 0) && (
//                          <tr><td colSpan="4" className="text-center text-muted">No Behavioral Parameters defined.</td></tr>
//                     )}
//                 </tbody>
//             </table>

//             {/* Section 3: Role Specific */}
//             <div className="section-header">
//                 <i className="bi bi-check-square"></i> 3. Role-Specific & Cultural Fit
//             </div>
//             <table className="pdf-table">
//                 <thead>
//                     <tr>
//                         <th className="col-competency">Attribute</th>
//                         <th className="col-desc">Description</th> {/* Using description column for spacing consistency */}
//                         <th className="col-rating">Rating</th>
//                         <th className="col-comments">Comments</th>
//                     </tr>
//                 </thead>
//                 <tbody>
//                     {filteredEvaluationParameters["ROLE_FIT"]?.map(renderRow)}
//                      {(!filteredEvaluationParameters["ROLE_FIT"] || filteredEvaluationParameters["ROLE_FIT"].length === 0) && (
//                          <tr><td colSpan="4" className="text-center text-muted">No Role-Specific Parameters defined.</td></tr>
//                     )}
//                 </tbody>
//             </table>

//             {/* Section 4: Overall Recommendation */}
//             <div className="section-header">
//                 <i className="bi bi-flag-fill"></i> 4. Overall Recommendation
//             </div>
            
//             <div className="list-section">
//                 <strong>• Overall Suitability:</strong>
//                 <div className="checkbox-group">
//                     <div className="checkbox-item">
//                         <span className="box-icon">{isStrongly ? "☒" : "☐"}</span> Strongly Recommend
//                     </div>
//                     <div className="checkbox-item">
//                         <span className="box-icon">{isRec ? "☒" : "☐"}</span> Recommend
//                     </div>
//                     <div className="checkbox-item">
//                         <span className="box-icon">{isNeutral ? "☒" : "☐"}</span> Neutral / Hold
//                     </div>
//                     <div className="checkbox-item">
//                         <span className="box-icon">{isNot ? "☒" : "☐"}</span> Do Not Recommend
//                     </div>
//                 </div>
//             </div>

//             <div className="list-section">
//                 <strong>• Top Strengths:</strong>
//                 <div className="mt-2">
//                     {[0, 1, 2].map(i => (
//                         <div className="list-line" key={i}>
//                             <div className="list-number">{i+1}.</div>
//                             <div className="list-content">{strengths[i] || ""}</div>
//                         </div>
//                     ))}
//                 </div>
//             </div>

//             <div className="list-section">
//                 <strong>• Key Development Areas:</strong>
//                 <div className="mt-2">
//                     {[0, 1, 2].map(i => (
//                         <div className="list-line" key={i}>
//                             <div className="list-number">{i+1}.</div>
//                             <div className="list-content">{developments[i] || ""}</div>
//                         </div>
//                     ))}
//                 </div>
//             </div>

//             <div className="list-section">
//                 <strong>• Final Comments:</strong>
//                 <div style={{ marginTop: "5px", border: "1px solid #ccc", minHeight: "80px", padding: "10px" }}>
//                     {paramData.final_comments}
//                 </div>
//             </div>

//             {/* Footer */}
//             <div className="signature-section">
//                 <div>Interviewer’s Signature: ________________________</div>
//                 <div>Date: <u>{formatDate(new Date())}</u></div>
//             </div>

//             <div className="text-muted text-center mt-5 small">
//                 Feel free to adjust any rating scales or add/remove parameters to suit your specific role or organizational priorities!
//             </div>
//         </div>
//       </div>
//     )
//   }

//   return (
//     <>
//       <CustomStyles />
//       <DashNavbar />
//       <div className="wrapper d-flex interviewer-feedback-page">
//         <Sidebar />
//         <div className="main-content flex-grow-1 p-3 p-md-4">
//           <Card className="shadow-sm mb-4 h-100">
//             <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center border-bottom">
//               <h4 className="mb-0 text-primary fw-bold">
//                 <i className="bi bi-clipboard-data me-2"></i>Feedback Evaluations
//               </h4>
//               <InputGroup style={{ maxWidth: "300px" }}>
//                 <InputGroup.Text className="bg-light">
//                   <i className="bi bi-search"></i>
//                 </InputGroup.Text>
//                 <Form.Control
//                   type="search"
//                   placeholder="Search candidates..."
//                   value={searchTerm}
//                   onChange={(e) => setSearchTerm(e.target.value)}
//                 />
//               </InputGroup>
//             </Card.Header>

//             <Card.Body className="p-0">
//               <div className="table-responsive">
//                 <Table hover className="align-middle mb-0">
//                   <thead className="table-light">
//                     <tr>
//                       <th className="ps-4">#</th>
//                       <th>Candidate Name</th>
//                       <th>Email</th>
//                       <th className="text-center">Interviews</th>
//                       <th className="text-center">Action</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {isLoading ? (
//                       <tr>
//                         <td colSpan="5" className="text-center py-5">
//                           <Spinner animation="border" variant="primary" />
//                         </td>
//                       </tr>
//                     ) : paginatedNames.length > 0 ? (
//                       paginatedNames.map((name, idx) => {
//                         const group = groupedCandidates[name]
//                         const isExpanded = expandedCandidate === name
//                         return (
//                           <React.Fragment key={name}>
//                             <tr className={isExpanded ? "table-active" : ""}>
//                               <td className="ps-4 text-muted">{(currentPage - 1) * itemsPerPage + idx + 1}</td>
//                               <td className="fw-bold text-dark">{name}</td>
//                               <td className="text-muted">{group.email}</td>
//                               <td className="text-center">
//                                 <Badge bg="secondary" pill>
//                                   {group.sessions.length}
//                                 </Badge>
//                               </td>
//                               <td className="text-center">
//                                 <Button
//                                   variant={isExpanded ? "primary" : "outline-primary"}
//                                   size="sm"
//                                   onClick={() => setExpandedCandidate(isExpanded ? null : name)}
//                                 >
//                                   {isExpanded ? (
//                                     <i className="bi bi-chevron-up"></i>
//                                   ) : (
//                                     <i className="bi bi-chevron-down"></i>
//                                   )}
//                                 </Button>
//                               </td>
//                             </tr>
//                             {isExpanded && (
//                               <tr className="sub-table-row">
//                                 <td colSpan="5">
//                                   <div className="sub-table-container fade-in">
//                                     <h6 className="mb-3 text-primary border-bottom pb-2">Interview History: {name}</h6>
//                                     <Table className="sub-table mb-0">
//                                       <thead>
//                                         <tr>
//                                           <th>Title</th>
//                                           <th>Opportunity Type</th>
//                                           <th>Opportunity ID</th>
//                                           <th>Date</th>
//                                           <th>Data Available</th>
//                                           <th className="text-center">Action</th>
//                                         </tr>
//                                       </thead>
//                                       <tbody>
//                                         {group.sessions.map((session) => (
//                                           <tr key={session.id}>
//                                             <td>{session.job_title}</td>
//                                             <td>
//                                               <Badge bg="light" text="dark" className="border">
//                                                 {session.job_type || "N/A"}
//                                               </Badge>
//                                             </td>
//                                             <td className="font-monospace text-muted small">
//                                               {session.job_posting_id}
//                                             </td>
//                                             <td>{formatDate(session.date)}</td>
//                                             <td>
//                                               {session.general_data && (
//                                                 <Badge bg="info" className="me-1">
//                                                   General
//                                                 </Badge>
//                                               )}
//                                               {(session.parameter_data || session.interview_id) && <Badge bg="success">Params</Badge>}
//                                             </td>
//                                             <td className="text-center">
//                                               <Button
//                                                 variant="outline-dark"
//                                                 size="sm"
//                                                 onClick={() => handleViewDetails(session)}
//                                               >
//                                                 <i className="bi bi-eye me-1"></i> View Report
//                                               </Button>
//                                             </td>
//                                           </tr>
//                                         ))}
//                                       </tbody>
//                                     </Table>
//                                   </div>
//                                 </td>
//                               </tr>
//                             )}
//                           </React.Fragment>
//                         )
//                       })
//                     ) : (
//                       <tr>
//                         <td colSpan="5" className="text-center py-5 text-muted">
//                           No evaluations found.
//                         </td>
//                       </tr>
//                     )}
//                   </tbody>
//                 </Table>
//               </div>
//             </Card.Body>

//             {totalPages > 1 && (
//               <Card.Footer className="bg-white d-flex justify-content-end py-3">
//                 <Pagination>
//                   <Pagination.Prev
//                     onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
//                     disabled={currentPage === 1}
//                   />
//                   {[...Array(totalPages)].map((_, i) => (
//                     <Pagination.Item key={i + 1} active={i + 1 === currentPage} onClick={() => setCurrentPage(i + 1)}>
//                       {i + 1}
//                     </Pagination.Item>
//                   ))}
//                   <Pagination.Next
//                     onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
//                     disabled={currentPage === totalPages}
//                   />
//                 </Pagination>
//               </Card.Footer>
//             )}
//           </Card>
//         </div>
//       </div>

//       {/* --- Details Modal --- */}
//       <Modal show={selectedSession !== null} onHide={() => setSelectedSession(null)} size="xl" centered scrollable>
//         <Modal.Header closeButton className="bg-light border-bottom-0">
//           <Modal.Title className="h5 text-primary">Evaluation Details</Modal.Title>
//         </Modal.Header>
//         <Modal.Body className="p-0" style={{ background: modalViewMode === "parameters" ? "#525659" : "white" }}>
//           {selectedSession && (
//             <div className="d-flex flex-column h-100">
//               {/* Tabs */}
//               {modalViewMode !== "parameters" && (
//                   <div className="p-4 bg-light border-bottom">
//                     <h4 className="mb-1">{selectedSession.candidate_name}</h4>
//                     <div className="text-muted d-flex gap-3 small mt-2">
//                         <span><i className="bi bi-briefcase me-1"></i> {selectedSession.job_title}</span>
//                         <span><i className="bi bi-tag me-1"></i> {selectedSession.job_type}</span>
//                     </div>
//                   </div>
//               )}
              
//               <div className="px-4 py-2 bg-white sticky-top border-bottom">
//                 <ButtonGroup className="w-100 shadow-sm">
//                   <Button
//                     variant={modalViewMode === "general" ? "primary" : "outline-light text-dark border"}
//                     onClick={() => setModalViewMode("general")}
//                     disabled={!selectedSession.general_data}
//                   >
//                     General Questions {selectedSession.general_data ? "" : "(N/A)"}
//                   </Button>
//                   <Button
//                     variant={modalViewMode === "parameters" ? "primary" : "outline-light text-dark border"}
//                     onClick={() => setModalViewMode("parameters")}
//                     disabled={!selectedSession.parameter_data}
//                   >
//                   Evaluation Form {selectedSession.parameter_data ? "" : "(N/A)"}
//                   </Button>
//                 </ButtonGroup>
//               </div>

//               {/* Content */}
//               <div className="flex-grow-1">
//                 {modalViewMode === "general" && selectedSession.general_data ? (
//                   <div className="p-4 fade-in">
//                     <h5 className="mb-3 text-secondary border-bottom pb-2">General Screening Questions</h5>
//                     {renderGeneralTable(selectedSession.general_data.evaluation_details)}
//                   </div>
//                 ) : modalViewMode === "parameters" && selectedSession.parameter_data ? (
//                   renderParameterPDF(selectedSession.parameter_data)
//                 ) : (
//                   <div className="text-center py-5 text-muted">
//                     <i className="bi bi-exclamation-circle fs-1"></i>
//                     <p className="mt-2">No data available for this section.</p>
//                   </div>
//                 )}
//               </div>
//             </div>
//           )}
//         </Modal.Body>
//         <Modal.Footer className="bg-light">
//           <Button variant="secondary" onClick={() => setSelectedSession(null)}>
//             Close
//           </Button>
//           <Button variant="primary" onClick={() => window.print()}>
//             <i className="bi bi-printer me-2"></i>Save as PDF
//           </Button>
//         </Modal.Footer>
//       </Modal>
//     </>
//   )
// }

// export default InterviewerFeedback


// "use client"
// import React, { useState, useEffect } from "react"
// import axios from "axios"
// import DashNavbar from "./DashNavbar"
// import Sidebar from "./Sidebar"
// import { Pagination, Modal, Card, Form, InputGroup, Button, Table, Spinner, ButtonGroup, Badge } from "react-bootstrap"
// import Swal from "sweetalert2"

// // --- Custom CSS ---
// const CustomStyles = () => (
//   <style>{`
//     /* Main Layout */
//     .interviewer-feedback-page .main-content { background-color: #f8f9fa; }
//     .interviewer-feedback-page.wrapper { display: flex; height: calc(100vh - 60px); }
//     .interviewer-feedback-page .main-content { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; }
    
//     /* Table & Card Styling */
//     .interviewer-feedback-page .card { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; }
//     .interviewer-feedback-page .table-responsive { flex-grow: 1; overflow-y: auto; padding: 1.5rem; padding-top: 0; }
//     .sub-table-container { padding: 1rem; border-top: 2px solid #0d6efd; background: white; margin: 10px; border-radius: 5px; box-shadow: 0 2px 4px rgba(0,0,0,0.05); }

//     /* --- PDF/Form REPLICATION STYLES --- */
//     .pdf-viewer-container {
//         background-color: #525659; /* Acrobat Reader Grey Background */
//         padding: 2rem;
//         display: flex;
//         justify-content: center;
//         min-height: 100%;
//     }

//     .pdf-paper {
//         background-color: white;
//         width: 100%;
//         max-width: 850px; /* A4 width approx */
//         min-height: 1100px;
//         padding: 40px 50px;
//         box-shadow: 0 0 10px rgba(0,0,0,0.3);
//         font-family: "Times New Roman", Times, serif; /* Match PDF Font */
//         color: #000;
//     }

//     /* PDF Header */
//     .pdf-header h2 {
//         font-size: 24px;
//         font-weight: bold;
//         margin-bottom: 25px;
//         color: #000;
//     }
    
//     .info-line {
//         display: flex;
//         margin-bottom: 8px;
//         align-items: flex-end;
//     }
//     .info-label {
//         font-weight: bold;
//         min-width: 160px;
//         font-size: 16px;
//     }
//     .info-value {
//         flex-grow: 1;
//         border-bottom: 1px solid #333;
//         padding-left: 10px;
//         font-size: 16px;
//         min-height: 24px;
//     }

//     /* PDF Sections */
//     .section-header {
//         font-size: 18px;
//         font-weight: bold;
//         margin-top: 30px;
//         margin-bottom: 10px;
//         display: flex;
//         align-items: center;
//         gap: 10px;
//     }
//     .section-header i { font-size: 18px; }
//     .rating-instruction { font-size: 14px; font-style: italic; margin-bottom: 15px; color: #444; }

//     /* PDF Tables */
//     .pdf-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
//     .pdf-table th {
//         text-align: left;
//         font-weight: bold;
//         padding: 5px 0;
//         border-bottom: 1px solid #000; /* Minimal header border */
//         font-size: 15px;
//     }
//     .pdf-table td {
//         padding: 10px 5px;
//         vertical-align: top;
//         font-size: 14px;
//         border-bottom: 1px solid #e0e0e0; /* Faint row lines */
//     }
//     .col-competency { width: 25%; font-weight: bold; }
//     .col-desc { width: 40%; }
//     .col-rating { width: 10%; text-align: center; }
//     .col-comments { width: 25%; }

//     /* Recommendation Section */
//     .checkbox-group { display: flex; flex-direction: column; gap: 8px; margin-top: 10px; }
//     .checkbox-item { display: flex; align-items: center; gap: 10px; font-size: 15px; }
//     .box-icon { font-size: 18px; line-height: 1; }

//     .list-section { margin-top: 15px; }
//     .list-line {
//         display: flex;
//         margin-bottom: 5px;
//     }
//     .list-number { width: 20px; font-weight: bold; }
//     .list-content { flex-grow: 1; border-bottom: 1px solid #ccc; min-height: 20px; }

//     /* Signature */
//     .signature-section { margin-top: 50px; border-top: 1px solid #ccc; padding-top: 10px; display: flex; justify-content: space-between; font-size: 15px; }

//     /* Print Styling */
//     @media print {
//         body * { visibility: hidden; }
//         .modal-content, .modal-content * { visibility: visible; }
//         .modal-content { position: absolute; left: 0; top: 0; width: 100%; box-shadow: none !important; border: none !important; }
//         .pdf-viewer-container { background: white; padding: 0; }
//         .pdf-paper { box-shadow: none; width: 100%; max-width: 100%; padding: 0; }
//         .btn-close, .modal-footer, .modal-header { display: none !important; }
//     }
//   `}</style>
// )

// const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com"

// function InterviewerFeedback() {
//   // Data Stores
//   const [mergedEvaluations, setMergedEvaluations] = useState([])
//   const [groupedCandidates, setGroupedCandidates] = useState({})
//   const [evaluationParameters, setEvaluationParameters] = useState([]) 

//   // UI State
//   const [searchTerm, setSearchTerm] = useState("")
//   const [currentPage, setCurrentPage] = useState(1)
//   const [itemsPerPage, setItemsPerPage] = useState(10)
//   const [isLoading, setIsLoading] = useState(false)
//   const [expandedCandidate, setExpandedCandidate] = useState(null)

//   // Modal State
//   const [selectedSession, setSelectedSession] = useState(null)
//   const [modalViewMode, setModalViewMode] = useState("general") // 'general' or 'parameters'

//   // Filtered Data State
//   const [filteredEvaluationParameters, setFilteredEvaluationParameters] = useState({})

//   // --- 1. Fetch Data ---
//   const fetchAllData = async () => {
//     setIsLoading(true)
//     try {
//       const parametersRes = await axios.get(`${API_BASE_URL}/job-postings/business-028/evaluation-parameters/`)
//       const allParametersData = parametersRes.data || []
//       const allParametersArray = Array.isArray(allParametersData) ? allParametersData : allParametersData.results || []

//       setEvaluationParameters(allParametersArray)

//       const [generalRes, paramRes] = await Promise.all([
//         axios.get(`${API_BASE_URL}/interview-evaluations/`),
//         axios.get(`${API_BASE_URL}/evaluations/`),
//       ])

//       const generalList = generalRes.data || []
//       const safeGeneralList = Array.isArray(generalList) ? generalList : generalList.results || []

//       const paramList = paramRes.data || []
//       const safeParamList = Array.isArray(paramList) ? paramList : paramList.results || []

//       processAndMergeData(safeGeneralList, safeParamList)
//     } catch (error) {
//       console.error("Error fetching data:", error)
//       Swal.fire("Error", "Could not fetch evaluation data. Please check API connections.", "error")
//     } finally {
//       setIsLoading(false)
//     }
//   }

//   useEffect(() => {
//     fetchAllData()
//   }, [])

//   // --- 2. Process & Merge Logic ---
//   const processAndMergeData = (generalList, paramList) => {
//     const sessionMap = new Map()

//     const getKey = (item) => {
//       const jobId = item.job_posting || item.job_posting_id || item.linked_posting_id || "unknown-job"
//       const name = item.candidate_name || "unknown-candidate"
//       return `${name}|${jobId}`
//     }

//     generalList.forEach((item) => {
//       if (item.candidate_name === "N/A - Template") return

//       const key = getKey(item)
//       if (!sessionMap.has(key)) {
//         sessionMap.set(key, {
//           id: key,
//           candidate_name: item.candidate_name,
//           candidate_email: item.candidate_email,
//           job_title: item.job_title || "N/A",
//           job_type: item.job_type,
//           job_posting_id: item.job_posting,
//           date: item.evaluation_date,
//           // Capture Interview ID for General type if available
//           interview_id: item.id || item.interview_id, 
//           general_data: item,
//           parameter_data: null,
//         })
//       } else {
//         const session = sessionMap.get(key)
//         session.general_data = item
//         if (!session.job_type) session.job_type = item.job_type
//         if (!session.job_posting_id) session.job_posting_id = item.job_posting
//       }
//     })

//     paramList.forEach((item) => {
//       const key = getKey(item)

//       if (sessionMap.has(key)) {
//         const session = sessionMap.get(key)
//         session.parameter_data = item
//         // Capture Interview ID from Param type (Preferred)
//         if (item.interview) session.interview_id = item.interview
//         if (item.job_title) session.job_title = item.job_title
//       } else {
//         sessionMap.set(key, {
//           id: key,
//           candidate_name: item.candidate_name,
//           candidate_email: item.candidate_email || "N/A",
//           job_title: item.job_title || "N/A",
//           job_type: item.posting_type || "N/A",
//           job_posting_id: item.posting_id || item.job_posting || "N/A",
//           date: item.interview_date || item.created_at,
//           // Capture Interview ID
//           interview_id: item.interview, 
//           general_data: null,
//           parameter_data: item,
//         })
//       }
//     })

//     const mergedArray = Array.from(sessionMap.values())
//     setMergedEvaluations(mergedArray)
//     groupDataByCandidate(mergedArray)
//   }

//   const groupDataByCandidate = (data) => {
//     const groups = data.reduce((acc, session) => {
//       const name = session.candidate_name
//       if (!acc[name]) {
//         acc[name] = {
//           candidate_name: name,
//           email: session.candidate_email,
//           sessions: [],
//         }
//       }
//       acc[name].sessions.push(session)
//       return acc
//     }, {})
//     setGroupedCandidates(groups)
//   }

//   // --- 3. Filtering & Pagination ---
//   const candidateNames = Object.keys(groupedCandidates).filter(
//     (name) =>
//       name.toLowerCase().includes(searchTerm.toLowerCase()) ||
//       groupedCandidates[name].email?.toLowerCase().includes(searchTerm.toLowerCase()),
//   )

//   const totalPages = Math.ceil(candidateNames.length / itemsPerPage)
//   const paginatedNames = candidateNames.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

//   // --- 4. Helpers ---
//   const formatDate = (dateStr) => {
//     if (!dateStr) return "N/A"
//     return new Date(dateStr).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
//   }

//   const handleViewDetails = (session) => {
//     setSelectedSession(session)
//     setModalViewMode("general")
//     fetchFilteredDataForJob(session)
//   }

//   const fetchFilteredDataForJob = async (session) => {
//     try {
//       const jobPostingId = session.job_posting_id
//       const businessOpportunity = session.general_data?.business_opportunity || null
//       const technicalConsultant = session.general_data?.technical_consultant || null
      
//       // Get the interview ID captured in processAndMergeData
//       const interviewId = session.interview_id; 

//       // 1. Fetch General Data
//       const generalRes = await axios.get(`${API_BASE_URL}/interview-evaluations/`)
//       const generalList = Array.isArray(generalRes.data) ? generalRes.data : generalRes.data.results || []
//       const filteredGeneral = generalList.find((item) => item.job_posting === jobPostingId)

//       // 2. Fetch Detailed Parameter Data using Interview ID
//       let detailedParamData = session.parameter_data || {}; // Initialize with existing data to preserve 'interviewer' etc.
      
//       if (interviewId) {
//           try {
//               // API Call to get specific rating/comments
//               const specificUrl = `${API_BASE_URL}/interviews/${interviewId}/submit-evaluation/`
//               console.log("Fetching details from:", specificUrl)
//               const response = await axios.get(specificUrl)
              
//               // Merge existing info (like interviewer name) with new detailed info (like comments/ratings)
//               detailedParamData = { ...detailedParamData, ...response.data }
//           } catch (err) {
//               console.warn("Could not fetch detailed evaluation:", err)
//               // If detailed fetch fails, detailedParamData remains as session.parameter_data
//           }
//       } 

//       setSelectedSession((prev) => ({
//         ...prev,
//         general_data: filteredGeneral,
//         parameter_data: detailedParamData, 
//       }))

//       if (Array.isArray(evaluationParameters) && evaluationParameters.length > 0) {
//         const matchedParameters = evaluationParameters.filter((param) => {
//           const jobMatch = param.job_posting === jobPostingId
//           const businessMatch = businessOpportunity === null || param.business_opportunity === businessOpportunity
//           const consultantMatch = technicalConsultant === null || param.technical_consultant === technicalConsultant
//           return jobMatch && businessMatch && consultantMatch
//         })

//         const groupedParams = matchedParameters.reduce((acc, param) => {
//           const area = param.area || "GENERAL"
//           if (!acc[area]) acc[area] = []
//           acc[area].push(param)
//           return acc
//         }, {})

//         setFilteredEvaluationParameters(groupedParams)
//       }
//     } catch (error) {
//       console.error("Error fetching filtered data:", error)
//     }
//   }

//   // --- Render General Questions (Keeping Bootstrap Table) ---
//   const renderGeneralTable = (details) => {
//     if (!details || details.length === 0) return <p className="text-muted">No general questions recorded.</p>
//     return (
//       <Table bordered hover>
//         <thead className="table-light">
//           <tr>
//             <th style={{ width: "70%" }}>Question</th>
//             <th className="text-center">Rating</th>
//           </tr>
//         </thead>
//         <tbody>
//           {details.map((d, i) => (
//             <tr key={i}>
//               <td className="justified-text">{d.question_text}</td>
//               <td className="text-center">
//                 {d.answer_value ? <Badge bg="info">{d.answer_value}/5</Badge> : <span className="text-muted">-</span>}
//               </td>
//             </tr>
//           ))}
//         </tbody>
//       </Table>
//     )
//   }

//   // --- Render PDF Replica (The core request) ---
//   const renderParameterPDF = (paramData) => {
//     // paramData is a merge of existing session data and new detailed responses
//     const responses = paramData.responses || []
    
//     // Fallback if specific filtering isn't ready
//     const parametersToUse = Object.keys(filteredEvaluationParameters).length > 0 ? filteredEvaluationParameters : {
//         "CORE_COMPETENCIES": [], "BEHAVIORAL": [], "ROLE_FIT": []
//     }

//     // Helper to find the specific response object for a given parameter ID
//     const getResponse = (paramId) => {
//         // We match the 'parameter' field in the response object with the 'id' of the parameter definition
//         return responses.find(r => r.parameter === paramId);
//     }

//     // Helper to render a table row
//     const renderRow = (param) => {
//         const resp = getResponse(param.id);
        
//         // Rating Badge Style
//         const getRatingContent = (rating) => {
//              if (!rating) return "";
//              return (
//                  <div style={{
//                     display: 'inline-block',
//                     border: '1px solid #000',
//                     borderRadius: '50%',
//                     width: '26px', 
//                     height: '26px', 
//                     textAlign: 'center',
//                     lineHeight: '24px',
//                     fontWeight: 'bold'
//                  }}>
//                     {rating}
//                  </div>
//              )
//         }

//         return (
//             <tr key={param.id}>
//                 <td className="col-competency">{param.parameter_text}</td>
//                 <td className="col-desc">{param.description}</td>
//                 {/* Display Rating */}
//                 <td className="col-rating">
//                     {getRatingContent(resp?.rating)}
//                 </td>
//                 {/* Display Comments */}
//                 <td className="col-comments" style={{ fontSize: '13px', fontStyle: resp?.comments ? 'normal' : 'italic' }}>
//                     {resp?.comments || "No comments"}
//                 </td>
//             </tr>
//         )
//     }

//     // Recommendation logic
//     const rec = paramData.overall_recommendation || "";
//     const isStrongly = rec.toLowerCase().includes("strongly recommend") || rec === "STRONGLY_RECOMMEND";
//     const isRec = !isStrongly && (rec.toLowerCase().includes("recommend") || rec === "RECOMMEND") && !rec.toLowerCase().includes("not");
//     const isNeutral = rec.toLowerCase().includes("neutral") || rec.toLowerCase().includes("hold") || rec === "NEUTRAL";
//     const isNot = rec.toLowerCase().includes("not recommend") || rec === "DO_NOT_RECOMMEND";

//     // Arrays for lists
//     const strengths = Array.isArray(paramData.strengths) ? paramData.strengths : [paramData.strengths || ""];
//     const developments = Array.isArray(paramData.development_areas) ? paramData.development_areas : [paramData.development_areas || ""];

//     return (
//       <div className="pdf-viewer-container">
//         <div className="pdf-paper">
            
//             {/* Header */}
//             <div className="pdf-header">
//                 <h2>Post-Interview Employee Evaluation Form</h2>
//                 <div className="info-line">
//                     <div className="info-label">Candidate Name:</div>
//                     <div className="info-value">{paramData.candidate_name || selectedSession?.candidate_name}</div>
//                 </div>
//                 <div className="info-line">
//                     <div className="info-label">Position Applied For:</div>
//                     <div className="info-value">{selectedSession?.job_title}</div>
//                 </div>
//                 <div className="info-line">
//                     <div className="info-label">Department:</div>
//                     <div className="info-value">{paramData.department || "N/A"}</div>
//                 </div>
//                 <div className="info-line">
//                     <div className="info-label">Interview Date:</div>
//                     <div className="info-value">{formatDate(paramData.display_date || selectedSession?.date)}</div>
//                 </div>
//                 <div className="info-line">
//                     <div className="info-label">Interviewer(s):</div>
//                     <div className="info-value">{paramData.interviewer || "N/A"}</div>
//                 </div>
//                 <hr style={{ borderTop: "1px solid #ccc", marginTop: "20px" }} />
//             </div>

//             {/* Section 1: Core Competencies */}
//             <div className="section-header">
//                 <i className="bi bi-search"></i> 1. Core Competencies
//             </div>
//             <div className="rating-instruction">Rate 1 – 5 (1 = Poor, 5 = Excellent) and add comments/evidence:</div>
            
//             <table className="pdf-table">
//                 <thead>
//                     <tr>
//                         <th className="col-competency">Competency</th>
//                         <th className="col-desc">Description</th>
//                         <th className="col-rating">Rating</th>
//                         <th className="col-comments">Comments / Evidence</th>
//                     </tr>
//                 </thead>
//                 <tbody>
//                     {filteredEvaluationParameters["CORE_COMPETENCIES"]?.map(renderRow)}
//                     {(!filteredEvaluationParameters["CORE_COMPETENCIES"] || filteredEvaluationParameters["CORE_COMPETENCIES"].length === 0) && (
//                          <tr><td colSpan="4" className="text-center text-muted">No Core Competencies defined.</td></tr>
//                     )}
//                 </tbody>
//             </table>

//             {/* Section 2: Behavioral */}
//             <div className="section-header">
//                 <i className="bi bi-file-text"></i> 2. Additional Behavioral & Functional Parameters
//             </div>
//             <table className="pdf-table">
//                 <thead>
//                     <tr>
//                         <th className="col-competency">Parameter</th>
//                         <th className="col-desc">Description</th>
//                         <th className="col-rating">Rating</th>
//                         <th className="col-comments">Comments / Evidence</th>
//                     </tr>
//                 </thead>
//                 <tbody>
//                     {filteredEvaluationParameters["BEHAVIORAL"]?.map(renderRow)}
//                     {(!filteredEvaluationParameters["BEHAVIORAL"] || filteredEvaluationParameters["BEHAVIORAL"].length === 0) && (
//                          <tr><td colSpan="4" className="text-center text-muted">No Behavioral Parameters defined.</td></tr>
//                     )}
//                 </tbody>
//             </table>

//             {/* Section 3: Role Specific */}
//             <div className="section-header">
//                 <i className="bi bi-check-square"></i> 3. Role-Specific & Cultural Fit
//             </div>
//             <table className="pdf-table">
//                 <thead>
//                     <tr>
//                         <th className="col-competency">Attribute</th>
//                         <th className="col-desc">Description</th> {/* Using description column for spacing consistency */}
//                         <th className="col-rating">Rating</th>
//                         <th className="col-comments">Comments</th>
//                     </tr>
//                 </thead>
//                 <tbody>
//                     {filteredEvaluationParameters["ROLE_FIT"]?.map(renderRow)}
//                      {(!filteredEvaluationParameters["ROLE_FIT"] || filteredEvaluationParameters["ROLE_FIT"].length === 0) && (
//                          <tr><td colSpan="4" className="text-center text-muted">No Role-Specific Parameters defined.</td></tr>
//                     )}
//                 </tbody>
//             </table>

//             {/* Section 4: Overall Recommendation */}
//             <div className="section-header">
//                 <i className="bi bi-flag-fill"></i> 4. Overall Recommendation
//             </div>
            
//             <div className="list-section">
//                 <strong>• Overall Suitability:</strong>
//                 <div className="checkbox-group">
//                     <div className="checkbox-item">
//                         <span className="box-icon">{isStrongly ? "☒" : "☐"}</span> Strongly Recommend
//                     </div>
//                     <div className="checkbox-item">
//                         <span className="box-icon">{isRec ? "☒" : "☐"}</span> Recommend
//                     </div>
//                     <div className="checkbox-item">
//                         <span className="box-icon">{isNeutral ? "☒" : "☐"}</span> Neutral / Hold
//                     </div>
//                     <div className="checkbox-item">
//                         <span className="box-icon">{isNot ? "☒" : "☐"}</span> Do Not Recommend
//                     </div>
//                 </div>
//             </div>

//             <div className="list-section">
//                 <strong>• Top Strengths:</strong>
//                 <div className="mt-2">
//                     {[0, 1, 2].map(i => (
//                         <div className="list-line" key={i}>
//                             <div className="list-number">{i+1}.</div>
//                             <div className="list-content">{strengths[i] || ""}</div>
//                         </div>
//                     ))}
//                 </div>
//             </div>

//             <div className="list-section">
//                 <strong>• Key Development Areas:</strong>
//                 <div className="mt-2">
//                     {[0, 1, 2].map(i => (
//                         <div className="list-line" key={i}>
//                             <div className="list-number">{i+1}.</div>
//                             <div className="list-content">{developments[i] || ""}</div>
//                         </div>
//                     ))}
//                 </div>
//             </div>

//             <div className="list-section">
//                 <strong>• Final Comments:</strong>
//                 <div style={{ marginTop: "5px", border: "1px solid #ccc", minHeight: "80px", padding: "10px" }}>
//                     {paramData.final_comments}
//                 </div>
//             </div>

//             {/* Footer */}
//             <div className="signature-section">
//                 <div>Interviewer’s Signature: ________________________</div>
//                 <div>Date: <u>{formatDate(new Date())}</u></div>
//             </div>

//             <div className="text-muted text-center mt-5 small">
//                 Feel free to adjust any rating scales or add/remove parameters to suit your specific role or organizational priorities!
//             </div>
//         </div>
//       </div>
//     )
//   }

//   return (
//     <>
//       <CustomStyles />
//       <DashNavbar />
//       <div className="wrapper d-flex interviewer-feedback-page">
//         <Sidebar />
//         <div className="main-content flex-grow-1 p-3 p-md-4">
//           <Card className="shadow-sm mb-4 h-100">
//             <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center border-bottom">
//               <h4 className="mb-0 text-primary fw-bold">
//                 <i className="bi bi-clipboard-data me-2"></i>Feedback Evaluations
//               </h4>
//               <InputGroup style={{ maxWidth: "300px" }}>
//                 <InputGroup.Text className="bg-light">
//                   <i className="bi bi-search"></i>
//                 </InputGroup.Text>
//                 <Form.Control
//                   type="search"
//                   placeholder="Search candidates..."
//                   value={searchTerm}
//                   onChange={(e) => setSearchTerm(e.target.value)}
//                 />
//               </InputGroup>
//             </Card.Header>

//             <Card.Body className="p-0">
//               <div className="table-responsive">
//                 <Table hover className="align-middle mb-0">
//                   <thead className="table-light">
//                     <tr>
//                       <th className="ps-4">#</th>
//                       <th>Candidate Name</th>
//                       <th>Email</th>
//                       <th className="text-center">Interviews</th>
//                       <th className="text-center">Action</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {isLoading ? (
//                       <tr>
//                         <td colSpan="5" className="text-center py-5">
//                           <Spinner animation="border" variant="primary" />
//                         </td>
//                       </tr>
//                     ) : paginatedNames.length > 0 ? (
//                       paginatedNames.map((name, idx) => {
//                         const group = groupedCandidates[name]
//                         const isExpanded = expandedCandidate === name
//                         return (
//                           <React.Fragment key={name}>
//                             <tr className={isExpanded ? "table-active" : ""}>
//                               <td className="ps-4 text-muted">{(currentPage - 1) * itemsPerPage + idx + 1}</td>
//                               <td className="fw-bold text-dark">{name}</td>
//                               <td className="text-muted">{group.email}</td>
//                               <td className="text-center">
//                                 <Badge bg="secondary" pill>
//                                   {group.sessions.length}
//                                 </Badge>
//                               </td>
//                               <td className="text-center">
//                                 <Button
//                                   variant={isExpanded ? "primary" : "outline-primary"}
//                                   size="sm"
//                                   onClick={() => setExpandedCandidate(isExpanded ? null : name)}
//                                 >
//                                   {isExpanded ? (
//                                     <i className="bi bi-chevron-up"></i>
//                                   ) : (
//                                     <i className="bi bi-chevron-down"></i>
//                                   )}
//                                 </Button>
//                               </td>
//                             </tr>
//                             {isExpanded && (
//                               <tr className="sub-table-row">
//                                 <td colSpan="5">
//                                   <div className="sub-table-container fade-in">
//                                     <h6 className="mb-3 text-primary border-bottom pb-2">Interview History: {name}</h6>
//                                     <Table className="sub-table mb-0">
//                                       <thead>
//                                         <tr>
//                                           <th>Job Title</th>
//                                           <th>Opportunity Type</th>
//                                           <th>Opportunity ID</th>
//                                           <th>Date</th>
//                                           <th>Data Available</th>
//                                           <th className="text-center">Action</th>
//                                         </tr>
//                                       </thead>
//                                       <tbody>
//                                         {group.sessions.map((session) => (
//                                           <tr key={session.id}>
//                                             <td>{session.job_title}</td>
//                                             <td>
//                                               <Badge bg="light" text="dark" className="border">
//                                                 {session.job_type || "N/A"}
//                                               </Badge>
//                                             </td>
//                                             <td className="font-monospace text-muted small">
//                                               {session.job_posting_id}
//                                             </td>
//                                             <td>{formatDate(session.date)}</td>
//                                             <td>
//                                               {session.general_data && (
//                                                 <Badge bg="info" className="me-1">
//                                                   General
//                                                 </Badge>
//                                               )}
//                                               {(session.parameter_data || session.interview_id) && <Badge bg="success">Params</Badge>}
//                                             </td>
//                                             <td className="text-center">
//                                               <Button
//                                                 variant="outline-dark"
//                                                 size="sm"
//                                                 onClick={() => handleViewDetails(session)}
//                                               >
//                                                 <i className="bi bi-eye me-1"></i> View Report
//                                               </Button>
//                                             </td>
//                                           </tr>
//                                         ))}
//                                       </tbody>
//                                     </Table>
//                                   </div>
//                                 </td>
//                               </tr>
//                             )}
//                           </React.Fragment>
//                         )
//                       })
//                     ) : (
//                       <tr>
//                         <td colSpan="5" className="text-center py-5 text-muted">
//                           No evaluations found.
//                         </td>
//                       </tr>
//                     )}
//                   </tbody>
//                 </Table>
//               </div>
//             </Card.Body>

//             {totalPages > 1 && (
//               <Card.Footer className="bg-white d-flex justify-content-end py-3">
//                 <Pagination>
//                   <Pagination.Prev
//                     onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
//                     disabled={currentPage === 1}
//                   />
//                   {[...Array(totalPages)].map((_, i) => (
//                     <Pagination.Item key={i + 1} active={i + 1 === currentPage} onClick={() => setCurrentPage(i + 1)}>
//                       {i + 1}
//                     </Pagination.Item>
//                   ))}
//                   <Pagination.Next
//                     onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
//                     disabled={currentPage === totalPages}
//                   />
//                 </Pagination>
//               </Card.Footer>
//             )}
//           </Card>
//         </div>
//       </div>

//       {/* --- Details Modal --- */}
//       <Modal show={selectedSession !== null} onHide={() => setSelectedSession(null)} size="xl" centered scrollable>
//         <Modal.Header closeButton className="bg-light border-bottom-0">
//           <Modal.Title className="h5 text-primary">Evaluation Details</Modal.Title>
//         </Modal.Header>
//         <Modal.Body className="p-0" style={{ background: modalViewMode === "parameters" ? "#525659" : "white" }}>
//           {selectedSession && (
//             <div className="d-flex flex-column h-100">
//               {/* Tabs */}
//               {modalViewMode !== "parameters" && (
//                   <div className="p-4 bg-light border-bottom">
//                     <h4 className="mb-1">{selectedSession.candidate_name}</h4>
//                     <div className="text-muted d-flex gap-3 small mt-2">
//                         <span><i className="bi bi-briefcase me-1"></i> {selectedSession.job_title}</span>
//                         <span><i className="bi bi-tag me-1"></i> {selectedSession.job_type}</span>
//                     </div>
//                   </div>
//               )}
              
//               <div className="px-4 py-2 bg-white sticky-top border-bottom">
//                 <ButtonGroup className="w-100 shadow-sm">
//                   <Button
//                     variant={modalViewMode === "general" ? "primary" : "outline-light text-dark border"}
//                     onClick={() => setModalViewMode("general")}
//                     disabled={!selectedSession.general_data}
//                   >
//                     General Questions {selectedSession.general_data ? "" : "(N/A)"}
//                   </Button>
//                   <Button
//                     variant={modalViewMode === "parameters" ? "primary" : "outline-light text-dark border"}
//                     onClick={() => setModalViewMode("parameters")}
//                     disabled={!selectedSession.parameter_data}
//                   >
//                     PDF Evaluation Form {selectedSession.parameter_data ? "" : "(N/A)"}
//                   </Button>
//                 </ButtonGroup>
//               </div>

//               {/* Content */}
//               <div className="flex-grow-1">
//                 {modalViewMode === "general" && selectedSession.general_data ? (
//                   <div className="p-4 fade-in">
//                     <h5 className="mb-3 text-secondary border-bottom pb-2">General Screening Questions</h5>
//                     {renderGeneralTable(selectedSession.general_data.evaluation_details)}
//                   </div>
//                 ) : modalViewMode === "parameters" && selectedSession.parameter_data ? (
//                   renderParameterPDF(selectedSession.parameter_data)
//                 ) : (
//                   <div className="text-center py-5 text-muted">
//                     <i className="bi bi-exclamation-circle fs-1"></i>
//                     <p className="mt-2">No data available for this section.</p>
//                   </div>
//                 )}
//               </div>
//             </div>
//           )}
//         </Modal.Body>
//         <Modal.Footer className="bg-light">
//           <Button variant="secondary" onClick={() => setSelectedSession(null)}>
//             Close
//           </Button>
//           <Button variant="primary" onClick={() => window.print()}>
//             <i className="bi bi-printer me-2"></i>Print / Save as PDF
//           </Button>
//         </Modal.Footer>
//       </Modal>
//     </>
//   )
// }

// export default InterviewerFeedback




// "use client"
// import React, { useState, useEffect } from "react"
// import axios from "axios"
// import DashNavbar from "./DashNavbar"
// import Sidebar from "./Sidebar"
// import { Pagination, Modal, Card, Form, InputGroup, Button, Table, Spinner, ButtonGroup, Badge } from "react-bootstrap"
// import Swal from "sweetalert2"

// // --- Custom CSS ---
// const CustomStyles = () => (
//   <style>{`
//     /* Main Layout */
//     .interviewer-feedback-page .main-content { background-color: #f8f9fa; }
//     .interviewer-feedback-page.wrapper { display: flex; height: calc(100vh - 60px); }
//     .interviewer-feedback-page .main-content { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; }
    
//     /* Table & Card Styling */
//     .interviewer-feedback-page .card { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; }
//     .interviewer-feedback-page .table-responsive { flex-grow: 1; overflow-y: auto; padding: 1.5rem; padding-top: 0; }
//     .sub-table-container { padding: 1rem; border-top: 2px solid #0d6efd; background: white; margin: 10px; border-radius: 5px; box-shadow: 0 2px 4px rgba(0,0,0,0.05); }

//     /* --- PDF/Form REPLICATION STYLES --- */
//     .pdf-viewer-container {
//         background-color: #525659; /* Acrobat Reader Grey Background */
//         padding: 2rem;
//         display: flex;
//         justify-content: center;
//         min-height: 100%;
//     }

//     .pdf-paper {
//         background-color: white;
//         width: 100%;
//         max-width: 850px; /* A4 width approx */
//         min-height: 1100px;
//         padding: 40px 50px;
//         box-shadow: 0 0 10px rgba(0,0,0,0.3);
//         font-family: "Times New Roman", Times, serif; /* Match PDF Font */
//         color: #000;
//     }

//     /* PDF Header */
//     .pdf-header h2 {
//         font-size: 24px;
//         font-weight: bold;
//         margin-bottom: 25px;
//         color: #000;
//     }
    
//     .info-line {
//         display: flex;
//         margin-bottom: 8px;
//         align-items: flex-end;
//     }
//     .info-label {
//         font-weight: bold;
//         min-width: 160px;
//         font-size: 16px;
//     }
//     .info-value {
//         flex-grow: 1;
//         border-bottom: 1px solid #333;
//         padding-left: 10px;
//         font-size: 16px;
//         min-height: 24px;
//     }

//     /* PDF Sections */
//     .section-header {
//         font-size: 18px;
//         font-weight: bold;
//         margin-top: 30px;
//         margin-bottom: 10px;
//         display: flex;
//         align-items: center;
//         gap: 10px;
//     }
//     .section-header i { font-size: 18px; }
//     .rating-instruction { font-size: 14px; font-style: italic; margin-bottom: 15px; color: #444; }

//     /* PDF Tables */
//     .pdf-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
//     .pdf-table th {
//         text-align: left;
//         font-weight: bold;
//         padding: 5px 0;
//         border-bottom: 1px solid #000; /* Minimal header border */
//         font-size: 15px;
//     }
//     .pdf-table td {
//         padding: 10px 5px;
//         vertical-align: top;
//         font-size: 14px;
//         border-bottom: 1px solid #e0e0e0; /* Faint row lines */
//     }
//     .col-competency { width: 25%; font-weight: bold; }
//     .col-desc { width: 40%; }
//     .col-rating { width: 10%; text-align: center; }
//     .col-comments { width: 25%; }

//     /* Recommendation Section */
//     .checkbox-group { display: flex; flex-direction: column; gap: 8px; margin-top: 10px; }
//     .checkbox-item { display: flex; align-items: center; gap: 10px; font-size: 15px; }
//     .box-icon { font-size: 18px; line-height: 1; }

//     .list-section { margin-top: 15px; }
//     .list-line {
//         display: flex;
//         margin-bottom: 5px;
//     }
//     .list-number { width: 20px; font-weight: bold; }
//     .list-content { flex-grow: 1; border-bottom: 1px solid #ccc; min-height: 20px; }

//     /* Signature */
//     .signature-section { margin-top: 50px; border-top: 1px solid #ccc; padding-top: 10px; display: flex; justify-content: space-between; font-size: 15px; }

//     /* Print Styling */
//     @media print {
//         body * { visibility: hidden; }
//         .modal-content, .modal-content * { visibility: visible; }
//         .modal-content { position: absolute; left: 0; top: 0; width: 100%; box-shadow: none !important; border: none !important; }
//         .pdf-viewer-container { background: white; padding: 0; }
//         .pdf-paper { box-shadow: none; width: 100%; max-width: 100%; padding: 0; }
//         .btn-close, .modal-footer, .modal-header { display: none !important; }
//     }
//   `}</style>
// )

// const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com"

// function InterviewerFeedback() {
//   // Data Stores
//   const [mergedEvaluations, setMergedEvaluations] = useState([])
//   const [groupedCandidates, setGroupedCandidates] = useState({})
//   const [evaluationParameters, setEvaluationParameters] = useState([]) 

//   // UI State
//   const [searchTerm, setSearchTerm] = useState("")
//   const [currentPage, setCurrentPage] = useState(1)
//   const [itemsPerPage, setItemsPerPage] = useState(10)
//   const [isLoading, setIsLoading] = useState(false)
//   const [expandedCandidate, setExpandedCandidate] = useState(null)

//   // Modal State
//   const [selectedSession, setSelectedSession] = useState(null)
//   const [modalViewMode, setModalViewMode] = useState("general") // 'general' or 'parameters'

//   // Filtered Data State
//   const [filteredEvaluationParameters, setFilteredEvaluationParameters] = useState({})

//   // --- 1. Fetch Data ---
//   const fetchAllData = async () => {
//     setIsLoading(true)
//     try {
//       const parametersRes = await axios.get(`${API_BASE_URL}/job-postings/business-028/evaluation-parameters/`)
//       const allParametersData = parametersRes.data || []
//       const allParametersArray = Array.isArray(allParametersData) ? allParametersData : allParametersData.results || []

//       setEvaluationParameters(allParametersArray)

//       const [generalRes, paramRes] = await Promise.all([
//         axios.get(`${API_BASE_URL}/interview-evaluations/`),
//         axios.get(`${API_BASE_URL}/evaluations/`),
//       ])

//       const generalList = generalRes.data || []
//       const safeGeneralList = Array.isArray(generalList) ? generalList : generalList.results || []

//       const paramList = paramRes.data || []
//       const safeParamList = Array.isArray(paramList) ? paramList : paramList.results || []

//       processAndMergeData(safeGeneralList, safeParamList)
//     } catch (error) {
//       console.error("Error fetching data:", error)
//       Swal.fire("Error", "Could not fetch evaluation data. Please check API connections.", "error")
//     } finally {
//       setIsLoading(false)
//     }
//   }

//   useEffect(() => {
//     fetchAllData()
//   }, [])

//   // --- 2. Process & Merge Logic ---
//   const processAndMergeData = (generalList, paramList) => {
//     const sessionMap = new Map()

//     const getKey = (item) => {
//       const jobId = item.job_posting || item.job_posting_id || item.linked_posting_id || "unknown-job"
//       const name = item.candidate_name || "unknown-candidate"
//       return `${name}|${jobId}`
//     }

//     generalList.forEach((item) => {
//       if (item.candidate_name === "N/A - Template") return

//       const key = getKey(item)
//       if (!sessionMap.has(key)) {
//         sessionMap.set(key, {
//           id: key,
//           candidate_name: item.candidate_name,
//           candidate_email: item.candidate_email,
//           job_title: item.job_title || "N/A",
//           job_type: item.job_type,
//           job_posting_id: item.job_posting,
//           date: item.evaluation_date,
//           // Capture Interview ID for General type if available
//           interview_id: item.id || item.interview_id, 
//           general_data: item,
//           parameter_data: null,
//         })
//       } else {
//         const session = sessionMap.get(key)
//         session.general_data = item
//         if (!session.job_type) session.job_type = item.job_type
//         if (!session.job_posting_id) session.job_posting_id = item.job_posting
//       }
//     })

//     paramList.forEach((item) => {
//       const key = getKey(item)

//       if (sessionMap.has(key)) {
//         const session = sessionMap.get(key)
//         session.parameter_data = item
//         // Capture Interview ID from Param type (Preferred)
//         if (item.interview) session.interview_id = item.interview
//         if (item.job_title) session.job_title = item.job_title
//       } else {
//         sessionMap.set(key, {
//           id: key,
//           candidate_name: item.candidate_name,
//           candidate_email: item.candidate_email || "N/A",
//           job_title: item.job_title || "N/A",
//           job_type: item.posting_type || "N/A",
//           job_posting_id: item.posting_id || item.job_posting || "N/A",
//           date: item.interview_date || item.created_at,
//           // Capture Interview ID
//           interview_id: item.interview, 
//           general_data: null,
//           parameter_data: item,
//         })
//       }
//     })

//     const mergedArray = Array.from(sessionMap.values())
//     setMergedEvaluations(mergedArray)
//     groupDataByCandidate(mergedArray)
//   }

//   const groupDataByCandidate = (data) => {
//     const groups = data.reduce((acc, session) => {
//       const name = session.candidate_name
//       if (!acc[name]) {
//         acc[name] = {
//           candidate_name: name,
//           email: session.candidate_email,
//           sessions: [],
//         }
//       }
//       acc[name].sessions.push(session)
//       return acc
//     }, {})
//     setGroupedCandidates(groups)
//   }

//   // --- 3. Filtering & Pagination ---
//   const candidateNames = Object.keys(groupedCandidates).filter(
//     (name) =>
//       name.toLowerCase().includes(searchTerm.toLowerCase()) ||
//       groupedCandidates[name].email?.toLowerCase().includes(searchTerm.toLowerCase()),
//   )

//   const totalPages = Math.ceil(candidateNames.length / itemsPerPage)
//   const paginatedNames = candidateNames.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

//   // --- 4. Helpers ---
//   const formatDate = (dateStr) => {
//     if (!dateStr) return "N/A"
//     return new Date(dateStr).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
//   }

//   const handleViewDetails = (session) => {
//     setSelectedSession(session)
//     setModalViewMode("general")
//     fetchFilteredDataForJob(session)
//   }

//   const fetchFilteredDataForJob = async (session) => {
//     try {
//       const jobPostingId = session.job_posting_id
//       const businessOpportunity = session.general_data?.business_opportunity || null
//       const technicalConsultant = session.general_data?.technical_consultant || null
      
//       // Get the interview ID captured in processAndMergeData
//       const interviewId = session.interview_id; 

//       // 1. Fetch General Data
//       const generalRes = await axios.get(`${API_BASE_URL}/interview-evaluations/`)
//       const generalList = Array.isArray(generalRes.data) ? generalRes.data : generalRes.data.results || []
//       const filteredGeneral = generalList.find((item) => item.job_posting === jobPostingId)

//       // 2. Fetch Detailed Parameter Data using Interview ID
//       let detailedParamData = null;
//       if (interviewId) {
//           try {
//               // API Call to get specific rating/comments
//               const specificUrl = `${API_BASE_URL}/interviews/${interviewId}/submit-evaluation/`
//               console.log("Fetching details from:", specificUrl)
//               const response = await axios.get(specificUrl)
//               detailedParamData = response.data
//           } catch (err) {
//               console.warn("Could not fetch detailed evaluation:", err)
//               // If detailed fetch fails, fall back to what we had in the list (though it lacks comments)
//               detailedParamData = session.parameter_data
//           }
//       } else {
//           // Fallback if no interview ID (unlikely if data is good)
//           detailedParamData = session.parameter_data
//       }

//       setSelectedSession((prev) => ({
//         ...prev,
//         general_data: filteredGeneral,
//         parameter_data: detailedParamData, // This now contains the 'responses' array with ratings/comments
//       }))

//       if (Array.isArray(evaluationParameters) && evaluationParameters.length > 0) {
//         const matchedParameters = evaluationParameters.filter((param) => {
//           const jobMatch = param.job_posting === jobPostingId
//           const businessMatch = businessOpportunity === null || param.business_opportunity === businessOpportunity
//           const consultantMatch = technicalConsultant === null || param.technical_consultant === technicalConsultant
//           return jobMatch && businessMatch && consultantMatch
//         })

//         const groupedParams = matchedParameters.reduce((acc, param) => {
//           const area = param.area || "GENERAL"
//           if (!acc[area]) acc[area] = []
//           acc[area].push(param)
//           return acc
//         }, {})

//         setFilteredEvaluationParameters(groupedParams)
//       }
//     } catch (error) {
//       console.error("Error fetching filtered data:", error)
//     }
//   }

//   // --- Render General Questions (Keeping Bootstrap Table) ---
//   const renderGeneralTable = (details) => {
//     if (!details || details.length === 0) return <p className="text-muted">No general questions recorded.</p>
//     return (
//       <Table bordered hover>
//         <thead className="table-light">
//           <tr>
//             <th style={{ width: "70%" }}>Question</th>
//             <th className="text-center">Rating</th>
//           </tr>
//         </thead>
//         <tbody>
//           {details.map((d, i) => (
//             <tr key={i}>
//               <td className="justified-text">{d.question_text}</td>
//               <td className="text-center">
//                 {d.answer_value ? <Badge bg="info">{d.answer_value}/5</Badge> : <span className="text-muted">-</span>}
//               </td>
//             </tr>
//           ))}
//         </tbody>
//       </Table>
//     )
//   }

//   // --- Render PDF Replica (The core request) ---
//   const renderParameterPDF = (paramData) => {
//     // paramData is now the response from /interviews/{id}/submit-evaluation/
//     // responses structure: [{ id: 176, parameter: 214, rating: 2, comments: "..." }, ...]
//     const responses = paramData.responses || []
    
//     // Fallback if specific filtering isn't ready
//     const parametersToUse = Object.keys(filteredEvaluationParameters).length > 0 ? filteredEvaluationParameters : {
//         "CORE_COMPETENCIES": [], "BEHAVIORAL": [], "ROLE_FIT": []
//     }

//     // Helper to find the specific response object for a given parameter ID
//     const getResponse = (paramId) => {
//         // We match the 'parameter' field in the response object with the 'id' of the parameter definition
//         return responses.find(r => r.parameter === paramId);
//     }

//     // Helper to render a table row
//     const renderRow = (param) => {
//         const resp = getResponse(param.id);
        
//         // Rating Badge Style
//         const getRatingContent = (rating) => {
//              if (!rating) return "";
//              return (
//                  <div style={{
//                     display: 'inline-block',
//                     border: '1px solid #000',
//                     borderRadius: '50%',
//                     width: '26px', 
//                     height: '26px', 
//                     textAlign: 'center',
//                     lineHeight: '24px',
//                     fontWeight: 'bold'
//                  }}>
//                     {rating}
//                  </div>
//              )
//         }

//         return (
//             <tr key={param.id}>
//                 <td className="col-competency">{param.parameter_text}</td>
//                 <td className="col-desc">{param.description}</td>
//                 {/* Display Rating */}
//                 <td className="col-rating">
//                     {getRatingContent(resp?.rating)}
//                 </td>
//                 {/* Display Comments */}
//                 <td className="col-comments" style={{ fontSize: '13px', fontStyle: resp?.comments ? 'normal' : 'italic' }}>
//                     {resp?.comments || "No comments"}
//                 </td>
//             </tr>
//         )
//     }

//     // Recommendation logic
//     const rec = paramData.overall_recommendation || "";
//     const isStrongly = rec.toLowerCase().includes("strongly recommend") || rec === "STRONGLY_RECOMMEND";
//     const isRec = !isStrongly && (rec.toLowerCase().includes("recommend") || rec === "RECOMMEND") && !rec.toLowerCase().includes("not");
//     const isNeutral = rec.toLowerCase().includes("neutral") || rec.toLowerCase().includes("hold") || rec === "NEUTRAL";
//     const isNot = rec.toLowerCase().includes("not recommend") || rec === "DO_NOT_RECOMMEND";

//     // Arrays for lists
//     const strengths = Array.isArray(paramData.strengths) ? paramData.strengths : [paramData.strengths || ""];
//     const developments = Array.isArray(paramData.development_areas) ? paramData.development_areas : [paramData.development_areas || ""];

//     return (
//       <div className="pdf-viewer-container">
//         <div className="pdf-paper">
            
//             {/* Header */}
//             <div className="pdf-header">
//                 <h2>Post-Interview Employee Evaluation Form</h2>
//                 <div className="info-line">
//                     <div className="info-label">Candidate Name:</div>
//                     <div className="info-value">{paramData.candidate_name || selectedSession?.candidate_name}</div>
//                 </div>
//                 <div className="info-line">
//                     <div className="info-label">Position Applied For:</div>
//                     <div className="info-value">{selectedSession?.job_title}</div>
//                 </div>
//                 <div className="info-line">
//                     <div className="info-label">Department:</div>
//                     <div className="info-value">{paramData.department || "N/A"}</div>
//                 </div>
//                 <div className="info-line">
//                     <div className="info-label">Interview Date:</div>
//                     <div className="info-value">{formatDate(paramData.display_date || selectedSession?.date)}</div>
//                 </div>
//                 <div className="info-line">
//                     <div className="info-label">Interviewer(s):</div>
//                     <div className="info-value">{paramData.interviewer || "N/A"}</div>
//                 </div>
//                 <hr style={{ borderTop: "1px solid #ccc", marginTop: "20px" }} />
//             </div>

//             {/* Section 1: Core Competencies */}
//             <div className="section-header">
//                 <i className="bi bi-search"></i> 1. Core Competencies
//             </div>
//             <div className="rating-instruction">Rate 1 – 5 (1 = Poor, 5 = Excellent) and add comments/evidence:</div>
            
//             <table className="pdf-table">
//                 <thead>
//                     <tr>
//                         <th className="col-competency">Competency</th>
//                         <th className="col-desc">Description</th>
//                         <th className="col-rating">Rating</th>
//                         <th className="col-comments">Comments / Evidence</th>
//                     </tr>
//                 </thead>
//                 <tbody>
//                     {filteredEvaluationParameters["CORE_COMPETENCIES"]?.map(renderRow)}
//                     {(!filteredEvaluationParameters["CORE_COMPETENCIES"] || filteredEvaluationParameters["CORE_COMPETENCIES"].length === 0) && (
//                          <tr><td colSpan="4" className="text-center text-muted">No Core Competencies defined.</td></tr>
//                     )}
//                 </tbody>
//             </table>

//             {/* Section 2: Behavioral */}
//             <div className="section-header">
//                 <i className="bi bi-file-text"></i> 2. Additional Behavioral & Functional Parameters
//             </div>
//             <table className="pdf-table">
//                 <thead>
//                     <tr>
//                         <th className="col-competency">Parameter</th>
//                         <th className="col-desc">Description</th>
//                         <th className="col-rating">Rating</th>
//                         <th className="col-comments">Comments / Evidence</th>
//                     </tr>
//                 </thead>
//                 <tbody>
//                     {filteredEvaluationParameters["BEHAVIORAL"]?.map(renderRow)}
//                     {(!filteredEvaluationParameters["BEHAVIORAL"] || filteredEvaluationParameters["BEHAVIORAL"].length === 0) && (
//                          <tr><td colSpan="4" className="text-center text-muted">No Behavioral Parameters defined.</td></tr>
//                     )}
//                 </tbody>
//             </table>

//             {/* Section 3: Role Specific */}
//             <div className="section-header">
//                 <i className="bi bi-check-square"></i> 3. Role-Specific & Cultural Fit
//             </div>
//             <table className="pdf-table">
//                 <thead>
//                     <tr>
//                         <th className="col-competency">Attribute</th>
//                         <th className="col-desc">Description</th> {/* Using description column for spacing consistency */}
//                         <th className="col-rating">Rating</th>
//                         <th className="col-comments">Comments</th>
//                     </tr>
//                 </thead>
//                 <tbody>
//                     {filteredEvaluationParameters["ROLE_FIT"]?.map(renderRow)}
//                      {(!filteredEvaluationParameters["ROLE_FIT"] || filteredEvaluationParameters["ROLE_FIT"].length === 0) && (
//                          <tr><td colSpan="4" className="text-center text-muted">No Role-Specific Parameters defined.</td></tr>
//                     )}
//                 </tbody>
//             </table>

//             {/* Section 4: Overall Recommendation */}
//             <div className="section-header">
//                 <i className="bi bi-flag-fill"></i> 4. Overall Recommendation
//             </div>
            
//             <div className="list-section">
//                 <strong>• Overall Suitability:</strong>
//                 <div className="checkbox-group">
//                     <div className="checkbox-item">
//                         <span className="box-icon">{isStrongly ? "☒" : "☐"}</span> Strongly Recommend
//                     </div>
//                     <div className="checkbox-item">
//                         <span className="box-icon">{isRec ? "☒" : "☐"}</span> Recommend
//                     </div>
//                     <div className="checkbox-item">
//                         <span className="box-icon">{isNeutral ? "☒" : "☐"}</span> Neutral / Hold
//                     </div>
//                     <div className="checkbox-item">
//                         <span className="box-icon">{isNot ? "☒" : "☐"}</span> Do Not Recommend
//                     </div>
//                 </div>
//             </div>

//             <div className="list-section">
//                 <strong>• Top Strengths:</strong>
//                 <div className="mt-2">
//                     {[0, 1, 2].map(i => (
//                         <div className="list-line" key={i}>
//                             <div className="list-number">{i+1}.</div>
//                             <div className="list-content">{strengths[i] || ""}</div>
//                         </div>
//                     ))}
//                 </div>
//             </div>

//             <div className="list-section">
//                 <strong>• Key Development Areas:</strong>
//                 <div className="mt-2">
//                     {[0, 1, 2].map(i => (
//                         <div className="list-line" key={i}>
//                             <div className="list-number">{i+1}.</div>
//                             <div className="list-content">{developments[i] || ""}</div>
//                         </div>
//                     ))}
//                 </div>
//             </div>

//             <div className="list-section">
//                 <strong>• Final Comments:</strong>
//                 <div style={{ marginTop: "5px", border: "1px solid #ccc", minHeight: "80px", padding: "10px" }}>
//                     {paramData.final_comments}
//                 </div>
//             </div>

//             {/* Footer */}
//             <div className="signature-section">
//                 <div>Interviewer’s Signature: ________________________</div>
//                 <div>Date: <u>{formatDate(new Date())}</u></div>
//             </div>

//             <div className="text-muted text-center mt-5 small">
//                 Feel free to adjust any rating scales or add/remove parameters to suit your specific role or organizational priorities!
//             </div>
//         </div>
//       </div>
//     )
//   }

//   return (
//     <>
//       <CustomStyles />
//       <DashNavbar />
//       <div className="wrapper d-flex interviewer-feedback-page">
//         <Sidebar />
//         <div className="main-content flex-grow-1 p-3 p-md-4">
//           <Card className="shadow-sm mb-4 h-100">
//             <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center border-bottom">
//               <h4 className="mb-0 text-primary fw-bold">
//                 <i className="bi bi-clipboard-data me-2"></i>Feedback Evaluations
//               </h4>
//               <InputGroup style={{ maxWidth: "300px" }}>
//                 <InputGroup.Text className="bg-light">
//                   <i className="bi bi-search"></i>
//                 </InputGroup.Text>
//                 <Form.Control
//                   type="search"
//                   placeholder="Search candidates..."
//                   value={searchTerm}
//                   onChange={(e) => setSearchTerm(e.target.value)}
//                 />
//               </InputGroup>
//             </Card.Header>

//             <Card.Body className="p-0">
//               <div className="table-responsive">
//                 <Table hover className="align-middle mb-0">
//                   <thead className="table-light">
//                     <tr>
//                       <th className="ps-4">#</th>
//                       <th>Candidate Name</th>
//                       <th>Email</th>
//                       <th className="text-center">Interviews</th>
//                       <th className="text-center">Action</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {isLoading ? (
//                       <tr>
//                         <td colSpan="5" className="text-center py-5">
//                           <Spinner animation="border" variant="primary" />
//                         </td>
//                       </tr>
//                     ) : paginatedNames.length > 0 ? (
//                       paginatedNames.map((name, idx) => {
//                         const group = groupedCandidates[name]
//                         const isExpanded = expandedCandidate === name
//                         return (
//                           <React.Fragment key={name}>
//                             <tr className={isExpanded ? "table-active" : ""}>
//                               <td className="ps-4 text-muted">{(currentPage - 1) * itemsPerPage + idx + 1}</td>
//                               <td className="fw-bold text-dark">{name}</td>
//                               <td className="text-muted">{group.email}</td>
//                               <td className="text-center">
//                                 <Badge bg="secondary" pill>
//                                   {group.sessions.length}
//                                 </Badge>
//                               </td>
//                               <td className="text-center">
//                                 <Button
//                                   variant={isExpanded ? "primary" : "outline-primary"}
//                                   size="sm"
//                                   onClick={() => setExpandedCandidate(isExpanded ? null : name)}
//                                 >
//                                   {isExpanded ? (
//                                     <i className="bi bi-chevron-up"></i>
//                                   ) : (
//                                     <i className="bi bi-chevron-down"></i>
//                                   )}
//                                 </Button>
//                               </td>
//                             </tr>
//                             {isExpanded && (
//                               <tr className="sub-table-row">
//                                 <td colSpan="5">
//                                   <div className="sub-table-container fade-in">
//                                     <h6 className="mb-3 text-primary border-bottom pb-2">Interview History: {name}</h6>
//                                     <Table className="sub-table mb-0">
//                                       <thead>
//                                         <tr>
//                                           <th>Job Title</th>
//                                           <th>Opportunity Type</th>
//                                           <th>Opportunity ID</th>
//                                           <th>Date</th>
//                                           <th>Data Available</th>
//                                           <th className="text-center">Action</th>
//                                         </tr>
//                                       </thead>
//                                       <tbody>
//                                         {group.sessions.map((session) => (
//                                           <tr key={session.id}>
//                                             <td>{session.job_title}</td>
//                                             <td>
//                                               <Badge bg="light" text="dark" className="border">
//                                                 {session.job_type || "N/A"}
//                                               </Badge>
//                                             </td>
//                                             <td className="font-monospace text-muted small">
//                                               {session.job_posting_id}
//                                             </td>
//                                             <td>{formatDate(session.date)}</td>
//                                             <td>
//                                               {session.general_data && (
//                                                 <Badge bg="info" className="me-1">
//                                                   General
//                                                 </Badge>
//                                               )}
//                                               {(session.parameter_data || session.interview_id) && <Badge bg="success">Params</Badge>}
//                                             </td>
//                                             <td className="text-center">
//                                               <Button
//                                                 variant="outline-dark"
//                                                 size="sm"
//                                                 onClick={() => handleViewDetails(session)}
//                                               >
//                                                 <i className="bi bi-eye me-1"></i> View Report
//                                               </Button>
//                                             </td>
//                                           </tr>
//                                         ))}
//                                       </tbody>
//                                     </Table>
//                                   </div>
//                                 </td>
//                               </tr>
//                             )}
//                           </React.Fragment>
//                         )
//                       })
//                     ) : (
//                       <tr>
//                         <td colSpan="5" className="text-center py-5 text-muted">
//                           No evaluations found.
//                         </td>
//                       </tr>
//                     )}
//                   </tbody>
//                 </Table>
//               </div>
//             </Card.Body>

//             {totalPages > 1 && (
//               <Card.Footer className="bg-white d-flex justify-content-end py-3">
//                 <Pagination>
//                   <Pagination.Prev
//                     onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
//                     disabled={currentPage === 1}
//                   />
//                   {[...Array(totalPages)].map((_, i) => (
//                     <Pagination.Item key={i + 1} active={i + 1 === currentPage} onClick={() => setCurrentPage(i + 1)}>
//                       {i + 1}
//                     </Pagination.Item>
//                   ))}
//                   <Pagination.Next
//                     onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
//                     disabled={currentPage === totalPages}
//                   />
//                 </Pagination>
//               </Card.Footer>
//             )}
//           </Card>
//         </div>
//       </div>

//       {/* --- Details Modal --- */}
//       <Modal show={selectedSession !== null} onHide={() => setSelectedSession(null)} size="xl" centered scrollable>
//         <Modal.Header closeButton className="bg-light border-bottom-0">
//           <Modal.Title className="h5 text-primary">Evaluation Details</Modal.Title>
//         </Modal.Header>
//         <Modal.Body className="p-0" style={{ background: modalViewMode === "parameters" ? "#525659" : "white" }}>
//           {selectedSession && (
//             <div className="d-flex flex-column h-100">
//               {/* Tabs */}
//               {modalViewMode !== "parameters" && (
//                   <div className="p-4 bg-light border-bottom">
//                     <h4 className="mb-1">{selectedSession.candidate_name}</h4>
//                     <div className="text-muted d-flex gap-3 small mt-2">
//                         <span><i className="bi bi-briefcase me-1"></i> {selectedSession.job_title}</span>
//                         <span><i className="bi bi-tag me-1"></i> {selectedSession.job_type}</span>
//                     </div>
//                   </div>
//               )}
              
//               <div className="px-4 py-2 bg-white sticky-top border-bottom">
//                 <ButtonGroup className="w-100 shadow-sm">
//                   <Button
//                     variant={modalViewMode === "general" ? "primary" : "outline-light text-dark border"}
//                     onClick={() => setModalViewMode("general")}
//                     disabled={!selectedSession.general_data}
//                   >
//                     General Questions {selectedSession.general_data ? "" : "(N/A)"}
//                   </Button>
//                   <Button
//                     variant={modalViewMode === "parameters" ? "primary" : "outline-light text-dark border"}
//                     onClick={() => setModalViewMode("parameters")}
//                     disabled={!selectedSession.parameter_data}
//                   >
//                     PDF Evaluation Form {selectedSession.parameter_data ? "" : "(N/A)"}
//                   </Button>
//                 </ButtonGroup>
//               </div>

//               {/* Content */}
//               <div className="flex-grow-1">
//                 {modalViewMode === "general" && selectedSession.general_data ? (
//                   <div className="p-4 fade-in">
//                     <h5 className="mb-3 text-secondary border-bottom pb-2">General Screening Questions</h5>
//                     {renderGeneralTable(selectedSession.general_data.evaluation_details)}
//                   </div>
//                 ) : modalViewMode === "parameters" && selectedSession.parameter_data ? (
//                   renderParameterPDF(selectedSession.parameter_data)
//                 ) : (
//                   <div className="text-center py-5 text-muted">
//                     <i className="bi bi-exclamation-circle fs-1"></i>
//                     <p className="mt-2">No data available for this section.</p>
//                   </div>
//                 )}
//               </div>
//             </div>
//           )}
//         </Modal.Body>
//         <Modal.Footer className="bg-light">
//           <Button variant="secondary" onClick={() => setSelectedSession(null)}>
//             Close
//           </Button>
//           <Button variant="primary" onClick={() => window.print()}>
//             <i className="bi bi-printer me-2"></i>Print / Save as PDF
//           </Button>
//         </Modal.Footer>
//       </Modal>
//     </>
//   )
// }

// export default InterviewerFeedback



// "use client"
// import React, { useState, useEffect } from "react"
// import axios from "axios"
// import DashNavbar from "./DashNavbar"
// import Sidebar from "./Sidebar"
// import { Pagination, Modal, Card, Form, InputGroup, Button, Table, Spinner, ButtonGroup, Badge } from "react-bootstrap"
// import Swal from "sweetalert2"

// // --- Custom CSS ---
// const CustomStyles = () => (
//   <style>{`
//     /* Main Layout */
//     .interviewer-feedback-page .main-content { background-color: #f8f9fa; }
//     .interviewer-feedback-page.wrapper { display: flex; height: calc(100vh - 60px); }
//     .interviewer-feedback-page .main-content { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; }
    
//     /* Table & Card Styling */
//     .interviewer-feedback-page .card { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; }
//     .interviewer-feedback-page .table-responsive { flex-grow: 1; overflow-y: auto; padding: 1.5rem; padding-top: 0; }
//     .sub-table-container { padding: 1rem; border-top: 2px solid #0d6efd; background: white; margin: 10px; border-radius: 5px; box-shadow: 0 2px 4px rgba(0,0,0,0.05); }

//     /* --- PDF/Form REPLICATION STYLES --- */
//     .pdf-viewer-container {
//         background-color: #525659; /* Acrobat Reader Grey Background */
//         padding: 2rem;
//         display: flex;
//         justify-content: center;
//         min-height: 100%;
//     }

//     .pdf-paper {
//         background-color: white;
//         width: 100%;
//         max-width: 850px; /* A4 width approx */
//         min-height: 1100px;
//         padding: 40px 50px;
//         box-shadow: 0 0 10px rgba(0,0,0,0.3);
//         font-family: "Times New Roman", Times, serif; /* Match PDF Font */
//         color: #000;
//     }

//     /* PDF Header */
//     .pdf-header h2 {
//         font-size: 24px;
//         font-weight: bold;
//         margin-bottom: 25px;
//         color: #000;
//     }
    
//     .info-line {
//         display: flex;
//         margin-bottom: 8px;
//         align-items: flex-end;
//     }
//     .info-label {
//         font-weight: bold;
//         min-width: 160px;
//         font-size: 16px;
//     }
//     .info-value {
//         flex-grow: 1;
//         border-bottom: 1px solid #333;
//         padding-left: 10px;
//         font-size: 16px;
//         min-height: 24px;
//     }

//     /* PDF Sections */
//     .section-header {
//         font-size: 18px;
//         font-weight: bold;
//         margin-top: 30px;
//         margin-bottom: 10px;
//         display: flex;
//         align-items: center;
//         gap: 10px;
//     }
//     .section-header i { font-size: 18px; }
//     .rating-instruction { font-size: 14px; font-style: italic; margin-bottom: 15px; color: #444; }

//     /* PDF Tables */
//     .pdf-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
//     .pdf-table th {
//         text-align: left;
//         font-weight: bold;
//         padding: 5px 0;
//         border-bottom: 1px solid #000; /* Minimal header border */
//         font-size: 15px;
//     }
//     .pdf-table td {
//         padding: 10px 5px;
//         vertical-align: top;
//         font-size: 14px;
//         border-bottom: 1px solid #e0e0e0; /* Faint row lines */
//     }
//     .col-competency { width: 25%; font-weight: bold; }
//     .col-desc { width: 40%; }
//     .col-rating { width: 10%; text-align: center; }
//     .col-comments { width: 25%; }

//     /* Recommendation Section */
//     .checkbox-group { display: flex; flex-direction: column; gap: 8px; margin-top: 10px; }
//     .checkbox-item { display: flex; align-items: center; gap: 10px; font-size: 15px; }
//     .box-icon { font-size: 18px; line-height: 1; }

//     .list-section { margin-top: 15px; }
//     .list-line {
//         display: flex;
//         margin-bottom: 5px;
//     }
//     .list-number { width: 20px; font-weight: bold; }
//     .list-content { flex-grow: 1; border-bottom: 1px solid #ccc; min-height: 20px; }

//     /* Signature */
//     .signature-section { margin-top: 50px; border-top: 1px solid #ccc; padding-top: 10px; display: flex; justify-content: space-between; font-size: 15px; }

//     /* Print Styling */
//     @media print {
//         body * { visibility: hidden; }
//         .modal-content, .modal-content * { visibility: visible; }
//         .modal-content { position: absolute; left: 0; top: 0; width: 100%; box-shadow: none !important; border: none !important; }
//         .pdf-viewer-container { background: white; padding: 0; }
//         .pdf-paper { box-shadow: none; width: 100%; max-width: 100%; padding: 0; }
//         .btn-close, .modal-footer, .modal-header { display: none !important; }
//     }
//   `}</style>
// )

// const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com"

// function InterviewerFeedback() {
//   // Data Stores
//   const [mergedEvaluations, setMergedEvaluations] = useState([])
//   const [groupedCandidates, setGroupedCandidates] = useState({})
//   const [evaluationParameters, setEvaluationParameters] = useState([]) 

//   // UI State
//   const [searchTerm, setSearchTerm] = useState("")
//   const [currentPage, setCurrentPage] = useState(1)
//   const [itemsPerPage, setItemsPerPage] = useState(10)
//   const [isLoading, setIsLoading] = useState(false)
//   const [expandedCandidate, setExpandedCandidate] = useState(null)

//   // Modal State
//   const [selectedSession, setSelectedSession] = useState(null)
//   const [modalViewMode, setModalViewMode] = useState("general") // 'general' or 'parameters'

//   // Filtered Data State
//   const [filteredEvaluationParameters, setFilteredEvaluationParameters] = useState({})

//   // --- 1. Fetch Data ---
//   const fetchAllData = async () => {
//     setIsLoading(true)
//     try {
//       const parametersRes = await axios.get(`${API_BASE_URL}/job-postings/business-028/evaluation-parameters/`)
//       const allParametersData = parametersRes.data || []
//       const allParametersArray = Array.isArray(allParametersData) ? allParametersData : allParametersData.results || []

//       setEvaluationParameters(allParametersArray)

//       const [generalRes, paramRes] = await Promise.all([
//         axios.get(`${API_BASE_URL}/interview-evaluations/`),
//         axios.get(`${API_BASE_URL}/evaluations/`),
//       ])

//       const generalList = generalRes.data || []
//       const safeGeneralList = Array.isArray(generalList) ? generalList : generalList.results || []

//       const paramList = paramRes.data || []
//       const safeParamList = Array.isArray(paramList) ? paramList : paramList.results || []

//       processAndMergeData(safeGeneralList, safeParamList)
//     } catch (error) {
//       console.error("Error fetching data:", error)
//       Swal.fire("Error", "Could not fetch evaluation data. Please check API connections.", "error")
//     } finally {
//       setIsLoading(false)
//     }
//   }

//   useEffect(() => {
//     fetchAllData()
//   }, [])

//   // --- 2. Process & Merge Logic ---
//   const processAndMergeData = (generalList, paramList) => {
//     const sessionMap = new Map()

//     const getKey = (item) => {
//       const jobId = item.job_posting || item.job_posting_id || item.linked_posting_id || "unknown-job"
//       const name = item.candidate_name || "unknown-candidate"
//       return `${name}|${jobId}`
//     }

//     generalList.forEach((item) => {
//       if (item.candidate_name === "N/A - Template") return

//       const key = getKey(item)
//       if (!sessionMap.has(key)) {
//         sessionMap.set(key, {
//           id: key,
//           candidate_name: item.candidate_name,
//           candidate_email: item.candidate_email,
//           job_title: item.job_title || "N/A",
//           job_type: item.job_type,
//           job_posting_id: item.job_posting,
//           date: item.evaluation_date,
//           general_data: item,
//           parameter_data: null,
//         })
//       } else {
//         const session = sessionMap.get(key)
//         session.general_data = item
//         if (!session.job_type) session.job_type = item.job_type
//         if (!session.job_posting_id) session.job_posting_id = item.job_posting
//       }
//     })

//     paramList.forEach((item) => {
//       const key = getKey(item)

//       if (sessionMap.has(key)) {
//         const session = sessionMap.get(key)
//         session.parameter_data = item
//         if (item.job_title) session.job_title = item.job_title
//       } else {
//         sessionMap.set(key, {
//           id: key,
//           candidate_name: item.candidate_name,
//           candidate_email: item.candidate_email || "N/A",
//           job_title: item.job_title || "N/A",
//           job_type: item.posting_type || "N/A",
//           job_posting_id: item.posting_id || item.job_posting || "N/A",
//           date: item.interview_date || item.created_at,
//           general_data: null,
//           parameter_data: item,
//         })
//       }
//     })

//     const mergedArray = Array.from(sessionMap.values())
//     setMergedEvaluations(mergedArray)
//     groupDataByCandidate(mergedArray)
//   }

//   const groupDataByCandidate = (data) => {
//     const groups = data.reduce((acc, session) => {
//       const name = session.candidate_name
//       if (!acc[name]) {
//         acc[name] = {
//           candidate_name: name,
//           email: session.candidate_email,
//           sessions: [],
//         }
//       }
//       acc[name].sessions.push(session)
//       return acc
//     }, {})
//     setGroupedCandidates(groups)
//   }

//   // --- 3. Filtering & Pagination ---
//   const candidateNames = Object.keys(groupedCandidates).filter(
//     (name) =>
//       name.toLowerCase().includes(searchTerm.toLowerCase()) ||
//       groupedCandidates[name].email?.toLowerCase().includes(searchTerm.toLowerCase()),
//   )

//   const totalPages = Math.ceil(candidateNames.length / itemsPerPage)
//   const paginatedNames = candidateNames.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

//   // --- 4. Helpers ---
//   const formatDate = (dateStr) => {
//     if (!dateStr) return "N/A"
//     return new Date(dateStr).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
//   }

//   const handleViewDetails = (session) => {
//     setSelectedSession(session)
//     setModalViewMode("general")
//     fetchFilteredDataForJob(session)
//   }

//   const fetchFilteredDataForJob = async (session) => {
//     try {
//       const jobPostingId = session.job_posting_id
//       const businessOpportunity = session.general_data?.business_opportunity || null
//       const technicalConsultant = session.general_data?.technical_consultant || null

//       const generalRes = await axios.get(`${API_BASE_URL}/interview-evaluations/`)
//       const generalList = Array.isArray(generalRes.data) ? generalRes.data : generalRes.data.results || []
//       const filteredGeneral = generalList.find((item) => item.job_posting === jobPostingId)

//       const paramRes = await axios.get(`${API_BASE_URL}/evaluations/`)
//       const paramList = Array.isArray(paramRes.data) ? paramRes.data : paramRes.data.results || []
//       const filteredParam = paramList.find((item) => item.posting_id === jobPostingId)

//       setSelectedSession((prev) => ({
//         ...prev,
//         general_data: filteredGeneral,
//         parameter_data: filteredParam,
//       }))

//       if (Array.isArray(evaluationParameters) && evaluationParameters.length > 0) {
//         const matchedParameters = evaluationParameters.filter((param) => {
//           const jobMatch = param.job_posting === jobPostingId
//           const businessMatch = businessOpportunity === null || param.business_opportunity === businessOpportunity
//           const consultantMatch = technicalConsultant === null || param.technical_consultant === technicalConsultant
//           return jobMatch && businessMatch && consultantMatch
//         })

//         const groupedParams = matchedParameters.reduce((acc, param) => {
//           const area = param.area || "GENERAL"
//           if (!acc[area]) acc[area] = []
//           acc[area].push(param)
//           return acc
//         }, {})

//         setFilteredEvaluationParameters(groupedParams)
//       }
//     } catch (error) {
//       console.error("Error fetching filtered data:", error)
//     }
//   }

//   // --- Render General Questions (Keeping Bootstrap Table) ---
//   const renderGeneralTable = (details) => {
//     if (!details || details.length === 0) return <p className="text-muted">No general questions recorded.</p>
//     return (
//       <Table bordered hover>
//         <thead className="table-light">
//           <tr>
//             <th style={{ width: "70%" }}>Question</th>
//             <th className="text-center">Rating</th>
//           </tr>
//         </thead>
//         <tbody>
//           {details.map((d, i) => (
//             <tr key={i}>
//               <td className="justified-text">{d.question_text}</td>
//               <td className="text-center">
//                 {d.answer_value ? <Badge bg="info">{d.answer_value}/5</Badge> : <span className="text-muted">-</span>}
//               </td>
//             </tr>
//           ))}
//         </tbody>
//       </Table>
//     )
//   }

//   // --- Render PDF Replica (The core request) ---
//   const renderParameterPDF = (paramData) => {
//     const responses = paramData.responses || []
    
//     // Fallback if specific filtering isn't ready
//     const parametersToUse = Object.keys(filteredEvaluationParameters).length > 0 ? filteredEvaluationParameters : {
//         "CORE_COMPETENCIES": [], "BEHAVIORAL": [], "ROLE_FIT": []
//     }

//     // Helper to find response
//     const getResponse = (paramId) => {
//         return responses.find(r => r.parameter_id === paramId || r.parameter?.id === paramId);
//     }

//     // Helper to render a table row
//     const renderRow = (param) => {
//         const resp = getResponse(param.id);
//         return (
//             <tr key={param.id}>
//                 <td className="col-competency">{param.parameter_text}</td>
//                 <td className="col-desc">{param.description}</td>
//                 <td className="col-rating">{resp?.rating || ""}</td>
//                 <td className="col-comments">{resp?.comments || ""}</td>
//             </tr>
//         )
//     }

//     // Recommendation logic
//     const rec = paramData.overall_recommendation || "";
//     const isStrongly = rec.toLowerCase().includes("strongly recommend");
//     const isRec = !isStrongly && rec.toLowerCase().includes("recommend") && !rec.toLowerCase().includes("not");
//     const isNeutral = rec.toLowerCase().includes("neutral") || rec.toLowerCase().includes("hold");
//     const isNot = rec.toLowerCase().includes("not recommend");

//     // Arrays for lists
//     const strengths = Array.isArray(paramData.strengths) ? paramData.strengths : [paramData.strengths || ""];
//     const developments = Array.isArray(paramData.development_areas) ? paramData.development_areas : [paramData.development_areas || ""];

//     return (
//       <div className="pdf-viewer-container">
//         <div className="pdf-paper">
            
//             {/* Header */}
//             <div className="pdf-header">
//                 <h2>Post-Interview Employee Evaluation Form</h2>
//                 <div className="info-line">
//                     <div className="info-label">Candidate Name:</div>
//                     <div className="info-value">{paramData.candidate_name || selectedSession?.candidate_name}</div>
//                 </div>
//                 <div className="info-line">
//                     <div className="info-label">Position Applied For:</div>
//                     <div className="info-value">{selectedSession?.job_title}</div>
//                 </div>
//                 <div className="info-line">
//                     <div className="info-label">Department:</div>
//                     <div className="info-value">{paramData.department || "N/A"}</div>
//                 </div>
//                 <div className="info-line">
//                     <div className="info-label">Interview Date:</div>
//                     <div className="info-value">{formatDate(paramData.display_date || selectedSession?.date)}</div>
//                 </div>
//                 <div className="info-line">
//                     <div className="info-label">Interviewer(s):</div>
//                     <div className="info-value">{paramData.interviewer || "N/A"}</div>
//                 </div>
//                 <hr style={{ borderTop: "1px solid #ccc", marginTop: "20px" }} />
//             </div>

//             {/* Section 1: Core Competencies */}
//             <div className="section-header">
//                 <i className="bi bi-search"></i> 1. Core Competencies
//             </div>
//             <div className="rating-instruction">Rate 1 – 5 (1 = Poor, 5 = Excellent) and add comments/evidence:</div>
            
//             <table className="pdf-table">
//                 <thead>
//                     <tr>
//                         <th className="col-competency">Competency</th>
//                         <th className="col-desc">Description</th>
//                         <th className="col-rating">Rating</th>
//                         <th className="col-comments">Comments / Evidence</th>
//                     </tr>
//                 </thead>
//                 <tbody>
//                     {filteredEvaluationParameters["CORE_COMPETENCIES"]?.map(renderRow)}
//                     {(!filteredEvaluationParameters["CORE_COMPETENCIES"] || filteredEvaluationParameters["CORE_COMPETENCIES"].length === 0) && (
//                          <tr><td colSpan="4" className="text-center text-muted">No Core Competencies defined.</td></tr>
//                     )}
//                 </tbody>
//             </table>

//             {/* Section 2: Behavioral */}
//             <div className="section-header">
//                 <i className="bi bi-file-text"></i> 2. Additional Behavioral & Functional Parameters
//             </div>
//             <table className="pdf-table">
//                 <thead>
//                     <tr>
//                         <th className="col-competency">Parameter</th>
//                         <th className="col-desc">Description</th>
//                         <th className="col-rating">Rating</th>
//                         <th className="col-comments">Comments / Evidence</th>
//                     </tr>
//                 </thead>
//                 <tbody>
//                     {filteredEvaluationParameters["BEHAVIORAL"]?.map(renderRow)}
//                     {(!filteredEvaluationParameters["BEHAVIORAL"] || filteredEvaluationParameters["BEHAVIORAL"].length === 0) && (
//                          <tr><td colSpan="4" className="text-center text-muted">No Behavioral Parameters defined.</td></tr>
//                     )}
//                 </tbody>
//             </table>

//             {/* Section 3: Role Specific */}
//             <div className="section-header">
//                 <i className="bi bi-check-square"></i> 3. Role-Specific & Cultural Fit
//             </div>
//             <table className="pdf-table">
//                 <thead>
//                     <tr>
//                         <th className="col-competency">Attribute</th>
//                         <th className="col-desc">Description</th> {/* Using description column for spacing consistency */}
//                         <th className="col-rating">Rating</th>
//                         <th className="col-comments">Comments</th>
//                     </tr>
//                 </thead>
//                 <tbody>
//                     {filteredEvaluationParameters["ROLE_FIT"]?.map(renderRow)}
//                      {(!filteredEvaluationParameters["ROLE_FIT"] || filteredEvaluationParameters["ROLE_FIT"].length === 0) && (
//                          <tr><td colSpan="4" className="text-center text-muted">No Role-Specific Parameters defined.</td></tr>
//                     )}
//                 </tbody>
//             </table>

//             {/* Section 4: Overall Recommendation */}
//             <div className="section-header">
//                 <i className="bi bi-flag-fill"></i> 4. Overall Recommendation
//             </div>
            
//             <div className="list-section">
//                 <strong>• Overall Suitability:</strong>
//                 <div className="checkbox-group">
//                     <div className="checkbox-item">
//                         <span className="box-icon">{isStrongly ? "☒" : "☐"}</span> Strongly Recommend
//                     </div>
//                     <div className="checkbox-item">
//                         <span className="box-icon">{isRec ? "☒" : "☐"}</span> Recommend
//                     </div>
//                     <div className="checkbox-item">
//                         <span className="box-icon">{isNeutral ? "☒" : "☐"}</span> Neutral / Hold
//                     </div>
//                     <div className="checkbox-item">
//                         <span className="box-icon">{isNot ? "☒" : "☐"}</span> Do Not Recommend
//                     </div>
//                 </div>
//             </div>

//             <div className="list-section">
//                 <strong>• Top Strengths:</strong>
//                 <div className="mt-2">
//                     {[0, 1, 2].map(i => (
//                         <div className="list-line" key={i}>
//                             <div className="list-number">{i+1}.</div>
//                             <div className="list-content">{strengths[i] || ""}</div>
//                         </div>
//                     ))}
//                 </div>
//             </div>

//             <div className="list-section">
//                 <strong>• Key Development Areas:</strong>
//                 <div className="mt-2">
//                     {[0, 1, 2].map(i => (
//                         <div className="list-line" key={i}>
//                             <div className="list-number">{i+1}.</div>
//                             <div className="list-content">{developments[i] || ""}</div>
//                         </div>
//                     ))}
//                 </div>
//             </div>

//             <div className="list-section">
//                 <strong>• Final Comments:</strong>
//                 <div style={{ marginTop: "5px", border: "1px solid #ccc", minHeight: "80px", padding: "10px" }}>
//                     {paramData.final_comments}
//                 </div>
//             </div>

//             {/* Footer */}
//             <div className="signature-section">
//                 <div>Interviewer’s Signature: ________________________</div>
//                 <div>Date: <u>{formatDate(new Date())}</u></div>
//             </div>

//             <div className="text-muted text-center mt-5 small">
//                 Feel free to adjust any rating scales or add/remove parameters to suit your specific role or organizational priorities!
//             </div>
//         </div>
//       </div>
//     )
//   }

//   return (
//     <>
//       <CustomStyles />
//       <DashNavbar />
//       <div className="wrapper d-flex interviewer-feedback-page">
//         <Sidebar />
//         <div className="main-content flex-grow-1 p-3 p-md-4">
//           <Card className="shadow-sm mb-4 h-100">
//             <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center border-bottom">
//               <h4 className="mb-0 text-primary fw-bold">
//                 <i className="bi bi-clipboard-data me-2"></i>Feedback Evaluations
//               </h4>
//               <InputGroup style={{ maxWidth: "300px" }}>
//                 <InputGroup.Text className="bg-light">
//                   <i className="bi bi-search"></i>
//                 </InputGroup.Text>
//                 <Form.Control
//                   type="search"
//                   placeholder="Search candidates..."
//                   value={searchTerm}
//                   onChange={(e) => setSearchTerm(e.target.value)}
//                 />
//               </InputGroup>
//             </Card.Header>

//             <Card.Body className="p-0">
//               <div className="table-responsive">
//                 <Table hover className="align-middle mb-0">
//                   <thead className="table-light">
//                     <tr>
//                       <th className="ps-4">#</th>
//                       <th>Candidate Name</th>
//                       <th>Email</th>
//                       <th className="text-center">Interviews</th>
//                       <th className="text-center">Action</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {isLoading ? (
//                       <tr>
//                         <td colSpan="5" className="text-center py-5">
//                           <Spinner animation="border" variant="primary" />
//                         </td>
//                       </tr>
//                     ) : paginatedNames.length > 0 ? (
//                       paginatedNames.map((name, idx) => {
//                         const group = groupedCandidates[name]
//                         const isExpanded = expandedCandidate === name
//                         return (
//                           <React.Fragment key={name}>
//                             <tr className={isExpanded ? "table-active" : ""}>
//                               <td className="ps-4 text-muted">{(currentPage - 1) * itemsPerPage + idx + 1}</td>
//                               <td className="fw-bold text-dark">{name}</td>
//                               <td className="text-muted">{group.email}</td>
//                               <td className="text-center">
//                                 <Badge bg="secondary" pill>
//                                   {group.sessions.length}
//                                 </Badge>
//                               </td>
//                               <td className="text-center">
//                                 <Button
//                                   variant={isExpanded ? "primary" : "outline-primary"}
//                                   size="sm"
//                                   onClick={() => setExpandedCandidate(isExpanded ? null : name)}
//                                 >
//                                   {isExpanded ? (
//                                     <i className="bi bi-chevron-up"></i>
//                                   ) : (
//                                     <i className="bi bi-chevron-down"></i>
//                                   )}
//                                 </Button>
//                               </td>
//                             </tr>
//                             {isExpanded && (
//                               <tr className="sub-table-row">
//                                 <td colSpan="5">
//                                   <div className="sub-table-container fade-in">
//                                     <h6 className="mb-3 text-primary border-bottom pb-2">Interview History: {name}</h6>
//                                     <Table className="sub-table mb-0">
//                                       <thead>
//                                         <tr>
//                                           <th>Job Title</th>
//                                           <th>Opportunity Type</th>
//                                           <th>Opportunity ID</th>
//                                           <th>Date</th>
//                                           <th>Data Available</th>
//                                           <th className="text-center">Action</th>
//                                         </tr>
//                                       </thead>
//                                       <tbody>
//                                         {group.sessions.map((session) => (
//                                           <tr key={session.id}>
//                                             <td>{session.job_title}</td>
//                                             <td>
//                                               <Badge bg="light" text="dark" className="border">
//                                                 {session.job_type || "N/A"}
//                                               </Badge>
//                                             </td>
//                                             <td className="font-monospace text-muted small">
//                                               {session.job_posting_id}
//                                             </td>
//                                             <td>{formatDate(session.date)}</td>
//                                             <td>
//                                               {session.general_data && (
//                                                 <Badge bg="info" className="me-1">
//                                                   General
//                                                 </Badge>
//                                               )}
//                                               {session.parameter_data && <Badge bg="success">Params</Badge>}
//                                             </td>
//                                             <td className="text-center">
//                                               <Button
//                                                 variant="outline-dark"
//                                                 size="sm"
//                                                 onClick={() => handleViewDetails(session)}
//                                               >
//                                                 <i className="bi bi-eye me-1"></i> View Report
//                                               </Button>
//                                             </td>
//                                           </tr>
//                                         ))}
//                                       </tbody>
//                                     </Table>
//                                   </div>
//                                 </td>
//                               </tr>
//                             )}
//                           </React.Fragment>
//                         )
//                       })
//                     ) : (
//                       <tr>
//                         <td colSpan="5" className="text-center py-5 text-muted">
//                           No evaluations found.
//                         </td>
//                       </tr>
//                     )}
//                   </tbody>
//                 </Table>
//               </div>
//             </Card.Body>

//             {totalPages > 1 && (
//               <Card.Footer className="bg-white d-flex justify-content-end py-3">
//                 <Pagination>
//                   <Pagination.Prev
//                     onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
//                     disabled={currentPage === 1}
//                   />
//                   {[...Array(totalPages)].map((_, i) => (
//                     <Pagination.Item key={i + 1} active={i + 1 === currentPage} onClick={() => setCurrentPage(i + 1)}>
//                       {i + 1}
//                     </Pagination.Item>
//                   ))}
//                   <Pagination.Next
//                     onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
//                     disabled={currentPage === totalPages}
//                   />
//                 </Pagination>
//               </Card.Footer>
//             )}
//           </Card>
//         </div>
//       </div>

//       {/* --- Details Modal --- */}
//       <Modal show={selectedSession !== null} onHide={() => setSelectedSession(null)} size="xl" centered scrollable>
//         <Modal.Header closeButton className="bg-light border-bottom-0">
//           <Modal.Title className="h5 text-primary">Evaluation Details</Modal.Title>
//         </Modal.Header>
//         <Modal.Body className="p-0" style={{ background: modalViewMode === "parameters" ? "#525659" : "white" }}>
//           {selectedSession && (
//             <div className="d-flex flex-column h-100">
//               {/* Tabs */}
//               {modalViewMode !== "parameters" && (
//                   <div className="p-4 bg-light border-bottom">
//                     <h4 className="mb-1">{selectedSession.candidate_name}</h4>
//                     <div className="text-muted d-flex gap-3 small mt-2">
//                         <span><i className="bi bi-briefcase me-1"></i> {selectedSession.job_title}</span>
//                         <span><i className="bi bi-tag me-1"></i> {selectedSession.job_type}</span>
//                     </div>
//                   </div>
//               )}
              
//               <div className="px-4 py-2 bg-white sticky-top border-bottom">
//                 <ButtonGroup className="w-100 shadow-sm">
//                   <Button
//                     variant={modalViewMode === "general" ? "primary" : "outline-light text-dark border"}
//                     onClick={() => setModalViewMode("general")}
//                     disabled={!selectedSession.general_data}
//                   >
//                     General Questions {selectedSession.general_data ? "" : "(N/A)"}
//                   </Button>
//                   <Button
//                     variant={modalViewMode === "parameters" ? "primary" : "outline-light text-dark border"}
//                     onClick={() => setModalViewMode("parameters")}
//                     disabled={!selectedSession.parameter_data}
//                   >
//                     PDF Evaluation Form {selectedSession.parameter_data ? "" : "(N/A)"}
//                   </Button>
//                 </ButtonGroup>
//               </div>

//               {/* Content */}
//               <div className="flex-grow-1">
//                 {modalViewMode === "general" && selectedSession.general_data ? (
//                   <div className="p-4 fade-in">
//                     <h5 className="mb-3 text-secondary border-bottom pb-2">General Screening Questions</h5>
//                     {renderGeneralTable(selectedSession.general_data.evaluation_details)}
//                   </div>
//                 ) : modalViewMode === "parameters" && selectedSession.parameter_data ? (
//                   renderParameterPDF(selectedSession.parameter_data)
//                 ) : (
//                   <div className="text-center py-5 text-muted">
//                     <i className="bi bi-exclamation-circle fs-1"></i>
//                     <p className="mt-2">No data available for this section.</p>
//                   </div>
//                 )}
//               </div>
//             </div>
//           )}
//         </Modal.Body>
//         <Modal.Footer className="bg-light">
//           <Button variant="secondary" onClick={() => setSelectedSession(null)}>
//             Close
//           </Button>
//           <Button variant="primary" onClick={() => window.print()}>
//             <i className="bi bi-printer me-2"></i>Print / Save as PDF
//           </Button>
//         </Modal.Footer>
//       </Modal>
//     </>
//   )
// }

// export default InterviewerFeedback

// "use client"
// import React, { useState, useEffect } from "react"
// import axios from "axios"
// import DashNavbar from "./DashNavbar"
// import Sidebar from "./Sidebar"
// import { Pagination, Modal, Card, Form, InputGroup, Button, Table, Spinner, ButtonGroup, Badge } from "react-bootstrap"
// import Swal from "sweetalert2"

// // --- Custom CSS ---
// const CustomStyles = () => (
//   <style>{`
//     .interviewer-feedback-page .main-content { background-color: #f8f9fa; }
//     .gradient-text { background: linear-gradient(45deg, #0d6efd, #0d6efd); -webkit-background-clip: text; -webkit-text-fill-color: transparent; font-weight: 600; }
//     .interviewer-feedback-page.wrapper { display: flex; height: calc(100vh - 60px); }
//     .interviewer-feedback-page .main-content { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; }
//     .interviewer-feedback-page .card { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; }
//     .interviewer-feedback-page .card-body { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; padding: 0; }
//     .interviewer-feedback-page .table-responsive { flex-grow: 1; overflow-y: auto; padding: 1.5rem; padding-top: 0; }
    
//     /* Sub-table styling */
//     .sub-table-row td { padding: 0 !important; border: 0; background-color: #f8f9fa; }
//     .sub-table-container { padding: 1rem; border-top: 2px solid #0d6efd; background: white; margin: 10px; border-radius: 5px; box-shadow: 0 2px 4px rgba(0,0,0,0.05); }
//     .sub-table th { background-color: #e9ecef !important; font-size: 0.9rem; }
//     .sub-table td { font-size: 0.9rem; vertical-align: middle; }

//     /* PDF Modal Styling */
//     .modal-pdf-form .form-group-inline { display: flex; align-items: baseline; margin-bottom: 0.8rem; }
//     .modal-pdf-form .form-group-inline label { font-weight: 600; margin-right: 1rem; min-width: 150px; }
//     .modal-pdf-form .form-group-inline span { flex-grow: 1; border-bottom: 1px solid #ccc; padding-bottom: 2px; }
//     .modal-pdf-form .section-heading { font-size: 1.25rem; font-weight: 600; margin-top: 1.5rem; margin-bottom: 1rem; border-bottom: 2px solid #0d6efd; padding-bottom: 0.5rem; }
//     .modal-pdf-form .overall-recommendation-box { border: 1px solid #dee2e6; padding: 1.5rem; border-radius: .375rem; background-color: #f8f9fa; }
//     .modal-pdf-form .signature-line { margin-top: 3rem; border-top: 1px solid #ccc; padding-top: 0.5rem; display: flex; justify-content: space-between; color: #6c757d; }
//     .modal-pdf-form .form-title { text-align: center; font-size: 1.5rem; font-weight: 700; margin-bottom: 2rem; color: #0d6efd; border-bottom: 3px solid #0d6efd; padding-bottom: 1rem; }
    
//     .justified-text { text-align: justify; }
    
//     /* Parameter Section Styling */
//     .parameter-section { margin-bottom: 2rem; }
//     .parameter-section-title { font-size: 1.1rem; font-weight: 600; background-color: #e9ecef; padding: 0.75rem; margin-bottom: 1rem; border-left: 4px solid #0d6efd; }
//     .parameter-input-group { display: flex; gap: 1rem; align-items: center; margin-bottom: 0.5rem; }
//     .parameter-input-group input { max-width: 80px; }
//     .parameter-description { font-size: 0.9rem; color: #6c757d; margin-bottom: 0.5rem; font-style: italic; }
//   `}</style>
// )

// const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com"

// function InterviewerFeedback() {
//   // Data Stores
//   const [mergedEvaluations, setMergedEvaluations] = useState([])
//   const [groupedCandidates, setGroupedCandidates] = useState({})
//   const [evaluationParameters, setEvaluationParameters] = useState([]) // New state to store API parameters grouped by area

//   // UI State
//   const [searchTerm, setSearchTerm] = useState("")
//   const [currentPage, setCurrentPage] = useState(1)
//   const [itemsPerPage, setItemsPerPage] = useState(10)
//   const [isLoading, setIsLoading] = useState(false)
//   const [expandedCandidate, setExpandedCandidate] = useState(null)

//   // Modal State
//   const [selectedSession, setSelectedSession] = useState(null)
//   const [modalViewMode, setModalViewMode] = useState("general") // 'general' or 'parameters'

//   // Filtered Data State
//   const [filteredGeneralData, setFilteredGeneralData] = useState(null)
//   const [filteredParameterData, setFilteredParameterData] = useState(null)
//   const [filteredEvaluationParameters, setFilteredEvaluationParameters] = useState({})

//   // --- 1. Fetch Data ---
//   const fetchAllData = async () => {
//     setIsLoading(true)
//     try {
//       const parametersRes = await axios.get(`${API_BASE_URL}/job-postings/business-028/evaluation-parameters/`)
//       const allParametersData = parametersRes.data || []
//       const allParametersArray = Array.isArray(allParametersData) ? allParametersData : allParametersData.results || []

//       // Store ALL parameters globally for filtering later
//       setEvaluationParameters(allParametersArray)

//       // Fetch General Evaluations
//       // Fetch Parameterized Evaluations
//       const [generalRes, paramRes] = await Promise.all([
//         axios.get(`${API_BASE_URL}/interview-evaluations/`),
//         axios.get(`${API_BASE_URL}/evaluations/`),
//       ])

//       const generalList = generalRes.data || []
//       const safeGeneralList = Array.isArray(generalList) ? generalList : generalList.results || []

//       const paramList = paramRes.data || []
//       const safeParamList = Array.isArray(paramList) ? paramList : paramList.results || []

//       processAndMergeData(safeGeneralList, safeParamList)
//     } catch (error) {
//       console.error("Error fetching data:", error)
//       Swal.fire("Error", "Could not fetch evaluation data. Please check API connections.", "error")
//     } finally {
//       setIsLoading(false)
//     }
//   }

//   useEffect(() => {
//     fetchAllData()
//   }, [])

//   // --- 2. Process & Merge Logic ---
//   const processAndMergeData = (generalList, paramList) => {
//     const sessionMap = new Map()

//     const getKey = (item) => {
//       const jobId = item.job_posting || item.job_posting_id || item.linked_posting_id || "unknown-job"
//       const name = item.candidate_name || "unknown-candidate"
//       return `${name}|${jobId}`
//     }

//     generalList.forEach((item) => {
//       if (item.candidate_name === "N/A - Template") return

//       const key = getKey(item)
//       if (!sessionMap.has(key)) {
//         sessionMap.set(key, {
//           id: key,
//           candidate_name: item.candidate_name,
//           candidate_email: item.candidate_email,
//           job_title: item.job_title || "N/A",
//           job_type: item.job_type,
//           job_posting_id: item.job_posting,
//           date: item.evaluation_date,
//           general_data: item,
//           parameter_data: null,
//         })
//       } else {
//         const session = sessionMap.get(key)
//         session.general_data = item
//         if (!session.job_type) session.job_type = item.job_type
//         if (!session.job_posting_id) session.job_posting_id = item.job_posting
//       }
//     })

//     paramList.forEach((item) => {
//       const key = getKey(item)

//       if (sessionMap.has(key)) {
//         const session = sessionMap.get(key)
//         session.parameter_data = item
//         if (item.job_title) session.job_title = item.job_title
//       } else {
//         sessionMap.set(key, {
//           id: key,
//           candidate_name: item.candidate_name,
//           candidate_email: item.candidate_email || "N/A",
//           job_title: item.job_title || "N/A",
//           job_type: item.posting_type || "N/A",
//           job_posting_id: item.posting_id || item.job_posting || "N/A",
//           date: item.interview_date || item.created_at,
//           general_data: null,
//           parameter_data: item,
//         })
//       }
//     })

//     const mergedArray = Array.from(sessionMap.values())
//     setMergedEvaluations(mergedArray)
//     groupDataByCandidate(mergedArray)
//   }

//   const groupDataByCandidate = (data) => {
//     const groups = data.reduce((acc, session) => {
//       const name = session.candidate_name
//       if (!acc[name]) {
//         acc[name] = {
//           candidate_name: name,
//           email: session.candidate_email,
//           sessions: [],
//         }
//       }
//       acc[name].sessions.push(session)
//       return acc
//     }, {})
//     setGroupedCandidates(groups)
//   }

//   // --- 3. Filtering & Pagination ---
//   const candidateNames = Object.keys(groupedCandidates).filter(
//     (name) =>
//       name.toLowerCase().includes(searchTerm.toLowerCase()) ||
//       groupedCandidates[name].email?.toLowerCase().includes(searchTerm.toLowerCase()),
//   )

//   const totalPages = Math.ceil(candidateNames.length / itemsPerPage)
//   const paginatedNames = candidateNames.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

//   // --- 4. Helpers ---
//   const formatDate = (dateStr) => {
//     if (!dateStr) return "N/A"
//     return new Date(dateStr).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
//   }

//   const handleViewDetails = (session) => {
//     setSelectedSession(session)
//     setModalViewMode("general") // Reset to general first, then fetch filtered data
//     fetchFilteredDataForJob(session)
//   }

//   const fetchFilteredDataForJob = async (session) => {
//     try {
//       console.log("[v0] Fetching filtered data for session:", session)

//       const jobPostingId = session.job_posting_id
//       const businessOpportunity = session.general_data?.business_opportunity || null
//       const technicalConsultant = session.general_data?.technical_consultant || null

//       console.log(
//         "[v0] Filter criteria - Job:",
//         jobPostingId,
//         "Business:",
//         businessOpportunity,
//         "Consultant:",
//         technicalConsultant,
//       )

//       // Fetch General Evaluations filtered by job_posting
//       const generalRes = await axios.get(`${API_BASE_URL}/interview-evaluations/`)
//       const generalList = Array.isArray(generalRes.data) ? generalRes.data : generalRes.data.results || []
//       const filteredGeneral = generalList.find((item) => item.job_posting === jobPostingId)
//       console.log("[v0] Filtered general data:", filteredGeneral)

//       // Fetch Parameterized Evaluations filtered by posting_id
//       const paramRes = await axios.get(`${API_BASE_URL}/evaluations/`)
//       const paramList = Array.isArray(paramRes.data) ? paramRes.data : paramRes.data.results || []
//       const filteredParam = paramList.find((item) => item.posting_id === jobPostingId)
//       console.log("[v0] Filtered parameter data:", filteredParam)

//       setSelectedSession((prev) => ({
//         ...prev,
//         general_data: filteredGeneral,
//         parameter_data: filteredParam,
//       }))

//       if (Array.isArray(evaluationParameters) && evaluationParameters.length > 0) {
//         const matchedParameters = evaluationParameters.filter((param) => {
//           const jobMatch = param.job_posting === jobPostingId
//           const businessMatch = businessOpportunity === null || param.business_opportunity === businessOpportunity
//           const consultantMatch = technicalConsultant === null || param.technical_consultant === technicalConsultant

//           console.log(
//             "[v0] Param check:",
//             param.parameter_text,
//             "- Job match:",
//             jobMatch,
//             "- Business match:",
//             businessMatch,
//             "- Consultant match:",
//             consultantMatch,
//           )

//           return jobMatch && businessMatch && consultantMatch
//         })

//         console.log("[v0] Matched parameters count:", matchedParameters.length)

//         // Group the matched parameters by area
//         const groupedParams = matchedParameters.reduce((acc, param) => {
//           const area = param.area || "GENERAL"
//           if (!acc[area]) acc[area] = []
//           acc[area].push(param)
//           return acc
//         }, {})

//         setFilteredEvaluationParameters(groupedParams)
//       }
//     } catch (error) {
//       console.error("Error fetching filtered data:", error)
//     }
//   }

//   const renderGeneralTable = (details) => {
//     if (!details || details.length === 0) return <p className="text-muted">No general questions recorded.</p>

//     return (
//       <Table bordered hover>
//         <thead className="table-light">
//           <tr>
//             <th style={{ width: "70%" }}>Question</th>
//             <th className="text-center">Rating</th>
//           </tr>
//         </thead>
//         <tbody>
//           {details.map((d, i) => (
//             <tr key={i}>
//               <td className="justified-text">{d.question_text}</td>
//               <td className="text-center">
//                 {d.answer_value ? (
//                   <Badge bg="info" className="fs-6">
//                     {d.answer_value}/5
//                   </Badge>
//                 ) : (
//                   <span className="text-muted">-</span>
//                 )}
//               </td>
//             </tr>
//           ))}
//         </tbody>
//       </Table>
//     )
//   }

//   const renderParameterPDF = (paramData) => {
//     const responses = paramData.responses || []

//     const parametersToUse =
//       Object.keys(filteredEvaluationParameters).length > 0 ? filteredEvaluationParameters : evaluationParameters

//     const allowedAreas = ["CORE_COMPETENCIES", "BEHAVIORAL", "ROLE_FIT"]
//     const filteredParameters = Object.entries(parametersToUse)
//       .filter(([area]) => allowedAreas.includes(area))
//       .reduce((acc, [area, params]) => {
//         acc[area] = params
//         return acc
//       }, {})

//     console.log("[v0] Rendering with parameters:", Object.keys(filteredParameters))

//     return (
//       <div className="modal-pdf-form fade-in">
//         <div className="form-title">Post-Interview Evaluation</div>

//         {/* Metadata Section */}
//         <div className="mb-4 p-3 bg-light border rounded">
//           <div className="form-group-inline">
//             <label>Interviewer:</label>
//             <span>{paramData.interviewer || "N/A"}</span>
//           </div>
//           <div className="form-group-inline">
//             <label>Date:</label>
//             <span>{formatDate(paramData.display_date || selectedSession?.date)}</span>
//           </div>
//         </div>

//         {Object.keys(filteredParameters).length > 0 ? (
//           Object.entries(filteredParameters).map(([area, params], idx) => (
//             <div key={idx} className="parameter-section">
//               <div className="parameter-section-title">{area.replace(/_/g, " ")}</div>
//               <Table bordered size="sm">
//                 <thead className="table-secondary">
//                   <tr>
//                     <th style={{ width: "30%" }}>Parameter</th>
//                     <th style={{ width: "50%" }}>Description</th>
//                     <th className="text-center" style={{ width: "20%" }}>
//                       Rating / Comments
//                     </th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {params.map((param, pIdx) => {
//                     const matchingResponse = responses.find(
//                       (r) => r.parameter_id === param.id || r.parameter?.id === param.id,
//                     )
//                     return (
//                       <tr key={pIdx}>
//                         <td className="fw-bold">{param.parameter_text}</td>
//                         <td>
//                           <div className="parameter-description">{param.description}</div>
//                         </td>
//                         <td className="text-center align-middle">
//                           {matchingResponse ? (
//                             <>
//                               <Badge
//                                 bg={
//                                   matchingResponse.rating >= 4
//                                     ? "success"
//                                     : matchingResponse.rating >= 3
//                                       ? "warning"
//                                       : "danger"
//                                 }
//                                 className="mb-2"
//                               >
//                                 {matchingResponse.rating || "-"}
//                               </Badge>
//                               {matchingResponse.comments && (
//                                 <div className="small mt-1">{matchingResponse.comments}</div>
//                               )}
//                             </>
//                           ) : (
//                             <span className="text-muted">-</span>
//                           )}
//                         </td>
//                       </tr>
//                     )
//                   })}
//                 </tbody>
//               </Table>
//             </div>
//           ))
//         ) : (
//           <p className="text-muted text-center">No evaluation parameters found for this job.</p>
//         )}

//         {/* Summary Box */}
//         <div className="overall-recommendation-box mt-4">
//           <h5 className="text-primary mb-3">Final Assessment</h5>

//           <div className="mb-3">
//             <strong>Overall Recommendation: </strong>
//             <Badge bg="dark" className="ms-2 fs-6">
//               {paramData.overall_recommendation || "N/A"}
//             </Badge>
//           </div>

//           <div className="row">
//             <div className="col-md-6">
//               <strong className="text-success">Strengths:</strong>
//               <ul className="mt-1">
//                 {Array.isArray(paramData.strengths) ? (
//                   paramData.strengths.map((s, i) => <li key={i}>{s}</li>)
//                 ) : (
//                   <li>{paramData.strengths || "None listed"}</li>
//                 )}
//               </ul>
//             </div>
//             <div className="col-md-6">
//               <strong className="text-warning">Development Areas:</strong>
//               <ul className="mt-1">
//                 {Array.isArray(paramData.development_areas) ? (
//                   paramData.development_areas.map((s, i) => <li key={i}>{s}</li>)
//                 ) : (
//                   <li>{paramData.development_areas || "None listed"}</li>
//                 )}
//               </ul>
//             </div>
//           </div>

//           <div className="mt-3">
//             <strong>Final Comments:</strong>
//             <p className="mt-1 justified-text bg-white p-2 border rounded">
//               {paramData.final_comments || "No final comments."}
//             </p>
//           </div>
//         </div>

//         <div className="signature-line">
//           <span>Signature: _______________________</span>
//           <span>Date: {formatDate(new Date())}</span>
//         </div>
//       </div>
//     )
//   }

//   return (
//     <>
//       <CustomStyles />
//       <DashNavbar />
//       <div className="wrapper d-flex interviewer-feedback-page">
//         <Sidebar />
//         <div className="main-content flex-grow-1 p-3 p-md-4">
//           <Card className="shadow-sm mb-4 h-100">
//             <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center border-bottom">
//               <h4 className="mb-0 text-primary fw-bold">
//                 <i className="bi bi-clipboard-data me-2"></i>Feedback Evaluations
//               </h4>
//               <InputGroup style={{ maxWidth: "300px" }}>
//                 <InputGroup.Text className="bg-light">
//                   <i className="bi bi-search"></i>
//                 </InputGroup.Text>
//                 <Form.Control
//                   type="search"
//                   placeholder="Search candidates..."
//                   value={searchTerm}
//                   onChange={(e) => setSearchTerm(e.target.value)}
//                 />
//               </InputGroup>
//             </Card.Header>

//             <Card.Body className="p-0">
//               <div className="table-responsive">
//                 <Table hover className="align-middle mb-0">
//                   <thead className="table-light">
//                     <tr>
//                       <th className="ps-4">#</th>
//                       <th>Candidate Name</th>
//                       <th>Email</th>
//                       <th className="text-center">Interviews</th>
//                       <th className="text-center">Action</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {isLoading ? (
//                       <tr>
//                         <td colSpan="5" className="text-center py-5">
//                           <Spinner animation="border" variant="primary" />
//                         </td>
//                       </tr>
//                     ) : paginatedNames.length > 0 ? (
//                       paginatedNames.map((name, idx) => {
//                         const group = groupedCandidates[name]
//                         const isExpanded = expandedCandidate === name
//                         return (
//                           <React.Fragment key={name}>
//                             <tr className={isExpanded ? "table-active" : ""}>
//                               <td className="ps-4 text-muted">{(currentPage - 1) * itemsPerPage + idx + 1}</td>
//                               <td className="fw-bold text-dark">{name}</td>
//                               <td className="text-muted">{group.email}</td>
//                               <td className="text-center">
//                                 <Badge bg="secondary" pill>
//                                   {group.sessions.length}
//                                 </Badge>
//                               </td>
//                               <td className="text-center">
//                                 <Button
//                                   variant={isExpanded ? "primary" : "outline-primary"}
//                                   size="sm"
//                                   onClick={() => setExpandedCandidate(isExpanded ? null : name)}
//                                 >
//                                   {isExpanded ? (
//                                     <i className="bi bi-chevron-up"></i>
//                                   ) : (
//                                     <i className="bi bi-chevron-down"></i>
//                                   )}
//                                 </Button>
//                               </td>
//                             </tr>
//                             {isExpanded && (
//                               <tr className="sub-table-row">
//                                 <td colSpan="5">
//                                   <div className="sub-table-container fade-in">
//                                     <h6 className="mb-3 text-primary border-bottom pb-2">Interview History: {name}</h6>
//                                     <Table className="sub-table mb-0">
//                                       <thead>
//                                         <tr>
//                                           <th>Job Title</th>
//                                           <th>Opportunity Type</th>
//                                           <th>Opportunity ID</th>
//                                           <th>Date</th>
//                                           <th>Data Available</th>
//                                           <th className="text-center">Action</th>
//                                         </tr>
//                                       </thead>
//                                       <tbody>
//                                         {group.sessions.map((session) => (
//                                           <tr key={session.id}>
//                                             <td>{session.job_title}</td>
//                                             <td>
//                                               <Badge bg="light" text="dark" className="border">
//                                                 {session.job_type || "N/A"}
//                                               </Badge>
//                                             </td>
//                                             <td className="font-monospace text-muted small">
//                                               {session.job_posting_id}
//                                             </td>
//                                             <td>{formatDate(session.date)}</td>
//                                             <td>
//                                               {session.general_data && (
//                                                 <Badge bg="info" className="me-1">
//                                                   General
//                                                 </Badge>
//                                               )}
//                                               {session.parameter_data && <Badge bg="success">Params</Badge>}
//                                             </td>
//                                             <td className="text-center">
//                                               <Button
//                                                 variant="outline-dark"
//                                                 size="sm"
//                                                 onClick={() => handleViewDetails(session)}
//                                               >
//                                                 <i className="bi bi-eye me-1"></i> View Report
//                                               </Button>
//                                             </td>
//                                           </tr>
//                                         ))}
//                                       </tbody>
//                                     </Table>
//                                   </div>
//                                 </td>
//                               </tr>
//                             )}
//                           </React.Fragment>
//                         )
//                       })
//                     ) : (
//                       <tr>
//                         <td colSpan="5" className="text-center py-5 text-muted">
//                           No evaluations found.
//                         </td>
//                       </tr>
//                     )}
//                   </tbody>
//                 </Table>
//               </div>
//             </Card.Body>

//             {totalPages > 1 && (
//               <Card.Footer className="bg-white d-flex justify-content-end py-3">
//                 <Pagination>
//                   <Pagination.Prev
//                     onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
//                     disabled={currentPage === 1}
//                   />
//                   {[...Array(totalPages)].map((_, i) => (
//                     <Pagination.Item key={i + 1} active={i + 1 === currentPage} onClick={() => setCurrentPage(i + 1)}>
//                       {i + 1}
//                     </Pagination.Item>
//                   ))}
//                   <Pagination.Next
//                     onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
//                     disabled={currentPage === totalPages}
//                   />
//                 </Pagination>
//               </Card.Footer>
//             )}
//           </Card>
//         </div>
//       </div>

//       {/* --- Details Modal --- */}
//       <Modal show={selectedSession !== null} onHide={() => setSelectedSession(null)} size="xl" centered scrollable>
//         <Modal.Header closeButton className="bg-light border-bottom-0">
//           <Modal.Title className="h5 text-primary">Evaluation Details</Modal.Title>
//         </Modal.Header>
//         <Modal.Body className="p-0">
//           {selectedSession && (
//             <div className="d-flex flex-column h-100">
//               {/* Header Info */}
//               <div className="p-4 bg-light border-bottom">
//                 <h4 className="mb-1">{selectedSession.candidate_name}</h4>
//                 <div className="text-muted d-flex gap-3 small mt-2">
//                   <span>
//                     <i className="bi bi-briefcase me-1"></i> {selectedSession.job_title}
//                   </span>
//                   <span>
//                     <i className="bi bi-tag me-1"></i> {selectedSession.job_type} ({selectedSession.job_posting_id})
//                   </span>
//                   <span>
//                     <i className="bi bi-calendar me-1"></i> {formatDate(selectedSession.date)}
//                   </span>
//                 </div>
//               </div>

//               {/* Tabs */}
//               <div className="px-4 pt-3 pb-0 bg-white">
//                 <ButtonGroup className="w-100 shadow-sm">
//                   <Button
//                     variant={modalViewMode === "general" ? "primary" : "outline-light text-dark border"}
//                     onClick={() => setModalViewMode("general")}
//                     disabled={!selectedSession.general_data}
//                   >
//                     General Questions {selectedSession.general_data ? "" : "(N/A)"}
//                   </Button>
//                   <Button
//                     variant={modalViewMode === "parameters" ? "primary" : "outline-light text-dark border"}
//                     onClick={() => setModalViewMode("parameters")}
//                     disabled={!selectedSession.parameter_data}
//                   >
//                     Parametrized Assessment {selectedSession.parameter_data ? "" : "(N/A)"}
//                   </Button>
//                 </ButtonGroup>
//               </div>

//               {/* Content */}
//               <div className="p-4 bg-white flex-grow-1">
//                 {modalViewMode === "general" && selectedSession.general_data ? (
//                   <div className="fade-in">
//                     <h5 className="mb-3 text-secondary border-bottom pb-2">General Screening Questions</h5>
//                     {renderGeneralTable(selectedSession.general_data.evaluation_details)}
//                   </div>
//                 ) : modalViewMode === "parameters" && selectedSession.parameter_data ? (
//                   renderParameterPDF(selectedSession.parameter_data)
//                 ) : (
//                   <div className="text-center py-5 text-muted">
//                     <i className="bi bi-exclamation-circle fs-1"></i>
//                     <p className="mt-2">No data available for this section.</p>
//                   </div>
//                 )}
//               </div>
//             </div>
//           )}
//         </Modal.Body>
//         <Modal.Footer className="bg-light">
//           <Button variant="secondary" onClick={() => setSelectedSession(null)}>
//             Close
//           </Button>
//           <Button variant="primary" onClick={() => window.print()}>
//             <i className="bi bi-printer me-2"></i>Print Report
//           </Button>
//         </Modal.Footer>
//       </Modal>
//     </>
//   )
// }

// export default InterviewerFeedback




// "use client"
// import React, { useState, useEffect } from "react"
// import axios from "axios"
// import DashNavbar from "./DashNavbar"
// import Sidebar from "./Sidebar"
// import { Pagination, Modal, Card, Form, InputGroup, Button, Table, Spinner, ButtonGroup, Badge } from "react-bootstrap"
// import Swal from "sweetalert2"

// // --- Custom CSS ---
// const CustomStyles = () => (
//   <style>{`
//     .interviewer-feedback-page .main-content { background-color: #f8f9fa; }
//     .gradient-text { background: linear-gradient(45deg, #0d6efd, #0d6efd); -webkit-background-clip: text; -webkit-text-fill-color: transparent; font-weight: 600; }
//     .interviewer-feedback-page.wrapper { display: flex; height: calc(100vh - 60px); }
//     .interviewer-feedback-page .main-content { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; }
//     .interviewer-feedback-page .card { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; }
//     .interviewer-feedback-page .card-body { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; padding: 0; }
//     .interviewer-feedback-page .table-responsive { flex-grow: 1; overflow-y: auto; padding: 1.5rem; padding-top: 0; }
    
//     /* Sub-table styling */
//     .sub-table-row td { padding: 0 !important; border: 0; background-color: #f8f9fa; }
//     .sub-table-container { padding: 1rem; border-top: 2px solid #0d6efd; background: white; margin: 10px; border-radius: 5px; box-shadow: 0 2px 4px rgba(0,0,0,0.05); }
//     .sub-table th { background-color: #e9ecef !important; font-size: 0.9rem; }
//     .sub-table td { font-size: 0.9rem; vertical-align: middle; }

//     /* PDF Modal Styling */
//     .modal-pdf-form .form-group-inline { display: flex; align-items: baseline; margin-bottom: 0.8rem; }
//     .modal-pdf-form .form-group-inline label { font-weight: 600; margin-right: 1rem; min-width: 150px; }
//     .modal-pdf-form .form-group-inline span { flex-grow: 1; border-bottom: 1px solid #ccc; padding-bottom: 2px; }
//     .modal-pdf-form .section-heading { font-size: 1.25rem; font-weight: 600; margin-top: 1.5rem; margin-bottom: 1rem; border-bottom: 2px solid #0d6efd; padding-bottom: 0.5rem; }
//     .modal-pdf-form .overall-recommendation-box { border: 1px solid #dee2e6; padding: 1.5rem; border-radius: .375rem; background-color: #f8f9fa; }
//     .modal-pdf-form .signature-line { margin-top: 3rem; border-top: 1px solid #ccc; padding-top: 0.5rem; display: flex; justify-content: space-between; color: #6c757d; }
//     .modal-pdf-form .form-title { text-align: center; font-size: 1.5rem; font-weight: 700; margin-bottom: 2rem; color: #0d6efd; border-bottom: 3px solid #0d6efd; padding-bottom: 1rem; }
    
//     .justified-text { text-align: justify; }
    
//     /* Parameter Section Styling */
//     .parameter-section { margin-bottom: 2rem; }
//     .parameter-section-title { font-size: 1.1rem; font-weight: 600; background-color: #e9ecef; padding: 0.75rem; margin-bottom: 1rem; border-left: 4px solid #0d6efd; }
//     .parameter-input-group { display: flex; gap: 1rem; align-items: center; margin-bottom: 0.5rem; }
//     .parameter-input-group input { max-width: 80px; }
//     .parameter-description { font-size: 0.9rem; color: #6c757d; margin-bottom: 0.5rem; font-style: italic; }
//   `}</style>
// )

// const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com"

// function InterviewerFeedback() {
//   // Data Stores
//   const [mergedEvaluations, setMergedEvaluations] = useState([])
//   const [groupedCandidates, setGroupedCandidates] = useState({})
//   const [evaluationParameters, setEvaluationParameters] = useState([]) // New state to store API parameters grouped by area
//   const [allGeneralEvaluations, setAllGeneralEvaluations] = useState([]) // State to store all general evaluations for later filtering

//   // UI State
//   const [searchTerm, setSearchTerm] = useState("")
//   const [currentPage, setCurrentPage] = useState(1)
//   const [itemsPerPage, setItemsPerPage] = useState(10)
//   const [isLoading, setIsLoading] = useState(false)
//   const [expandedCandidate, setExpandedCandidate] = useState(null)

//   // Modal State
//   const [selectedSession, setSelectedSession] = useState(null)
//   const [modalViewMode, setModalViewMode] = useState("general") // 'general' or 'parameters'

//   // Filtered Data State
//   const [filteredGeneralData, setFilteredGeneralData] = useState(null)
//   const [filteredParameterData, setFilteredParameterData] = useState(null)
//   const [filteredEvaluationParameters, setFilteredEvaluationParameters] = useState({})

//   // --- 1. Fetch Data ---
//   const fetchAllData = async () => {
//     setIsLoading(true)
//     try {
//       const parametersRes = await axios.get(`${API_BASE_URL}/job-postings/business-028/evaluation-parameters/`)
//       const allParametersData = parametersRes.data || []
//       const allParametersArray = Array.isArray(allParametersData) ? allParametersData : allParametersData.results || []

//       // Store ALL parameters globally for filtering later
//       setEvaluationParameters(allParametersArray)

//       // Fetch General Evaluations
//       // Fetch Parameterized Evaluations
//       const [generalRes, paramRes] = await Promise.all([
//         axios.get(`${API_BASE_URL}/interview-evaluations/`),
//         axios.get(`${API_BASE_URL}/evaluations/`),
//       ])

//       const generalList = generalRes.data || []
//       const safeGeneralList = Array.isArray(generalList) ? generalList : generalList.results || []

//       setAllGeneralEvaluations(safeGeneralList)

//       const paramList = paramRes.data || []
//       const safeParamList = Array.isArray(paramList) ? paramList : paramList.results || []

//       processAndMergeData(safeGeneralList, safeParamList)
//     } catch (error) {
//       console.error("Error fetching data:", error)
//       Swal.fire("Error", "Could not fetch evaluation data. Please check API connections.", "error")
//     } finally {
//       setIsLoading(false)
//     }
//   }

//   useEffect(() => {
//     fetchAllData()
//   }, [])

//   // --- 2. Process & Merge Logic ---
//   const processAndMergeData = (generalList, paramList) => {
//     const sessionMap = new Map()

//     const getKey = (item) => {
//       const jobId = item.job_posting || item.job_posting_id || item.linked_posting_id || "unknown-job"
//       const name = item.candidate_name || "unknown-candidate"
//       return `${name}|${jobId}`
//     }

//     generalList.forEach((item) => {
//       if (item.candidate_name === "N/A - Template") return

//       const key = getKey(item)
//       if (!sessionMap.has(key)) {
//         sessionMap.set(key, {
//           id: key,
//           candidate_name: item.candidate_name,
//           candidate_email: item.candidate_email,
//           job_title: item.job_title || "N/A",
//           job_type: item.job_type,
//           job_posting_id: item.job_posting,
//           date: item.evaluation_date,
//           general_data: item,
//           parameter_data: null,
//         })
//       } else {
//         const session = sessionMap.get(key)
//         session.general_data = item
//         if (!session.job_type) session.job_type = item.job_type
//         if (!session.job_posting_id) session.job_posting_id = item.job_posting
//       }
//     })

//     paramList.forEach((item) => {
//       const key = getKey(item)

//       if (sessionMap.has(key)) {
//         const session = sessionMap.get(key)
//         session.parameter_data = item
//         if (item.job_title) session.job_title = item.job_title
//       } else {
//         sessionMap.set(key, {
//           id: key,
//           candidate_name: item.candidate_name,
//           candidate_email: item.candidate_email || "N/A",
//           job_title: item.job_title || "N/A",
//           job_type: item.posting_type || "N/A",
//           job_posting_id: item.posting_id || item.job_posting || "N/A",
//           date: item.interview_date || item.created_at,
//           general_data: null,
//           parameter_data: item,
//         })
//       }
//     })

//     const mergedArray = Array.from(sessionMap.values())
//     setMergedEvaluations(mergedArray)
//     groupDataByCandidate(mergedArray)
//   }

//   const groupDataByCandidate = (data) => {
//     const groups = data.reduce((acc, session) => {
//       const name = session.candidate_name
//       if (!acc[name]) {
//         acc[name] = {
//           candidate_name: name,
//           email: session.candidate_email,
//           sessions: [],
//         }
//       }
//       acc[name].sessions.push(session)
//       return acc
//     }, {})
//     setGroupedCandidates(groups)
//   }

//   // --- 3. Filtering & Pagination ---
//   const candidateNames = Object.keys(groupedCandidates).filter(
//     (name) =>
//       name.toLowerCase().includes(searchTerm.toLowerCase()) ||
//       groupedCandidates[name].email?.toLowerCase().includes(searchTerm.toLowerCase()),
//   )

//   const totalPages = Math.ceil(candidateNames.length / itemsPerPage)
//   const paginatedNames = candidateNames.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

//   // --- 4. Helpers ---
//   const formatDate = (dateStr) => {
//     if (!dateStr) return "N/A"
//     return new Date(dateStr).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
//   }

//   // Fetch and filter general evaluations based on job_posting
//   const fetchFilteredGeneralData = async (jobPostingId) => {
//     try {
//       const generalRes = await axios.get(`${API_BASE_URL}/interview-evaluations/`)
//       const generalList = generalRes.data || []
//       const safeGeneralList = Array.isArray(generalList) ? generalList : generalList.results || []

//       // Filter general evaluations by job_posting
//       const filteredGeneral = safeGeneralList.filter((item) => item.job_posting === jobPostingId)

//       return filteredGeneral.length > 0 ? filteredGeneral[0] : null
//     } catch (error) {
//       console.error("Error fetching filtered general data:", error)
//       return null
//     }
//   }

//   const handleViewDetails = async (session) => {
//     setSelectedSession(session)
//     setModalViewMode("general")

//     try {
//       // Fetch and filter general evaluations by job_posting
//       const filteredGeneral = await fetchFilteredGeneralData(session.job_posting_id)

//       // Fetch and filter parametrized evaluations
//       const jobPostingId = session.job_posting_id
//       const businessOpp = session.business_opportunity || "business-028"
//       const consultant = session.technical_consultant || "consultant-023"

//       const filteredParams = evaluationParameters.filter(
//         (param) =>
//           param.job_posting === jobPostingId &&
//           param.business_opportunity === businessOpp &&
//           param.technical_consultant === consultant,
//       )

//       const groupedByArea = {}
//       if (filteredParams.length > 0) {
//         filteredParams.forEach((param) => {
//           const area = param.area || "General"
//           if (!groupedByArea[area]) {
//             groupedByArea[area] = []
//           }
//           groupedByArea[area].push(param)
//         })
//       }

//       setSelectedSession({
//         ...session,
//         general_data: filteredGeneral,
//         parameter_data: Object.keys(groupedByArea).length > 0 ? groupedByArea : null,
//       })
//     } catch (error) {
//       console.error("Error fetching filtered data:", error)
//     }
//   }

//   const renderGeneralTable = (details) => {
//     if (!details || details.length === 0) return <p className="text-muted">No general questions recorded.</p>

//     return (
//       <Table bordered hover>
//         <thead className="table-light">
//           <tr>
//             <th style={{ width: "70%" }}>Question</th>
//             <th className="text-center">Rating</th>
//           </tr>
//         </thead>
//         <tbody>
//           {details.map((d, i) => (
//             <tr key={i}>
//               <td className="justified-text">{d.question_text}</td>
//               <td className="text-center">
//                 {d.answer_value ? (
//                   <Badge bg="info" className="fs-6">
//                     {d.answer_value}/5
//                   </Badge>
//                 ) : (
//                   <span className="text-muted">-</span>
//                 )}
//               </td>
//             </tr>
//           ))}
//         </tbody>
//       </Table>
//     )
//   }

//   const renderParameterPDF = (paramData) => {
//     const responses = paramData.responses || []

//     const parametersToUse =
//       Object.keys(filteredEvaluationParameters).length > 0 ? filteredEvaluationParameters : evaluationParameters

//     const allowedAreas = ["CORE_COMPETENCIES", "BEHAVIORAL", "ROLE_FIT"]
//     const filteredParameters = Object.entries(parametersToUse)
//       .filter(([area]) => allowedAreas.includes(area))
//       .reduce((acc, [area, params]) => {
//         acc[area] = params
//         return acc
//       }, {})

//     console.log("[v0] Rendering with parameters:", Object.keys(filteredParameters))

//     return (
//       <div className="modal-pdf-form fade-in">
//         <div className="form-title">Post-Interview Evaluation</div>

//         {/* Metadata Section */}
//         <div className="mb-4 p-3 bg-light border rounded">
//           <div className="form-group-inline">
//             <label>Interviewer:</label>
//             <span>{paramData.interviewer || "N/A"}</span>
//           </div>
//           <div className="form-group-inline">
//             <label>Date:</label>
//             <span>{formatDate(paramData.display_date || selectedSession?.date)}</span>
//           </div>
//         </div>

//         {Object.keys(filteredParameters).length > 0 ? (
//           Object.entries(filteredParameters).map(([area, params], idx) => (
//             <div key={idx} className="parameter-section">
//               <div className="parameter-section-title">{area.replace(/_/g, " ")}</div>
//               <Table bordered size="sm">
//                 <thead className="table-secondary">
//                   <tr>
//                     <th style={{ width: "30%" }}>Parameter</th>
//                     <th style={{ width: "50%" }}>Description</th>
//                     <th className="text-center" style={{ width: "20%" }}>
//                       Rating / Comments
//                     </th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {params.map((param, pIdx) => {
//                     const matchingResponse = responses.find(
//                       (r) => r.parameter_id === param.id || r.parameter?.id === param.id,
//                     )
//                     return (
//                       <tr key={pIdx}>
//                         <td className="fw-bold">{param.parameter_text}</td>
//                         <td>
//                           <div className="parameter-description">{param.description}</div>
//                         </td>
//                         <td className="text-center align-middle">
//                           {matchingResponse ? (
//                             <>
//                               <Badge
//                                 bg={
//                                   matchingResponse.rating >= 4
//                                     ? "success"
//                                     : matchingResponse.rating >= 3
//                                       ? "warning"
//                                       : "danger"
//                                 }
//                                 className="mb-2"
//                               >
//                                 {matchingResponse.rating || "-"}
//                               </Badge>
//                               {matchingResponse.comments && (
//                                 <div className="small mt-1">{matchingResponse.comments}</div>
//                               )}
//                             </>
//                           ) : (
//                             <span className="text-muted">-</span>
//                           )}
//                         </td>
//                       </tr>
//                     )
//                   })}
//                 </tbody>
//               </Table>
//             </div>
//           ))
//         ) : (
//           <p className="text-muted text-center">No evaluation parameters found for this job.</p>
//         )}

//         {/* Summary Box */}
//         <div className="overall-recommendation-box mt-4">
//           <h5 className="text-primary mb-3">Final Assessment</h5>

//           <div className="mb-3">
//             <strong>Overall Recommendation: </strong>
//             <Badge bg="dark" className="ms-2 fs-6">
//               {paramData.overall_recommendation || "N/A"}
//             </Badge>
//           </div>

//           <div className="row">
//             <div className="col-md-6">
//               <strong className="text-success">Strengths:</strong>
//               <ul className="mt-1">
//                 {Array.isArray(paramData.strengths) ? (
//                   paramData.strengths.map((s, i) => <li key={i}>{s}</li>)
//                 ) : (
//                   <li>{paramData.strengths || "None listed"}</li>
//                 )}
//               </ul>
//             </div>
//             <div className="col-md-6">
//               <strong className="text-warning">Development Areas:</strong>
//               <ul className="mt-1">
//                 {Array.isArray(paramData.development_areas) ? (
//                   paramData.development_areas.map((s, i) => <li key={i}>{s}</li>)
//                 ) : (
//                   <li>{paramData.development_areas || "None listed"}</li>
//                 )}
//               </ul>
//             </div>
//           </div>

//           <div className="mt-3">
//             <strong>Final Comments:</strong>
//             <p className="mt-1 justified-text bg-white p-2 border rounded">
//               {paramData.final_comments || "No final comments."}
//             </p>
//           </div>
//         </div>

//         <div className="signature-line">
//           <span>Signature: _______________________</span>
//           <span>Date: {formatDate(new Date())}</span>
//         </div>
//       </div>
//     )
//   }

//   return (
//     <>
//       <CustomStyles />
//       <DashNavbar />
//       <div className="wrapper d-flex interviewer-feedback-page">
//         <Sidebar />
//         <div className="main-content flex-grow-1 p-3 p-md-4">
//           <Card className="shadow-sm mb-4 h-100">
//             <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center border-bottom">
//               <h4 className="mb-0 text-primary fw-bold">
//                 <i className="bi bi-clipboard-data me-2"></i>Feedback Evaluations
//               </h4>
//               <InputGroup style={{ maxWidth: "300px" }}>
//                 <InputGroup.Text className="bg-light">
//                   <i className="bi bi-search"></i>
//                 </InputGroup.Text>
//                 <Form.Control
//                   type="search"
//                   placeholder="Search candidates..."
//                   value={searchTerm}
//                   onChange={(e) => setSearchTerm(e.target.value)}
//                 />
//               </InputGroup>
//             </Card.Header>

//             <Card.Body className="p-0">
//               <div className="table-responsive">
//                 <Table hover className="align-middle mb-0">
//                   <thead className="table-light">
//                     <tr>
//                       <th className="ps-4">#</th>
//                       <th>Candidate Name</th>
//                       <th>Email</th>
//                       <th className="text-center">Interviews</th>
//                       <th className="text-center">Action</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {isLoading ? (
//                       <tr>
//                         <td colSpan="5" className="text-center py-5">
//                           <Spinner animation="border" variant="primary" />
//                         </td>
//                       </tr>
//                     ) : paginatedNames.length > 0 ? (
//                       paginatedNames.map((name, idx) => {
//                         const group = groupedCandidates[name]
//                         const isExpanded = expandedCandidate === name
//                         return (
//                           <React.Fragment key={name}>
//                             <tr className={isExpanded ? "table-active" : ""}>
//                               <td className="ps-4 text-muted">{(currentPage - 1) * itemsPerPage + idx + 1}</td>
//                               <td className="fw-bold text-dark">{name}</td>
//                               <td className="text-muted">{group.email}</td>
//                               <td className="text-center">
//                                 <Badge bg="secondary" pill>
//                                   {group.sessions.length}
//                                 </Badge>
//                               </td>
//                               <td className="text-center">
//                                 <Button
//                                   variant={isExpanded ? "primary" : "outline-primary"}
//                                   size="sm"
//                                   onClick={() => setExpandedCandidate(isExpanded ? null : name)}
//                                 >
//                                   {isExpanded ? (
//                                     <i className="bi bi-chevron-up"></i>
//                                   ) : (
//                                     <i className="bi bi-chevron-down"></i>
//                                   )}
//                                 </Button>
//                               </td>
//                             </tr>
//                             {isExpanded && (
//                               <tr className="sub-table-row">
//                                 <td colSpan="5">
//                                   <div className="sub-table-container fade-in">
//                                     <h6 className="mb-3 text-primary border-bottom pb-2">Interview History: {name}</h6>
//                                     <Table className="sub-table mb-0">
//                                       <thead>
//                                         <tr>
//                                           <th>Job Title</th>
//                                           <th>Opportunity Type</th>
//                                           <th>Opportunity ID</th>
//                                           <th>Date</th>
//                                           <th>Data Available</th>
//                                           <th className="text-center">Action</th>
//                                         </tr>
//                                       </thead>
//                                       <tbody>
//                                         {group.sessions.map((session) => (
//                                           <tr key={session.id}>
//                                             <td>{session.job_title}</td>
//                                             <td>
//                                               <Badge bg="light" text="dark" className="border">
//                                                 {session.job_type || "N/A"}
//                                               </Badge>
//                                             </td>
//                                             <td className="font-monospace text-muted small">
//                                               {session.job_posting_id}
//                                             </td>
//                                             <td>{formatDate(session.date)}</td>
//                                             <td>
//                                               {session.general_data && (
//                                                 <Badge bg="info" className="me-1">
//                                                   General
//                                                 </Badge>
//                                               )}
//                                               {session.parameter_data && <Badge bg="success">Params</Badge>}
//                                             </td>
//                                             <td className="text-center">
//                                               <Button
//                                                 variant="outline-dark"
//                                                 size="sm"
//                                                 onClick={() => handleViewDetails(session)}
//                                               >
//                                                 <i className="bi bi-eye me-1"></i> View Report
//                                               </Button>
//                                             </td>
//                                           </tr>
//                                         ))}
//                                       </tbody>
//                                     </Table>
//                                   </div>
//                                 </td>
//                               </tr>
//                             )}
//                           </React.Fragment>
//                         )
//                       })
//                     ) : (
//                       <tr>
//                         <td colSpan="5" className="text-center py-5 text-muted">
//                           No evaluations found.
//                         </td>
//                       </tr>
//                     )}
//                   </tbody>
//                 </Table>
//               </div>
//             </Card.Body>

//             {totalPages > 1 && (
//               <Card.Footer className="bg-white d-flex justify-content-end py-3">
//                 <Pagination>
//                   <Pagination.Prev
//                     onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
//                     disabled={currentPage === 1}
//                   />
//                   {[...Array(totalPages)].map((_, i) => (
//                     <Pagination.Item key={i + 1} active={i + 1 === currentPage} onClick={() => setCurrentPage(i + 1)}>
//                       {i + 1}
//                     </Pagination.Item>
//                   ))}
//                   <Pagination.Next
//                     onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
//                     disabled={currentPage === totalPages}
//                   />
//                 </Pagination>
//               </Card.Footer>
//             )}
//           </Card>
//         </div>
//       </div>

//       {/* --- Details Modal --- */}
//       <Modal show={selectedSession !== null} onHide={() => setSelectedSession(null)} size="xl" centered scrollable>
//         <Modal.Header closeButton className="bg-light border-bottom-0">
//           <Modal.Title className="h5 text-primary">Evaluation Details</Modal.Title>
//         </Modal.Header>
//         <Modal.Body className="p-0">
//           {selectedSession && (
//             <div className="d-flex flex-column h-100">
//               {/* Header Info */}
//               <div className="p-4 bg-light border-bottom">
//                 <h4 className="mb-1">{selectedSession.candidate_name}</h4>
//                 <div className="text-muted d-flex gap-3 small mt-2">
//                   <span>
//                     <i className="bi bi-briefcase me-1"></i> {selectedSession.job_title}
//                   </span>
//                   <span>
//                     <i className="bi bi-tag me-1"></i> {selectedSession.job_type} ({selectedSession.job_posting_id})
//                   </span>
//                   <span>
//                     <i className="bi bi-calendar me-1"></i> {formatDate(selectedSession.date)}
//                   </span>
//                 </div>
//               </div>

//               {/* Tabs */}
//               <div className="px-4 pt-3 pb-0 bg-white">
//                 <ButtonGroup className="w-100 shadow-sm">
//                   <Button
//                     variant={modalViewMode === "general" ? "primary" : "outline-light text-dark border"}
//                     onClick={() => setModalViewMode("general")}
//                     disabled={!selectedSession.general_data}
//                   >
//                     General Questions {selectedSession.general_data ? "" : "(N/A)"}
//                   </Button>
//                   <Button
//                     variant={modalViewMode === "parameters" ? "primary" : "outline-light text-dark border"}
//                     onClick={() => setModalViewMode("parameters")}
//                     disabled={!selectedSession.parameter_data}
//                   >
//                     Parametrized Assessment {selectedSession.parameter_data ? "" : "(N/A)"}
//                   </Button>
//                 </ButtonGroup>
//               </div>

//               {/* Content */}
//               <div className="p-4 bg-white flex-grow-1">
//                 {modalViewMode === "general" && selectedSession.general_data ? (
//                   <div className="fade-in">
//                     <h5 className="mb-3 text-secondary border-bottom pb-2">General Screening Questions</h5>
//                     {renderGeneralTable(selectedSession.general_data.evaluation_details)}
//                   </div>
//                 ) : modalViewMode === "parameters" && selectedSession.parameter_data ? (
//                   renderParameterPDF(selectedSession.parameter_data)
//                 ) : (
//                   <div className="text-center py-5 text-muted">
//                     <i className="bi bi-exclamation-circle fs-1"></i>
//                     <p className="mt-2">No data available for this section.</p>
//                   </div>
//                 )}
//               </div>
//             </div>
//           )}
//         </Modal.Body>
//         <Modal.Footer className="bg-light">
//           <Button variant="secondary" onClick={() => setSelectedSession(null)}>
//             Close
//           </Button>
//           <Button variant="primary" onClick={() => window.print()}>
//             <i className="bi bi-printer me-2"></i>Print Report
//           </Button>
//         </Modal.Footer>
//       </Modal>
//     </>
//   )
// }

// export default InterviewerFeedback



// "use client"
// import React, { useState, useEffect } from "react"
// import axios from "axios"
// import DashNavbar from "./DashNavbar"
// import Sidebar from "./Sidebar"
// import { Pagination, Modal, Card, Form, InputGroup, Button, Table, Spinner, ButtonGroup, Badge } from "react-bootstrap"
// import Swal from "sweetalert2"

// // --- Custom CSS ---
// const CustomStyles = () => (
//   <style>{`
//     .interviewer-feedback-page .main-content { background-color: #f8f9fa; }
//     .gradient-text { background: linear-gradient(45deg, #0d6efd, #0d6efd); -webkit-background-clip: text; -webkit-text-fill-color: transparent; font-weight: 600; }
//     .interviewer-feedback-page.wrapper { display: flex; height: calc(100vh - 60px); }
//     .interviewer-feedback-page .main-content { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; }
//     .interviewer-feedback-page .card { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; }
//     .interviewer-feedback-page .card-body { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; padding: 0; }
//     .interviewer-feedback-page .table-responsive { flex-grow: 1; overflow-y: auto; padding: 1.5rem; padding-top: 0; }
    
//     /* Sub-table styling */
//     .sub-table-row td { padding: 0 !important; border: 0; background-color: #f8f9fa; }
//     .sub-table-container { padding: 1rem; border-top: 2px solid #0d6efd; background: white; margin: 10px; border-radius: 5px; box-shadow: 0 2px 4px rgba(0,0,0,0.05); }
//     .sub-table th { background-color: #e9ecef !important; font-size: 0.9rem; }
//     .sub-table td { font-size: 0.9rem; vertical-align: middle; }

//     /* PDF Modal Styling */
//     .modal-pdf-form .form-group-inline { display: flex; align-items: baseline; margin-bottom: 0.8rem; }
//     .modal-pdf-form .form-group-inline label { font-weight: 600; margin-right: 1rem; min-width: 150px; }
//     .modal-pdf-form .form-group-inline span { flex-grow: 1; border-bottom: 1px solid #ccc; padding-bottom: 2px; }
//     .modal-pdf-form .section-heading { font-size: 1.25rem; font-weight: 600; margin-top: 1.5rem; margin-bottom: 1rem; border-bottom: 2px solid #0d6efd; padding-bottom: 0.5rem; }
//     .modal-pdf-form .overall-recommendation-box { border: 1px solid #dee2e6; padding: 1.5rem; border-radius: .375rem; background-color: #f8f9fa; }
//     .modal-pdf-form .signature-line { margin-top: 3rem; border-top: 1px solid #ccc; padding-top: 0.5rem; display: flex; justify-content: space-between; color: #6c757d; }
//     .modal-pdf-form .form-title { text-align: center; font-size: 1.5rem; font-weight: 700; margin-bottom: 2rem; color: #0d6efd; border-bottom: 3px solid #0d6efd; padding-bottom: 1rem; }
    
//     .justified-text { text-align: justify; }
    
//     /* Parameter Section Styling */
//     .parameter-section { margin-bottom: 2rem; }
//     .parameter-section-title { font-size: 1.1rem; font-weight: 600; background-color: #e9ecef; padding: 0.75rem; margin-bottom: 1rem; border-left: 4px solid #0d6efd; }
//     .parameter-input-group { display: flex; gap: 1rem; align-items: center; margin-bottom: 0.5rem; }
//     .parameter-input-group input { max-width: 80px; }
//     .parameter-description { font-size: 0.9rem; color: #6c757d; margin-bottom: 0.5rem; font-style: italic; }
//   `}</style>
// )

// const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com"

// function InterviewerFeedback() {
//   // Data Stores
//   const [mergedEvaluations, setMergedEvaluations] = useState([])
//   const [groupedCandidates, setGroupedCandidates] = useState({})
//   const [evaluationParameters, setEvaluationParameters] = useState([]) // New state to store API parameters grouped by area

//   // UI State
//   const [searchTerm, setSearchTerm] = useState("")
//   const [currentPage, setCurrentPage] = useState(1)
//   const [itemsPerPage, setItemsPerPage] = useState(10)
//   const [isLoading, setIsLoading] = useState(false)
//   const [expandedCandidate, setExpandedCandidate] = useState(null)

//   // Modal State
//   const [selectedSession, setSelectedSession] = useState(null)
//   const [modalViewMode, setModalViewMode] = useState("general") // 'general' or 'parameters'

//   // Filtered Data State
//   const [filteredGeneralData, setFilteredGeneralData] = useState(null)
//   const [filteredParameterData, setFilteredParameterData] = useState(null)
//   const [filteredEvaluationParameters, setFilteredEvaluationParameters] = useState({})

//   // --- 1. Fetch Data ---
//   const fetchAllData = async () => {
//     setIsLoading(true)
//     try {
//       const parametersRes = await axios.get(`${API_BASE_URL}/job-postings/business-028/evaluation-parameters/`)
//       const allParametersData = parametersRes.data || []
//       const allParametersArray = Array.isArray(allParametersData) ? allParametersData : allParametersData.results || []

//       // Store ALL parameters globally for filtering later
//       setEvaluationParameters(allParametersArray)

//       // Fetch General Evaluations
//       // Fetch Parameterized Evaluations
//       const [generalRes, paramRes] = await Promise.all([
//         axios.get(`${API_BASE_URL}/interview-evaluations/`),
//         axios.get(`${API_BASE_URL}/evaluations/`),
//       ])

//       const generalList = generalRes.data || []
//       const safeGeneralList = Array.isArray(generalList) ? generalList : generalList.results || []

//       const paramList = paramRes.data || []
//       const safeParamList = Array.isArray(paramList) ? paramList : paramList.results || []

//       processAndMergeData(safeGeneralList, safeParamList)
//     } catch (error) {
//       console.error("Error fetching data:", error)
//       Swal.fire("Error", "Could not fetch evaluation data. Please check API connections.", "error")
//     } finally {
//       setIsLoading(false)
//     }
//   }

//   useEffect(() => {
//     fetchAllData()
//   }, [])

//   // --- 2. Process & Merge Logic ---
//   const processAndMergeData = (generalList, paramList) => {
//     const sessionMap = new Map()

//     const getKey = (item) => {
//       const jobId = item.job_posting || item.job_posting_id || item.linked_posting_id || "unknown-job"
//       const name = item.candidate_name || "unknown-candidate"
//       return `${name}|${jobId}`
//     }

//     generalList.forEach((item) => {
//       if (item.candidate_name === "N/A - Template") return

//       const key = getKey(item)
//       if (!sessionMap.has(key)) {
//         sessionMap.set(key, {
//           id: key,
//           candidate_name: item.candidate_name,
//           candidate_email: item.candidate_email,
//           job_title: item.job_title || "N/A",
//           job_type: item.job_type,
//           job_posting_id: item.job_posting,
//           date: item.evaluation_date,
//           general_data: item,
//           parameter_data: null,
//         })
//       } else {
//         const session = sessionMap.get(key)
//         session.general_data = item
//         if (!session.job_type) session.job_type = item.job_type
//         if (!session.job_posting_id) session.job_posting_id = item.job_posting
//       }
//     })

//     paramList.forEach((item) => {
//       const key = getKey(item)

//       if (sessionMap.has(key)) {
//         const session = sessionMap.get(key)
//         session.parameter_data = item
//         if (item.job_title) session.job_title = item.job_title
//       } else {
//         sessionMap.set(key, {
//           id: key,
//           candidate_name: item.candidate_name,
//           candidate_email: item.candidate_email || "N/A",
//           job_title: item.job_title || "N/A",
//           job_type: item.posting_type || "N/A",
//           job_posting_id: item.posting_id || item.job_posting || "N/A",
//           date: item.interview_date || item.created_at,
//           general_data: null,
//           parameter_data: item,
//         })
//       }
//     })

//     const mergedArray = Array.from(sessionMap.values())
//     setMergedEvaluations(mergedArray)
//     groupDataByCandidate(mergedArray)
//   }

//   const groupDataByCandidate = (data) => {
//     const groups = data.reduce((acc, session) => {
//       const name = session.candidate_name
//       if (!acc[name]) {
//         acc[name] = {
//           candidate_name: name,
//           email: session.candidate_email,
//           sessions: [],
//         }
//       }
//       acc[name].sessions.push(session)
//       return acc
//     }, {})
//     setGroupedCandidates(groups)
//   }

//   // --- 3. Filtering & Pagination ---
//   const candidateNames = Object.keys(groupedCandidates).filter(
//     (name) =>
//       name.toLowerCase().includes(searchTerm.toLowerCase()) ||
//       groupedCandidates[name].email?.toLowerCase().includes(searchTerm.toLowerCase()),
//   )

//   const totalPages = Math.ceil(candidateNames.length / itemsPerPage)
//   const paginatedNames = candidateNames.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

//   // --- 4. Helpers ---
//   const formatDate = (dateStr) => {
//     if (!dateStr) return "N/A"
//     return new Date(dateStr).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
//   }

//   const handleViewDetails = (session) => {
//     setSelectedSession(session)
//     setModalViewMode("general") // Reset to general first, then fetch filtered data
//     fetchFilteredDataForJob(session)
//   }

//   const fetchFilteredDataForJob = async (session) => {
//     try {
//       console.log("[v0] Fetching filtered data for session:", session)

//       const jobPostingId = session.job_posting_id
//       const businessOpportunity = session.general_data?.business_opportunity || null
//       const technicalConsultant = session.general_data?.technical_consultant || null

//       console.log(
//         "[v0] Filter criteria - Job:",
//         jobPostingId,
//         "Business:",
//         businessOpportunity,
//         "Consultant:",
//         technicalConsultant,
//       )

//       // Fetch General Evaluations filtered by job_posting
//       const generalRes = await axios.get(`${API_BASE_URL}/interview-evaluations/`)
//       const generalList = Array.isArray(generalRes.data) ? generalRes.data : generalRes.data.results || []
//       const filteredGeneral = generalList.find((item) => item.job_posting === jobPostingId)
//       console.log("[v0] Filtered general data:", filteredGeneral)

//       // Fetch Parameterized Evaluations filtered by posting_id
//       const paramRes = await axios.get(`${API_BASE_URL}/evaluations/`)
//       const paramList = Array.isArray(paramRes.data) ? paramRes.data : paramRes.data.results || []
//       const filteredParam = paramList.find((item) => item.posting_id === jobPostingId)
//       console.log("[v0] Filtered parameter data:", filteredParam)

//       setSelectedSession((prev) => ({
//         ...prev,
//         general_data: filteredGeneral,
//         parameter_data: filteredParam,
//       }))

//       if (Array.isArray(evaluationParameters) && evaluationParameters.length > 0) {
//         const matchedParameters = evaluationParameters.filter((param) => {
//           const jobMatch = param.job_posting === jobPostingId
//           const businessMatch = businessOpportunity === null || param.business_opportunity === businessOpportunity
//           const consultantMatch = technicalConsultant === null || param.technical_consultant === technicalConsultant

//           console.log(
//             "[v0] Param check:",
//             param.parameter_text,
//             "- Job match:",
//             jobMatch,
//             "- Business match:",
//             businessMatch,
//             "- Consultant match:",
//             consultantMatch,
//           )

//           return jobMatch && businessMatch && consultantMatch
//         })

//         console.log("[v0] Matched parameters count:", matchedParameters.length)

//         // Group the matched parameters by area
//         const groupedParams = matchedParameters.reduce((acc, param) => {
//           const area = param.area || "GENERAL"
//           if (!acc[area]) acc[area] = []
//           acc[area].push(param)
//           return acc
//         }, {})

//         setFilteredEvaluationParameters(groupedParams)
//       }
//     } catch (error) {
//       console.error("Error fetching filtered data:", error)
//     }
//   }

//   const renderGeneralTable = (details) => {
//     if (!details || details.length === 0) return <p className="text-muted">No general questions recorded.</p>

//     return (
//       <Table bordered hover>
//         <thead className="table-light">
//           <tr>
//             <th style={{ width: "70%" }}>Question</th>
//             <th className="text-center">Rating</th>
//           </tr>
//         </thead>
//         <tbody>
//           {details.map((d, i) => (
//             <tr key={i}>
//               <td className="justified-text">{d.question_text}</td>
//               <td className="text-center">
//                 {d.answer_value ? (
//                   <Badge bg="info" className="fs-6">
//                     {d.answer_value}/5
//                   </Badge>
//                 ) : (
//                   <span className="text-muted">-</span>
//                 )}
//               </td>
//             </tr>
//           ))}
//         </tbody>
//       </Table>
//     )
//   }

//   const renderParameterPDF = (paramData) => {
//     const responses = paramData.responses || []

//     const parametersToUse =
//       Object.keys(filteredEvaluationParameters).length > 0 ? filteredEvaluationParameters : evaluationParameters

//     const allowedAreas = ["CORE_COMPETENCIES", "BEHAVIORAL", "ROLE_FIT"]
//     const filteredParameters = Object.entries(parametersToUse)
//       .filter(([area]) => allowedAreas.includes(area))
//       .reduce((acc, [area, params]) => {
//         acc[area] = params
//         return acc
//       }, {})

//     console.log("[v0] Rendering with parameters:", Object.keys(filteredParameters))

//     return (
//       <div className="modal-pdf-form fade-in">
//         <div className="form-title">Post-Interview Evaluation</div>

//         {/* Metadata Section */}
//         <div className="mb-4 p-3 bg-light border rounded">
//           <div className="form-group-inline">
//             <label>Interviewer:</label>
//             <span>{paramData.interviewer || "N/A"}</span>
//           </div>
//           <div className="form-group-inline">
//             <label>Date:</label>
//             <span>{formatDate(paramData.display_date || selectedSession?.date)}</span>
//           </div>
//         </div>

//         {Object.keys(filteredParameters).length > 0 ? (
//           Object.entries(filteredParameters).map(([area, params], idx) => (
//             <div key={idx} className="parameter-section">
//               <div className="parameter-section-title">{area.replace(/_/g, " ")}</div>
//               <Table bordered size="sm">
//                 <thead className="table-secondary">
//                   <tr>
//                     <th style={{ width: "30%" }}>Parameter</th>
//                     <th style={{ width: "50%" }}>Description</th>
//                     <th className="text-center" style={{ width: "20%" }}>
//                       Rating / Comments
//                     </th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {params.map((param, pIdx) => {
//                     const matchingResponse = responses.find(
//                       (r) => r.parameter_id === param.id || r.parameter?.id === param.id,
//                     )
//                     return (
//                       <tr key={pIdx}>
//                         <td className="fw-bold">{param.parameter_text}</td>
//                         <td>
//                           <div className="parameter-description">{param.description}</div>
//                         </td>
//                         <td className="text-center align-middle">
//                           {matchingResponse ? (
//                             <>
//                               <Badge
//                                 bg={
//                                   matchingResponse.rating >= 4
//                                     ? "success"
//                                     : matchingResponse.rating >= 3
//                                       ? "warning"
//                                       : "danger"
//                                 }
//                                 className="mb-2"
//                               >
//                                 {matchingResponse.rating || "-"}
//                               </Badge>
//                               {matchingResponse.comments && (
//                                 <div className="small mt-1">{matchingResponse.comments}</div>
//                               )}
//                             </>
//                           ) : (
//                             <span className="text-muted">-</span>
//                           )}
//                         </td>
//                       </tr>
//                     )
//                   })}
//                 </tbody>
//               </Table>
//             </div>
//           ))
//         ) : (
//           <p className="text-muted text-center">No evaluation parameters found for this job.</p>
//         )}

//         {/* Summary Box */}
//         <div className="overall-recommendation-box mt-4">
//           <h5 className="text-primary mb-3">Final Assessment</h5>

//           <div className="mb-3">
//             <strong>Overall Recommendation: </strong>
//             <Badge bg="dark" className="ms-2 fs-6">
//               {paramData.overall_recommendation || "N/A"}
//             </Badge>
//           </div>

//           <div className="row">
//             <div className="col-md-6">
//               <strong className="text-success">Strengths:</strong>
//               <ul className="mt-1">
//                 {Array.isArray(paramData.strengths) ? (
//                   paramData.strengths.map((s, i) => <li key={i}>{s}</li>)
//                 ) : (
//                   <li>{paramData.strengths || "None listed"}</li>
//                 )}
//               </ul>
//             </div>
//             <div className="col-md-6">
//               <strong className="text-warning">Development Areas:</strong>
//               <ul className="mt-1">
//                 {Array.isArray(paramData.development_areas) ? (
//                   paramData.development_areas.map((s, i) => <li key={i}>{s}</li>)
//                 ) : (
//                   <li>{paramData.development_areas || "None listed"}</li>
//                 )}
//               </ul>
//             </div>
//           </div>

//           <div className="mt-3">
//             <strong>Final Comments:</strong>
//             <p className="mt-1 justified-text bg-white p-2 border rounded">
//               {paramData.final_comments || "No final comments."}
//             </p>
//           </div>
//         </div>

//         <div className="signature-line">
//           <span>Signature: _______________________</span>
//           <span>Date: {formatDate(new Date())}</span>
//         </div>
//       </div>
//     )
//   }

//   return (
//     <>
//       <CustomStyles />
//       <DashNavbar />
//       <div className="wrapper d-flex interviewer-feedback-page">
//         <Sidebar />
//         <div className="main-content flex-grow-1 p-3 p-md-4">
//           <Card className="shadow-sm mb-4 h-100">
//             <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center border-bottom">
//               <h4 className="mb-0 text-primary fw-bold">
//                 <i className="bi bi-clipboard-data me-2"></i>Feedback Evaluations
//               </h4>
//               <InputGroup style={{ maxWidth: "300px" }}>
//                 <InputGroup.Text className="bg-light">
//                   <i className="bi bi-search"></i>
//                 </InputGroup.Text>
//                 <Form.Control
//                   type="search"
//                   placeholder="Search candidates..."
//                   value={searchTerm}
//                   onChange={(e) => setSearchTerm(e.target.value)}
//                 />
//               </InputGroup>
//             </Card.Header>

//             <Card.Body className="p-0">
//               <div className="table-responsive">
//                 <Table hover className="align-middle mb-0">
//                   <thead className="table-light">
//                     <tr>
//                       <th className="ps-4">#</th>
//                       <th>Candidate Name</th>
//                       <th>Email</th>
//                       <th className="text-center">Interviews</th>
//                       <th className="text-center">Action</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {isLoading ? (
//                       <tr>
//                         <td colSpan="5" className="text-center py-5">
//                           <Spinner animation="border" variant="primary" />
//                         </td>
//                       </tr>
//                     ) : paginatedNames.length > 0 ? (
//                       paginatedNames.map((name, idx) => {
//                         const group = groupedCandidates[name]
//                         const isExpanded = expandedCandidate === name
//                         return (
//                           <React.Fragment key={name}>
//                             <tr className={isExpanded ? "table-active" : ""}>
//                               <td className="ps-4 text-muted">{(currentPage - 1) * itemsPerPage + idx + 1}</td>
//                               <td className="fw-bold text-dark">{name}</td>
//                               <td className="text-muted">{group.email}</td>
//                               <td className="text-center">
//                                 <Badge bg="secondary" pill>
//                                   {group.sessions.length}
//                                 </Badge>
//                               </td>
//                               <td className="text-center">
//                                 <Button
//                                   variant={isExpanded ? "primary" : "outline-primary"}
//                                   size="sm"
//                                   onClick={() => setExpandedCandidate(isExpanded ? null : name)}
//                                 >
//                                   {isExpanded ? (
//                                     <i className="bi bi-chevron-up"></i>
//                                   ) : (
//                                     <i className="bi bi-chevron-down"></i>
//                                   )}
//                                 </Button>
//                               </td>
//                             </tr>
//                             {isExpanded && (
//                               <tr className="sub-table-row">
//                                 <td colSpan="5">
//                                   <div className="sub-table-container fade-in">
//                                     <h6 className="mb-3 text-primary border-bottom pb-2">Interview History: {name}</h6>
//                                     <Table className="sub-table mb-0">
//                                       <thead>
//                                         <tr>
//                                           <th>Job Title</th>
//                                           <th>Opportunity Type</th>
//                                           <th>Opportunity ID</th>
//                                           <th>Date</th>
//                                           <th>Data Available</th>
//                                           <th className="text-center">Action</th>
//                                         </tr>
//                                       </thead>
//                                       <tbody>
//                                         {group.sessions.map((session) => (
//                                           <tr key={session.id}>
//                                             <td>{session.job_title}</td>
//                                             <td>
//                                               <Badge bg="light" text="dark" className="border">
//                                                 {session.job_type || "N/A"}
//                                               </Badge>
//                                             </td>
//                                             <td className="font-monospace text-muted small">
//                                               {session.job_posting_id}
//                                             </td>
//                                             <td>{formatDate(session.date)}</td>
//                                             <td>
//                                               {session.general_data && (
//                                                 <Badge bg="info" className="me-1">
//                                                   General
//                                                 </Badge>
//                                               )}
//                                               {session.parameter_data && <Badge bg="success">Params</Badge>}
//                                             </td>
//                                             <td className="text-center">
//                                               <Button
//                                                 variant="outline-dark"
//                                                 size="sm"
//                                                 onClick={() => handleViewDetails(session)}
//                                               >
//                                                 <i className="bi bi-eye me-1"></i> View Report
//                                               </Button>
//                                             </td>
//                                           </tr>
//                                         ))}
//                                       </tbody>
//                                     </Table>
//                                   </div>
//                                 </td>
//                               </tr>
//                             )}
//                           </React.Fragment>
//                         )
//                       })
//                     ) : (
//                       <tr>
//                         <td colSpan="5" className="text-center py-5 text-muted">
//                           No evaluations found.
//                         </td>
//                       </tr>
//                     )}
//                   </tbody>
//                 </Table>
//               </div>
//             </Card.Body>

//             {totalPages > 1 && (
//               <Card.Footer className="bg-white d-flex justify-content-end py-3">
//                 <Pagination>
//                   <Pagination.Prev
//                     onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
//                     disabled={currentPage === 1}
//                   />
//                   {[...Array(totalPages)].map((_, i) => (
//                     <Pagination.Item key={i + 1} active={i + 1 === currentPage} onClick={() => setCurrentPage(i + 1)}>
//                       {i + 1}
//                     </Pagination.Item>
//                   ))}
//                   <Pagination.Next
//                     onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
//                     disabled={currentPage === totalPages}
//                   />
//                 </Pagination>
//               </Card.Footer>
//             )}
//           </Card>
//         </div>
//       </div>

//       {/* --- Details Modal --- */}
//       <Modal show={selectedSession !== null} onHide={() => setSelectedSession(null)} size="xl" centered scrollable>
//         <Modal.Header closeButton className="bg-light border-bottom-0">
//           <Modal.Title className="h5 text-primary">Evaluation Details</Modal.Title>
//         </Modal.Header>
//         <Modal.Body className="p-0">
//           {selectedSession && (
//             <div className="d-flex flex-column h-100">
//               {/* Header Info */}
//               <div className="p-4 bg-light border-bottom">
//                 <h4 className="mb-1">{selectedSession.candidate_name}</h4>
//                 <div className="text-muted d-flex gap-3 small mt-2">
//                   <span>
//                     <i className="bi bi-briefcase me-1"></i> {selectedSession.job_title}
//                   </span>
//                   <span>
//                     <i className="bi bi-tag me-1"></i> {selectedSession.job_type} ({selectedSession.job_posting_id})
//                   </span>
//                   <span>
//                     <i className="bi bi-calendar me-1"></i> {formatDate(selectedSession.date)}
//                   </span>
//                 </div>
//               </div>

//               {/* Tabs */}
//               <div className="px-4 pt-3 pb-0 bg-white">
//                 <ButtonGroup className="w-100 shadow-sm">
//                   <Button
//                     variant={modalViewMode === "general" ? "primary" : "outline-light text-dark border"}
//                     onClick={() => setModalViewMode("general")}
//                     disabled={!selectedSession.general_data}
//                   >
//                     General Questions {selectedSession.general_data ? "" : "(N/A)"}
//                   </Button>
//                   <Button
//                     variant={modalViewMode === "parameters" ? "primary" : "outline-light text-dark border"}
//                     onClick={() => setModalViewMode("parameters")}
//                     disabled={!selectedSession.parameter_data}
//                   >
//                     Parametrized Assessment {selectedSession.parameter_data ? "" : "(N/A)"}
//                   </Button>
//                 </ButtonGroup>
//               </div>

//               {/* Content */}
//               <div className="p-4 bg-white flex-grow-1">
//                 {modalViewMode === "general" && selectedSession.general_data ? (
//                   <div className="fade-in">
//                     <h5 className="mb-3 text-secondary border-bottom pb-2">General Screening Questions</h5>
//                     {renderGeneralTable(selectedSession.general_data.evaluation_details)}
//                   </div>
//                 ) : modalViewMode === "parameters" && selectedSession.parameter_data ? (
//                   renderParameterPDF(selectedSession.parameter_data)
//                 ) : (
//                   <div className="text-center py-5 text-muted">
//                     <i className="bi bi-exclamation-circle fs-1"></i>
//                     <p className="mt-2">No data available for this section.</p>
//                   </div>
//                 )}
//               </div>
//             </div>
//           )}
//         </Modal.Body>
//         <Modal.Footer className="bg-light">
//           <Button variant="secondary" onClick={() => setSelectedSession(null)}>
//             Close
//           </Button>
//           <Button variant="primary" onClick={() => window.print()}>
//             <i className="bi bi-printer me-2"></i>Print Report
//           </Button>
//         </Modal.Footer>
//       </Modal>
//     </>
//   )
// }

// export default InterviewerFeedback




// "use client"
// import React, { useState, useEffect } from "react"
// import axios from "axios"
// import DashNavbar from "./DashNavbar"
// import Sidebar from "./Sidebar"
// import { Pagination, Modal, Card, Form, InputGroup, Button, Table, Spinner, ButtonGroup, Badge } from "react-bootstrap"
// import Swal from "sweetalert2"

// // --- Custom CSS ---
// const CustomStyles = () => (
//   <style>{`
//     .interviewer-feedback-page .main-content { background-color: #f8f9fa; }
//     .gradient-text { background: linear-gradient(45deg, #0d6efd, #0d6efd); -webkit-background-clip: text; -webkit-text-fill-color: transparent; font-weight: 600; }
//     .interviewer-feedback-page.wrapper { display: flex; height: calc(100vh - 60px); }
//     .interviewer-feedback-page .main-content { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; }
//     .interviewer-feedback-page .card { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; }
//     .interviewer-feedback-page .card-body { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; padding: 0; }
//     .interviewer-feedback-page .table-responsive { flex-grow: 1; overflow-y: auto; padding: 1.5rem; padding-top: 0; }
    
//     /* Sub-table styling */
//     .sub-table-row td { padding: 0 !important; border: 0; background-color: #f8f9fa; }
//     .sub-table-container { padding: 1rem; border-top: 2px solid #0d6efd; background: white; margin: 10px; border-radius: 5px; box-shadow: 0 2px 4px rgba(0,0,0,0.05); }
//     .sub-table th { background-color: #e9ecef !important; font-size: 0.9rem; }
//     .sub-table td { font-size: 0.9rem; vertical-align: middle; }

//     /* PDF Modal Styling */
//     .modal-pdf-form .form-group-inline { display: flex; align-items: baseline; margin-bottom: 0.8rem; }
//     .modal-pdf-form .form-group-inline label { font-weight: 600; margin-right: 1rem; min-width: 150px; }
//     .modal-pdf-form .form-group-inline span { flex-grow: 1; border-bottom: 1px solid #ccc; padding-bottom: 2px; }
//     .modal-pdf-form .section-heading { font-size: 1.25rem; font-weight: 600; margin-top: 1.5rem; margin-bottom: 1rem; border-bottom: 2px solid #0d6efd; padding-bottom: 0.5rem; }
//     .modal-pdf-form .overall-recommendation-box { border: 1px solid #dee2e6; padding: 1.5rem; border-radius: .375rem; background-color: #f8f9fa; }
//     .modal-pdf-form .signature-line { margin-top: 3rem; border-top: 1px solid #ccc; padding-top: 0.5rem; display: flex; justify-content: space-between; color: #6c757d; }
//     .modal-pdf-form .form-title { text-align: center; font-size: 1.5rem; font-weight: 700; margin-bottom: 2rem; color: #0d6efd; border-bottom: 3px solid #0d6efd; padding-bottom: 1rem; }
    
//     .justified-text { text-align: justify; }
//   `}</style>
// )

// const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com"

// function InterviewerFeedback() {
//   // Data Stores
//   const [mergedEvaluations, setMergedEvaluations] = useState([])
//   const [groupedCandidates, setGroupedCandidates] = useState({})
  
//   // UI State
//   const [searchTerm, setSearchTerm] = useState("")
//   const [currentPage, setCurrentPage] = useState(1)
//   const [itemsPerPage, setItemsPerPage] = useState(10)
//   const [isLoading, setIsLoading] = useState(false)
//   const [expandedCandidate, setExpandedCandidate] = useState(null)
  
//   // Modal State
//   const [selectedSession, setSelectedSession] = useState(null)
//   const [modalViewMode, setModalViewMode] = useState("general") // 'general' or 'parameters'

//   // --- 1. Fetch Data ---
//   const fetchAllData = async () => {
//     setIsLoading(true)
//     try {
//       // Fetch General Evaluations (as per prompt URL)
//       // Fetch Parameterized Evaluations (Assuming 'evaluations/' endpoint returns submitted params)
//       const [generalRes, paramRes] = await Promise.all([
//         axios.get(`${API_BASE_URL}/interview-evaluations/`), 
//         axios.get(`${API_BASE_URL}/evaluations/`) 
//       ])

//       const generalList = generalRes.data || [] // Handle if response is array directly
//       // If generalRes.data is paginated { results: [] }, handle that:
//       const safeGeneralList = Array.isArray(generalList) ? generalList : (generalList.results || [])

//       const paramList = paramRes.data || []
//       const safeParamList = Array.isArray(paramList) ? paramList : (paramList.results || [])

//       processAndMergeData(safeGeneralList, safeParamList)

//     } catch (error) {
//       console.error("Error fetching data:", error)
//       Swal.fire("Error", "Could not fetch evaluation data. Please check API connections.", "error")
//     } finally {
//       setIsLoading(false)
//     }
//   }

//   useEffect(() => {
//     fetchAllData()
//   }, [])

//   // --- 2. Process & Merge Logic ---
//   const processAndMergeData = (generalList, paramList) => {
//     const sessionMap = new Map()

//     // Helper to generate a unique key for an interview session
//     // We prioritize using the Job Posting ID + Candidate Name
//     const getKey = (item) => {
//         // Fallback logic if IDs are missing
//         const jobId = item.job_posting || item.job_posting_id || item.linked_posting_id || "unknown-job"
//         const name = item.candidate_name || "unknown-candidate"
//         return `${name}|${jobId}`
//     }

//     // 1. Process General Evaluations
//     generalList.forEach(item => {
//         // Exclude Templates if necessary, or keep them
//         if (item.candidate_name === "N/A - Template") return;

//         const key = getKey(item)
//         if (!sessionMap.has(key)) {
//             sessionMap.set(key, {
//                 id: key,
//                 candidate_name: item.candidate_name,
//                 candidate_email: item.candidate_email,
//                 job_title: item.job_title || "N/A", // Often null in general, might need lookup
//                 job_type: item.job_type,       // e.g. Business_Opportunity
//                 job_posting_id: item.job_posting, // e.g. business-028
//                 date: item.evaluation_date,
//                 general_data: item,
//                 parameter_data: null
//             })
//         } else {
//             // Update existing (rare case of duplicates, just overwrite general)
//             const session = sessionMap.get(key)
//             session.general_data = item
//             // Enhance metadata if missing
//             if (!session.job_type) session.job_type = item.job_type
//             if (!session.job_posting_id) session.job_posting_id = item.job_posting
//         }
//     })

//     // 2. Process Parameter Evaluations & Merge
//     paramList.forEach(item => {
//         const key = getKey(item)
        
//         if (sessionMap.has(key)) {
//             const session = sessionMap.get(key)
//             session.parameter_data = item
//             // Prefer parameter data for Job Title if available as it's usually richer in this endpoint
//             if (item.job_title) session.job_title = item.job_title
//         } else {
//             // New Session (Parameter only, no General)
//             sessionMap.set(key, {
//                 id: key,
//                 candidate_name: item.candidate_name,
//                 candidate_email: item.candidate_email || "N/A",
//                 job_title: item.job_title || "N/A",
//                 job_type: item.posting_type || "N/A", // Assuming logic
//                 job_posting_id: item.posting_id || item.job_posting || "N/A",
//                 date: item.interview_date || item.created_at,
//                 general_data: null,
//                 parameter_data: item
//             })
//         }
//     })

//     const mergedArray = Array.from(sessionMap.values())
//     setMergedEvaluations(mergedArray)
//     groupDataByCandidate(mergedArray)
//   }

//   const groupDataByCandidate = (data) => {
//     const groups = data.reduce((acc, session) => {
//         const name = session.candidate_name
//         if (!acc[name]) {
//             acc[name] = {
//                 candidate_name: name,
//                 email: session.candidate_email,
//                 sessions: []
//             }
//         }
//         acc[name].sessions.push(session)
//         return acc
//     }, {})
//     setGroupedCandidates(groups)
//   }

//   // --- 3. Filtering & Pagination ---
//   const candidateNames = Object.keys(groupedCandidates).filter(name => 
//     name.toLowerCase().includes(searchTerm.toLowerCase()) || 
//     groupedCandidates[name].email?.toLowerCase().includes(searchTerm.toLowerCase())
//   )
  
//   const totalPages = Math.ceil(candidateNames.length / itemsPerPage)
//   const paginatedNames = candidateNames.slice(
//     (currentPage - 1) * itemsPerPage, 
//     currentPage * itemsPerPage
//   )

//   // --- 4. Helpers ---
//   const formatDate = (dateStr) => {
//     if (!dateStr) return "N/A"
//     return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
//   }

//   const handleViewDetails = (session) => {
//     setSelectedSession(session)
//     // Default to Parameter view if available, else General
//     if (session.parameter_data) setModalViewMode("parameters")
//     else setModalViewMode("general")
//   }

//   // --- Render Functions ---

//   const renderGeneralTable = (details) => {
//     if (!details || details.length === 0) return <p className="text-muted">No general questions recorded.</p>
    
//     // Group by question text if needed, strictly just list them here based on API response
//     return (
//         <Table bordered hover>
//             <thead className="table-light">
//                 <tr>
//                     <th style={{width: '70%'}}>Question</th>
//                     <th className="text-center">Rating</th>
//                 </tr>
//             </thead>
//             <tbody>
//                 {details.map((d, i) => (
//                     <tr key={i}>
//                         <td className="justified-text">{d.question_text}</td>
//                         <td className="text-center">
//                             {d.answer_value ? <Badge bg="info" className="fs-6">{d.answer_value}/5</Badge> : <span className="text-muted">-</span>}
//                         </td>
//                     </tr>
//                 ))}
//             </tbody>
//         </Table>
//     )
//   }

//   const renderParameterPDF = (paramData) => {
//      // Helper to group responses by area (assuming 'area' exists in parameter object inside response)
//      // Since response structure might vary, we handle flat lists or grouped
//      const responses = paramData.responses || []
//      const groupedResponses = responses.reduce((acc, curr) => {
//          const area = curr.parameter?.area || "Evaluation Parameters" // adjust based on actual nested structure
//          if(!acc[area]) acc[area] = []
//          acc[area].push(curr)
//          return acc
//      }, {})

//      return (
//         <div className="modal-pdf-form fade-in">
//             <div className="form-title">Post-Interview Evaluation</div>
            
//             {/* Metadata Section inside PDF */}
//             <div className="mb-4 p-3 bg-light border rounded">
//                 <div className="form-group-inline"><label>Interviewer:</label><span>{paramData.interviewer || "N/A"}</span></div>
//                 <div className="form-group-inline"><label>Date:</label><span>{formatDate(paramData.display_date || selectedSession.date)}</span></div>
//             </div>

//             {Object.keys(groupedResponses).length > 0 ? (
//                 Object.entries(groupedResponses).map(([area, items], idx) => (
//                     <div key={idx}>
//                         <h5 className="section-heading text-capitalize">{area.replace(/_/g, " ")}</h5>
//                         <Table bordered size="sm">
//                             <thead className="table-secondary">
//                                 <tr>
//                                     <th style={{width: '30%'}}>Parameter</th>
//                                     <th style={{width: '40%'}}>Description / Comments</th>
//                                     <th className="text-center">Rating</th>
//                                 </tr>
//                             </thead>
//                             <tbody>
//                                 {items.map((r, rIdx) => (
//                                     <tr key={rIdx}>
//                                         <td className="fw-bold">{r.parameter?.parameter_text || r.parameter_text}</td>
//                                         <td>
//                                             <div className="small text-muted mb-1">{r.parameter?.description}</div>
//                                             <div className="fst-italic">{r.comments}</div>
//                                         </td>
//                                         <td className="text-center align-middle">
//                                             <Badge bg={r.rating >= 4 ? 'success' : r.rating >= 3 ? 'warning' : 'danger'}>
//                                                 {r.rating || '-'}
//                                             </Badge>
//                                         </td>
//                                     </tr>
//                                 ))}
//                             </tbody>
//                         </Table>
//                     </div>
//                 ))
//             ) : (
//                 <p className="text-muted text-center">No parameter responses found.</p>
//             )}

//             {/* Summary Box */}
//             <div className="overall-recommendation-box mt-4">
//                 <h5 className="text-primary mb-3">Final Assessment</h5>
                
//                 <div className="mb-3">
//                     <strong>Overall Recommendation: </strong> 
//                     <Badge bg="dark" className="ms-2 fs-6">{paramData.overall_recommendation || "N/A"}</Badge>
//                 </div>

//                 <div className="row">
//                     <div className="col-md-6">
//                         <strong className="text-success">Strengths:</strong>
//                         <ul className="mt-1">
//                             {Array.isArray(paramData.strengths) ? paramData.strengths.map((s, i) => <li key={i}>{s}</li>) : <li>{paramData.strengths || "None listed"}</li>}
//                         </ul>
//                     </div>
//                     <div className="col-md-6">
//                         <strong className="text-warning">Development Areas:</strong>
//                         <ul className="mt-1">
//                             {Array.isArray(paramData.development_areas) ? paramData.development_areas.map((s, i) => <li key={i}>{s}</li>) : <li>{paramData.development_areas || "None listed"}</li>}
//                         </ul>
//                     </div>
//                 </div>

//                 <div className="mt-3">
//                     <strong>Final Comments:</strong>
//                     <p className="mt-1 justified-text bg-white p-2 border rounded">{paramData.final_comments || "No final comments."}</p>
//                 </div>
//             </div>

//             <div className="signature-line">
//                 <span>Signature: _______________________</span>
//                 <span>Date: {formatDate(new Date())}</span>
//             </div>
//         </div>
//      )
//   }

//   return (
//     <>
//       <CustomStyles />
//       <DashNavbar />
//       <div className="wrapper d-flex interviewer-feedback-page">
//         <Sidebar />
//         <div className="main-content flex-grow-1 p-3 p-md-4">
//           <Card className="shadow-sm mb-4 h-100">
//             <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center border-bottom">
//               <h4 className="mb-0 text-primary fw-bold"><i className="bi bi-clipboard-data me-2"></i>Feedback Evaluations</h4>
//               <InputGroup style={{ maxWidth: '300px' }}>
//                 <InputGroup.Text className="bg-light"><i className="bi bi-search"></i></InputGroup.Text>
//                 <Form.Control 
//                     type="search" 
//                     placeholder="Search candidates..." 
//                     value={searchTerm} 
//                     onChange={(e) => setSearchTerm(e.target.value)} 
//                 />
//               </InputGroup>
//             </Card.Header>
            
//             <Card.Body className="p-0">
//               <div className="table-responsive">
//                 <Table hover className="align-middle mb-0">
//                   <thead className="table-light">
//                     <tr>
//                       <th className="ps-4">#</th>
//                       <th>Candidate Name</th>
//                       <th>Email</th>
//                       <th className="text-center">Interviews</th>
//                       <th className="text-center">Action</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {isLoading ? (
//                       <tr><td colSpan="5" className="text-center py-5"><Spinner animation="border" variant="primary"/></td></tr>
//                     ) : paginatedNames.length > 0 ? (
//                         paginatedNames.map((name, idx) => {
//                         const group = groupedCandidates[name]
//                         const isExpanded = expandedCandidate === name
//                         return (
//                           <React.Fragment key={name}>
//                             <tr className={isExpanded ? "table-active" : ""}>
//                               <td className="ps-4 text-muted">{(currentPage - 1) * itemsPerPage + idx + 1}</td>
//                               <td className="fw-bold text-dark">{name}</td>
//                               <td className="text-muted">{group.email}</td>
//                               <td className="text-center"><Badge bg="secondary" pill>{group.sessions.length}</Badge></td>
//                               <td className="text-center">
//                                 <Button 
//                                     variant={isExpanded ? "primary" : "outline-primary"} 
//                                     size="sm" 
//                                     onClick={() => setExpandedCandidate(isExpanded ? null : name)}
//                                 >
//                                   {isExpanded ? <i className="bi bi-chevron-up"></i> : <i className="bi bi-chevron-down"></i>}
//                                 </Button>
//                               </td>
//                             </tr>
//                             {isExpanded && (
//                               <tr className="sub-table-row">
//                                 <td colSpan="5">
//                                   <div className="sub-table-container fade-in">
//                                     <h6 className="mb-3 text-primary border-bottom pb-2">Interview History: {name}</h6>
//                                     <Table className="sub-table mb-0">
//                                       <thead>
//                                         <tr>
//                                           <th>Job Title</th>
//                                           <th>Opportunity Type</th>
//                                           <th>Opportunity ID</th>
//                                           <th>Date</th>
//                                           <th>Data Available</th>
//                                           <th className="text-center">Action</th>
//                                         </tr>
//                                       </thead>
//                                       <tbody>
//                                         {group.sessions.map((session) => (
//                                           <tr key={session.id}>
//                                             <td>{session.job_title}</td>
//                                             <td><Badge bg="light" text="dark" className="border">{session.job_type || "N/A"}</Badge></td>
//                                             <td className="font-monospace text-muted small">{session.job_posting_id}</td>
//                                             <td>{formatDate(session.date)}</td>
//                                             <td>
//                                                 {session.general_data && <Badge bg="info" className="me-1">General</Badge>}
//                                                 {session.parameter_data && <Badge bg="success">Params</Badge>}
//                                             </td>
//                                             <td className="text-center">
//                                               <Button variant="outline-dark" size="sm" onClick={() => handleViewDetails(session)}>
//                                                 <i className="bi bi-eye me-1"></i> View Report
//                                               </Button>
//                                             </td>
//                                           </tr>
//                                         ))}
//                                       </tbody>
//                                     </Table>
//                                   </div>
//                                 </td>
//                               </tr>
//                             )}
//                           </React.Fragment>
//                         )
//                       })
//                     ) : (
//                         <tr><td colSpan="5" className="text-center py-5 text-muted">No evaluations found.</td></tr>
//                     )}
//                   </tbody>
//                 </Table>
//               </div>
//             </Card.Body>
            
//             {totalPages > 1 && (
//                 <Card.Footer className="bg-white d-flex justify-content-end py-3">
//                     <Pagination>
//                         <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}/>
//                         {[...Array(totalPages)].map((_, i) => (
//                              <Pagination.Item key={i+1} active={i+1 === currentPage} onClick={() => setCurrentPage(i+1)}>
//                                 {i+1}
//                              </Pagination.Item>
//                         ))}
//                         <Pagination.Next onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}/>
//                     </Pagination>
//                 </Card.Footer>
//             )}
//           </Card>
//         </div>
//       </div>

//       {/* --- Details Modal --- */}
//       <Modal show={selectedSession !== null} onHide={() => setSelectedSession(null)} size="xl" centered scrollable>
//         <Modal.Header closeButton className="bg-light border-bottom-0">
//             <Modal.Title className="h5 text-primary">
//                 Evaluation Details
//             </Modal.Title>
//         </Modal.Header>
//         <Modal.Body className="p-0">
//             {selectedSession && (
//                 <div className="d-flex flex-column h-100">
//                     {/* Header Info */}
//                     <div className="p-4 bg-light border-bottom">
//                         <h4 className="mb-1">{selectedSession.candidate_name}</h4>
//                         <div className="text-muted d-flex gap-3 small mt-2">
//                              <span><i className="bi bi-briefcase me-1"></i> {selectedSession.job_title}</span>
//                              <span><i className="bi bi-tag me-1"></i> {selectedSession.job_type} ({selectedSession.job_posting_id})</span>
//                              <span><i className="bi bi-calendar me-1"></i> {formatDate(selectedSession.date)}</span>
//                         </div>
//                     </div>

//                     {/* Tabs */}
//                     <div className="px-4 pt-3 pb-0 bg-white">
//                         <ButtonGroup className="w-100 shadow-sm">
//                             <Button 
//                                 variant={modalViewMode === "general" ? "primary" : "outline-light text-dark border"} 
//                                 onClick={() => setModalViewMode("general")}
//                                 disabled={!selectedSession.general_data}
//                             >
//                                 General Questions {selectedSession.general_data ? "" : "(N/A)"}
//                             </Button>
//                             <Button 
//                                 variant={modalViewMode === "parameters" ? "primary" : "outline-light text-dark border"} 
//                                 onClick={() => setModalViewMode("parameters")}
//                                 disabled={!selectedSession.parameter_data}
//                             >
//                                 Parametrized Assessment {selectedSession.parameter_data ? "" : "(N/A)"}
//                             </Button>
//                         </ButtonGroup>
//                     </div>

//                     {/* Content */}
//                     <div className="p-4 bg-white flex-grow-1">
//                         {modalViewMode === "general" && selectedSession.general_data ? (
//                             <div className="fade-in">
//                                 <h5 className="mb-3 text-secondary border-bottom pb-2">General Screening Questions</h5>
//                                 {renderGeneralTable(selectedSession.general_data.evaluation_details)}
//                             </div>
//                         ) : modalViewMode === "parameters" && selectedSession.parameter_data ? (
//                             renderParameterPDF(selectedSession.parameter_data)
//                         ) : (
//                             <div className="text-center py-5 text-muted">
//                                 <i className="bi bi-exclamation-circle fs-1"></i>
//                                 <p className="mt-2">No data available for this section.</p>
//                             </div>
//                         )}
//                     </div>
//                 </div>
//             )}
//         </Modal.Body>
//         <Modal.Footer className="bg-light">
//             <Button variant="secondary" onClick={() => setSelectedSession(null)}>Close</Button>
//             <Button variant="primary" onClick={() => window.print()}><i className="bi bi-printer me-2"></i>Print Report</Button>
//         </Modal.Footer>
//       </Modal>
//     </>
//   )
// }

// export default InterviewerFeedback

// "use client"
// import React, { useState, useEffect } from "react"
// import axios from "axios"
// import DashNavbar from "./DashNavbar"
// import Sidebar from "./Sidebar"
// import { Pagination, Modal, Card, Form, InputGroup, Button, Table, Spinner, ButtonGroup } from "react-bootstrap"
// import Swal from "sweetalert2"

// // Custom Styles Component (Embedded CSS)
// const CustomStyles = () => (
//   <style>{`
//     .interviewer-feedback-page .main-content { background-color: #f8f9fa; }
//     .gradient-text { background: linear-gradient(45deg, #0d6efd, #0d6efd); -webkit-background-clip: text; -webkit-text-fill-color: transparent; font-weight: 600; }
//     .interviewer-feedback-page.wrapper { display: flex; height: calc(100vh - 60px); }
//     .interviewer-feedback-page .main-content { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; }
//     .interviewer-feedback-page .card { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; }
//     .interviewer-feedback-page .card-body { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; padding: 0; }
//     .interviewer-feedback-page .table-responsive { flex-grow: 1; overflow-y: auto; padding: 1.5rem; padding-top: 0; }
//     .interviewer-feedback-page .card-header, .interviewer-feedback-page .card-footer { flex-shrink: 0; }
//     .summary-box { background-color: #f8f9fa; border: 1px solid #dee2e6; padding: 1rem; border-radius: .375rem; }
    
//     /* New styles for the sub-table */
//     .sub-table-row td {
//       padding: 0 !important;
//       border: 0;
//       background-color: #f8f9fa;
//     }
//     .sub-table-container {
//       padding: 1rem;
//       border-top: 2px solid #0d6efd;
//     }
//     .sub-table th {
//       background-color: #e9ecef !important;
//     }
        
//     /* Styles for the PDF-like modal */
//     .modal-pdf-form .form-group-inline { display: flex; align-items: baseline; margin-bottom: 0.8rem; }
//     .modal-pdf-form .form-group-inline label { font-weight: 600; margin-right: 1rem; min-width: 180px; }
//     .modal-pdf-form .form-group-inline span { flex-grow: 1; border-bottom: 1px solid #ccc; padding-bottom: 2px; text-align: justify; }
//     .modal-pdf-form .section-heading { font-size: 1.25rem; font-weight: 600; margin-top: 1.5rem; margin-bottom: 1rem; border-bottom: 2px solid #0d6efd; padding-bottom: 0.5rem; text-align: justify; }
//     .modal-pdf-form .table th, .modal-pdf-form .table td { vertical-align: middle; padding: 0.75rem; text-align: justify; }
//     .modal-pdf-form .table th { text-align: center !important; font-weight: 600; }
//     .modal-pdf-form .table .text-center { text-align: center !important; }
//     .modal-pdf-form .table thead th { background-color: #f0f0f0; }
//     .modal-pdf-form .overall-recommendation-box { border: 1px solid #dee2e6; padding: 1.5rem; border-radius: .375rem; background-color: #f8f9fa; }
//     .modal-pdf-form .overall-recommendation-box h5 { margin-bottom: 1rem; color: #0d6efd; text-align: justify; }
//     .modal-pdf-form .overall-recommendation-box .recommendation-item { margin-bottom: 1rem; display: flex; align-items: flex-start; }
//     .modal-pdf-form .overall-recommendation-box .recommendation-item .bullet-point { color: #0d6efd; font-size: 1.2rem; margin-right: 0.5rem; font-weight: bold; flex-shrink: 0; margin-top: 0.1rem; }
//     .modal-pdf-form .overall-recommendation-box .recommendation-content { flex-grow: 1; }
//     .modal-pdf-form .overall-recommendation-box .recommendation-content strong { display: block; margin-bottom: 0.5rem; text-align: justify; }
//     .modal-pdf-form .overall-recommendation-box .recommendation-text { text-align: justify; line-height: 1.6; margin-left: 0; }
//     .modal-pdf-form .overall-recommendation-box .recommendation-list { margin-left: 0; padding-left: 0; }
//     .modal-pdf-form .overall-recommendation-box .recommendation-subpoint { margin-left: 1.5rem; margin-bottom: 0.3rem; text-align: justify; line-height: 1.5; display: flex; align-items: flex-start; }
//     .modal-pdf-form .overall-recommendation-box .recommendation-numbering { font-weight: 600; color: #6f42c1; margin-right: 0.5rem; flex-shrink: 0; }
//     .modal-pdf-form .overall-recommendation-box .recommendation-point-text { flex-grow: 1; text-align: justify; }
//     .modal-pdf-form .signature-line { margin-top: 3rem; border-top: 1px solid #ccc; padding-top: 0.5rem; display: flex; justify-content: space-between; font-size: 0.9rem; color: #6c757d; }
//     .modal-pdf-form .signature-line span { flex-basis: 48%; }
//     .modal-pdf-form .candidate-info-section { background-color: #f8f9fa; padding: 1.5rem; border-radius: .375rem; margin-bottom: 2rem; border: 1px solid #dee2e6; }
//     .modal-pdf-form .form-title { text-align: center; font-size: 1.5rem; font-weight: 700; margin-bottom: 2rem; color: #0d6efd; border-bottom: 3px solid #0d6efd; padding-bottom: 1rem; }
//     .modal-pdf-form .justified-text { text-align: justify; line-height: 1.5; }
//     .modal-pdf-form .section-description { text-align: justify; font-style: italic; color: #6c757d; margin-bottom: 1rem; }
    
//     .table th, .table td { text-align: justify; }
//     .table th { text-align: center !important; font-weight: 600; }
//     .table .text-center { text-align: center !important; }
//     .table .align-middle { vertical-align: middle; }
    
//     .text-secondary { text-align: justify; }
//   `}</style>
// )

// function InterviewerFeedback() {
//   const [generalEvaluations, setGeneralEvaluations] = useState([])
//   const [parameterEvaluations, setParameterEvaluations] = useState([])
//   const [combinedEvaluations, setCombinedEvaluations] = useState([])
  
//   const [searchTerm, setSearchTerm] = useState("")
//   const [currentPage, setCurrentPage] = useState(1)
//   const [selectedEvaluation, setSelectedEvaluation] = useState(null)
//   const [selectedParameterEvaluation, setSelectedParameterEvaluation] = useState(null)
//   const [isLoading, setIsLoading] = useState(false)
//   const [itemsPerPage, setItemsPerPage] = useState(10)
//   const [modalViewMode, setModalViewMode] = useState("general")
//   const [expandedCandidate, setExpandedCandidate] = useState(null)

//   const fetchAllData = async () => {
//     setIsLoading(true)
//     try {
//       const [generalEvalsResponse, parameterEvalsResponse] = await Promise.all([
//         axios.get(`https://raasbackend.vetrinahealthcare.com/interview-evaluations/?page_size=1000`),
//         axios.get(`https://raasbackend.vetrinahealthcare.com/evaluations/`),
//       ])
//       setGeneralEvaluations(generalEvalsResponse.data.results || [])
//       setParameterEvaluations(parameterEvalsResponse.data || [])
//     } catch (error) {
//       console.error("Error fetching evaluation data:", error)
//       Swal.fire("Error", "Could not fetch all evaluation data.", "error")
//     } finally {
//       setIsLoading(false)
//     }
//   }

//   useEffect(() => {
//     fetchAllData()
//   }, [])

//   useEffect(() => {
//     if (parameterEvaluations.length > 0) {
//       const mergedData = parameterEvaluations.map(paramEval => {
//         const generalEval = generalEvaluations.find(
//           genEval => genEval.candidate_name === paramEval.candidate_name && genEval.job_title === paramEval.job_title
//         );
        
//         return {
//           ...paramEval,
//           candidate_email: generalEval?.candidate_email || 'N/A', 
//           evaluation_details: generalEval?.evaluation_details || [],
//           display_date: paramEval.interview_date || generalEval?.evaluation_date
//         };
//       });
//       setCombinedEvaluations(mergedData);
//     }
//   }, [parameterEvaluations, generalEvaluations]);

//   const groupEvaluationsByCandidate = (evaluations) => {
//     return evaluations.reduce((acc, evaluation) => {
//       const name = evaluation.candidate_name;
//       if (!acc[name]) {
//         acc[name] = { evaluations: [], email: evaluation.candidate_email };
//       }
//       acc[name].evaluations.push(evaluation);
//       return acc;
//     }, {});
//   };

//   const handlePageChange = (page) => {
//     if (page >= 1) setCurrentPage(page)
//   }

//   const handleItemsPerPageChange = (e) => {
//     setItemsPerPage(Number(e.target.value))
//     setCurrentPage(1)
//   }

//   const filteredResults = combinedEvaluations.filter(
//     (ev) =>
//       (ev.candidate_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
//       (ev.job_title || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
//       (ev.candidate_email || "").toLowerCase().includes(searchTerm.toLowerCase()),
//   )

//   const groupedByCandidate = groupEvaluationsByCandidate(filteredResults)
//   const candidateNames = Object.keys(groupedByCandidate)

//   const paginatedCandidateNames = candidateNames.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
//   const calculatedTotalPages = Math.ceil(candidateNames.length / itemsPerPage)

//   const handleToggleExpand = (candidateName) => {
//     setExpandedCandidate((prev) => (prev === candidateName ? null : candidateName))
//   }
  
//   const handleViewDetails = (evaluation) => {
//     setSelectedEvaluation(evaluation); 
//     setSelectedParameterEvaluation(evaluation); 
//     setModalViewMode("general");
//   };

//   const handleCloseDetails = () => {
//     setSelectedEvaluation(null)
//   }

//   const handleModeChange = (mode) => {
//     setModalViewMode(mode)
//   }

//   const formatDate = (dateString) => {
//     if (!dateString) return "N/A"
//     return new Date(dateString).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
//   }

//   const mapRatingToText = (rating) => {
//     const ratings = { 5: "Excellent", 4: "Good", 3: "Average", 2: "Below Average", 1: "Poor" }
//     return ratings[rating] || "N/A"
//   }

//   // **FIXED**: Re-added the missing function definition.
//   const groupEvaluationsByArea = (evaluationDetails) => {
//     if (!evaluationDetails || !Array.isArray(evaluationDetails)) return {}
//     return evaluationDetails.reduce((groups, detail) => {
//       const area = detail.area || "General Questions"
//       if (!groups[area]) groups[area] = []
//       groups[area].push(detail)
//       return groups
//     }, {})
//   }

//   const groupParameterEvaluationsByArea = (responses) => {
//     if (!responses || !Array.isArray(responses)) return {}
//     const orderedSections = {
//       CORE_COMPETENCIES: "Core Competencies",
//       BEHAVIORAL_TRAITS: "Additional Behavioral & Functional Parameters",
//       TECHNICAL_SKILLS: "Role-Specific & Cultural Fit",
//     }
//     const groups = {}
//     Object.values(orderedSections).forEach((sectionName) => {
//       groups[sectionName] = []
//     })
//     responses.forEach((res) => {
//       const area = res.parameter.area
//       const sectionName = orderedSections[area] || "Other Parameters"
//       if (!groups[sectionName]) {
//         groups[sectionName] = []
//       }
//       groups[sectionName].push(res)
//     })
//     Object.keys(groups).forEach((key) => {
//       if (groups[key].length === 0) delete groups[key]
//     })
//     return groups
//   }
  
//   const formatTextWithNumbering = (text, defaultText) => {
//     let textToProcess = text;
//     if (Array.isArray(textToProcess)) {
//       textToProcess = textToProcess.join('. ');
//     }
//     if (typeof textToProcess !== 'string' || !textToProcess.trim() || textToProcess.trim().toLowerCase() === 'n/a') {
//       return <div className="recommendation-text justified-text">{defaultText}</div>;
//     }
//     const points = textToProcess.split(/\n|;|\.\s+/).map((point) => point.replace(/^[-\d.]+\s*/, "").trim()).filter(Boolean);
//     if (points.length <= 1) {
//       return <div className="recommendation-text justified-text">{textToProcess}</div>;
//     }
//     return (
//       <div className="recommendation-list">
//         {points.map((point, index) => (
//           <div key={index} className="recommendation-subpoint">
//             <span className="recommendation-numbering">{index + 1}.</span>
//             <span className="recommendation-point-text">{point}</span>
//           </div>
//         ))}
//       </div>
//     );
//   };

//   return (
//     <>
//       <CustomStyles />
//       <DashNavbar />
//       <div className="wrapper d-flex interviewer-feedback-page">
//         <Sidebar />
//         <div className="main-content flex-grow-1 p-3 p-md-4">
//           <Card className="shadow-sm mb-4">
//             <Card.Header className="bg-light d-flex justify-content-between align-items-center py-3">
//               <h3 className="mb-0 gradient-text h5">Interview Feedback Evaluations</h3>
//               <div className="w-auto" style={{ maxWidth: "300px" }}>
//                 <InputGroup>
//                   <InputGroup.Text><i className="bi bi-search"></i></InputGroup.Text>
//                   <Form.Control type="search" placeholder="Search by name, job, or email..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
//                 </InputGroup>
//               </div>
//             </Card.Header>
//             <Card.Body>
//               <div className="table-responsive">
//                 <Table hover striped className="align-middle">
//                   <thead className="table-dark">
//                     <tr>
//                       <th>#</th>
//                       <th>Candidate Name</th>
//                       <th>Email</th>
//                       <th className="text-center">Evaluations</th>
//                       <th className="text-center">Actions</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {isLoading ? (
//                       <tr><td colSpan="5" className="text-center py-5"><Spinner /><p className="mt-2 mb-0">Loading...</p></td></tr>
//                     ) : paginatedCandidateNames.length > 0 ? (
//                       paginatedCandidateNames.map((candidateName, index) => {
//                         const candidateData = groupedByCandidate[candidateName];
//                         const evaluations = candidateData.evaluations;
//                         const isExpanded = expandedCandidate === candidateName;
//                         return (
//                           <React.Fragment key={candidateName}>
//                             <tr>
//                               <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
//                               <td><strong>{candidateName}</strong></td>
//                               <td>{candidateData.email || "N/A"}</td>
//                               <td className="text-center"><span className="badge bg-secondary">{evaluations.length}</span></td>
//                               <td className="text-center">
//                                 <Button variant="outline-info" size="sm" onClick={() => handleToggleExpand(candidateName)}>
//                                   <i className={`bi ${isExpanded ? "bi-chevron-up" : "bi-chevron-down"} me-1`}></i>
//                                   {isExpanded ? "Hide Jobs" : "View Jobs"}
//                                 </Button>
//                               </td>
//                             </tr>
//                             {isExpanded && (
//                               <tr className="sub-table-row">
//                                 <td colSpan="5">
//                                   <div className="sub-table-container">
//                                     <h6 className="mb-3">Evaluations for {candidateName}</h6>
//                                     <Table striped bordered size="sm" className="sub-table">
//                                       <thead>
//                                         <tr>
//                                           <th style={{ width: "60%" }}>Job Title</th>
//                                           <th style={{ width: "20%" }}>Evaluation Date</th>
//                                           <th style={{ width: "20%" }} className="text-center">View Details</th>
//                                         </tr>
//                                       </thead>
//                                       <tbody>
//                                         {evaluations.map((evaluation) => (
//                                           <tr key={evaluation.id}>
//                                             <td>{evaluation.job_title}</td>
//                                             <td>{formatDate(evaluation.display_date)}</td>
//                                             <td className="text-center">
//                                               <Button variant="outline-primary" size="sm" onClick={() => handleViewDetails(evaluation)}>
//                                                 <i className="bi bi-eye-fill me-1"></i> View
//                                               </Button>
//                                             </td>
//                                           </tr>
//                                         ))}
//                                       </tbody>
//                                     </Table>
//                                   </div>
//                                 </td>
//                               </tr>
//                             )}
//                           </React.Fragment>
//                         )
//                       })
//                     ) : (
//                       <tr><td colSpan="5" className="text-center py-5"><i className="bi bi-journal-x fs-1 text-muted"></i><p className="mt-2 mb-0">No feedback found.</p></td></tr>
//                     )}
//                   </tbody>
//                 </Table>
//               </div>
//             </Card.Body>
//             {calculatedTotalPages > 1 && (
//               <Card.Footer className="bg-light py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
//                 <div>
//                   <Form.Select size="sm" style={{ width: "auto", display: "inline-block" }} value={itemsPerPage} onChange={handleItemsPerPageChange}>
//                     <option value="10">10</option><option value="25">25</option><option value="50">50</option>
//                   </Form.Select>
//                   <span className="ms-2 small text-muted">candidates per page</span>
//                 </div>
//                 <Pagination className="mb-0">
//                   <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
//                   <Pagination.Item active>{currentPage}</Pagination.Item>
//                   <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === calculatedTotalPages} />
//                 </Pagination>
//               </Card.Footer>
//             )}
//           </Card>
//         </div>
//       </div>

//       <Modal show={selectedEvaluation !== null} onHide={handleCloseDetails} size="xl" centered scrollable>
//         <Modal.Header closeButton className="bg-light">
//           <Modal.Title className="h5 gradient-text">Complete Evaluation Details</Modal.Title>
//         </Modal.Header>
//         <Modal.Body className="p-4">
//           {selectedEvaluation && (
//             <>
//               <div className="mb-4 pb-3 border-bottom">
//                 <h5 className="mb-1 justified-text">{selectedEvaluation.candidate_name}</h5>
//                 <p className="mb-0 text-muted small justified-text">Job: {selectedEvaluation.job_title} | Interviewer: {selectedEvaluation.interviewer}</p>
//               </div>

//               <ButtonGroup className="w-100 mb-4">
//                 <Button variant={modalViewMode === "general" ? "primary" : "outline-primary"} onClick={() => handleModeChange("general")}>General Questions</Button>
//                 <Button variant={modalViewMode === "parameters" ? "primary" : "outline-primary"} onClick={() => handleModeChange("parameters")}>Parameter-Based Evaluation</Button>
//               </ButtonGroup>
              
//               {modalViewMode === "general" ? (
//                 selectedEvaluation.evaluation_details?.length > 0 ? (
//                   Object.entries(groupEvaluationsByArea(selectedEvaluation.evaluation_details)).map(([area, details], areaIndex) => (
//                     <div key={areaIndex} className="mb-4">
//                       <h6 className="text-secondary mb-3 justified-text">{area || 'General'}</h6>
//                       <Table striped bordered hover responsive>
//                         <thead className="table-light">
//                           <tr>
//                             <th style={{ width: "60%" }}>Question</th>
//                             <th style={{ width: "20%" }} className="text-center">Answer</th>
//                             <th style={{ width: "20%" }}>Comments</th>
//                           </tr>
//                         </thead>
//                         <tbody>
//                           {details.map((detail, index) => (
//                             <tr key={detail.question_id || index}>
//                               <td className="align-middle justified-text"><strong>{detail.question_text}</strong></td>
//                               <td className="text-center align-middle"><span className="badge bg-dark">{detail.answer_value || "N/A"}</span></td>
//                               <td className="align-middle justified-text">{detail.comments || <span className="text-muted fst-italic">No comments.</span>}</td>
//                             </tr>
//                           ))}
//                         </tbody>
//                       </Table>
//                     </div>
//                   ))
//                 ) : <div className="alert alert-secondary small mt-4"><div className="justified-text">No general questions were submitted for this interview.</div></div>
//               ) : selectedParameterEvaluation ? (
//                 <div className="modal-pdf-form">
//                   <div className="form-title">Post-Interview Employee Evaluation Form</div>
//                   <div className="candidate-info-section">
//                     <div className="form-group-inline"><label>Candidate Name:</label><span>{selectedParameterEvaluation.candidate_name || "N/A"}</span></div>
//                     <div className="form-group-inline"><label>Position Applied For:</label><span>{selectedParameterEvaluation.job_title || "N/A"}</span></div>
//                     <div className="form-group-inline"><label>Interview Date:</label><span>{formatDate(selectedParameterEvaluation.display_date)}</span></div>
//                     <div className="form-group-inline"><label>Interviewer(s):</label><span>{selectedParameterEvaluation.interviewer || "N/A"}</span></div>
//                   </div>
//                   {Object.entries(groupParameterEvaluationsByArea(selectedParameterEvaluation.responses)).map(([area, responses], areaIndex) => (
//                     <div key={areaIndex} className="mb-4">
//                       <h4 className="section-heading">{areaIndex + 1}. {area}</h4>
//                       <p className="mb-3 text-muted section-description">Rate 1 – 5 (1 = Poor, 5 = Excellent) and add comments/evidence to support your assessment.</p>
//                       <Table striped bordered hover responsive className="mb-3">
//                         <thead className="table-light">
//                           <tr>
//                             <th style={{ width: "25%" }}>{area === "Role-Specific & Cultural Fit" ? "Attribute" : area === "Core Competencies" ? "Competency" : "Parameter"}</th>
//                             {area !== "Role-Specific & Cultural Fit" && <th style={{ width: "35%" }}>Description</th>}
//                             <th style={{ width: "15%" }} className="text-center">Rating</th>
//                             <th style={{ width: area === "Role-Specific & Cultural Fit" ? "60%" : "25%" }}>{area === "Role-Specific & Cultural Fit" ? "Rating / Comments" : "Comments / Evidence"}</th>
//                           </tr>
//                         </thead>
//                         <tbody>
//                           {responses.map((res, resIndex) => (
//                             <tr key={res.id || resIndex}>
//                               <td className="align-middle justified-text"><strong>{res.parameter.parameter_text}</strong></td>
//                               {area !== "Role-Specific & Cultural Fit" && <td className="align-middle justified-text">{res.parameter.description || "N/A"}</td>}
//                               <td className="text-center align-middle"><span className="badge bg-primary px-3 py-2">{mapRatingToText(res.rating)}</span></td>
//                               <td className="align-middle justified-text">{res.comments || <span className="text-muted fst-italic">No comments.</span>}</td>
//                             </tr>
//                           ))}
//                         </tbody>
//                       </Table>
//                     </div>
//                   ))}
//                   <div className="overall-recommendation-box mt-4">
//                     <h5 className="section-heading">4. Overall Recommendation</h5>
//                     <div className="recommendation-item"><span className="bullet-point">●</span><div className="recommendation-content"><strong>Overall Suitability:</strong><div className="recommendation-text"><span className="badge bg-success fs-6">{selectedParameterEvaluation.overall_recommendation_display || "N/A"}</span></div></div></div>
//                     <div className="recommendation-item"><span className="bullet-point">●</span><div className="recommendation-content"><strong>Top Strengths:</strong>{formatTextWithNumbering(selectedParameterEvaluation.strengths, "No specific strengths were documented.")}</div></div>
//                     <div className="recommendation-item"><span className="bullet-point">●</span><div className="recommendation-content"><strong>Key Development Areas:</strong>{formatTextWithNumbering(selectedParameterEvaluation.development_areas, "No specific development areas were identified.")}</div></div>
//                     <div className="recommendation-item"><span className="bullet-point">●</span><div className="recommendation-content"><strong>Final Comments:</strong>{formatTextWithNumbering(selectedParameterEvaluation.final_comments, "No additional final comments were provided.")}</div></div>
//                   </div>
//                   <div className="signature-line"><span>Interviewer's Signature: ________________________</span><span>Date: ________________________</span></div>
//                 </div>
//               ) : (
//                 <div className="alert alert-secondary small mt-4"><div className="justified-text">No parameter-based evaluation was submitted for this interview.</div></div>
//               )}
//             </>
//           )}
//         </Modal.Body>
//         <Modal.Footer>
//           <Button variant="outline-secondary" onClick={handleCloseDetails}>Close</Button>
//         </Modal.Footer>
//       </Modal>
//     </>
//   )
// }

// export default InterviewerFeedback


// "use client"
// import { useState, useEffect } from "react"
// import axios from "axios"
// import DashNavbar from "./DashNavbar"
// import Sidebar from "./Sidebar"
// import { Pagination, Modal, Card, Form, InputGroup, Button, Table, Spinner, ButtonGroup } from "react-bootstrap"
// import Swal from "sweetalert2"

// // Custom Styles Component (Embedded CSS)
// const CustomStyles = () => (
//   <style>{`
//     .interviewer-feedback-page .main-content { background-color: #f8f9fa; }
//     .gradient-text { background: linear-gradient(45deg, #0d6efd, #0d6efd); -webkit-background-clip: text; -webkit-text-fill-color: transparent; font-weight: 600; }
//     .interviewer-feedback-page.wrapper { display: flex; height: calc(100vh - 60px); }
//     .interviewer-feedback-page .main-content { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; }
//     .interviewer-feedback-page .card { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; }
//     .interviewer-feedback-page .card-body { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; padding: 0; }
//     .interviewer-feedback-page .table-responsive { flex-grow: 1; overflow-y: auto; padding: 1.5rem; padding-top: 0; }
//     .interviewer-feedback-page .card-header, .interviewer-feedback-page .card-footer { flex-shrink: 0; }
//     .summary-box { background-color: #f8f9fa; border: 1px solid #dee2e6; padding: 1rem; border-radius: .375rem; }
        
//     /* New styles for the PDF-like modal */
//     .modal-pdf-form .form-group-inline {
//       display: flex;
//       align-items: baseline;
//       margin-bottom: 0.8rem;
//     }
//     .modal-pdf-form .form-group-inline label {
//       font-weight: 600;
//       margin-right: 1rem;
//       min-width: 180px;
//     }
//     .modal-pdf-form .form-group-inline span {
//       flex-grow: 1;
//       border-bottom: 1px solid #ccc;
//       padding-bottom: 2px;
//       text-align: justify;
//     }
//     .modal-pdf-form .section-heading {
//       font-size: 1.25rem;
//       font-weight: 600;
//       margin-top: 1.5rem;
//       margin-bottom: 1rem;
//       border-bottom: 2px solid #0d6efd;
//       padding-bottom: 0.5rem;
//       text-align: justify;
//     }
//     .modal-pdf-form .table th, .modal-pdf-form .table td {
//       vertical-align: middle;
//       padding: 0.75rem;
//       text-align: justify;
//     }
//     .modal-pdf-form .table th {
//       text-align: center !important;
//       font-weight: 600;
//     }
//     .modal-pdf-form .table .text-center {
//       text-align: center !important;
//     }
//     .modal-pdf-form .table thead th {
//       background-color: #f0f0f0;
//     }
//     .modal-pdf-form .overall-recommendation-box {
//       border: 1px solid #dee2e6;
//       padding: 1.5rem;
//       border-radius: .375rem;
//       background-color: #f8f9fa;
//     }
//     .modal-pdf-form .overall-recommendation-box h5 {
//       margin-bottom: 1rem;
//       color: #0d6efd;
//       text-align: justify;
//     }
//     .modal-pdf-form .overall-recommendation-box .recommendation-item {
//       margin-bottom: 1rem;
//       display: flex;
//       align-items: flex-start;
//     }
//     .modal-pdf-form .overall-recommendation-box .recommendation-item .bullet-point {
//       color: #0d6efd;
//       font-size: 1.2rem;
//       margin-right: 0.5rem;
//       font-weight: bold;
//       flex-shrink: 0;
//       margin-top: 0.1rem;
//     }
//     .modal-pdf-form .overall-recommendation-box .recommendation-content {
//       flex-grow: 1;
//     }
//     .modal-pdf-form .overall-recommendation-box .recommendation-content strong {
//       display: block;
//       margin-bottom: 0.5rem;
//       text-align: justify;
//     }
//     .modal-pdf-form .overall-recommendation-box .recommendation-text {
//       text-align: justify;
//       line-height: 1.6;
//       margin-left: 0;
//     }
//     .modal-pdf-form .overall-recommendation-box .recommendation-list {
//       margin-left: 0;
//       padding-left: 0;
//     }
//     .modal-pdf-form .overall-recommendation-box .recommendation-subpoint {
//       margin-left: 1.5rem;
//       margin-bottom: 0.3rem;
//       text-align: justify;
//       line-height: 1.5;
//       display: flex;
//       align-items: flex-start;
//     }
//     .modal-pdf-form .overall-recommendation-box .recommendation-numbering {
//       font-weight: 600;
//       color: #6f42c1;
//       margin-right: 0.5rem;
//       flex-shrink: 0;
//     }
//     .modal-pdf-form .overall-recommendation-box .recommendation-point-text {
//       flex-grow: 1;
//       text-align: justify;
//     }
//     .modal-pdf-form .signature-line {
//       margin-top: 3rem;
//       border-top: 1px solid #ccc;
//       padding-top: 0.5rem;
//       display: flex;
//       justify-content: space-between;
//       font-size: 0.9rem;
//       color: #6c757d;
//     }
//     .modal-pdf-form .signature-line span {
//       flex-basis: 48%;
//     }
//     .modal-pdf-form .candidate-info-section {
//       background-color: #f8f9fa;
//       padding: 1.5rem;
//       border-radius: .375rem;
//       margin-bottom: 2rem;
//       border: 1px solid #dee2e6;
//     }
//     .modal-pdf-form .form-title {
//       text-align: center;
//       font-size: 1.5rem;
//       font-weight: 700;
//       margin-bottom: 2rem;
//       color: #0d6efd;
//       border-bottom: 3px solid #0d6efd;
//       padding-bottom: 1rem;
//     }
//     .modal-pdf-form .parameter-numbering {
//       font-weight: 600;
//       color: #0d6efd;
//       margin-right: 0.5rem;
//     }
//     .modal-pdf-form .competency-numbering {
//       font-weight: 600;
//       color: #28a745;
//       margin-right: 0.5rem;
//     }
//     .modal-pdf-form .attribute-numbering {
//       font-weight: 600;
//       color: #dc3545;
//       margin-right: 0.5rem;
//     }
//     .modal-pdf-form .justified-text {
//       text-align: justify;
//       line-height: 1.5;
//     }
//     .modal-pdf-form .section-description {
//       text-align: justify;
//       font-style: italic;
//       color: #6c757d;
//       margin-bottom: 1rem;
//     }
    
//     /* General table text justification */
//     .table th, .table td {
//       text-align: justify;
//     }
//     .table th {
//       text-align: center !important;
//       font-weight: 600;
//     }
//     .table .text-center {
//       text-align: center !important;
//     }
//     .table .align-middle {
//       vertical-align: middle;
//     }
    
//     /* Section headings justification */
//     .text-secondary {
//       text-align: justify;
//     }
//   `}</style>
// )

// function InterviewerFeedback() {
//   const [evaluationResults, setEvaluationResults] = useState([])
//   const [allParameterEvaluations, setAllParameterEvaluations] = useState([])
//   const [searchTerm, setSearchTerm] = useState("")
//   const [currentPage, setCurrentPage] = useState(1)
//   const [selectedEvaluation, setSelectedEvaluation] = useState(null)
//   const [selectedParameterEvaluation, setSelectedParameterEvaluation] = useState(null)
//   const [isLoading, setIsLoading] = useState(false)
//   const [itemsPerPage, setItemsPerPage] = useState(10)
//   const [modalViewMode, setModalViewMode] = useState("general")

//   const fetchAllData = async () => {
//     setIsLoading(true)
//     try {
//       // Fetch a large number of general evaluations to allow for effective client-side search/pagination
//       const [generalEvalsResponse, parameterEvalsResponse] = await Promise.all([
//         axios.get(`https://raasbackend.vetrinahealthcare.com/interview-evaluations/?page_size=1000`),
//         axios.get(`https://raasbackend.vetrinahealthcare.com/evaluations/`),
//       ])
//       setEvaluationResults(generalEvalsResponse.data.results || [])
//       setAllParameterEvaluations(parameterEvalsResponse.data || [])
//     } catch (error) {
//       console.error("Error fetching evaluation data:", error)
//       Swal.fire("Error", "Could not fetch all evaluation data.", "error")
//       setEvaluationResults([])
//       setAllParameterEvaluations([])
//     } finally {
//       setIsLoading(false)
//     }
//   }

//   useEffect(() => {
//     fetchAllData()
//   }, [])

//   const handlePageChange = (page) => {
//     if (page >= 1) setCurrentPage(page)
//   }

//   const handleItemsPerPageChange = (e) => {
//     setItemsPerPage(Number(e.target.value))
//     setCurrentPage(1)
//   }

//   // Client-side filtering logic
//   const filteredResults = evaluationResults.filter(
//     (ev) =>
//       (ev.candidate_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
//       (ev.job_title || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
//       (ev.candidate_email || "").toLowerCase().includes(searchTerm.toLowerCase()),
//   )

//   const paginatedResults = filteredResults.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

//   const calculatedTotalPages = Math.ceil(filteredResults.length / itemsPerPage)

//   const handleViewDetails = (evaluation) => {
//     setSelectedEvaluation(evaluation)
//     // FIX: Match only on candidate name and job title, as the interviewer keys are different.
//     // This is the most reliable way to link the two records based on the available data.
//     const matchingParamEval = allParameterEvaluations.find(
//       (paramEval) =>
//         paramEval.candidate_name === evaluation.candidate_name && paramEval.job_title === evaluation.job_title,
//     )
//     setSelectedParameterEvaluation(matchingParamEval || null)
//     setModalViewMode("general")
//   }

//   const handleCloseDetails = () => {
//     setSelectedEvaluation(null)
//   }

//   const handleModeChange = (mode) => {
//     setModalViewMode(mode)
//   }

//   const formatDate = (dateString) => {
//     if (!dateString) return "N/A"
//     return new Date(dateString).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
//   }

//   const mapRatingToText = (rating) => {
//     const ratings = { 5: "Excellent", 4: "Good", 3: "Average", 2: "Below Average", 1: "Poor" }
//     return ratings[rating] || "N/A"
//   }

//   const groupEvaluationsByArea = (evaluationDetails) => {
//     if (!evaluationDetails || !Array.isArray(evaluationDetails)) return {}
//     return evaluationDetails.reduce((groups, detail) => {
//       const area = detail.area || "General Questions"
//       if (!groups[area]) groups[area] = []
//       groups[area].push(detail)
//       return groups
//     }, {})
//   }

//   const groupParameterEvaluationsByArea = (responses) => {
//     if (!responses || !Array.isArray(responses)) return {}

//     // Define the order of sections as per the form - FIXED MAPPING
//     const orderedSections = {
//       CORE_COMPETENCIES: "Core Competencies",
//       BEHAVIORAL_TRAITS: "Additional Behavioral & Functional Parameters",
//       TECHNICAL_SKILLS: "Role-Specific & Cultural Fit",
//     }

//     const groups = {}

//     // Initialize groups in the correct order
//     Object.values(orderedSections).forEach((sectionName) => {
//       groups[sectionName] = []
//     })

//     responses.forEach((res) => {
//       const area = res.parameter.area
//       const sectionName = orderedSections[area] || "Other Parameters"

//       if (!groups[sectionName]) {
//         groups[sectionName] = []
//       }
//       groups[sectionName].push(res)
//     })

//     // Remove empty groups
//     Object.keys(groups).forEach((key) => {
//       if (groups[key].length === 0) {
//         delete groups[key]
//       }
//     })

//     return groups
//   }

//   // Enhanced helper function to parse and format text with numbered points
//   const formatTextWithNumbering = (text, defaultText) => {
//     // Check if text is null, undefined, empty, or "N/A"
//     if (!text || text === "N/A" || typeof text !== "string") {
//       return (
//         <div className="recommendation-list">
//           <div className="recommendation-subpoint">
//             <span className="recommendation-numbering">1.</span>
//             <span className="recommendation-point-text">{defaultText}</span>
//           </div>
//         </div>
//       )
//     }

//     // Convert to string if it's not already (safety check)
//     const textString = String(text).trim()

//     if (!textString) {
//       return (
//         <div className="recommendation-list">
//           <div className="recommendation-subpoint">
//             <span className="recommendation-numbering">1.</span>
//             <span className="recommendation-point-text">{defaultText}</span>
//           </div>
//         </div>
//       )
//     }

//     // Split by common delimiters and filter out empty strings
//     const points = textString
//       .split(/[.\n;]/)
//       .map((point) => point.trim())
//       .filter((point) => point.length > 0 && point !== "")

//     // Always show numbered format, even for single points
//     if (points.length === 0) {
//       return (
//         <div className="recommendation-list">
//           <div className="recommendation-subpoint">
//             <span className="recommendation-numbering">1.</span>
//             <span className="recommendation-point-text">{textString}</span>
//           </div>
//         </div>
//       )
//     }

//     // If only one point and it's the same as original text, show as single numbered item
//     if (points.length === 1 && points[0] === textString) {
//       return (
//         <div className="recommendation-list">
//           <div className="recommendation-subpoint">
//             <span className="recommendation-numbering">1.</span>
//             <span className="recommendation-point-text">{textString}</span>
//           </div>
//         </div>
//       )
//     }

//     // Multiple points - show each with numbering
//     return (
//       <div className="recommendation-list">
//         {points.map((point, index) => (
//           <div key={index} className="recommendation-subpoint">
//             <span className="recommendation-numbering">{index + 1}.</span>
//             <span className="recommendation-point-text">{point}</span>
//           </div>
//         ))}
//       </div>
//     )
//   }

//   return (
//     <>
//       <CustomStyles />
//       <DashNavbar />
//       <div className="wrapper d-flex interviewer-feedback-page">
//         <Sidebar />
//         <div className="main-content flex-grow-1 p-3 p-md-4">
//           <Card className="shadow-sm mb-4">
//             <Card.Header className="bg-light d-flex justify-content-between align-items-center py-3">
//               <h3 className="mb-0 gradient-text h5">Interview Feedback Evaluations</h3>
//               <div className="w-auto" style={{ maxWidth: "300px" }}>
//                 <InputGroup>
//                   <InputGroup.Text>
//                     <i className="bi bi-search"></i>
//                   </InputGroup.Text>
//                   <Form.Control
//                     type="search"
//                     placeholder="Search by name, job, or email..."
//                     value={searchTerm}
//                     onChange={(e) => setSearchTerm(e.target.value)}
//                   />
//                 </InputGroup>
//               </div>
//             </Card.Header>
//             <Card.Body>
//               <div className="table-responsive">
//                 <Table hover striped className="align-middle">
//                   <thead className="table-dark">
//                     <tr>
//                       <th>#</th>
//                       <th>Candidate Name</th>
//                       <th>Job Title</th>
//                       <th>Email</th>
//                       <th>Evaluation Date</th>
//                       <th className="text-center">Actions</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {isLoading ? (
//                       <tr>
//                         <td colSpan="6" className="text-center py-5">
//                           <Spinner />
//                           <p className="mt-2 mb-0">Loading...</p>
//                         </td>
//                       </tr>
//                     ) : paginatedResults.length > 0 ? (
//                       paginatedResults.map((evaluation, index) => (
//                         <tr key={evaluation.id}>
//                           <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
//                           <td>{evaluation.candidate_name || "N/A"}</td>
//                           <td>{evaluation.job_title || "N/A"}</td>
//                           <td>{evaluation.candidate_email || "N/A"}</td>
//                           <td>{formatDate(evaluation.evaluation_date)}</td>
//                           <td className="text-center">
//                             <Button variant="outline-primary" size="sm" onClick={() => handleViewDetails(evaluation)}>
//                               <i className="bi bi-eye-fill me-1"></i> View
//                             </Button>
//                           </td>
//                         </tr>
//                       ))
//                     ) : (
//                       <tr>
//                         <td colSpan="6" className="text-center py-5">
//                           <i className="bi bi-journal-x fs-1 text-muted"></i>
//                           <p className="mt-2 mb-0">No feedback found.</p>
//                         </td>
//                       </tr>
//                     )}
//                   </tbody>
//                 </Table>
//               </div>
//             </Card.Body>
//             {calculatedTotalPages > 1 && (
//               <Card.Footer className="bg-light py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
//                 <div>
//                   <Form.Select
//                     size="sm"
//                     style={{ width: "auto", display: "inline-block" }}
//                     value={itemsPerPage}
//                     onChange={handleItemsPerPageChange}
//                   >
//                     <option value="10">10</option>
//                     <option value="25">25</option>
//                     <option value="50">50</option>
//                   </Form.Select>
//                   <span className="ms-2 small text-muted">entries per page</span>
//                 </div>
//                 <Pagination className="mb-0">
//                   <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
//                   {/* Simplified Pagination Items for brevity */}
//                   <Pagination.Item active>{currentPage}</Pagination.Item>
//                   <Pagination.Next
//                     onClick={() => handlePageChange(currentPage + 1)}
//                     disabled={currentPage === calculatedTotalPages}
//                   />
//                 </Pagination>
//               </Card.Footer>
//             )}
//           </Card>
//         </div>
//       </div>

//       <Modal show={selectedEvaluation !== null} onHide={handleCloseDetails} size="xl" centered scrollable>
//         <Modal.Header closeButton className="bg-light">
//           <Modal.Title className="h5 gradient-text">Complete Evaluation Details</Modal.Title>
//         </Modal.Header>
//         <Modal.Body className="p-4">
//           {selectedEvaluation && (
//             <>
//               <div className="mb-4 pb-3 border-bottom">
//                 <h5 className="mb-1 justified-text">{selectedEvaluation.candidate_name}</h5>
//                 <p className="mb-0 text-muted small justified-text">
//                   Job: {selectedEvaluation.job_title} | Interviewer: {selectedEvaluation.interviewer_email}
//                 </p>
//               </div>

//               <ButtonGroup className="w-100 mb-4">
//                 <Button
//                   variant={modalViewMode === "general" ? "primary" : "outline-primary"}
//                   onClick={() => handleModeChange("general")}
//                 >
//                   General Questions
//                 </Button>
//                 <Button
//                   variant={modalViewMode === "parameters" ? "primary" : "outline-primary"}
//                   onClick={() => handleModeChange("parameters")}
//                 >
//                   Parameter-Based Evaluation
//                 </Button>
//               </ButtonGroup>

//               {modalViewMode === "general" ? (
//                 selectedEvaluation.evaluation_details?.length > 0 ? (
//                   Object.entries(groupEvaluationsByArea(selectedEvaluation.evaluation_details)).map(
//                     ([area, details], areaIndex) => (
//                       <div key={areaIndex} className="mb-4">
//                         <h6 className="text-secondary mb-3 justified-text">{area}</h6>
//                         <Table striped bordered hover responsive>
//                           <thead className="table-light">
//                             <tr>
//                               <th style={{ width: "60%" }}>Question</th>
//                               <th style={{ width: "20%" }} className="text-center">
//                                 Answer
//                               </th>
//                               <th style={{ width: "20%" }}>Comments</th>
//                             </tr>
//                           </thead>
//                           <tbody>
//                             {details.map((detail, index) => (
//                               <tr key={detail.question_id || index}>
//                                 <td className="align-middle justified-text">
//                                   <strong>{detail.question_text}</strong>
//                                 </td>
//                                 <td className="text-center align-middle">
//                                   <span className="badge bg-dark">{detail.answer_value || "N/A"}</span>
//                                 </td>
//                                 <td className="align-middle justified-text">
//                                   {detail.comments || <span className="text-muted fst-italic">No comments.</span>}
//                                 </td>
//                               </tr>
//                             ))}
//                           </tbody>
//                         </Table>
//                       </div>
//                     ),
//                   )
//                 ) : (
//                   <div className="alert alert-secondary small mt-4">
//                     <div className="justified-text">No general questions were submitted for this interview.</div>
//                   </div>
//                 )
//               ) : selectedParameterEvaluation ? (
//                 <div className="modal-pdf-form">
//                   <div className="form-title">Post-Interview Employee Evaluation Form</div>

//                   <div className="candidate-info-section">
//                     <div className="form-group-inline">
//                       <label>Candidate Name:</label>
//                       <span>{selectedEvaluation.candidate_name || "N/A"}</span>
//                     </div>
//                     <div className="form-group-inline">
//                       <label>Position Applied For:</label>
//                       <span>{selectedEvaluation.job_title || "N/A"}</span>
//                     </div>
//                     <div className="form-group-inline">
//                       <label>Interview Date:</label>
//                       <span>{formatDate(selectedEvaluation.evaluation_date)}</span>
//                     </div>
//                     <div className="form-group-inline">
//                       <label>Interviewer(s):</label>
//                       <span>{selectedEvaluation.interviewer_email || "N/A"}</span>
//                     </div>
//                   </div>

//                   {Object.entries(groupParameterEvaluationsByArea(selectedParameterEvaluation.responses)).map(
//                     ([area, responses], areaIndex) => (
//                       <div key={areaIndex} className="mb-4">
//                         <h4 className="section-heading">
//                           {areaIndex + 1}. {area}
//                         </h4>
//                         <p className="mb-3 text-muted section-description">
//                           Rate 1 – 5 (1 = Poor, 5 = Excellent) and add comments/evidence to support your assessment.
//                           Each parameter should be evaluated based on observable behaviors and specific examples from
//                           the interview.
//                         </p>

//                         <Table striped bordered hover responsive className="mb-3">
//                           <thead className="table-light">
//                             <tr>
//                               <th style={{ width: "25%" }}>
//                                 {area === "Role-Specific & Cultural Fit"
//                                   ? "Attribute"
//                                   : area === "Core Competencies"
//                                     ? "Competency"
//                                     : "Parameter"}
//                               </th>
//                               {area !== "Role-Specific & Cultural Fit" && <th style={{ width: "35%" }}>Description</th>}
//                               <th style={{ width: "15%" }} className="text-center">
//                                 Rating
//                               </th>
//                               <th style={{ width: area === "Role-Specific & Cultural Fit" ? "60%" : "25%" }}>
//                                 {area === "Role-Specific & Cultural Fit" ? "Rating / Comments" : "Comments / Evidence"}
//                               </th>
//                             </tr>
//                           </thead>
//                           <tbody>
//                             {responses.map((res, resIndex) => (
//                               <tr key={res.id || resIndex}>
//                                 <td className="align-middle justified-text">
//                                   <strong>{res.parameter.parameter_text}</strong>
//                                 </td>
//                                 {area !== "Role-Specific & Cultural Fit" && (
//                                   <td className="align-middle justified-text">{res.parameter.description || "N/A"}</td>
//                                 )}
//                                 <td className="text-center align-middle">
//                                   <span className="badge bg-primary px-3 py-2">{mapRatingToText(res.rating)}</span>
//                                 </td>
//                                 <td className="align-middle justified-text">
//                                   {res.comments || <span className="text-muted fst-italic">No comments.</span>}
//                                 </td>
//                               </tr>
//                             ))}
//                           </tbody>
//                         </Table>
//                       </div>
//                     ),
//                   )}

//                   <div className="overall-recommendation-box mt-4">
//                     <h5 className="section-heading">4. Overall Recommendation</h5>

//                     <div className="recommendation-item">
//                       <span className="bullet-point">●</span>
//                       <div className="recommendation-content">
//                         <strong>Overall Suitability:</strong>
//                         <div className="recommendation-text">
//                           <span className="badge bg-success fs-6">
//                             {selectedParameterEvaluation.overall_recommendation_display || "N/A"}
//                           </span>
//                         </div>
//                       </div>
//                     </div>

//                     <div className="recommendation-item">
//                       <span className="bullet-point">●</span>
//                       <div className="recommendation-content">
//                         <strong>Top Strengths:</strong>
//                         {formatTextWithNumbering(
//                           selectedParameterEvaluation.strengths,
//                           "No specific strengths were documented during this evaluation process.",
//                         )}
//                       </div>
//                     </div>

//                     <div className="recommendation-item">
//                       <span className="bullet-point">●</span>
//                       <div className="recommendation-content">
//                         <strong>Key Development Areas:</strong>
//                         {formatTextWithNumbering(
//                           selectedParameterEvaluation.development_areas,
//                           "No specific development areas were identified during this evaluation process.",
//                         )}
//                       </div>
//                     </div>

//                     <div className="recommendation-item">
//                       <span className="bullet-point">●</span>
//                       <div className="recommendation-content">
//                         <strong>Final Comments:</strong>
//                         {formatTextWithNumbering(
//                           selectedParameterEvaluation.final_comments,
//                           "No additional final comments were provided by the interviewer for this candidate evaluation.",
//                         )}
//                       </div>
//                     </div>
//                   </div>

//                   <div className="signature-line">
//                     <span>Interviewer's Signature: ________________________</span>
//                     <span>Date: ________________________</span>
//                   </div>
//                 </div>
//               ) : (
//                 <div className="alert alert-secondary small mt-4">
//                   <div className="justified-text">No parameter-based evaluation was submitted for this interview.</div>
//                 </div>
//               )}
//             </>
//           )}
//         </Modal.Body>
//         <Modal.Footer>
//           <Button variant="outline-secondary" onClick={handleCloseDetails}>
//             Close
//           </Button>
//         </Modal.Footer>
//       </Modal>
//     </>
//   )
// }

// export default InterviewerFeedback


// "use client"
// import { useState, useEffect } from "react"
// import axios from "axios"
// import DashNavbar from "./DashNavbar"
// import Sidebar from "./Sidebar"
// import { Pagination, Modal, Card, Form, InputGroup, Button, Table, Spinner, ButtonGroup } from "react-bootstrap"
// import Swal from "sweetalert2"

// // Custom Styles Component (Embedded CSS)
// const CustomStyles = () => (
//   <style>{`
//     .interviewer-feedback-page .main-content { background-color: #f8f9fa; }
//     .gradient-text { background: linear-gradient(45deg, #0d6efd, #0d6efd); -webkit-background-clip: text; -webkit-text-fill-color: transparent; font-weight: 600; }
//     .interviewer-feedback-page.wrapper { display: flex; height: calc(100vh - 60px); }
//     .interviewer-feedback-page .main-content { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; }
//     .interviewer-feedback-page .card { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; }
//     .interviewer-feedback-page .card-body { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; padding: 0; }
//     .interviewer-feedback-page .table-responsive { flex-grow: 1; overflow-y: auto; padding: 1.5rem; padding-top: 0; }
//     .interviewer-feedback-page .card-header, .interviewer-feedback-page .card-footer { flex-shrink: 0; }
//     .summary-box { background-color: #f8f9fa; border: 1px solid #dee2e6; padding: 1rem; border-radius: .375rem; }
        
//     /* New styles for the PDF-like modal */
//     .modal-pdf-form .form-group-inline {
//       display: flex;
//       align-items: baseline;
//       margin-bottom: 0.8rem;
//     }
//     .modal-pdf-form .form-group-inline label {
//       font-weight: 600;
//       margin-right: 1rem;
//       min-width: 180px;
//     }
//     .modal-pdf-form .form-group-inline span {
//       flex-grow: 1;
//       border-bottom: 1px solid #ccc;
//       padding-bottom: 2px;
//       text-align: justify;
//     }
//     .modal-pdf-form .section-heading {
//       font-size: 1.25rem;
//       font-weight: 600;
//       margin-top: 1.5rem;
//       margin-bottom: 1rem;
//       border-bottom: 2px solid #0d6efd;
//       padding-bottom: 0.5rem;
//       text-align: justify;
//     }
//     .modal-pdf-form .table th, .modal-pdf-form .table td {
//       vertical-align: middle;
//       padding: 0.75rem;
//       text-align: justify;
//     }
//     .modal-pdf-form .table th {
//       text-align: center !important;
//       font-weight: 600;
//     }
//     .modal-pdf-form .table .text-center {
//       text-align: center !important;
//     }
//     .modal-pdf-form .table thead th {
//       background-color: #f0f0f0;
//     }
//     .modal-pdf-form .overall-recommendation-box {
//       border: 1px solid #dee2e6;
//       padding: 1.5rem;
//       border-radius: .375rem;
//       background-color: #f8f9fa;
//     }
//     .modal-pdf-form .overall-recommendation-box h5 {
//       margin-bottom: 1rem;
//       color: #0d6efd;
//       text-align: justify;
//     }
//     .modal-pdf-form .overall-recommendation-box .recommendation-item {
//       margin-bottom: 1rem;
//       display: flex;
//       align-items: flex-start;
//     }
//     .modal-pdf-form .overall-recommendation-box .recommendation-item .bullet-point {
//       color: #0d6efd;
//       font-size: 1.2rem;
//       margin-right: 0.5rem;
//       font-weight: bold;
//       flex-shrink: 0;
//       margin-top: 0.1rem;
//     }
//     .modal-pdf-form .overall-recommendation-box .recommendation-content {
//       flex-grow: 1;
//     }
//     .modal-pdf-form .overall-recommendation-box .recommendation-content strong {
//       display: block;
//       margin-bottom: 0.5rem;
//       text-align: justify;
//     }
//     .modal-pdf-form .overall-recommendation-box .recommendation-text {
//       text-align: justify;
//       line-height: 1.6;
//       margin-left: 0;
//     }
//     .modal-pdf-form .overall-recommendation-box .recommendation-list {
//       margin-left: 0;
//       padding-left: 0;
//     }
//     .modal-pdf-form .overall-recommendation-box .recommendation-subpoint {
//       margin-left: 1.5rem;
//       margin-bottom: 0.3rem;
//       text-align: justify;
//       line-height: 1.5;
//     }
//     .modal-pdf-form .overall-recommendation-box .recommendation-numbering {
//       font-weight: 600;
//       color: #6f42c1;
//       margin-right: 0.5rem;
//     }
//     .modal-pdf-form .signature-line {
//       margin-top: 3rem;
//       border-top: 1px solid #ccc;
//       padding-top: 0.5rem;
//       display: flex;
//       justify-content: space-between;
//       font-size: 0.9rem;
//       color: #6c757d;
//     }
//     .modal-pdf-form .signature-line span {
//       flex-basis: 48%;
//     }
//     .modal-pdf-form .candidate-info-section {
//       background-color: #f8f9fa;
//       padding: 1.5rem;
//       border-radius: .375rem;
//       margin-bottom: 2rem;
//       border: 1px solid #dee2e6;
//     }
//     .modal-pdf-form .form-title {
//       text-align: center;
//       font-size: 1.5rem;
//       font-weight: 700;
//       margin-bottom: 2rem;
//       color: #0d6efd;
//       border-bottom: 3px solid #0d6efd;
//       padding-bottom: 1rem;
//     }
//     .modal-pdf-form .parameter-numbering {
//       font-weight: 600;
//       color: #0d6efd;
//       margin-right: 0.5rem;
//     }
//     .modal-pdf-form .competency-numbering {
//       font-weight: 600;
//       color: #28a745;
//       margin-right: 0.5rem;
//     }
//     .modal-pdf-form .attribute-numbering {
//       font-weight: 600;
//       color: #dc3545;
//       margin-right: 0.5rem;
//     }
//     .modal-pdf-form .justified-text {
//       text-align: justify;
//       line-height: 1.5;
//     }
//     .modal-pdf-form .section-description {
//       text-align: justify;
//       font-style: italic;
//       color: #6c757d;
//       margin-bottom: 1rem;
//     }
    
//     /* General table text justification */
//     .table th, .table td {
//       text-align: justify;
//     }
//     .table th {
//       text-align: center !important;
//       font-weight: 600;
//     }
//     .table .text-center {
//       text-align: center !important;
//     }
//     .table .align-middle {
//       vertical-align: middle;
//     }
    
//     /* Section headings justification */
//     .text-secondary {
//       text-align: justify;
//     }
//   `}</style>
// )

// function InterviewerFeedback() {
//   const [evaluationResults, setEvaluationResults] = useState([])
//   const [allParameterEvaluations, setAllParameterEvaluations] = useState([])
//   const [searchTerm, setSearchTerm] = useState("")
//   const [currentPage, setCurrentPage] = useState(1)
//   const [selectedEvaluation, setSelectedEvaluation] = useState(null)
//   const [selectedParameterEvaluation, setSelectedParameterEvaluation] = useState(null)
//   const [isLoading, setIsLoading] = useState(false)
//   const [itemsPerPage, setItemsPerPage] = useState(10)
//   const [modalViewMode, setModalViewMode] = useState("general")

//   const fetchAllData = async () => {
//     setIsLoading(true)
//     try {
//       // Fetch a large number of general evaluations to allow for effective client-side search/pagination
//       const [generalEvalsResponse, parameterEvalsResponse] = await Promise.all([
//         axios.get(`https://raasbackend.vetrinahealthcare.com/interview-evaluations/?page_size=1000`),
//         axios.get(`https://raasbackend.vetrinahealthcare.com/evaluations/`),
//       ])
//       setEvaluationResults(generalEvalsResponse.data.results || [])
//       setAllParameterEvaluations(parameterEvalsResponse.data || [])
//     } catch (error) {
//       console.error("Error fetching evaluation data:", error)
//       Swal.fire("Error", "Could not fetch all evaluation data.", "error")
//       setEvaluationResults([])
//       setAllParameterEvaluations([])
//     } finally {
//       setIsLoading(false)
//     }
//   }

//   useEffect(() => {
//     fetchAllData()
//   }, [])

//   const handlePageChange = (page) => {
//     if (page >= 1) setCurrentPage(page)
//   }

//   const handleItemsPerPageChange = (e) => {
//     setItemsPerPage(Number(e.target.value))
//     setCurrentPage(1)
//   }

//   // Client-side filtering logic
//   const filteredResults = evaluationResults.filter(
//     (ev) =>
//       (ev.candidate_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
//       (ev.job_title || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
//       (ev.candidate_email || "").toLowerCase().includes(searchTerm.toLowerCase()),
//   )

//   const paginatedResults = filteredResults.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

//   const calculatedTotalPages = Math.ceil(filteredResults.length / itemsPerPage)

//   const handleViewDetails = (evaluation) => {
//     setSelectedEvaluation(evaluation)
//     // FIX: Match only on candidate name and job title, as the interviewer keys are different.
//     // This is the most reliable way to link the two records based on the available data.
//     const matchingParamEval = allParameterEvaluations.find(
//       (paramEval) =>
//         paramEval.candidate_name === evaluation.candidate_name && paramEval.job_title === evaluation.job_title,
//     )
//     setSelectedParameterEvaluation(matchingParamEval || null)
//     setModalViewMode("general")
//   }

//   const handleCloseDetails = () => {
//     setSelectedEvaluation(null)
//   }

//   const handleModeChange = (mode) => {
//     setModalViewMode(mode)
//   }

//   const formatDate = (dateString) => {
//     if (!dateString) return "N/A"
//     return new Date(dateString).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
//   }

//   const mapRatingToText = (rating) => {
//     const ratings = { 5: "Excellent", 4: "Good", 3: "Average", 2: "Below Average", 1: "Poor" }
//     return ratings[rating] || "N/A"
//   }

//   const groupEvaluationsByArea = (evaluationDetails) => {
//     if (!evaluationDetails || !Array.isArray(evaluationDetails)) return {}
//     return evaluationDetails.reduce((groups, detail) => {
//       const area = detail.area || "General Questions"
//       if (!groups[area]) groups[area] = []
//       groups[area].push(detail)
//       return groups
//     }, {})
//   }

//   const groupParameterEvaluationsByArea = (responses) => {
//     if (!responses || !Array.isArray(responses)) return {}

//     // Define the order of sections as per the form - FIXED MAPPING
//     const orderedSections = {
//       CORE_COMPETENCIES: "Core Competencies",
//       BEHAVIORAL_TRAITS: "Additional Behavioral & Functional Parameters",
//       TECHNICAL_SKILLS: "Role-Specific & Cultural Fit",
//     }

//     const groups = {}

//     // Initialize groups in the correct order
//     Object.values(orderedSections).forEach((sectionName) => {
//       groups[sectionName] = []
//     })

//     responses.forEach((res) => {
//       const area = res.parameter.area
//       const sectionName = orderedSections[area] || "Other Parameters"

//       if (!groups[sectionName]) {
//         groups[sectionName] = []
//       }
//       groups[sectionName].push(res)
//     })

//     // Remove empty groups
//     Object.keys(groups).forEach((key) => {
//       if (groups[key].length === 0) {
//         delete groups[key]
//       }
//     })

//     return groups
//   }

//   // Helper function to parse and format text with numbered points - FIXED
//   const formatTextWithNumbering = (text, defaultText) => {
//     // Check if text is null, undefined, empty, or "N/A"
//     if (!text || text === "N/A" || typeof text !== "string") {
//       return <div className="recommendation-text">{defaultText}</div>
//     }

//     // Convert to string if it's not already (safety check)
//     const textString = String(text).trim()

//     if (!textString) {
//       return <div className="recommendation-text">{defaultText}</div>
//     }

//     // Split by common delimiters and filter out empty strings
//     const points = textString
//       .split(/[.\n;]/)
//       .map((point) => point.trim())
//       .filter((point) => point.length > 0)

//     if (points.length <= 1) {
//       return <div className="recommendation-text">{textString}</div>
//     }

//     return (
//       <div className="recommendation-list">
//         {points.map((point, index) => (
//           <div key={index} className="recommendation-subpoint">
//             <span className="recommendation-numbering">{index + 1}.</span>
//             {point}
//           </div>
//         ))}
//       </div>
//     )
//   }

//   return (
//     <>
//       <CustomStyles />
//       <DashNavbar />
//       <div className="wrapper d-flex interviewer-feedback-page">
//         <Sidebar />
//         <div className="main-content flex-grow-1 p-3 p-md-4">
//           <Card className="shadow-sm mb-4">
//             <Card.Header className="bg-light d-flex justify-content-between align-items-center py-3">
//               <h3 className="mb-0 gradient-text h5">Interview Feedback Evaluations</h3>
//               <div className="w-auto" style={{ maxWidth: "300px" }}>
//                 <InputGroup>
//                   <InputGroup.Text>
//                     <i className="bi bi-search"></i>
//                   </InputGroup.Text>
//                   <Form.Control
//                     type="search"
//                     placeholder="Search by name, job, or email..."
//                     value={searchTerm}
//                     onChange={(e) => setSearchTerm(e.target.value)}
//                   />
//                 </InputGroup>
//               </div>
//             </Card.Header>
//             <Card.Body>
//               <div className="table-responsive">
//                 <Table hover striped className="align-middle">
//                   <thead className="table-dark">
//                     <tr>
//                       <th>#</th>
//                       <th>Candidate Name</th>
//                       <th>Job Title</th>
//                       <th>Email</th>
//                       <th>Evaluation Date</th>
//                       <th className="text-center">Actions</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {isLoading ? (
//                       <tr>
//                         <td colSpan="6" className="text-center py-5">
//                           <Spinner />
//                           <p className="mt-2 mb-0">Loading...</p>
//                         </td>
//                       </tr>
//                     ) : paginatedResults.length > 0 ? (
//                       paginatedResults.map((evaluation, index) => (
//                         <tr key={evaluation.id}>
//                           <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
//                           <td>{evaluation.candidate_name || "N/A"}</td>
//                           <td>{evaluation.job_title || "N/A"}</td>
//                           <td>{evaluation.candidate_email || "N/A"}</td>
//                           <td>{formatDate(evaluation.evaluation_date)}</td>
//                           <td className="text-center">
//                             <Button variant="outline-primary" size="sm" onClick={() => handleViewDetails(evaluation)}>
//                               <i className="bi bi-eye-fill me-1"></i> View
//                             </Button>
//                           </td>
//                         </tr>
//                       ))
//                     ) : (
//                       <tr>
//                         <td colSpan="6" className="text-center py-5">
//                           <i className="bi bi-journal-x fs-1 text-muted"></i>
//                           <p className="mt-2 mb-0">No feedback found.</p>
//                         </td>
//                       </tr>
//                     )}
//                   </tbody>
//                 </Table>
//               </div>
//             </Card.Body>
//             {calculatedTotalPages > 1 && (
//               <Card.Footer className="bg-light py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
//                 <div>
//                   <Form.Select
//                     size="sm"
//                     style={{ width: "auto", display: "inline-block" }}
//                     value={itemsPerPage}
//                     onChange={handleItemsPerPageChange}
//                   >
//                     <option value="10">10</option>
//                     <option value="25">25</option>
//                     <option value="50">50</option>
//                   </Form.Select>
//                   <span className="ms-2 small text-muted">entries per page</span>
//                 </div>
//                 <Pagination className="mb-0">
//                   <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
//                   {/* Simplified Pagination Items for brevity */}
//                   <Pagination.Item active>{currentPage}</Pagination.Item>
//                   <Pagination.Next
//                     onClick={() => handlePageChange(currentPage + 1)}
//                     disabled={currentPage === calculatedTotalPages}
//                   />
//                 </Pagination>
//               </Card.Footer>
//             )}
//           </Card>
//         </div>
//       </div>

//       <Modal show={selectedEvaluation !== null} onHide={handleCloseDetails} size="xl" centered scrollable>
//         <Modal.Header closeButton className="bg-light">
//           <Modal.Title className="h5 gradient-text">Complete Evaluation Details</Modal.Title>
//         </Modal.Header>
//         <Modal.Body className="p-4">
//           {selectedEvaluation && (
//             <>
//               <div className="mb-4 pb-3 border-bottom">
//                 <h5 className="mb-1 justified-text">{selectedEvaluation.candidate_name}</h5>
//                 <p className="mb-0 text-muted small justified-text">
//                   Job: {selectedEvaluation.job_title} | Interviewer: {selectedEvaluation.interviewer_email}
//                 </p>
//               </div>

//               <ButtonGroup className="w-100 mb-4">
//                 <Button
//                   variant={modalViewMode === "general" ? "primary" : "outline-primary"}
//                   onClick={() => handleModeChange("general")}
//                 >
//                   General Questions
//                 </Button>
//                 <Button
//                   variant={modalViewMode === "parameters" ? "primary" : "outline-primary"}
//                   onClick={() => handleModeChange("parameters")}
//                 >
//                   Parameter-Based Evaluation
//                 </Button>
//               </ButtonGroup>

//               {modalViewMode === "general" ? (
//                 selectedEvaluation.evaluation_details?.length > 0 ? (
//                   Object.entries(groupEvaluationsByArea(selectedEvaluation.evaluation_details)).map(
//                     ([area, details], areaIndex) => (
//                       <div key={areaIndex} className="mb-4">
//                         <h6 className="text-secondary mb-3 justified-text">{area}</h6>
//                         <Table striped bordered hover responsive>
//                           <thead className="table-light">
//                             <tr>
//                               <th style={{ width: "60%" }}>Question</th>
//                               <th style={{ width: "20%" }} className="text-center">
//                                 Answer
//                               </th>
//                               <th style={{ width: "20%" }}>Comments</th>
//                             </tr>
//                           </thead>
//                           <tbody>
//                             {details.map((detail, index) => (
//                               <tr key={detail.question_id || index}>
//                                 <td className="align-middle justified-text">
//                                   <strong>{detail.question_text}</strong>
//                                 </td>
//                                 <td className="text-center align-middle">
//                                   <span className="badge bg-dark">{detail.answer_value || "N/A"}</span>
//                                 </td>
//                                 <td className="align-middle justified-text">
//                                   {detail.comments || <span className="text-muted fst-italic">No comments.</span>}
//                                 </td>
//                               </tr>
//                             ))}
//                           </tbody>
//                         </Table>
//                       </div>
//                     ),
//                   )
//                 ) : (
//                   <div className="alert alert-secondary small mt-4">
//                     <div className="justified-text">No general questions were submitted for this interview.</div>
//                   </div>
//                 )
//               ) : selectedParameterEvaluation ? (
//                 <div className="modal-pdf-form">
//                   <div className="form-title">Post-Interview Employee Evaluation Form</div>

//                   <div className="candidate-info-section">
//                     <div className="form-group-inline">
//                       <label>Candidate Name:</label>
//                       <span>{selectedEvaluation.candidate_name || "N/A"}</span>
//                     </div>
//                     <div className="form-group-inline">
//                       <label>Position Applied For:</label>
//                       <span>{selectedEvaluation.job_title || "N/A"}</span>
//                     </div>
//                     <div className="form-group-inline">
//                       <label>Interview Date:</label>
//                       <span>{formatDate(selectedEvaluation.evaluation_date)}</span>
//                     </div>
//                     <div className="form-group-inline">
//                       <label>Interviewer(s):</label>
//                       <span>{selectedEvaluation.interviewer_email || "N/A"}</span>
//                     </div>
//                   </div>

//                   {Object.entries(groupParameterEvaluationsByArea(selectedParameterEvaluation.responses)).map(
//                     ([area, responses], areaIndex) => (
//                       <div key={areaIndex} className="mb-4">
//                         <h4 className="section-heading">
//                           {areaIndex + 1}. {area}
//                         </h4>
//                         <p className="mb-3 text-muted section-description">
//                           Rate 1 – 5 (1 = Poor, 5 = Excellent) and add comments/evidence to support your assessment.
//                           Each parameter should be evaluated based on observable behaviors and specific examples from
//                           the interview.
//                         </p>

//                         <Table striped bordered hover responsive className="mb-3">
//                           <thead className="table-light">
//                             <tr>
//                               <th style={{ width: "25%" }}>
//                                 {area === "Role-Specific & Cultural Fit"
//                                   ? "Attribute"
//                                   : area === "Core Competencies"
//                                     ? "Competency"
//                                     : "Parameter"}
//                               </th>
//                               {area !== "Role-Specific & Cultural Fit" && <th style={{ width: "35%" }}>Description</th>}
//                               <th style={{ width: "15%" }} className="text-center">
//                                 Rating
//                               </th>
//                               <th style={{ width: area === "Role-Specific & Cultural Fit" ? "60%" : "25%" }}>
//                                 {area === "Role-Specific & Cultural Fit" ? "Rating / Comments" : "Comments / Evidence"}
//                               </th>
//                             </tr>
//                           </thead>
//                           <tbody>
//                             {responses.map((res, resIndex) => (
//                               <tr key={res.id || resIndex}>
//                                 <td className="align-middle justified-text">
//                                   <strong>{res.parameter.parameter_text}</strong>
//                                 </td>
//                                 {area !== "Role-Specific & Cultural Fit" && (
//                                   <td className="align-middle justified-text">{res.parameter.description || "N/A"}</td>
//                                 )}
//                                 <td className="text-center align-middle">
//                                   <span className="badge bg-primary px-3 py-2">{mapRatingToText(res.rating)}</span>
//                                 </td>
//                                 <td className="align-middle justified-text">
//                                   {res.comments || <span className="text-muted fst-italic">No comments.</span>}
//                                 </td>
//                               </tr>
//                             ))}
//                           </tbody>
//                         </Table>
//                       </div>
//                     ),
//                   )}

//                   <div className="overall-recommendation-box mt-4">
//                     <h5 className="section-heading">4. Overall Recommendation</h5>

//                     <div className="recommendation-item">
//                       <span className="bullet-point">●</span>
//                       <div className="recommendation-content">
//                         <strong>Overall Suitability:</strong>
//                         <div className="recommendation-text">
//                           <span className="badge bg-success fs-6">
//                             {selectedParameterEvaluation.overall_recommendation_display || "N/A"}
//                           </span>
//                         </div>
//                       </div>
//                     </div>

//                     <div className="recommendation-item">
//                       <span className="bullet-point">●</span>
//                       <div className="recommendation-content">
//                         <strong>Top Strengths:</strong>
//                         {formatTextWithNumbering(
//                           selectedParameterEvaluation.strengths,
//                           "No specific strengths were documented during this evaluation process.",
//                         )}
//                       </div>
//                     </div>

//                     <div className="recommendation-item">
//                       <span className="bullet-point">●</span>
//                       <div className="recommendation-content">
//                         <strong>Key Development Areas:</strong>
//                         {formatTextWithNumbering(
//                           selectedParameterEvaluation.development_areas,
//                           "No specific development areas were identified during this evaluation process.",
//                         )}
//                       </div>
//                     </div>

//                     <div className="recommendation-item">
//                       <span className="bullet-point">●</span>
//                       <div className="recommendation-content">
//                         <strong>Final Comments:</strong>
//                         {formatTextWithNumbering(
//                           selectedParameterEvaluation.final_comments,
//                           "No additional final comments were provided by the interviewer for this candidate evaluation.",
//                         )}
//                       </div>
//                     </div>
//                   </div>

//                   <div className="signature-line">
//                     <span>Interviewer's Signature: ________________________</span>
//                     <span>Date: ________________________</span>
//                   </div>
//                 </div>
//               ) : (
//                 <div className="alert alert-secondary small mt-4">
//                   <div className="justified-text">No parameter-based evaluation was submitted for this interview.</div>
//                 </div>
//               )}
//             </>
//           )}
//         </Modal.Body>
//         <Modal.Footer>
//           <Button variant="outline-secondary" onClick={handleCloseDetails}>
//             Close
//           </Button>
//         </Modal.Footer>
//       </Modal>
//     </>
//   )
// }

// export default InterviewerFeedback



// "use client"
// import { useState, useEffect } from "react"
// import axios from "axios"
// import DashNavbar from "./DashNavbar"
// import Sidebar from "./Sidebar"
// import { Pagination, Modal, Card, Form, InputGroup, Button, Table, Spinner, ButtonGroup } from "react-bootstrap"
// import Swal from "sweetalert2"

// // Custom Styles Component (Embedded CSS)
// const CustomStyles = () => (
//   <style>{`
//     .interviewer-feedback-page .main-content { background-color: #f8f9fa; }
//     .gradient-text { background: linear-gradient(45deg, #0d6efd, #0d6efd); -webkit-background-clip: text; -webkit-text-fill-color: transparent; font-weight: 600; }
//     .interviewer-feedback-page.wrapper { display: flex; height: calc(100vh - 60px); }
//     .interviewer-feedback-page .main-content { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; }
//     .interviewer-feedback-page .card { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; }
//     .interviewer-feedback-page .card-body { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; padding: 0; }
//     .interviewer-feedback-page .table-responsive { flex-grow: 1; overflow-y: auto; padding: 1.5rem; padding-top: 0; }
//     .interviewer-feedback-page .card-header, .interviewer-feedback-page .card-footer { flex-shrink: 0; }
//     .summary-box { background-color: #f8f9fa; border: 1px solid #dee2e6; padding: 1rem; border-radius: .375rem; }
        
//     /* New styles for the PDF-like modal */
//     .modal-pdf-form .form-group-inline {
//       display: flex;
//       align-items: baseline;
//       margin-bottom: 0.5rem;
//     }
//     .modal-pdf-form .form-group-inline label {
//       font-weight: 600;
//       margin-right: 0.5rem;
//       min-width: 150px; /* Increased for better alignment */
//     }
//     .modal-pdf-form .form-group-inline span {
//       flex-grow: 1;
//       border-bottom: 1px solid #ccc;
//       padding-bottom: 2px;
//     }
//     .modal-pdf-form .section-heading {
//       font-size: 1.25rem;
//       font-weight: 600;
//       margin-top: 1.5rem;
//       margin-bottom: 1rem;
//       border-bottom: 2px solid #0d6efd;
//       padding-bottom: 0.5rem;
//     }
//     .modal-pdf-form .table th, .modal-pdf-form .table td {
//       vertical-align: middle;
//       padding: 0.75rem;
//     }
//     .modal-pdf-form .table thead th {
//       background-color: #f0f0f0;
//     }
//     .modal-pdf-form .overall-recommendation-box {
//       border: 1px solid #dee2e6;
//       padding: 1.5rem;
//       border-radius: .375rem;
//       background-color: #f8f9fa;
//     }
//     .modal-pdf-form .overall-recommendation-box h5 {
//       margin-bottom: 1rem;
//       color: #0d6efd;
//     }
//     .modal-pdf-form .overall-recommendation-box .recommendation-item {
//       margin-bottom: 0.75rem;
//     }
//     .modal-pdf-form .overall-recommendation-box .recommendation-item strong {
//       display: block;
//       margin-bottom: 0.25rem;
//     }
//     .modal-pdf-form .overall-recommendation-box .recommendation-text {
//       text-align: justify;
//       line-height: 1.6;
//     }
//     .modal-pdf-form .signature-line {
//       margin-top: 3rem;
//       border-top: 1px solid #ccc;
//       padding-top: 0.5rem;
//       display: flex;
//       justify-content: space-between;
//       font-size: 0.9rem;
//       color: #6c757d;
//     }
//     .modal-pdf-form .signature-line span {
//       flex-basis: 48%;
//     }
//     .modal-pdf-form .candidate-info-section {
//       background-color: #f8f9fa;
//       padding: 1.5rem;
//       border-radius: .375rem;
//       margin-bottom: 2rem;
//       border: 1px solid #dee2e6;
//     }
//     .modal-pdf-form .form-title {
//       text-align: center;
//       font-size: 1.5rem;
//       font-weight: 700;
//       margin-bottom: 2rem;
//       color: #0d6efd;
//       border-bottom: 3px solid #0d6efd;
//       padding-bottom: 1rem;
//     }
//   `}</style>
// )

// function InterviewerFeedback() {
//   const [evaluationResults, setEvaluationResults] = useState([])
//   const [allParameterEvaluations, setAllParameterEvaluations] = useState([])
//   const [searchTerm, setSearchTerm] = useState("")
//   const [currentPage, setCurrentPage] = useState(1)
//   const [selectedEvaluation, setSelectedEvaluation] = useState(null)
//   const [selectedParameterEvaluation, setSelectedParameterEvaluation] = useState(null)
//   const [isLoading, setIsLoading] = useState(false)
//   const [itemsPerPage, setItemsPerPage] = useState(10)
//   const [modalViewMode, setModalViewMode] = useState("general")

//   const fetchAllData = async () => {
//     setIsLoading(true)
//     try {
//       // Fetch a large number of general evaluations to allow for effective client-side search/pagination
//       const [generalEvalsResponse, parameterEvalsResponse] = await Promise.all([
//         axios.get(`https://raasbackend.vetrinahealthcare.com/interview-evaluations/?page_size=1000`),
//         axios.get(`https://raasbackend.vetrinahealthcare.com/evaluations/`),
//       ])
//       setEvaluationResults(generalEvalsResponse.data.results || [])
//       setAllParameterEvaluations(parameterEvalsResponse.data || [])
//     } catch (error) {
//       console.error("Error fetching evaluation data:", error)
//       Swal.fire("Error", "Could not fetch all evaluation data.", "error")
//       setEvaluationResults([])
//       setAllParameterEvaluations([])
//     } finally {
//       setIsLoading(false)
//     }
//   }

//   useEffect(() => {
//     fetchAllData()
//   }, [])

//   const handlePageChange = (page) => {
//     if (page >= 1) setCurrentPage(page)
//   }

//   const handleItemsPerPageChange = (e) => {
//     setItemsPerPage(Number(e.target.value))
//     setCurrentPage(1)
//   }

//   // Client-side filtering logic
//   const filteredResults = evaluationResults.filter(
//     (ev) =>
//       (ev.candidate_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
//       (ev.job_title || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
//       (ev.candidate_email || "").toLowerCase().includes(searchTerm.toLowerCase()),
//   )

//   const paginatedResults = filteredResults.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

//   const calculatedTotalPages = Math.ceil(filteredResults.length / itemsPerPage)

//   const handleViewDetails = (evaluation) => {
//     setSelectedEvaluation(evaluation)
//     // FIX: Match only on candidate name and job title, as the interviewer keys are different.
//     // This is the most reliable way to link the two records based on the available data.
//     const matchingParamEval = allParameterEvaluations.find(
//       (paramEval) =>
//         paramEval.candidate_name === evaluation.candidate_name && paramEval.job_title === evaluation.job_title,
//     )
//     setSelectedParameterEvaluation(matchingParamEval || null)
//     setModalViewMode("general")
//   }

//   const handleCloseDetails = () => {
//     setSelectedEvaluation(null)
//   }

//   const handleModeChange = (mode) => {
//     setModalViewMode(mode)
//   }

//   const formatDate = (dateString) => {
//     if (!dateString) return "N/A"
//     return new Date(dateString).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
//   }

//   const mapRatingToText = (rating) => {
//     const ratings = { 5: "Excellent", 4: "Good", 3: "Average", 2: "Below Average", 1: "Poor" }
//     return ratings[rating] || "N/A"
//   }

//   const groupEvaluationsByArea = (evaluationDetails) => {
//     if (!evaluationDetails || !Array.isArray(evaluationDetails)) return {}
//     return evaluationDetails.reduce((groups, detail) => {
//       const area = detail.area || "General Questions"
//       if (!groups[area]) groups[area] = []
//       groups[area].push(detail)
//       return groups
//     }, {})
//   }

//   const groupParameterEvaluationsByArea = (responses) => {
//     if (!responses || !Array.isArray(responses)) return {}

//     // Define the order of sections as per the form - FIXED MAPPING
//     const orderedSections = {
//       CORE_COMPETENCIES: "Core Competencies",
//       BEHAVIORAL_TRAITS: "Additional Behavioral & Functional Parameters",
//       TECHNICAL_SKILLS: "Role-Specific & Cultural Fit",
//     }

//     const groups = {}

//     // Initialize groups in the correct order
//     Object.values(orderedSections).forEach((sectionName) => {
//       groups[sectionName] = []
//     })

//     responses.forEach((res) => {
//       const area = res.parameter.area
//       const sectionName = orderedSections[area] || "Other Parameters"

//       if (!groups[sectionName]) {
//         groups[sectionName] = []
//       }
//       groups[sectionName].push(res)
//     })

//     // Remove empty groups
//     Object.keys(groups).forEach((key) => {
//       if (groups[key].length === 0) {
//         delete groups[key]
//       }
//     })

//     return groups
//   }

//   return (
//     <>
//       <CustomStyles />
//       <DashNavbar />
//       <div className="wrapper d-flex interviewer-feedback-page">
//         <Sidebar />
//         <div className="main-content flex-grow-1 p-3 p-md-4">
//           <Card className="shadow-sm mb-4">
//             <Card.Header className="bg-light d-flex justify-content-between align-items-center py-3">
//               <h3 className="mb-0 gradient-text h5">Interview Feedback Evaluations</h3>
//               <div className="w-auto" style={{ maxWidth: "300px" }}>
//                 <InputGroup>
//                   <InputGroup.Text>
//                     <i className="bi bi-search"></i>
//                   </InputGroup.Text>
//                   <Form.Control
//                     type="search"
//                     placeholder="Search by name, job, or email..."
//                     value={searchTerm}
//                     onChange={(e) => setSearchTerm(e.target.value)}
//                   />
//                 </InputGroup>
//               </div>
//             </Card.Header>
//             <Card.Body>
//               <div className="table-responsive">
//                 <Table hover striped className="align-middle">
//                   <thead className="table-dark">
//                     <tr>
//                       <th>#</th>
//                       <th>Candidate Name</th>
//                       <th>Job Title</th>
//                       <th>Email</th>
//                       <th>Evaluation Date</th>
//                       <th className="text-center">Actions</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {isLoading ? (
//                       <tr>
//                         <td colSpan="6" className="text-center py-5">
//                           <Spinner />
//                           <p className="mt-2 mb-0">Loading...</p>
//                         </td>
//                       </tr>
//                     ) : paginatedResults.length > 0 ? (
//                       paginatedResults.map((evaluation, index) => (
//                         <tr key={evaluation.id}>
//                           <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
//                           <td>{evaluation.candidate_name || "N/A"}</td>
//                           <td>{evaluation.job_title || "N/A"}</td>
//                           <td>{evaluation.candidate_email || "N/A"}</td>
//                           <td>{formatDate(evaluation.evaluation_date)}</td>
//                           <td className="text-center">
//                             <Button variant="outline-primary" size="sm" onClick={() => handleViewDetails(evaluation)}>
//                               <i className="bi bi-eye-fill me-1"></i> View
//                             </Button>
//                           </td>
//                         </tr>
//                       ))
//                     ) : (
//                       <tr>
//                         <td colSpan="6" className="text-center py-5">
//                           <i className="bi bi-journal-x fs-1 text-muted"></i>
//                           <p className="mt-2 mb-0">No feedback found.</p>
//                         </td>
//                       </tr>
//                     )}
//                   </tbody>
//                 </Table>
//               </div>
//             </Card.Body>
//             {calculatedTotalPages > 1 && (
//               <Card.Footer className="bg-light py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
//                 <div>
//                   <Form.Select
//                     size="sm"
//                     style={{ width: "auto", display: "inline-block" }}
//                     value={itemsPerPage}
//                     onChange={handleItemsPerPageChange}
//                   >
//                     <option value="10">10</option>
//                     <option value="25">25</option>
//                     <option value="50">50</option>
//                   </Form.Select>
//                   <span className="ms-2 small text-muted">entries per page</span>
//                 </div>
//                 <Pagination className="mb-0">
//                   <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
//                   {/* Simplified Pagination Items for brevity */}
//                   <Pagination.Item active>{currentPage}</Pagination.Item>
//                   <Pagination.Next
//                     onClick={() => handlePageChange(currentPage + 1)}
//                     disabled={currentPage === calculatedTotalPages}
//                   />
//                 </Pagination>
//               </Card.Footer>
//             )}
//           </Card>
//         </div>
//       </div>

//       <Modal show={selectedEvaluation !== null} onHide={handleCloseDetails} size="xl" centered scrollable>
//         <Modal.Header closeButton className="bg-light">
//           <Modal.Title className="h5 gradient-text">Complete Evaluation Details</Modal.Title>
//         </Modal.Header>
//         <Modal.Body className="p-4">
//           {selectedEvaluation && (
//             <>
//               <div className="mb-4 pb-3 border-bottom">
//                 <h5 className="mb-1">{selectedEvaluation.candidate_name}</h5>
//                 <p className="mb-0 text-muted small">
//                   Job: {selectedEvaluation.job_title} | Interviewer: {selectedEvaluation.interviewer_email}
//                 </p>
//               </div>

//               <ButtonGroup className="w-100 mb-4">
//                 <Button
//                   variant={modalViewMode === "general" ? "primary" : "outline-primary"}
//                   onClick={() => handleModeChange("general")}
//                 >
//                   General Questions
//                 </Button>
//                 <Button
//                   variant={modalViewMode === "parameters" ? "primary" : "outline-primary"}
//                   onClick={() => handleModeChange("parameters")}
//                 >
//                   Parameter-Based Evaluation
//                 </Button>
//               </ButtonGroup>

//               {modalViewMode === "general" ? (
//                 selectedEvaluation.evaluation_details?.length > 0 ? (
//                   Object.entries(groupEvaluationsByArea(selectedEvaluation.evaluation_details)).map(
//                     ([area, details], areaIndex) => (
//                       <div key={areaIndex} className="mb-4">
//                         <h6 className="text-secondary mb-3">{area}</h6>
//                         <Table striped bordered hover responsive>
//                           <thead className="table-light">
//                             <tr>
//                               <th style={{ width: "60%" }}>Question</th>
//                               <th style={{ width: "20%" }} className="text-center">
//                                 Answer
//                               </th>
//                               <th style={{ width: "20%" }}>Comments</th>
//                             </tr>
//                           </thead>
//                           <tbody>
//                             {details.map((detail, index) => (
//                               <tr key={detail.question_id || index}>
//                                 <td className="align-middle">
//                                   <strong>{detail.question_text}</strong>
//                                 </td>
//                                 <td className="text-center align-middle">
//                                   <span className="badge bg-dark">{detail.answer_value || "N/A"}</span>
//                                 </td>
//                                 <td className="align-middle">
//                                   {detail.comments || <span className="text-muted fst-italic">No comments.</span>}
//                                 </td>
//                               </tr>
//                             ))}
//                           </tbody>
//                         </Table>
//                       </div>
//                     ),
//                   )
//                 ) : (
//                   <div className="alert alert-secondary small mt-4">
//                     No general questions were submitted for this interview.
//                   </div>
//                 )
//               ) : selectedParameterEvaluation ? (
//                 <div className="modal-pdf-form">
//                   <div className="form-title">Post-Interview Employee Evaluation Form</div>

//                   <div className="candidate-info-section">
//                     <div className="form-group-inline">
//                       <label>Candidate Name:</label>
//                       <span>{selectedEvaluation.candidate_name || "N/A"}</span>
//                     </div>
//                     <div className="form-group-inline">
//                       <label>Position Applied For:</label>
//                       <span>{selectedEvaluation.job_title || "N/A"}</span>
//                     </div>
//                     <div className="form-group-inline">
//                       <label>Interview Date:</label>
//                       <span>{formatDate(selectedEvaluation.evaluation_date)}</span>
//                     </div>
//                     <div className="form-group-inline">
//                       <label>Interviewer(s):</label>
//                       <span>{selectedEvaluation.interviewer_email || "N/A"}</span>
//                     </div>
//                   </div>

//                   {Object.entries(groupParameterEvaluationsByArea(selectedParameterEvaluation.responses)).map(
//                     ([area, responses], areaIndex) => (
//                       <div key={areaIndex} className="mb-4">
//                         <h4 className="section-heading">
//                           {areaIndex + 1}. {area}
//                         </h4>
//                         <p className="mb-3 text-muted">
//                           Rate 1 – 5 (1 = Poor, 5 = Excellent) and add comments/evidence:
//                         </p>

//                         <Table striped bordered hover responsive className="mb-3">
//                           <thead className="table-light">
//                             <tr>
//                               <th style={{ width: "25%" }}>
//                                 {area === "Role-Specific & Cultural Fit"
//                                   ? "Attribute"
//                                   : area === "Core Competencies"
//                                     ? "Competency"
//                                     : "Parameter"}
//                               </th>
//                               {area !== "Role-Specific & Cultural Fit" && <th style={{ width: "35%" }}>Description</th>}
//                               <th style={{ width: "15%" }} className="text-center">
//                                 Rating
//                               </th>
//                               <th style={{ width: area === "Role-Specific & Cultural Fit" ? "60%" : "25%" }}>
//                                 {area === "Role-Specific & Cultural Fit" ? "Rating / Comments" : "Comments / Evidence"}
//                               </th>
//                             </tr>
//                           </thead>
//                           <tbody>
//                             {responses.map((res, resIndex) => (
//                               <tr key={res.id || resIndex}>
//                                 <td className="align-middle">
//                                   <strong>{res.parameter.parameter_text}</strong>
//                                 </td>
//                                 {area !== "Role-Specific & Cultural Fit" && (
//                                   <td className="align-middle">{res.parameter.description || "N/A"}</td>
//                                 )}
//                                 <td className="text-center align-middle">
//                                   <span className="badge bg-primary px-3 py-2">{mapRatingToText(res.rating)}</span>
//                                 </td>
//                                 <td className="align-middle">
//                                   {res.comments || <span className="text-muted fst-italic">No comments.</span>}
//                                 </td>
//                               </tr>
//                             ))}
//                           </tbody>
//                         </Table>
//                       </div>
//                     ),
//                   )}

//                   <div className="overall-recommendation-box mt-4">
//                     <h5 className="section-heading">4. Overall Recommendation</h5>
//                     <div className="recommendation-item">
//                       <strong>Overall Suitability:</strong>
//                       <span className="badge bg-success fs-6 ms-2">
//                         {selectedParameterEvaluation.overall_recommendation_display || "N/A"}
//                       </span>
//                     </div>
//                     <div className="recommendation-item">
//                       <strong>Top Strengths:</strong>
//                       <div className="recommendation-text">{selectedParameterEvaluation.strengths || "N/A"}</div>
//                     </div>
//                     <div className="recommendation-item">
//                       <strong>Key Development Areas:</strong>
//                       <div className="recommendation-text">
//                         {selectedParameterEvaluation.development_areas || "N/A"}
//                       </div>
//                     </div>
//                     <div className="recommendation-item">
//                       <strong>Final Comments:</strong>
//                       <div className="recommendation-text">{selectedParameterEvaluation.final_comments || "N/A"}</div>
//                     </div>
//                   </div>

//                   <div className="signature-line">
//                     <span>Interviewer's Signature: ________________________</span>
//                     <span>Date: ________________________</span>
//                   </div>
//                 </div>
//               ) : (
//                 <div className="alert alert-secondary small mt-4">
//                   No parameter-based evaluation was submitted for this interview.
//                 </div>
//               )}
//             </>
//           )}
//         </Modal.Body>
//         <Modal.Footer>
//           <Button variant="outline-secondary" onClick={handleCloseDetails}>
//             Close
//           </Button>
//         </Modal.Footer>
//       </Modal>
//     </>
//   )
// }

// export default InterviewerFeedback

// "use client"
// import { useState, useEffect } from "react"
// import axios from "axios"
// import DashNavbar from "./DashNavbar"
// import Sidebar from "./Sidebar"
// import { Pagination, Modal, Card, Form, InputGroup, Button, Table, Spinner, ButtonGroup } from "react-bootstrap"
// import Swal from "sweetalert2"

// // Custom Styles Component (Embedded CSS)
// const CustomStyles = () => (
//   <style>{`
//     .interviewer-feedback-page .main-content { background-color: #f8f9fa; }
//     .gradient-text { background: linear-gradient(45deg, #0d6efd, #0d6efd); -webkit-background-clip: text; -webkit-text-fill-color: transparent; font-weight: 600; }
//     .interviewer-feedback-page.wrapper { display: flex; height: calc(100vh - 60px); }
//     .interviewer-feedback-page .main-content { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; }
//     .interviewer-feedback-page .card { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; }
//     .interviewer-feedback-page .card-body { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; padding: 0; }
//     .interviewer-feedback-page .table-responsive { flex-grow: 1; overflow-y: auto; padding: 1.5rem; padding-top: 0; }
//     .interviewer-feedback-page .card-header, .interviewer-feedback-page .card-footer { flex-shrink: 0; }
//     .summary-box { background-color: #f8f9fa; border: 1px solid #dee2e6; padding: 1rem; border-radius: .375rem; }
        
//     /* New styles for the PDF-like modal */
//     .modal-pdf-form .form-group-inline {
//       display: flex;
//       align-items: baseline;
//       margin-bottom: 0.5rem;
//     }
//     .modal-pdf-form .form-group-inline label {
//       font-weight: 600;
//       margin-right: 0.5rem;
//       min-width: 150px; /* Increased for better alignment */
//     }
//     .modal-pdf-form .form-group-inline span {
//       flex-grow: 1;
//       border-bottom: 1px solid #ccc;
//       padding-bottom: 2px;
//     }
//     .modal-pdf-form .section-heading {
//       font-size: 1.25rem;
//       font-weight: 600;
//       margin-top: 1.5rem;
//       margin-bottom: 1rem;
//       border-bottom: 2px solid #0d6efd;
//       padding-bottom: 0.5rem;
//     }
//     .modal-pdf-form .table th, .modal-pdf-form .table td {
//       vertical-align: middle;
//       padding: 0.75rem;
//     }
//     .modal-pdf-form .table thead th {
//       background-color: #f0f0f0;
//     }
//     .modal-pdf-form .overall-recommendation-box {
//       border: 1px solid #dee2e6;
//       padding: 1.5rem;
//       border-radius: .375rem;
//       background-color: #f8f9fa;
//     }
//     .modal-pdf-form .overall-recommendation-box h5 {
//       margin-bottom: 1rem;
//       color: #0d6efd;
//     }
//     .modal-pdf-form .overall-recommendation-box .recommendation-item {
//       margin-bottom: 0.75rem;
//     }
//     .modal-pdf-form .overall-recommendation-box .recommendation-item strong {
//       display: block;
//       margin-bottom: 0.25rem;
//     }
//     .modal-pdf-form .overall-recommendation-box .recommendation-text {
//       text-align: justify;
//       line-height: 1.6;
//     }
//     .modal-pdf-form .signature-line {
//       margin-top: 3rem;
//       border-top: 1px solid #ccc;
//       padding-top: 0.5rem;
//       display: flex;
//       justify-content: space-between;
//       font-size: 0.9rem;
//       color: #6c757d;
//     }
//     .modal-pdf-form .signature-line span {
//       flex-basis: 48%;
//     }
//     .modal-pdf-form .candidate-info-section {
//       background-color: #f8f9fa;
//       padding: 1.5rem;
//       border-radius: .375rem;
//       margin-bottom: 2rem;
//       border: 1px solid #dee2e6;
//     }
//     .modal-pdf-form .form-title {
//       text-align: center;
//       font-size: 1.5rem;
//       font-weight: 700;
//       margin-bottom: 2rem;
//       color: #0d6efd;
//       border-bottom: 3px solid #0d6efd;
//       padding-bottom: 1rem;
//     }
//   `}</style>
// )

// function InterviewerFeedback() {
//   const [evaluationResults, setEvaluationResults] = useState([])
//   const [allParameterEvaluations, setAllParameterEvaluations] = useState([])
//   const [searchTerm, setSearchTerm] = useState("")
//   const [currentPage, setCurrentPage] = useState(1)
//   const [selectedEvaluation, setSelectedEvaluation] = useState(null)
//   const [selectedParameterEvaluation, setSelectedParameterEvaluation] = useState(null)
//   const [isLoading, setIsLoading] = useState(false)
//   const [itemsPerPage, setItemsPerPage] = useState(10)
//   const [modalViewMode, setModalViewMode] = useState("general")

//   const fetchAllData = async () => {
//     setIsLoading(true)
//     try {
//       // Fetch a large number of general evaluations to allow for effective client-side search/pagination
//       const [generalEvalsResponse, parameterEvalsResponse] = await Promise.all([
//         axios.get(`https://raasbackend.vetrinahealthcare.com/interview-evaluations/?page_size=1000`),
//         axios.get(`https://raasbackend.vetrinahealthcare.com/evaluations/`),
//       ])
//       setEvaluationResults(generalEvalsResponse.data.results || [])
//       setAllParameterEvaluations(parameterEvalsResponse.data || [])
//     } catch (error) {
//       console.error("Error fetching evaluation data:", error)
//       Swal.fire("Error", "Could not fetch all evaluation data.", "error")
//       setEvaluationResults([])
//       setAllParameterEvaluations([])
//     } finally {
//       setIsLoading(false)
//     }
//   }

//   useEffect(() => {
//     fetchAllData()
//   }, [])

//   const handlePageChange = (page) => {
//     if (page >= 1) setCurrentPage(page)
//   }

//   const handleItemsPerPageChange = (e) => {
//     setItemsPerPage(Number(e.target.value))
//     setCurrentPage(1)
//   }

//   // Client-side filtering logic
//   const filteredResults = evaluationResults.filter(
//     (ev) =>
//       (ev.candidate_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
//       (ev.job_title || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
//       (ev.candidate_email || "").toLowerCase().includes(searchTerm.toLowerCase()),
//   )

//   const paginatedResults = filteredResults.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

//   const calculatedTotalPages = Math.ceil(filteredResults.length / itemsPerPage)

//   const handleViewDetails = (evaluation) => {
//     setSelectedEvaluation(evaluation)
//     // FIX: Match only on candidate name and job title, as the interviewer keys are different.
//     // This is the most reliable way to link the two records based on the available data.
//     const matchingParamEval = allParameterEvaluations.find(
//       (paramEval) =>
//         paramEval.candidate_name === evaluation.candidate_name && paramEval.job_title === evaluation.job_title,
//     )
//     setSelectedParameterEvaluation(matchingParamEval || null)
//     setModalViewMode("general")
//   }

//   const handleCloseDetails = () => {
//     setSelectedEvaluation(null)
//   }

//   const handleModeChange = (mode) => {
//     setModalViewMode(mode)
//   }

//   const formatDate = (dateString) => {
//     if (!dateString) return "N/A"
//     return new Date(dateString).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
//   }

//   const mapRatingToText = (rating) => {
//     const ratings = { 5: "Excellent", 4: "Good", 3: "Average", 2: "Below Average", 1: "Poor" }
//     return ratings[rating] || "N/A"
//   }

//   const groupEvaluationsByArea = (evaluationDetails) => {
//     if (!evaluationDetails || !Array.isArray(evaluationDetails)) return {}
//     return evaluationDetails.reduce((groups, detail) => {
//       const area = detail.area || "General Questions"
//       if (!groups[area]) groups[area] = []
//       groups[area].push(detail)
//       return groups
//     }, {})
//   }

//   const groupParameterEvaluationsByArea = (responses) => {
//     if (!responses || !Array.isArray(responses)) return {}

//     // Define the order of sections as per the form
//     const orderedSections = {
//       CORE_COMPETENCIES: "Core Competencies",
//       TECHNICAL_SKILLS: "Additional Behavioral & Functional Parameters",
//       BEHAVIORAL_TRAITS: "Role-Specific & Cultural Fit",
//     }

//     const groups = {}

//     // Initialize groups in the correct order
//     Object.values(orderedSections).forEach((sectionName) => {
//       groups[sectionName] = []
//     })

//     responses.forEach((res) => {
//       const area = res.parameter.area
//       const sectionName = orderedSections[area] || "Other Parameters"

//       if (!groups[sectionName]) {
//         groups[sectionName] = []
//       }
//       groups[sectionName].push(res)
//     })

//     // Remove empty groups
//     Object.keys(groups).forEach((key) => {
//       if (groups[key].length === 0) {
//         delete groups[key]
//       }
//     })

//     return groups
//   }

//   return (
//     <>
//       <CustomStyles />
//       <DashNavbar />
//       <div className="wrapper d-flex interviewer-feedback-page">
//         <Sidebar />
//         <div className="main-content flex-grow-1 p-3 p-md-4">
//           <Card className="shadow-sm mb-4">
//             <Card.Header className="bg-light d-flex justify-content-between align-items-center py-3">
//               <h3 className="mb-0 gradient-text h5">Interview Feedback Evaluations</h3>
//               <div className="w-auto" style={{ maxWidth: "300px" }}>
//                 <InputGroup>
//                   <InputGroup.Text>
//                     <i className="bi bi-search"></i>
//                   </InputGroup.Text>
//                   <Form.Control
//                     type="search"
//                     placeholder="Search by name, job, or email..."
//                     value={searchTerm}
//                     onChange={(e) => setSearchTerm(e.target.value)}
//                   />
//                 </InputGroup>
//               </div>
//             </Card.Header>
//             <Card.Body>
//               <div className="table-responsive">
//                 <Table hover striped className="align-middle">
//                   <thead className="table-dark">
//                     <tr>
//                       <th>#</th>
//                       <th>Candidate Name</th>
//                       <th>Job Title</th>
//                       <th>Email</th>
//                       <th>Evaluation Date</th>
//                       <th className="text-center">Actions</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {isLoading ? (
//                       <tr>
//                         <td colSpan="6" className="text-center py-5">
//                           <Spinner />
//                           <p className="mt-2 mb-0">Loading...</p>
//                         </td>
//                       </tr>
//                     ) : paginatedResults.length > 0 ? (
//                       paginatedResults.map((evaluation, index) => (
//                         <tr key={evaluation.id}>
//                           <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
//                           <td>{evaluation.candidate_name || "N/A"}</td>
//                           <td>{evaluation.job_title || "N/A"}</td>
//                           <td>{evaluation.candidate_email || "N/A"}</td>
//                           <td>{formatDate(evaluation.evaluation_date)}</td>
//                           <td className="text-center">
//                             <Button variant="outline-primary" size="sm" onClick={() => handleViewDetails(evaluation)}>
//                               <i className="bi bi-eye-fill me-1"></i> View
//                             </Button>
//                           </td>
//                         </tr>
//                       ))
//                     ) : (
//                       <tr>
//                         <td colSpan="6" className="text-center py-5">
//                           <i className="bi bi-journal-x fs-1 text-muted"></i>
//                           <p className="mt-2 mb-0">No feedback found.</p>
//                         </td>
//                       </tr>
//                     )}
//                   </tbody>
//                 </Table>
//               </div>
//             </Card.Body>
//             {calculatedTotalPages > 1 && (
//               <Card.Footer className="bg-light py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
//                 <div>
//                   <Form.Select
//                     size="sm"
//                     style={{ width: "auto", display: "inline-block" }}
//                     value={itemsPerPage}
//                     onChange={handleItemsPerPageChange}
//                   >
//                     <option value="10">10</option>
//                     <option value="25">25</option>
//                     <option value="50">50</option>
//                   </Form.Select>
//                   <span className="ms-2 small text-muted">entries per page</span>
//                 </div>
//                 <Pagination className="mb-0">
//                   <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
//                   {/* Simplified Pagination Items for brevity */}
//                   <Pagination.Item active>{currentPage}</Pagination.Item>
//                   <Pagination.Next
//                     onClick={() => handlePageChange(currentPage + 1)}
//                     disabled={currentPage === calculatedTotalPages}
//                   />
//                 </Pagination>
//               </Card.Footer>
//             )}
//           </Card>
//         </div>
//       </div>

//       <Modal show={selectedEvaluation !== null} onHide={handleCloseDetails} size="xl" centered scrollable>
//         <Modal.Header closeButton className="bg-light">
//           <Modal.Title className="h5 gradient-text">Complete Evaluation Details</Modal.Title>
//         </Modal.Header>
//         <Modal.Body className="p-4">
//           {selectedEvaluation && (
//             <>
//               <div className="mb-4 pb-3 border-bottom">
//                 <h5 className="mb-1">{selectedEvaluation.candidate_name}</h5>
//                 <p className="mb-0 text-muted small">
//                   Job: {selectedEvaluation.job_title} | Interviewer: {selectedEvaluation.interviewer_email}
//                 </p>
//               </div>

//               <ButtonGroup className="w-100 mb-4">
//                 <Button
//                   variant={modalViewMode === "general" ? "primary" : "outline-primary"}
//                   onClick={() => handleModeChange("general")}
//                 >
//                   General Questions
//                 </Button>
//                 <Button
//                   variant={modalViewMode === "parameters" ? "primary" : "outline-primary"}
//                   onClick={() => handleModeChange("parameters")}
//                 >
//                   Parameter-Based Evaluation
//                 </Button>
//               </ButtonGroup>

//               {modalViewMode === "general" ? (
//                 selectedEvaluation.evaluation_details?.length > 0 ? (
//                   Object.entries(groupEvaluationsByArea(selectedEvaluation.evaluation_details)).map(
//                     ([area, details], areaIndex) => (
//                       <div key={areaIndex} className="mb-4">
//                         <h6 className="text-secondary mb-3">{area}</h6>
//                         <Table striped bordered hover responsive>
//                           <thead className="table-light">
//                             <tr>
//                               <th style={{ width: "60%" }}>Question</th>
//                               <th style={{ width: "20%" }} className="text-center">
//                                 Answer
//                               </th>
//                               <th style={{ width: "20%" }}>Comments</th>
//                             </tr>
//                           </thead>
//                           <tbody>
//                             {details.map((detail, index) => (
//                               <tr key={detail.question_id || index}>
//                                 <td className="align-middle">
//                                   <strong>{detail.question_text}</strong>
//                                 </td>
//                                 <td className="text-center align-middle">
//                                   <span className="badge bg-dark">{detail.answer_value || "N/A"}</span>
//                                 </td>
//                                 <td className="align-middle">
//                                   {detail.comments || <span className="text-muted fst-italic">No comments.</span>}
//                                 </td>
//                               </tr>
//                             ))}
//                           </tbody>
//                         </Table>
//                       </div>
//                     ),
//                   )
//                 ) : (
//                   <div className="alert alert-secondary small mt-4">
//                     No general questions were submitted for this interview.
//                   </div>
//                 )
//               ) : selectedParameterEvaluation ? (
//                 <div className="modal-pdf-form">
//                   <div className="form-title">Post-Interview Employee Evaluation Form</div>

//                   <div className="candidate-info-section">
//                     <div className="form-group-inline">
//                       <label>Candidate Name:</label>
//                       <span>{selectedEvaluation.candidate_name || "N/A"}</span>
//                     </div>
//                     <div className="form-group-inline">
//                       <label>Position Applied For:</label>
//                       <span>{selectedEvaluation.job_title || "N/A"}</span>
//                     </div>
//                     <div className="form-group-inline">
//                       <label>Interview Date:</label>
//                       <span>{formatDate(selectedEvaluation.evaluation_date)}</span>
//                     </div>
//                     <div className="form-group-inline">
//                       <label>Interviewer(s):</label>
//                       <span>{selectedEvaluation.interviewer_email || "N/A"}</span>
//                     </div>
//                   </div>

//                   {Object.entries(groupParameterEvaluationsByArea(selectedParameterEvaluation.responses)).map(
//                     ([area, responses], areaIndex) => (
//                       <div key={areaIndex} className="mb-4">
//                         <h4 className="section-heading">
//                           {areaIndex + 1}. {area}
//                         </h4>
//                         <p className="mb-3 text-muted">
//                           Rate 1 – 5 (1 = Poor, 5 = Excellent) and add comments/evidence:
//                         </p>

//                         <Table striped bordered hover responsive className="mb-3">
//                           <thead className="table-light">
//                             <tr>
//                               <th style={{ width: "25%" }}>
//                                 {area === "Role-Specific & Cultural Fit"
//                                   ? "Attribute"
//                                   : area === "Core Competencies"
//                                     ? "Competency"
//                                     : "Parameter"}
//                               </th>
//                               {area !== "Role-Specific & Cultural Fit" && <th style={{ width: "35%" }}>Description</th>}
//                               <th style={{ width: "15%" }} className="text-center">
//                                 Rating
//                               </th>
//                               <th style={{ width: area === "Role-Specific & Cultural Fit" ? "60%" : "25%" }}>
//                                 {area === "Role-Specific & Cultural Fit" ? "Rating / Comments" : "Comments / Evidence"}
//                               </th>
//                             </tr>
//                           </thead>
//                           <tbody>
//                             {responses.map((res, resIndex) => (
//                               <tr key={res.id || resIndex}>
//                                 <td className="align-middle">
//                                   <strong>{res.parameter.parameter_text}</strong>
//                                 </td>
//                                 {area !== "Role-Specific & Cultural Fit" && (
//                                   <td className="align-middle">{res.parameter.description || "N/A"}</td>
//                                 )}
//                                 <td className="text-center align-middle">
//                                   <span className="badge bg-primary px-3 py-2">{mapRatingToText(res.rating)}</span>
//                                 </td>
//                                 <td className="align-middle">
//                                   {res.comments || <span className="text-muted fst-italic">No comments.</span>}
//                                 </td>
//                               </tr>
//                             ))}
//                           </tbody>
//                         </Table>
//                       </div>
//                     ),
//                   )}

//                   <div className="overall-recommendation-box mt-4">
//                     <h5 className="section-heading">4. Overall Recommendation</h5>
//                     <div className="recommendation-item">
//                       <strong>Overall Suitability:</strong>
//                       <span className="badge bg-success fs-6 ms-2">
//                         {selectedParameterEvaluation.overall_recommendation_display || "N/A"}
//                       </span>
//                     </div>
//                     <div className="recommendation-item">
//                       <strong>Top Strengths:</strong>
//                       <div className="recommendation-text">{selectedParameterEvaluation.strengths || "N/A"}</div>
//                     </div>
//                     <div className="recommendation-item">
//                       <strong>Key Development Areas:</strong>
//                       <div className="recommendation-text">
//                         {selectedParameterEvaluation.development_areas || "N/A"}
//                       </div>
//                     </div>
//                     <div className="recommendation-item">
//                       <strong>Final Comments:</strong>
//                       <div className="recommendation-text">{selectedParameterEvaluation.final_comments || "N/A"}</div>
//                     </div>
//                   </div>

//                   <div className="signature-line">
//                     <span>Interviewer's Signature: ________________________</span>
//                     <span>Date: ________________________</span>
//                   </div>
//                 </div>
//               ) : (
//                 <div className="alert alert-secondary small mt-4">
//                   No parameter-based evaluation was submitted for this interview.
//                 </div>
//               )}
//             </>
//           )}
//         </Modal.Body>
//         <Modal.Footer>
//           <Button variant="outline-secondary" onClick={handleCloseDetails}>
//             Close
//           </Button>
//         </Modal.Footer>
//       </Modal>
//     </>
//   )
// }

// export default InterviewerFeedback


// "use client"
// import { useState, useEffect, useCallback } from "react"
// import axios from "axios"
// import DashNavbar from "./DashNavbar"
// import Sidebar from "./Sidebar"
// import { Pagination, Modal, Card, Form, InputGroup, Button, Table, Spinner, Row, Col, ButtonGroup } from "react-bootstrap"
// import Swal from "sweetalert2"

// // Custom Styles Component (Embedded CSS)
// const CustomStyles = () => (
//   <style>{`
//     .interviewer-feedback-page .main-content { background-color: #f8f9fa; }
//     .gradient-text { background: linear-gradient(45deg, #0d6efd, #0d6efd); -webkit-background-clip: text; -webkit-text-fill-color: transparent; font-weight: 600; }
//     .interviewer-feedback-page.wrapper { display: flex; height: calc(100vh - 60px); }
//     .interviewer-feedback-page .main-content { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; }
//     .interviewer-feedback-page .card { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; }
//     .interviewer-feedback-page .card-body { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; padding: 0; }
//     .interviewer-feedback-page .table-responsive { flex-grow: 1; overflow-y: auto; padding: 1.5rem; padding-top: 0; }
//     .interviewer-feedback-page .card-header, .interviewer-feedback-page .card-footer { flex-shrink: 0; }
//     .summary-box { background-color: #f8f9fa; border: 1px solid #dee2e6; padding: 1rem; border-radius: .375rem; }
    
//     /* New styles for the PDF-like modal */
//     .modal-pdf-form .form-group-inline {
//       display: flex;
//       align-items: baseline;
//       margin-bottom: 0.5rem;
//     }
//     .modal-pdf-form .form-group-inline label {
//       font-weight: 600;
//       margin-right: 0.5rem;
//       min-width: 120px; /* Adjust as needed for alignment */
//     }
//     .modal-pdf-form .form-group-inline span {
//       flex-grow: 1;
//       border-bottom: 1px solid #ccc;
//       padding-bottom: 2px;
//     }
//     .modal-pdf-form .section-heading {
//       font-size: 1.25rem;
//       font-weight: 600;
//       margin-top: 1.5rem;
//       margin-bottom: 1rem;
//       border-bottom: 2px solid #0d6efd;
//       padding-bottom: 0.5rem;
//     }
//     .modal-pdf-form .table th, .modal-pdf-form .table td {
//       vertical-align: middle;
//       padding: 0.75rem;
//     }
//     .modal-pdf-form .table thead th {
//       background-color: #f0f0f0;
//     }
//     .modal-pdf-form .overall-recommendation-box {
//       border: 1px solid #dee2e6;
//       padding: 1.5rem;
//       border-radius: .375rem;
//       background-color: #f8f9fa;
//     }
//     .modal-pdf-form .overall-recommendation-box h5 {
//       margin-bottom: 1rem;
//       color: #0d6efd;
//     }
//     .modal-pdf-form .overall-recommendation-box .recommendation-item {
//       margin-bottom: 0.75rem;
//     }
//     .modal-pdf-form .overall-recommendation-box .recommendation-item strong {
//       display: block;
//       margin-bottom: 0.25rem;
//     }
//     .modal-pdf-form .signature-line {
//       margin-top: 3rem;
//       border-top: 1px solid #ccc;
//       padding-top: 0.5rem;
//       display: flex;
//       justify-content: space-between;
//       font-size: 0.9rem;
//       color: #6c757d;
//     }
//     .modal-pdf-form .signature-line span {
//       flex-basis: 48%;
//     }
//   `}</style>
// );

// function InterviewerFeedback() {
//   const [evaluationResults, setEvaluationResults] = useState([])
//   const [allParameterEvaluations, setAllParameterEvaluations] = useState([]);
//   const [searchTerm, setSearchTerm] = useState("")
//   const [currentPage, setCurrentPage] = useState(1)
//   const [selectedEvaluation, setSelectedEvaluation] = useState(null)
//   const [selectedParameterEvaluation, setSelectedParameterEvaluation] = useState(null);
//   const [isLoading, setIsLoading] = useState(false)
//   const [itemsPerPage, setItemsPerPage] = useState(10)
//   const [modalViewMode, setModalViewMode] = useState('general');

//   const fetchAllData = async () => {
//     setIsLoading(true);
//     try {
//       // Fetch a large number of general evaluations to allow for effective client-side search/pagination
//       const [generalEvalsResponse, parameterEvalsResponse] = await Promise.all([
//         axios.get(`https://raasbackend.vetrinahealthcare.com/interview-evaluations/?page_size=1000`),
//         axios.get(`https://raasbackend.vetrinahealthcare.com/evaluations/`)
//       ]);
//       setEvaluationResults(generalEvalsResponse.data.results || []);
//       setAllParameterEvaluations(parameterEvalsResponse.data || []);
//     } catch (error) {
//       console.error("Error fetching evaluation data:", error);
//       Swal.fire("Error", "Could not fetch all evaluation data.", "error");
//       setEvaluationResults([]);
//       setAllParameterEvaluations([]);
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchAllData();
//   }, []);

//   const handlePageChange = (page) => {
//     if (page >= 1) setCurrentPage(page);
//   }

//   const handleItemsPerPageChange = (e) => {
//     setItemsPerPage(Number(e.target.value))
//     setCurrentPage(1)
//   }

//   // Client-side filtering logic
//   const filteredResults = evaluationResults.filter(ev =>
//     (ev.candidate_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
//     (ev.job_title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
//     (ev.candidate_email || '').toLowerCase().includes(searchTerm.toLowerCase())
//   );

//   const paginatedResults = filteredResults.slice(
//     (currentPage - 1) * itemsPerPage,
//     currentPage * itemsPerPage
//   );
//   const calculatedTotalPages = Math.ceil(filteredResults.length / itemsPerPage);

//   const handleViewDetails = (evaluation) => {
//     setSelectedEvaluation(evaluation);

//     // FIX: Match only on candidate name and job title, as the interviewer keys are different.
//     // This is the most reliable way to link the two records based on the available data.
//     const matchingParamEval = allParameterEvaluations.find(
//       paramEval => paramEval.candidate_name === evaluation.candidate_name &&
//         paramEval.job_title === evaluation.job_title
//     );
//     setSelectedParameterEvaluation(matchingParamEval || null);

//     setModalViewMode('general');
//   };

//   const handleCloseDetails = () => {
//     setSelectedEvaluation(null)
//   }

//   const handleModeChange = (mode) => {
//     setModalViewMode(mode);
//   }

//   const formatDate = (dateString) => {
//     if (!dateString) return "N/A"
//     return new Date(dateString).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
//   }

//   const mapRatingToText = (rating) => {
//     const ratings = { 5: 'Exceptional', 4: 'Above Average', 3: 'Average', 2: 'Satisfactory', 1: 'Unsatisfactory' };
//     return ratings[rating] || 'N/A';
//   }

//   const groupEvaluationsByArea = (evaluationDetails) => {
//     if (!evaluationDetails || !Array.isArray(evaluationDetails)) return {}
//     return evaluationDetails.reduce((groups, detail) => {
//       const area = detail.area || "General Questions"
//       if (!groups[area]) groups[area] = []
//       groups[area].push(detail)
//       return groups
//     }, {})
//   }

//   const groupParameterEvaluationsByArea = (responses) => {
//     if (!responses || !Array.isArray(responses)) return {};
//     const groups = {};
//     responses.forEach(res => {
//       const area = res.parameter.area_display || "Other Parameters";
//       if (!groups[area]) {
//         groups[area] = [];
//       }
//       groups[area].push(res);
//     });
//     return groups;
//   };

//   return (
//     <>
//       <CustomStyles />
//       <DashNavbar />
//       <div className="wrapper d-flex interviewer-feedback-page">
//         <Sidebar />
//         <div className="main-content flex-grow-1 p-3 p-md-4">
//           <Card className="shadow-sm mb-4">
//             <Card.Header className="bg-light d-flex justify-content-between align-items-center py-3">
//               <h3 className="mb-0 gradient-text h5">Interview Feedback Evaluations</h3>
//               <div className="w-auto" style={{ maxWidth: "300px" }}>
//                 <InputGroup>
//                   <InputGroup.Text><i className="bi bi-search"></i></InputGroup.Text>
//                   <Form.Control type="search" placeholder="Search by name, job, or email..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
//                 </InputGroup>
//               </div>
//             </Card.Header>
//             <Card.Body>
//               <div className="table-responsive">
//                 <Table hover striped className="align-middle">
//                   <thead className="table-dark">
//                     <tr>
//                       <th>#</th><th>Candidate Name</th><th>Job Title</th><th>Email</th><th>Evaluation Date</th><th className="text-center">Actions</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {isLoading ? (
//                       <tr><td colSpan="6" className="text-center py-5"><Spinner /><p className="mt-2 mb-0">Loading...</p></td></tr>
//                     ) : paginatedResults.length > 0 ? (
//                       paginatedResults.map((evaluation, index) => (
//                         <tr key={evaluation.id}>
//                           <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
//                           <td>{evaluation.candidate_name || "N/A"}</td>
//                           <td>{evaluation.job_title || "N/A"}</td>
//                           <td>{evaluation.candidate_email || "N/A"}</td>
//                           <td>{formatDate(evaluation.evaluation_date)}</td>
//                           <td className="text-center">
//                             <Button variant="outline-primary" size="sm" onClick={() => handleViewDetails(evaluation)}><i className="bi bi-eye-fill me-1"></i> View</Button>
//                           </td>
//                         </tr>
//                       ))
//                     ) : (
//                       <tr><td colSpan="6" className="text-center py-5"><i className="bi bi-journal-x fs-1 text-muted"></i><p className="mt-2 mb-0">No feedback found.</p></td></tr>
//                     )}
//                   </tbody>
//                 </Table>
//               </div>
//             </Card.Body>
//             {calculatedTotalPages > 1 && (
//               <Card.Footer className="bg-light py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
//                 <div>
//                   <Form.Select size="sm" style={{ width: 'auto', display: 'inline-block' }} value={itemsPerPage} onChange={handleItemsPerPageChange}>
//                     <option value="10">10</option><option value="25">25</option><option value="50">50</option>
//                   </Form.Select>
//                   <span className="ms-2 small text-muted">entries per page</span>
//                 </div>
//                 <Pagination className="mb-0">
//                   <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
//                   {/* Simplified Pagination Items for brevity */}
//                   <Pagination.Item active>{currentPage}</Pagination.Item>
//                   <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === calculatedTotalPages} />
//                 </Pagination>
//               </Card.Footer>
//             )}
//           </Card>
//         </div>
//       </div>
//       <Modal show={selectedEvaluation !== null} onHide={handleCloseDetails} size="xl" centered scrollable>
//         <Modal.Header closeButton className="bg-light">
//           <Modal.Title className="h5 gradient-text">Complete Evaluation Details</Modal.Title>
//         </Modal.Header>
//         <Modal.Body className="p-4">
//           {selectedEvaluation && (
//             <>
//               <div className="mb-4 pb-3 border-bottom">
//                 <h5 className="mb-1">{selectedEvaluation.candidate_name}</h5>
//                 <p className="mb-0 text-muted small">Job: {selectedEvaluation.job_title} | Interviewer: {selectedEvaluation.interviewer_email}</p>
//               </div>

//               <ButtonGroup className="w-100 mb-4">
//                 <Button variant={modalViewMode === 'general' ? 'primary' : 'outline-primary'} onClick={() => handleModeChange('general')}>
//                   General Questions
//                 </Button>
//                 <Button variant={modalViewMode === 'parameters' ? 'primary' : 'outline-primary'} onClick={() => handleModeChange('parameters')}>
//                   Parameter-Based Evaluation
//                 </Button>
//               </ButtonGroup>

//               {modalViewMode === 'general' ? (
//                 selectedEvaluation.evaluation_details?.length > 0 ? (
//                   Object.entries(groupEvaluationsByArea(selectedEvaluation.evaluation_details)).map(([area, details], areaIndex) => (
//                     <div key={areaIndex} className="mb-4">
//                       <h6 className="text-secondary mb-3">{area}</h6>
//                       <Table striped bordered hover responsive>
//                         <thead className="table-light">
//                           <tr>
//                             <th style={{ width: "60%" }}>Question</th>
//                             <th style={{ width: "20%" }} className="text-center">Answer</th>
//                             <th style={{ width: "20%" }}>Comments</th>
//                           </tr>
//                         </thead>
//                         <tbody>
//                           {details.map((detail, index) => (
//                             <tr key={detail.question_id || index}>
//                               <td className="align-middle"><strong>{detail.question_text}</strong></td>
//                               <td className="text-center align-middle"><span className="badge bg-dark">{detail.answer_value || "N/A"}</span></td>
//                               <td className="align-middle">{detail.comments || <span className="text-muted fst-italic">No comments.</span>}</td>
//                             </tr>
//                           ))}
//                         </tbody>
//                       </Table>
//                     </div>
//                   ))
//                 ) : (
//                   <div className="alert alert-secondary small mt-4">No general questions were submitted for this interview.</div>
//                 )
//               ) : (
//                 selectedParameterEvaluation ? (
//                   <div className="modal-pdf-form">
//                     <Row className="mb-4">
//                       <Col md={6}>
//                         <div className="form-group-inline">
//                           <label>Candidate Name:</label>
//                           <span>{selectedEvaluation.candidate_name || 'N/A'}</span>
//                         </div>
//                         <div className="form-group-inline">
//                           <label>Position Applied For:</label>
//                           <span>{selectedEvaluation.job_title || 'N/A'}</span>
//                         </div>
//                       </Col>
//                       <Col md={6}>
//                         <div className="form-group-inline">
//                           <label>Interview Date:</label>
//                           <span>{formatDate(selectedEvaluation.evaluation_date)}</span>
//                         </div>
//                         <div className="form-group-inline">
//                           <label>Interviewer(s):</label>
//                           <span>{selectedEvaluation.interviewer_email || 'N/A'}</span>
//                         </div>
//                       </Col>
//                     </Row>

//                     {Object.entries(groupParameterEvaluationsByArea(selectedParameterEvaluation.responses)).map(([area, responses], areaIndex) => (
//                       <div key={areaIndex} className="mb-4">
//                         <h4 className="section-heading">{area}</h4>
//                         <Table striped bordered hover responsive className="mb-3">
//                           <thead className="table-light">
//                             <tr>
//                               <th style={{ width: "30%" }}>Parameter</th>
//                               <th style={{ width: "30%" }}>Description</th>
//                               <th style={{ width: "15%" }} className="text-center">Rating</th>
//                               <th style={{ width: "25%" }}>Comments / Evidence</th>
//                             </tr>
//                           </thead>
//                           <tbody>
//                             {responses.map((res, resIndex) => (
//                               <tr key={res.id || resIndex}>
//                                 <td className="align-middle"><strong>{res.parameter.parameter_text}</strong></td>
//                                 <td className="align-middle">{res.parameter.description || 'N/A'}</td>
//                                 <td className="text-center align-middle"><span className="badge bg-primary px-3 py-2">{mapRatingToText(res.rating)}</span></td>
//                                 <td className="align-middle">{res.comments || <span className="text-muted fst-italic">No comments.</span>}</td>
//                               </tr>
//                             ))}
//                           </tbody>
//                         </Table>
//                       </div>
//                     ))}

//                     <div className="overall-recommendation-box mt-4">
//                       <h5 className="section-heading">Overall Recommendation</h5>
//                       <div className="recommendation-item">
//                         <strong>Overall Suitability:</strong>
//                         <span className="badge bg-success fs-6">{selectedParameterEvaluation.overall_recommendation_display || 'N/A'}</span>
//                       </div>
//                       <div className="recommendation-item">
//                         <strong>Top Strengths:</strong>
//                         <p className="mb-0">{selectedParameterEvaluation.strengths || 'N/A'}</p>
//                       </div>
//                       <div className="recommendation-item">
//                         <strong>Key Development Areas:</strong>
//                         <p className="mb-0">{selectedParameterEvaluation.development_areas || 'N/A'}</p>
//                       </div>
//                       <div className="recommendation-item">
//                         <strong>Final Comments:</strong>
//                         <p className="mb-0">{selectedParameterEvaluation.final_comments || 'N/A'}</p>
//                       </div>
//                     </div>

//                     <div className="signature-line">
//                       <span>Interviewer’s Signature: ________________________</span>
//                       <span>Date: ________________________</span>
//                     </div>
//                   </div>
//                 ) : (
//                   <div className="alert alert-secondary small mt-4">No parameter-based evaluation was submitted for this interview.</div>
//                 )
//               )}
//             </>
//           )}
//         </Modal.Body>
//         <Modal.Footer>
//           <Button variant="outline-secondary" onClick={handleCloseDetails}>Close</Button>
//         </Modal.Footer>
//       </Modal>
//     </>
//   )
// }

// export default InterviewerFeedback


// "use client"

// import { useState, useEffect, useCallback } from "react"
// import axios from "axios"
// import DashNavbar from "./DashNavbar"
// import Sidebar from "./Sidebar"
// import { Pagination, Modal, Card, Form, InputGroup, Button, Table, Spinner, Row, Col, ButtonGroup } from "react-bootstrap"
// import Swal from "sweetalert2"

// // Custom Styles Component (Embedded CSS)
// const CustomStyles = () => (
//     <style>{`
//       .interviewer-feedback-page .main-content { background-color: #f8f9fa; }
//       .gradient-text { background: linear-gradient(45deg, #0d6efd, #0d6efd); -webkit-background-clip: text; -webkit-text-fill-color: transparent; font-weight: 600; }
//       .interviewer-feedback-page.wrapper { display: flex; height: calc(100vh - 60px); }
//       .interviewer-feedback-page .main-content { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; }
//       .interviewer-feedback-page .card { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; }
//       .interviewer-feedback-page .card-body { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; padding: 0; }
//       .interviewer-feedback-page .table-responsive { flex-grow: 1; overflow-y: auto; padding: 1.5rem; padding-top: 0; }
//       .interviewer-feedback-page .card-header, .interviewer-feedback-page .card-footer { flex-shrink: 0; }
//       .summary-box { background-color: #f8f9fa; border: 1px solid #dee2e6; padding: 1rem; border-radius: .375rem; }
//     `}</style>
//   );

// function InterviewerFeedback() {
//   const [evaluationResults, setEvaluationResults] = useState([])
//   const [allParameterEvaluations, setAllParameterEvaluations] = useState([]);
//   const [searchTerm, setSearchTerm] = useState("")
//   const [currentPage, setCurrentPage] = useState(1)
//   const [selectedEvaluation, setSelectedEvaluation] = useState(null)
//   const [selectedParameterEvaluation, setSelectedParameterEvaluation] = useState(null);
//   const [isLoading, setIsLoading] = useState(false)
//   const [itemsPerPage, setItemsPerPage] = useState(10)
//   const [modalViewMode, setModalViewMode] = useState('general');

//   const fetchAllData = async () => {
//     setIsLoading(true);
//     try {
//         // Fetch a large number of general evaluations to allow for effective client-side search/pagination
//         const [generalEvalsResponse, parameterEvalsResponse] = await Promise.all([
//             axios.get(`https://raasbackend.vetrinahealthcare.com/interview-evaluations/?page_size=1000`), 
//             axios.get(`https://raasbackend.vetrinahealthcare.com/evaluations/`)
//         ]);

//         setEvaluationResults(generalEvalsResponse.data.results || []);
//         setAllParameterEvaluations(parameterEvalsResponse.data || []);

//     } catch (error) {
//         console.error("Error fetching evaluation data:", error);
//         Swal.fire("Error", "Could not fetch all evaluation data.", "error");
//         setEvaluationResults([]);
//         setAllParameterEvaluations([]);
//     } finally {
//         setIsLoading(false);
//     }
//   };
  
//   useEffect(() => {
//     fetchAllData();
//   }, []);

//   const handlePageChange = (page) => {
//     if (page >= 1) setCurrentPage(page);
//   }

//   const handleItemsPerPageChange = (e) => {
//     setItemsPerPage(Number(e.target.value))
//     setCurrentPage(1)
//   }
  
//   // Client-side filtering logic
//   const filteredResults = evaluationResults.filter(ev => 
//     (ev.candidate_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
//     (ev.job_title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
//     (ev.candidate_email || '').toLowerCase().includes(searchTerm.toLowerCase())
//   );
  
//   const paginatedResults = filteredResults.slice(
//       (currentPage - 1) * itemsPerPage,
//       currentPage * itemsPerPage
//   );

//   const calculatedTotalPages = Math.ceil(filteredResults.length / itemsPerPage);

//   // =========================================================================================
//   // === MODIFIED SECTION: The matching logic here is now corrected ===
//   // =========================================================================================
//   const handleViewDetails = (evaluation) => {
//     setSelectedEvaluation(evaluation);
    
//     // FIX: Match only on candidate name and job title, as the interviewer keys are different.
//     // This is the most reliable way to link the two records based on the available data.
//     const matchingParamEval = allParameterEvaluations.find(
//       paramEval => paramEval.candidate_name === evaluation.candidate_name && 
//                    paramEval.job_title === evaluation.job_title
//     );
//     setSelectedParameterEvaluation(matchingParamEval || null);
    
//     setModalViewMode('general'); 
//   };
//   // =========================================================================================
//   // === END OF MODIFIED SECTION ===
//   // =========================================================================================


//   const handleCloseDetails = () => {
//     setSelectedEvaluation(null)
//   }
  
//   const handleModeChange = (mode) => {
//     setModalViewMode(mode);
//   }

//   const formatDate = (dateString) => {
//     if (!dateString) return "N/A"
//     return new Date(dateString).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
//   }
  
//   const mapRatingToText = (rating) => {
//     const ratings = { 5: 'Exceptional', 4: 'Above Average', 3: 'Average', 2: 'Satisfactory', 1: 'Unsatisfactory' };
//     return ratings[rating] || 'N/A';
//   }

//   const groupEvaluationsByArea = (evaluationDetails) => {
//     if (!evaluationDetails || !Array.isArray(evaluationDetails)) return {}
//     return evaluationDetails.reduce((groups, detail) => {
//       const area = detail.area || "General Questions"
//       if (!groups[area]) groups[area] = []
//       groups[area].push(detail)
//       return groups
//     }, {})
//   }

//   return (
//     <>
//       <CustomStyles />
//       <DashNavbar />
//       <div className="wrapper d-flex interviewer-feedback-page">
//         <Sidebar />
//         <div className="main-content flex-grow-1 p-3 p-md-4">
//           <Card className="shadow-sm mb-4">
//             <Card.Header className="bg-light d-flex justify-content-between align-items-center py-3">
//               <h3 className="mb-0 gradient-text h5">Interview Feedback Evaluations</h3>
//               <div className="w-auto" style={{ maxWidth: "300px" }}>
//                 <InputGroup>
//                   <InputGroup.Text><i className="bi bi-search"></i></InputGroup.Text>
//                   <Form.Control type="search" placeholder="Search by name, job, or email..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
//                 </InputGroup>
//               </div>
//             </Card.Header>

//             <Card.Body>
//               <div className="table-responsive">
//                 <Table hover striped className="align-middle">
//                   <thead className="table-dark">
//                     <tr>
//                       <th>#</th><th>Candidate Name</th><th>Job Title</th><th>Email</th><th>Evaluation Date</th><th className="text-center">Actions</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {isLoading ? (
//                       <tr><td colSpan="6" className="text-center py-5"><Spinner /><p className="mt-2 mb-0">Loading...</p></td></tr>
//                     ) : paginatedResults.length > 0 ? (
//                       paginatedResults.map((evaluation, index) => (
//                         <tr key={evaluation.id}>
//                           <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
//                           <td>{evaluation.candidate_name || "N/A"}</td>
//                           <td>{evaluation.job_title || "N/A"}</td>
//                           <td>{evaluation.candidate_email || "N/A"}</td>
//                           <td>{formatDate(evaluation.evaluation_date)}</td>
//                           <td className="text-center">
//                             <Button variant="outline-primary" size="sm" onClick={() => handleViewDetails(evaluation)}><i className="bi bi-eye-fill me-1"></i> View</Button>
//                           </td>
//                         </tr>
//                       ))
//                     ) : (
//                       <tr><td colSpan="6" className="text-center py-5"><i className="bi bi-journal-x fs-1 text-muted"></i><p className="mt-2 mb-0">No feedback found.</p></td></tr>
//                     )}
//                   </tbody>
//                 </Table>
//               </div>
//             </Card.Body>
//             {calculatedTotalPages > 1 && (
//               <Card.Footer className="bg-light py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
//                 <div>
//                   <Form.Select size="sm" style={{width: 'auto', display: 'inline-block'}} value={itemsPerPage} onChange={handleItemsPerPageChange}>
//                       <option value="10">10</option><option value="25">25</option><option value="50">50</option>
//                   </Form.Select>
//                   <span className="ms-2 small text-muted">entries per page</span>
//                 </div>
//                 <Pagination className="mb-0">
//                     <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
//                     {/* Simplified Pagination Items for brevity */}
//                     <Pagination.Item active>{currentPage}</Pagination.Item>
//                     <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === calculatedTotalPages} />
//                 </Pagination>
//               </Card.Footer>
//             )}
//           </Card>
//         </div>
//       </div>

//       <Modal show={selectedEvaluation !== null} onHide={handleCloseDetails} size="xl" centered scrollable>
//         <Modal.Header closeButton className="bg-light">
//           <Modal.Title className="h5 gradient-text">Complete Evaluation Details</Modal.Title>
//         </Modal.Header>
//         <Modal.Body className="p-4">
//           {selectedEvaluation && (
//             <>
//                 <div className="mb-4 pb-3 border-bottom">
//                     <h5 className="mb-1">{selectedEvaluation.candidate_name}</h5>
//                     <p className="mb-0 text-muted small">Job: {selectedEvaluation.job_title} | Interviewer: {selectedEvaluation.interviewer_email}</p>
//                 </div>
                
//                 <ButtonGroup className="w-100 mb-4">
//                     <Button variant={modalViewMode === 'general' ? 'primary' : 'outline-primary'} onClick={() => handleModeChange('general')}>
//                         General Questions
//                     </Button>
//                     <Button variant={modalViewMode === 'parameters' ? 'primary' : 'outline-primary'} onClick={() => handleModeChange('parameters')}>
//                         Parameter-Based Evaluation
//                     </Button>
//                 </ButtonGroup>
                
//                 {modalViewMode === 'general' ? (
//                     selectedEvaluation.evaluation_details?.length > 0 ? (
//                         Object.entries(groupEvaluationsByArea(selectedEvaluation.evaluation_details)).map(([area, details], areaIndex) => (
//                           <div key={areaIndex} className="mb-4">
//                             <h6 className="text-secondary mb-3">{area}</h6>
//                             <Table striped bordered hover responsive>
//                               <thead className="table-light">
//                                 <tr>
//                                   <th style={{ width: "60%" }}>Question</th>
//                                   <th style={{ width: "20%" }} className="text-center">Answer</th>
//                                   <th style={{ width: "20%" }}>Comments</th>
//                                 </tr>
//                               </thead>
//                               <tbody>
//                                 {details.map((detail, index) => (
//                                   <tr key={detail.question_id || index}>
//                                     <td className="align-middle"><strong>{detail.question_text}</strong></td>
//                                     <td className="text-center align-middle"><span className="badge bg-dark">{detail.answer_value || "N/A"}</span></td>
//                                     <td className="align-middle">{detail.comments || <span className="text-muted fst-italic">No comments.</span>}</td>
//                                   </tr>
//                                 ))}
//                               </tbody>
//                             </Table>
//                           </div>
//                         ))
//                     ) : (
//                         <div className="alert alert-secondary small mt-4">No general questions were submitted for this interview.</div>
//                     )
//                 ) : (
//                     selectedParameterEvaluation ? (
//                         <>
//                             <div className="summary-box mb-4">
//                                 <h5 className="mb-3">Evaluation Summary</h5>
//                                 <Row>
//                                     <Col md={12} className="mb-3">
//                                         <strong>Overall Recommendation:</strong> <span className="badge bg-success fs-6">{selectedParameterEvaluation.overall_recommendation_display}</span>
//                                     </Col>
//                                     <Col md={6}><strong>Strengths:</strong><p>{selectedParameterEvaluation.strengths || 'N/A'}</p></Col>
//                                     <Col md={6}><strong>Areas for Development:</strong><p>{selectedParameterEvaluation.development_areas || 'N/A'}</p></Col>
//                                     <Col md={12}><strong>Final Comments:</strong><p>{selectedParameterEvaluation.final_comments || 'N/A'}</p></Col>
//                                 </Row>
//                             </div>
//                             <Table striped bordered hover responsive>
//                                 <thead className="table-light">
//                                     <tr>
//                                         <th style={{ width: "40%" }}>Parameter</th>
//                                         <th style={{ width: "30%" }}>Area</th>
//                                         <th style={{ width: "10%" }} className="text-center">Rating</th>
//                                         <th style={{ width: "20%" }}>Comments</th>
//                                     </tr>
//                                 </thead>
//                                 <tbody>
//                                     {selectedParameterEvaluation.responses.map(res => (
//                                         <tr key={res.id}>
//                                             <td className="align-middle"><strong>{res.parameter.parameter_text}</strong><div className="text-muted small">{res.parameter.description}</div></td>
//                                             <td className="align-middle">{res.parameter.area_display}</td>
//                                             <td className="text-center align-middle"><span className="badge bg-primary px-3 py-2">{mapRatingToText(res.rating)}</span></td>
//                                             <td className="align-middle">{res.comments || <span className="text-muted fst-italic">No comments.</span>}</td>
//                                         </tr>
//                                     ))}
//                                 </tbody>
//                             </Table>
//                         </>
//                     ) : (
//                         <div className="alert alert-secondary small mt-4">No parameter-based evaluation was submitted for this interview.</div>
//                     )
//                 )}
//             </>
//           )}
//         </Modal.Body>
//         <Modal.Footer>
//           <Button variant="outline-secondary" onClick={handleCloseDetails}>Close</Button>
//         </Modal.Footer>
//       </Modal>
//     </>
//   )
// }

// export default InterviewerFeedback

// "use client"

// import { useState, useEffect, useCallback } from "react"
// import axios from "axios"
// import DashNavbar from "./DashNavbar"
// import Sidebar from "./Sidebar"
// import { Pagination, Modal, Card, Form, InputGroup, Button, Table, Spinner } from "react-bootstrap"

// // Custom Styles Component (Embedded CSS)
// const CustomStyles = () => (
//     <style>{`
//       .interviewer-feedback-page .main-content {
//         background-color: #f8f9fa;
//       }
  
//       .gradient-text {
//         background: linear-gradient(45deg, #0d6efd, #0d6efd);
//         -webkit-background-clip: text;
//         -webkit-text-fill-color: transparent;
//         font-weight: 600;
//       }

//       /*
//       =====================================================
//       --- Full Height Layout ---
//       =====================================================
//       */
//       .interviewer-feedback-page.wrapper {
//         display: flex;
//         height: calc(100vh - 60px); /* Adjust 60px to your DashNavbar's height */
//       }
  
//       .interviewer-feedback-page .main-content {
//         flex-grow: 1;
//         display: flex;
//         flex-direction: column;
//         overflow: hidden;
//       }
  
//       .interviewer-feedback-page .card {
//         flex-grow: 1; /* Allow the card to fill the container's height */
//         display: flex;
//         flex-direction: column; /* Stack children vertically */
//         overflow: hidden; /* Important */
//       }
      
//       .interviewer-feedback-page .card-body {
//         flex-grow: 1;
//         display: flex;
//         flex-direction: column;
//         overflow: hidden;
//         padding: 0; /* Remove padding to allow table-responsive to manage it */
//       }

//       .interviewer-feedback-page .table-responsive {
//         flex-grow: 1; /* This is the key: makes the table container expand */
//         overflow-y: auto; /* Adds a vertical scrollbar ONLY to the table */
//         padding: 1.5rem; /* Add padding here instead of card-body */
//         padding-top: 0;
//       }

//       .interviewer-feedback-page .card-header,
//       .interviewer-feedback-page .card-footer {
//         flex-shrink: 0; /* Prevent header and footer from shrinking */
//       }
//     `}</style>
//   );

// function InterviewerFeedback() {
//   const [evaluationResults, setEvaluationResults] = useState([])
//   const [searchTerm, setSearchTerm] = useState("")
//   const [currentPage, setCurrentPage] = useState(1)
//   const [selectedEvaluation, setSelectedEvaluation] = useState(null)
//   const [isLoading, setIsLoading] = useState(false)
//   const [totalPages, setTotalPages] = useState(0)
//   const [itemsPerPage, setItemsPerPage] = useState(10)

//   const fetchInterviewerDetails = useCallback(async (page, pageSize, search) => {
//     setIsLoading(true)
//     let API_URL = `https://raasbackend.vetrinahealthcare.com/interview-evaluations/?page=${page}&page_size=${pageSize}`
//     if (search) {
//       API_URL += `&search=${search}`
//     }

//     try {
//       const response = await axios.get(API_URL)
//       const responseData = response.data
//       setEvaluationResults(responseData.results || [])
//       setTotalPages(responseData.num_pages || responseData.total_pages || 0)
//     } catch (error) {
//       console.error("Error fetching data:", error)
//       setEvaluationResults([])
//       setTotalPages(0)
//     } finally {
//       setIsLoading(false)
//     }
//   }, [])

//   useEffect(() => {
//     const handler = setTimeout(() => {
//       fetchInterviewerDetails(currentPage, itemsPerPage, searchTerm)
//     }, 300) // 300ms debounce

//     return () => {
//       clearTimeout(handler)
//     }
//   }, [currentPage, itemsPerPage, searchTerm, fetchInterviewerDetails])

//   const handlePageChange = (page) => {
//     if (page < 1 || (totalPages > 0 && page > totalPages)) return
//     setCurrentPage(page)
//   }

//   const handleSearch = (e) => {
//     setSearchTerm(e.target.value)
//     setCurrentPage(1) // Reset to first page on new search
//   }

//   const handleItemsPerPageChange = (e) => {
//     setItemsPerPage(Number(e.target.value))
//     setCurrentPage(1) // Reset to first page
//   }

//   const handleViewDetails = (evaluation) => {
//     setSelectedEvaluation(evaluation)
//   }

//   const handleCloseDetails = () => {
//     setSelectedEvaluation(null)
//   }

//   const formatDate = (dateString) => {
//     if (!dateString) return "N/A"
//     return new Date(dateString).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
//   }

//   const groupEvaluationsByArea = (evaluationDetails) => {
//     if (!evaluationDetails || !Array.isArray(evaluationDetails)) return {}
//     return evaluationDetails.reduce((groups, detail) => {
//       const area = detail.area || "General Questions"
//       if (!groups[area]) {
//         groups[area] = []
//       }
//       groups[area].push(detail)
//       return groups
//     }, {})
//   }

//   return (
//     <>
//       <CustomStyles />
//       <DashNavbar />
//       <div className="wrapper d-flex interviewer-feedback-page">
//         <Sidebar />
//         <div className="main-content flex-grow-1 p-3 p-md-4">
//           <Card className="shadow-sm mb-4">
//             <Card.Header className="bg-light d-flex justify-content-between align-items-center py-3">
//               <h3 className="mb-0 gradient-text h5">Interview Feedback Evaluations</h3>
//               <div className="w-auto" style={{ maxWidth: "300px" }}>
//                 <InputGroup>
//                   <InputGroup.Text>
//                     <i className="bi bi-search"></i>
//                   </InputGroup.Text>
//                   <Form.Control type="search" placeholder="Search..." value={searchTerm} onChange={handleSearch} />
//                 </InputGroup>
//               </div>
//             </Card.Header>

//             <Card.Body>
//               <div className="table-responsive">
//                 <Table hover striped className="align-middle">
//                   <thead className="table-dark">
//                     <tr>
//                       <th scope="col">#</th>
//                       <th scope="col">Candidate Name</th>
//                       <th scope="col">Job Title</th>
//                       <th scope="col">Email</th>
//                       <th scope="col">Evaluation Date</th>
//                       <th scope="col" className="text-center">Actions</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {isLoading ? (
//                       <tr>
//                         <td colSpan="6" className="text-center py-5">
//                           <Spinner animation="border" variant="primary" />
//                           <p className="mt-2 mb-0">Loading evaluations...</p>
//                         </td>
//                       </tr>
//                     ) : evaluationResults.length > 0 ? (
//                       evaluationResults.map((evaluation, index) => (
//                         <tr key={evaluation.id || index}>
//                           <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
//                           <td>{evaluation.candidate_name || "N/A"}</td>
//                           <td>{evaluation.job_title || "N/A"}</td>
//                           <td>{evaluation.candidate_email || "N/A"}</td>
//                           <td>{formatDate(evaluation.evaluation_date)}</td>
//                           <td className="text-center">
//                             <Button variant="outline-primary" size="sm" onClick={() => handleViewDetails(evaluation)} title="View Details">
//                               <i className="bi bi-eye-fill me-1"></i> View
//                             </Button>
//                           </td>
//                         </tr>
//                       ))
//                     ) : (
//                       <tr>
//                         <td colSpan="6" className="text-center py-5">
//                           <i className="bi bi-journal-x fs-1 text-muted"></i>
//                           <p className="mt-2 mb-0">No feedback entries found.</p>
//                           {searchTerm && <p className="text-muted small mt-1">Try adjusting your search term.</p>}
//                         </td>
//                       </tr>
//                     )}
//                   </tbody>
//                 </Table>
//               </div>
//             </Card.Body>

//             {totalPages > 0 && evaluationResults.length > 0 && (
//               <Card.Footer className="bg-light py-3">
//                 <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
//                     <div className="d-flex align-items-center">
//                         <span className="me-2 small text-muted">Show</span>
//                         <Form.Select size="sm" style={{ width: 'auto' }} value={itemsPerPage} onChange={handleItemsPerPageChange}>
//                             <option value="5">5</option>
//                             <option value="10">10</option>
//                             <option value="25">25</option>
//                         </Form.Select>
//                         <span className="ms-2 small text-muted">entries</span>
//                     </div>

//                     <Pagination className="mb-0">
//                         <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
//                         {[...Array(totalPages).keys()].map((pageIndex) => (
//                         <Pagination.Item
//                             key={pageIndex + 1}
//                             active={pageIndex + 1 === currentPage}
//                             onClick={() => handlePageChange(pageIndex + 1)}
//                         >
//                             {pageIndex + 1}
//                         </Pagination.Item>
//                         ))}
//                         <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
//                     </Pagination>
//                 </div>
//               </Card.Footer>
//             )}
//           </Card>
//         </div>
//       </div>

//       {/* --- Details Modal --- */}
//       <Modal show={selectedEvaluation !== null} onHide={handleCloseDetails} size="xl" centered>
//         <Modal.Header closeButton className="bg-light">
//           <Modal.Title className="h5 gradient-text">Evaluation Details</Modal.Title>
//         </Modal.Header>
//         <Modal.Body className="p-4">
//           {selectedEvaluation && (
//             <>
//               <div className="mb-4 pb-3 border-bottom">
//                 <h5 className="mb-1">{selectedEvaluation.candidate_name}</h5>
//                 <p className="mb-0 text-muted small">{selectedEvaluation.candidate_email}</p>
//                 <p className="mb-0 text-muted small">
//                   Job: {selectedEvaluation.job_title || "N/A"} | Interviewer: {selectedEvaluation.interviewer_email || "N/A"}
//                 </p>
//                 <p className="mb-0 text-muted small">Date: {formatDate(selectedEvaluation.evaluation_date)}</p>
//               </div>

//               {/* ==================================================================== */}
//               {/* === MODIFIED SECTION: Displaying details in a 3-column table === */}
//               {/* ==================================================================== */}
//               {selectedEvaluation.evaluation_details?.length > 0 ? (
//                 Object.entries(groupEvaluationsByArea(selectedEvaluation.evaluation_details)).map(([area, details], areaIndex) => (
//                   <div key={areaIndex} className="mb-4">
//                     <h6 className="text-primary mb-3">{areaIndex + 1}. {area}</h6>
//                     <Table striped bordered hover responsive className="mb-4">
//                       <thead className="table-light">
//                         <tr>
//                           <th style={{ width: "50%" }}>Parameter / Question</th>
//                           <th style={{ width: "20%" }} className="text-center">Rating</th>
//                           <th style={{ width: "30%" }}>Comments / Evidence</th>
//                         </tr>
//                       </thead>
//                       <tbody>
//                         {details.map((detail, index) => (
//                           <tr key={detail.question_id || index}>
//                             <td className="align-middle">
//                               <strong>{detail.question_text}</strong>
//                             </td>
//                             <td className="text-center align-middle">
//                               {detail.answer_value ? (
//                                 <span className="badge bg-primary px-3 py-2">{detail.answer_value}</span>
//                               ) : (
//                                 <span className="badge bg-secondary px-3 py-2">N/A</span>
//                               )}
//                             </td>
//                             <td className="align-middle">
//                               {detail.comments || <span className="text-muted fst-italic">No comments provided.</span>}
//                             </td>
//                           </tr>
//                         ))}
//                       </tbody>
//                     </Table>
//                   </div>
//                 ))
//               ) : (
//                 <div className="text-center text-muted p-3">
//                   <i className="bi bi-info-circle fs-3"></i>
//                   <p className="mt-2">No specific evaluation details provided.</p>
//                 </div>
//               )}
//               {/* ==================================================================== */}
//               {/* === END OF MODIFIED SECTION === */}
//               {/* ==================================================================== */}
//             </>
//           )}
//         </Modal.Body>
//         <Modal.Footer>
//           <Button variant="outline-secondary" onClick={handleCloseDetails}>Close</Button>
//         </Modal.Footer>
//       </Modal>
//     </>
//   )
// }

// export default InterviewerFeedback


// "use client"

// import { useState, useEffect, useCallback } from "react"
// import axios from "axios"
// import DashNavbar from "./DashNavbar"
// import Sidebar from "./Sidebar"
// import { Pagination, Modal, Card, Form, InputGroup, Button, Table, Spinner } from "react-bootstrap"

// // Custom Styles Component (Embedded CSS)
// const CustomStyles = () => (
//     <style>{`
//       .interviewer-feedback-page .main-content {
//         background-color: #f8f9fa;
//       }
  
//       .gradient-text {
//         background: linear-gradient(45deg, #0d6efd, #0d6efd);
//         -webkit-background-clip: text;
//         -webkit-text-fill-color: transparent;
//         font-weight: 600;
//       }

//       /*
//       =====================================================
//       --- Full Height Layout ---
//       =====================================================
//       */
//       .interviewer-feedback-page.wrapper {
//         display: flex;
//         height: calc(100vh - 60px); /* Adjust 60px to your DashNavbar's height */
//       }
  
//       .interviewer-feedback-page .main-content {
//         flex-grow: 1;
//         display: flex;
//         flex-direction: column;
//         overflow: hidden;
//       }
  
//       .interviewer-feedback-page .card {
//         flex-grow: 1; /* Allow the card to fill the container's height */
//         display: flex;
//         flex-direction: column; /* Stack children vertically */
//         overflow: hidden; /* Important */
//       }
      
//       .interviewer-feedback-page .card-body {
//         flex-grow: 1;
//         display: flex;
//         flex-direction: column;
//         overflow: hidden;
//         padding: 0; /* Remove padding to allow table-responsive to manage it */
//       }

//       .interviewer-feedback-page .table-responsive {
//         flex-grow: 1; /* This is the key: makes the table container expand */
//         overflow-y: auto; /* Adds a vertical scrollbar ONLY to the table */
//         padding: 1.5rem; /* Add padding here instead of card-body */
//         padding-top: 0;
//       }

//       .interviewer-feedback-page .card-header,
//       .interviewer-feedback-page .card-footer {
//         flex-shrink: 0; /* Prevent header and footer from shrinking */
//       }
//     `}</style>
//   );

// function InterviewerFeedback() {
//   const [evaluationResults, setEvaluationResults] = useState([])
//   const [searchTerm, setSearchTerm] = useState("")
//   const [currentPage, setCurrentPage] = useState(1)
//   const [selectedEvaluation, setSelectedEvaluation] = useState(null)
//   const [isLoading, setIsLoading] = useState(false)
//   const [totalPages, setTotalPages] = useState(0)

//   // MODIFIED: State for items per page
//   const [itemsPerPage, setItemsPerPage] = useState(10)

//   // MODIFIED: Optimized data fetching
//   const fetchInterviewerDetails = useCallback(async (page, pageSize, search) => {
//     setIsLoading(true)
//     // MODIFIED: API call includes page_size and search parameters
//     let API_URL = `https://raasbackend.vetrinahealthcare.com/interview-evaluations/?page=${page}&page_size=${pageSize}`
//     if (search) {
//       API_URL += `&search=${search}`
//     }

//     try {
//       const response = await axios.get(API_URL)
//       const responseData = response.data
//       setEvaluationResults(responseData.results || [])
//       setTotalPages(responseData.num_pages || responseData.total_pages || 0)
//     } catch (error) {
//       console.error("Error fetching data:", error)
//       setEvaluationResults([])
//       setTotalPages(0)
//     } finally {
//       setIsLoading(false)
//     }
//   }, [])

//   // MODIFIED: useEffect with debouncing for search
//   useEffect(() => {
//     const handler = setTimeout(() => {
//       fetchInterviewerDetails(currentPage, itemsPerPage, searchTerm)
//     }, 300) // 300ms debounce

//     return () => {
//       clearTimeout(handler)
//     }
//   }, [currentPage, itemsPerPage, searchTerm, fetchInterviewerDetails])

//   const handlePageChange = (page) => {
//     if (page < 1 || (totalPages > 0 && page > totalPages)) return
//     setCurrentPage(page)
//   }

//   const handleSearch = (e) => {
//     setSearchTerm(e.target.value)
//     setCurrentPage(1) // Reset to first page on new search
//   }

//   // MODIFIED: Handler for items per page change
//   const handleItemsPerPageChange = (e) => {
//     setItemsPerPage(Number(e.target.value))
//     setCurrentPage(1) // Reset to first page
//   }

//   const handleViewDetails = (evaluation) => {
//     setSelectedEvaluation(evaluation)
//   }

//   const handleCloseDetails = () => {
//     setSelectedEvaluation(null)
//   }

//   const formatDate = (dateString) => {
//     if (!dateString) return "N/A"
//     return new Date(dateString).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
//   }

//   const groupEvaluationsByArea = (evaluationDetails) => {
//     if (!evaluationDetails || !Array.isArray(evaluationDetails)) return {}
//     return evaluationDetails.reduce((groups, detail) => {
//       const area = detail.area || "General Questions"
//       if (!groups[area]) {
//         groups[area] = []
//       }
//       groups[area].push(detail)
//       return groups
//     }, {})
//   }

//   return (
//     <>
//       <CustomStyles />
//       <DashNavbar />
//       <div className="wrapper d-flex interviewer-feedback-page">
//         <Sidebar />
//         <div className="main-content flex-grow-1 p-3 p-md-4">
//           <Card className="shadow-sm mb-4">
//             <Card.Header className="bg-light d-flex justify-content-between align-items-center py-3">
//               <h3 className="mb-0 gradient-text h5">Interview Feedback Evaluations</h3>
//               <div className="w-auto" style={{ maxWidth: "300px" }}>
//                 <InputGroup>
//                   <InputGroup.Text>
//                     <i className="bi bi-search"></i>
//                   </InputGroup.Text>
//                   <Form.Control type="search" placeholder="Search..." value={searchTerm} onChange={handleSearch} />
//                 </InputGroup>
//               </div>
//             </Card.Header>

//             <Card.Body>
//               <div className="table-responsive">
//                 <Table hover striped className="align-middle">
//                   <thead className="table-dark">
//                     <tr>
//                       <th scope="col">#</th>
//                       <th scope="col">Candidate Name</th>
//                       <th scope="col">Job Title</th>
//                       <th scope="col">Email</th>
//                       <th scope="col">Evaluation Date</th>
//                       <th scope="col" className="text-center">Actions</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {isLoading ? (
//                       <tr>
//                         <td colSpan="6" className="text-center py-5">
//                           <Spinner animation="border" variant="primary" />
//                           <p className="mt-2 mb-0">Loading evaluations...</p>
//                         </td>
//                       </tr>
//                     ) : evaluationResults.length > 0 ? (
//                       evaluationResults.map((evaluation, index) => (
//                         <tr key={evaluation.id || index}>
//                           <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
//                           <td>{evaluation.candidate_name || "N/A"}</td>
//                           <td>{evaluation.job_title || "N/A"}</td>
//                           <td>{evaluation.candidate_email || "N/A"}</td>
//                           <td>{formatDate(evaluation.evaluation_date)}</td>
//                           <td className="text-center">
//                             <Button variant="outline-primary" size="sm" onClick={() => handleViewDetails(evaluation)} title="View Details">
//                               <i className="bi bi-eye-fill me-1"></i> View
//                             </Button>
//                           </td>
//                         </tr>
//                       ))
//                     ) : (
//                       <tr>
//                         <td colSpan="6" className="text-center py-5">
//                           <i className="bi bi-journal-x fs-1 text-muted"></i>
//                           <p className="mt-2 mb-0">No feedback entries found.</p>
//                           {searchTerm && <p className="text-muted small mt-1">Try adjusting your search term.</p>}
//                         </td>
//                       </tr>
//                     )}
//                   </tbody>
//                 </Table>
//               </div>
//             </Card.Body>

//             {totalPages > 0 && evaluationResults.length > 0 && (
//               <Card.Footer className="bg-light py-3">
//                 <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
//                     {/* Items per page selector */}
//                     <div className="d-flex align-items-center">
//                         <span className="me-2 small text-muted">Show</span>
//                         <Form.Select size="sm" style={{ width: 'auto' }} value={itemsPerPage} onChange={handleItemsPerPageChange}>
//                             <option value="5">5</option>
//                             <option value="10">10</option>
//                             <option value="25">25</option>
//                         </Form.Select>
//                         <span className="ms-2 small text-muted">entries</span>
//                     </div>

//                     {/* Pagination */}
//                     <Pagination className="mb-0">
//                         <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
//                         {[...Array(totalPages).keys()].map((pageIndex) => (
//                         <Pagination.Item
//                             key={pageIndex + 1}
//                             active={pageIndex + 1 === currentPage}
//                             onClick={() => handlePageChange(pageIndex + 1)}
//                         >
//                             {pageIndex + 1}
//                         </Pagination.Item>
//                         ))}
//                         <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
//                     </Pagination>
//                 </div>
//               </Card.Footer>
//             )}
//           </Card>
//         </div>
//       </div>

//       {/* --- Details Modal --- */}
//       <Modal show={selectedEvaluation !== null} onHide={handleCloseDetails} size="xl" centered>
//         <Modal.Header closeButton className="bg-light">
//           <Modal.Title className="h5 gradient-text">Evaluation Details</Modal.Title>
//         </Modal.Header>
//         <Modal.Body className="p-4">
//           {selectedEvaluation && (
//             <>
//               <div className="mb-4 pb-3 border-bottom">
//                 <h5 className="mb-1">{selectedEvaluation.candidate_name}</h5>
//                 <p className="mb-0 text-muted small">{selectedEvaluation.candidate_email}</p>
//                 <p className="mb-0 text-muted small">
//                   Job: {selectedEvaluation.job_title || "N/A"} | Interviewer: {selectedEvaluation.interviewer_email || "N/A"}
//                 </p>
//                 <p className="mb-0 text-muted small">Date: {formatDate(selectedEvaluation.evaluation_date)}</p>
//               </div>

//               {selectedEvaluation.evaluation_details?.length > 0 ? (
//                 Object.entries(groupEvaluationsByArea(selectedEvaluation.evaluation_details)).map(([area, details], areaIndex) => (
//                   <div key={areaIndex} className="mb-4">
//                     <h6 className="text-primary mb-3">{areaIndex + 1}. {area}</h6>
//                     <Table striped bordered hover responsive className="mb-4">
//                       <thead className="table-light">
//                         <tr>
//                           <th style={{ width: "60%" }}>Question</th>
//                           <th style={{ width: "40%" }} className="text-center">Answer/Rating</th>
//                         </tr>
//                       </thead>
//                       <tbody>
//                         {details.map((detail, index) => (
//                           <tr key={detail.question_id || index}>
//                             <td><strong>{detail.question_text}</strong></td>
//                             <td className="text-center">
//                               <span className="badge bg-primary px-3 py-2">{detail.answer_value || "No Answer"}</span>
//                             </td>
//                           </tr>
//                         ))}
//                       </tbody>
//                     </Table>
//                   </div>
//                 ))
//               ) : (
//                 <div className="text-center text-muted p-3">
//                   <i className="bi bi-info-circle fs-3"></i>
//                   <p className="mt-2">No specific evaluation details provided.</p>
//                 </div>
//               )}
//             </>
//           )}
//         </Modal.Body>
//         <Modal.Footer>
//           <Button variant="outline-secondary" onClick={handleCloseDetails}>Close</Button>
//         </Modal.Footer>
//       </Modal>
//     </>
//   )
// }

// export default InterviewerFeedback

// import React, { useState, useEffect } from "react";
// import axios from "axios";
// import DashNavbar from "./DashNavbar";
// import Sidebar from "./Sidebar";
// import Pagination from "react-bootstrap/Pagination";
// import Modal from "react-bootstrap/Modal";

// function InterviewerFeedback() {
//   const [interviewers, setInterviewers] = useState([]);
//   const [searchTerm, setSearchTerm] = useState("");
//   const [filteredInterviewers, setFilteredInterviewers] = useState([]);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [selectedCandidate, setSelectedCandidate] = useState(null);
//   const itemsPerPage = 5;
//   const [isLoading, setIsLoading] = useState(false);

//   useEffect(() => {
//     fetchInterviewerDetails();
//   }, [currentPage, searchTerm]);

//   const fetchInterviewerDetails = async () => {
//     setIsLoading(true); // Start loading
//     try {
//       const response = await axios.get(`https://raasbackend.vetrinahealthcare.com/interview-evaluations/?page=${currentPage}`
//       );
//       setInterviewers(response.data);
//       filterInterviewers(response.data);
//     } catch (error) {
//       console.error("Error fetching data:", error);
//     }
//     finally {
//       // Stop the loading spinner
//       setIsLoading(false);
//     }
//   };

//   const filterInterviewers = (data) => {
//     const filtered = data.filter(
//       (interviewer) =>
//         interviewer.candidate
//           .toLowerCase()
//           .includes(searchTerm.toLowerCase()) ||
//         interviewer.candidate_email
//           .toLowerCase()
//           .includes(searchTerm.toLowerCase())
//     );
//     setFilteredInterviewers(filtered);
//   };

//   const handlePageChange = (page) => {
//     if (page < 1 || page > totalPages) {
//       return;
//     }
//     setCurrentPage(page);
//   };

//   const totalPages = Math.ceil(filteredInterviewers.length / itemsPerPage);
//   const paginatedInterviewers = filteredInterviewers.slice(
//     (currentPage - 1) * itemsPerPage,
//     currentPage * itemsPerPage
//   );

//   const handleSearch = (e) => {
//     setSearchTerm(e.target.value);
//     setCurrentPage(1);
//   };

//   const handleViewDetails = (candidate) => {
//     setSelectedCandidate(candidate);
//   };

//   const handleCloseDetails = () => {
//     setSelectedCandidate(null);
//   };

//   const convertRatingToText = (rating) => {
//     switch (rating) {
//       case 5:
//         return "Exceptional";
//       case 4:
//         return "Above Average";
//       case 3:
//         return "Average";
//       case 2:
//         return "Satisfactory";
//       case 1:
//         return "Unsatisfactory";
//       default:
//         return "Not Rated";
//     }
//   };

//   return (
//     <>
//       {/* <div
//         style={{ display: "flex", flexDirection: "column", height: "100vh" }}
//       > */}
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className="main">
//           <div className="container" style={{ marginTop: "25px" }}>
//             <div className="auth-inner mt-2  w-100" style={{ padding: "10px 10px 10px 10px" }}>
//               <h2 className="mb-4 gradient-text">
//                 Interview Feedback Evaluation
//               </h2>
//               <div className="input-group justify-content-center align-items-center">
//                 <div className="form-outline w-50">
//                   <input
//                     type="search"
//                     className="form-control"
//                     placeholder="Search by Name or Email"
//                     value={searchTerm}
//                     onChange={handleSearch}
//                   />
//                   <button className="btn btn-primary searchicon text-xs">
//                     <i className="fas fa-search"></i>
//                   </button>
//                 </div>
//               </div>
//               <div className="table-responsive">
//                 <table className="table">
//                   <thead className="thead-dark">
//                     <tr>
//                       <th scope="col">Sr.No</th>
//                       <th scope="col">Candidate</th>
//                       <th scope="col">Email</th>
//                       <th scope="col">Actions</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {isLoading ? (
//                       <tr>
//                         <td colSpan="4" className="text-center">
//                           <div className="spinner-border text-primary" role="status">
//                             <span className="sr-only">Loading...</span>
//                           </div>
//                         </td>
//                       </tr>
//                     ) : (
//                       paginatedInterviewers.map((interviewer, index) => (
//                         <tr key={index}>
//                           <td>{index + 1}</td>
//                           <td>{interviewer.candidate}</td>
//                           <td>{interviewer.candidate_email}</td>
//                           <td>
//                             <button
//                               className="btn btn-primary btn-sm"
//                               onClick={() => handleViewDetails(interviewer)}
//                             >
//                               View
//                             </button>
//                           </td>
//                         </tr>
//                       ))
//                     )}
//                   </tbody>
//                 </table>
//               </div>

//               <div className="d-flex justify-content-start mt-4">
//                 <Pagination>
//                   <Pagination.Prev
//                     onClick={() => handlePageChange(currentPage - 1)}
//                     disabled={currentPage === 1}
//                   />
//                   {Array.from({ length: totalPages }, (_, index) => (
//                     <Pagination.Item
//                       key={index + 1}
//                       active={index + 1 === currentPage}
//                       onClick={() => handlePageChange(index + 1)}
//                     >
//                       {index + 1}
//                     </Pagination.Item>
//                   ))}
//                   <Pagination.Next
//                     onClick={() => handlePageChange(currentPage + 1)}
//                     disabled={currentPage === totalPages}
//                   />
//                 </Pagination>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//       {/* </div> */}
//       <Modal show={selectedCandidate} onHide={handleCloseDetails} size="xl">
//         <Modal.Header closeButton>
//           <Modal.Title className=" gradient-text">
//             {selectedCandidate?.candidate} ({selectedCandidate?.candidate_email}
//             )
//           </Modal.Title>
//         </Modal.Header>
//         <Modal.Body>
//           <div className="row">
//             <div className="col-6">
//               <div>
//                 <p>
//                   <b>1. Educational Background</b>:{" "}
//                   {convertRatingToText(
//                     selectedCandidate?.educational_background
//                   )}
//                 </p>
//                 <p>
//                   Comment: {selectedCandidate?.educational_background_comments}
//                 </p>
//                 <hr />
//               </div>
//               <div>
//                 <p>
//                   <b>2. Prior Work Experience</b>:{" "}
//                   {convertRatingToText(
//                     selectedCandidate?.prior_work_experience
//                   )}
//                 </p>
//                 <p>
//                   Comment: {selectedCandidate?.prior_work_experience_comments}
//                 </p>
//                 <hr />
//               </div>
//               <div>
//                 <p>
//                   <b>3. Technical Qualifications</b>:{" "}
//                   {convertRatingToText(
//                     selectedCandidate?.technical_qualifications
//                   )}
//                 </p>
//                 <p>
//                   Comment:{" "}
//                   {selectedCandidate?.technical_qualifications_comments}
//                 </p>
//                 <hr />
//               </div>
//               <div>
//                 <p>
//                   <b>4. Verbal Communication</b>:{" "}
//                   {convertRatingToText(selectedCandidate?.verbal_communication)}
//                 </p>
//                 <p>
//                   Comment: {selectedCandidate?.verbal_communication_comments}
//                 </p>
//                 <hr />
//               </div>
//               <div>
//                 <p>
//                   <b>5. Candidate Interest</b>:{" "}
//                   {convertRatingToText(selectedCandidate?.candidate_interest)}
//                 </p>
//                 <p>Comment: {selectedCandidate?.candidate_interest_comments}</p>
//                 <hr />
//               </div>
//             </div>
//             <div className="col-6">
//               <div>
//                 <p>
//                   <b>6. Knowledge of Organization</b>:{" "}
//                   {convertRatingToText(
//                     selectedCandidate?.knowledge_of_organization
//                   )}
//                 </p>
//                 <p>
//                   Comment:{" "}
//                   {selectedCandidate?.knowledge_of_organization_comments}
//                 </p>
//                 <hr />
//               </div>
//               <div>
//                 <p>
//                   <b>7. Teambuilding & Interpersonal Skills</b>:{" "}
//                   {convertRatingToText(
//                     selectedCandidate?.teambuilding_interpersonal_skills
//                   )}
//                 </p>
//                 <p>
//                   Comment:{" "}
//                   {
//                     selectedCandidate?.teambuilding_interpersonal_skills_comments
//                   }
//                 </p>
//                 <hr />
//               </div>
//               <div>
//                 <p>
//                   <b>8. Initiative</b>:{" "}
//                   {convertRatingToText(selectedCandidate?.initiative)}
//                 </p>
//                 <p>Comment: {selectedCandidate?.initiative_comments}</p>
//                 <hr />
//               </div>
//               <div>
//                 <p>
//                   <b>9. Time Management</b>:{" "}
//                   {convertRatingToText(selectedCandidate?.time_management)}
//                 </p>
//                 <p>Comment: {selectedCandidate?.time_management_comments}</p>
//                 <hr />
//               </div>
//               <div>
//                 <p>
//                   <b>10. Overall Impression</b>:{" "}
//                   {convertRatingToText(selectedCandidate?.overall_impression)}
//                 </p>
//                 <p>Comment: {selectedCandidate?.overall_impression_comments}</p>
//                 <hr />
//               </div>
//             </div>
//           </div>
//         </Modal.Body>
//       </Modal>
//     </>
//   );
// }


// "use client"

// import { useState, useEffect, useCallback } from "react"
// import axios from "axios"
// import DashNavbar from "./DashNavbar"
// import Sidebar from "./Sidebar"
// import Pagination from "react-bootstrap/Pagination"
// import Modal from "react-bootstrap/Modal"
// import Card from "react-bootstrap/Card"
// import Form from "react-bootstrap/Form"
// import InputGroup from "react-bootstrap/InputGroup"
// import Button from "react-bootstrap/Button"
// import Table from "react-bootstrap/Table"

// function InterviewerFeedback() {
//   const [evaluationResults, setEvaluationResults] = useState([])
//   const [filteredResults, setFilteredResults] = useState([])
//   const [searchTerm, setSearchTerm] = useState("")
//   const [currentPage, setCurrentPage] = useState(1)
//   const [selectedEvaluation, setSelectedEvaluation] = useState(null)
//   const [isLoading, setIsLoading] = useState(false)
//   const [totalPages, setTotalPages] = useState(0)

//   const itemsPerPage = 5

//   const filterAndSetDisplayedResults = useCallback((resultsToFilter, currentSearchTerm) => {
//     if (!currentSearchTerm) {
//       setFilteredResults(resultsToFilter)
//       return
//     }

//     const lowerSearchTerm = currentSearchTerm.toLowerCase()
//     const filtered = resultsToFilter.filter(
//       (evaluation) =>
//         (evaluation.candidate_name && evaluation.candidate_name.toLowerCase().includes(lowerSearchTerm)) ||
//         (evaluation.candidate_email && evaluation.candidate_email.toLowerCase().includes(lowerSearchTerm)) ||
//         (evaluation.job_title && evaluation.job_title.toLowerCase().includes(lowerSearchTerm)),
//     )

//     setFilteredResults(filtered)
//   }, [])

//   const fetchInterviewerDetails = useCallback(
//     async (page, currentSearchTerm) => {
//       setIsLoading(true)
//       const API_URL = `https://raasbackend.vetrinahealthcare.com/interview-evaluations/?page=${page}`

//       try {
//         const response = await axios.get(API_URL)
//         const responseData = response.data

//         setEvaluationResults(responseData.results || [])
//         setTotalPages(responseData.num_pages || responseData.total_pages || 0)

//         filterAndSetDisplayedResults(responseData.results || [], currentSearchTerm)
//       } catch (error) {
//         console.error("Error fetching data:", error)
//         setEvaluationResults([])
//         setFilteredResults([])
//         setTotalPages(0)
//       } finally {
//         setIsLoading(false)
//       }
//     },
//     [filterAndSetDisplayedResults],
//   )

//   useEffect(() => {
//     fetchInterviewerDetails(currentPage, searchTerm)
//   }, [currentPage, searchTerm, fetchInterviewerDetails])

//   const handlePageChange = (page) => {
//     if (page < 1 || (totalPages > 0 && page > totalPages)) return
//     setCurrentPage(page)
//   }

//   const handleSearch = (e) => {
//     const newSearchTerm = e.target.value
//     setSearchTerm(newSearchTerm)
//     setCurrentPage(1)
//     filterAndSetDisplayedResults(evaluationResults, newSearchTerm)
//   }

//   const handleViewDetails = (evaluation) => {
//     setSelectedEvaluation(evaluation)
//   }

//   const handleCloseDetails = () => {
//     setSelectedEvaluation(null)
//   }

//   // Helper to format date
//   const formatDate = (dateString) => {
//     if (!dateString) return "N/A"
//     return new Date(dateString).toLocaleDateString(undefined, {
//       year: "numeric",
//       month: "long",
//       day: "numeric",
//     })
//   }

//   // Group evaluation details by area
//   const groupEvaluationsByArea = (evaluationDetails) => {
//     if (!evaluationDetails || !Array.isArray(evaluationDetails)) return {}

//     return evaluationDetails.reduce((groups, detail) => {
//       const area = detail.area || "General Questions"
//       if (!groups[area]) {
//         groups[area] = []
//       }
//       groups[area].push(detail)
//       return groups
//     }, {})
//   }

//   return (
//     <>
//       <DashNavbar />
//       <div className="wrapper d-flex">
//         <Sidebar />
//         <div className="main-content flex-grow-1 p-3 p-md-4">
//           <Card className="shadow-sm mb-4">
//             <Card.Header className="bg-light d-flex justify-content-between align-items-center py-3">
//               <h3 className="mb-0 gradient-text h5">Interview Feedback Evaluations</h3>
//               <div className="w-auto" style={{ maxWidth: "300px" }}>
//                 <InputGroup>
//                   <InputGroup.Text>
//                     <i className="bi bi-search"></i>
//                   </InputGroup.Text>
//                   <Form.Control type="search" placeholder="Search..." value={searchTerm} onChange={handleSearch} />
//                 </InputGroup>
//               </div>
//             </Card.Header>

//             <Card.Body>
//               <div className="table-responsive">
//                 <table className="table table-hover table-striped align-middle">
//                   <thead className="table-dark">
//                     <tr>
//                       <th scope="col" style={{ width: "5%" }}>
//                         #
//                       </th>
//                       <th scope="col">Candidate Name</th>
//                       <th scope="col">Job Title</th>
//                       <th scope="col">Email</th>
//                       <th scope="col">Evaluation Date</th>
//                       <th scope="col" style={{ width: "10%" }} className="text-center">
//                         Actions
//                       </th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {isLoading ? (
//                       <tr>
//                         <td colSpan="6" className="text-center py-5">
//                           <div className="spinner-border text-primary" role="status">
//                             <span className="visually-hidden">Loading...</span>
//                           </div>
//                           <p className="mt-2">Loading evaluations...</p>
//                         </td>
//                       </tr>
//                     ) : filteredResults.length > 0 ? (
//                       filteredResults.map((evaluation, index) => (
//                         <tr key={evaluation.id || index}>
//                           <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
//                           <td>{evaluation.candidate_name || "N/A"}</td>
//                           <td>{evaluation.job_title || "N/A"}</td>
//                           <td>{evaluation.candidate_email || "N/A"}</td>
//                           <td>{formatDate(evaluation.evaluation_date)}</td>
//                           <td className="text-center">
//                             <Button
//                               variant="outline-primary"
//                               size="sm"
//                               onClick={() => handleViewDetails(evaluation)}
//                               title="View Details"
//                             >
//                               <i className="bi bi-eye-fill me-1"></i> View
//                             </Button>
//                           </td>
//                         </tr>
//                       ))
//                     ) : (
//                       <tr>
//                         <td colSpan="6" className="text-center py-5">
//                           <i className="bi bi-journal-x fs-1 text-muted"></i>
//                           <p className="mt-2">No feedback entries found.</p>
//                           {searchTerm && <p className="text-muted small">Try adjusting your search term.</p>}
//                         </td>
//                       </tr>
//                     )}
//                   </tbody>
//                 </table>
//               </div>
//             </Card.Body>

//             {totalPages > 0 && filteredResults.length > 0 && (
//               <Card.Footer className="bg-light py-3">
//                 <div className="d-flex justify-content-center">
//                   <Pagination>
//                     <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
//                     {[...Array(totalPages).keys()].map((pageIndex) => (
//                       <Pagination.Item
//                         key={pageIndex + 1}
//                         active={pageIndex + 1 === currentPage}
//                         onClick={() => handlePageChange(pageIndex + 1)}
//                       >
//                         {pageIndex + 1}
//                       </Pagination.Item>
//                     ))}
//                     <Pagination.Next
//                       onClick={() => handlePageChange(currentPage + 1)}
//                       disabled={currentPage === totalPages}
//                     />
//                   </Pagination>
//                 </div>
//               </Card.Footer>
//             )}
//           </Card>
//         </div>
//       </div>

//       <Modal show={selectedEvaluation !== null} onHide={handleCloseDetails} size="xl" centered>
//         <Modal.Header closeButton className="bg-light">
//           <Modal.Title className="h5 gradient-text">Evaluation Details</Modal.Title>
//         </Modal.Header>
//         <Modal.Body className="p-4">
//           {selectedEvaluation && (
//             <>
//               <div className="mb-4 pb-3 border-bottom">
//                 <h5 className="mb-1">{selectedEvaluation.candidate_name}</h5>
//                 <p className="mb-0 text-muted small">{selectedEvaluation.candidate_email}</p>
//                 <p className="mb-0 text-muted small">
//                   Job: {selectedEvaluation.job_title || "N/A"} | Interviewer:{" "}
//                   {selectedEvaluation.interviewer_email || "N/A"}
//                 </p>
//                 <p className="mb-0 text-muted small">Date: {formatDate(selectedEvaluation.evaluation_date)}</p>
//               </div>

//               {selectedEvaluation.evaluation_details && selectedEvaluation.evaluation_details.length > 0 ? (
//                 (() => {
//                   const groupedEvaluations = groupEvaluationsByArea(selectedEvaluation.evaluation_details)
//                   return Object.entries(groupedEvaluations).map(([area, details], areaIndex) => (
//                     <div key={areaIndex} className="mb-4">
//                       <h6 className="text-primary mb-3">
//                         {areaIndex + 1}. {area}
//                       </h6>

//                       <Table striped bordered hover responsive className="mb-4">
//                         <thead className="table-light">
//                           <tr>
//                             <th style={{ width: "60%" }}>Question</th>
//                             <th style={{ width: "40%" }} className="text-center">
//                               Answer/Rating
//                             </th>
//                           </tr>
//                         </thead>
//                         <tbody>
//                           {details.map((detail, index) => (
//                             <tr key={detail.question_id || index}>
//                               <td>
//                                 <strong>{detail.question_text}</strong>
//                               </td>
//                               <td className="text-center">
//                                 <span className="badge bg-primary px-3 py-2">{detail.answer_value || "No Answer"}</span>
//                               </td>
//                             </tr>
//                           ))}
//                         </tbody>
//                       </Table>
//                     </div>
//                   ))
//                 })()
//               ) : (
//                 <div className="text-center text-muted p-3">
//                   <i className="bi bi-info-circle fs-3"></i>
//                   <p className="mt-2">No specific evaluation details provided.</p>
//                 </div>
//               )}
//             </>
//           )}
//         </Modal.Body>
//         <Modal.Footer>
//           <Button variant="outline-secondary" onClick={handleCloseDetails}>
//             Close
//           </Button>
//         </Modal.Footer>
//       </Modal>
//     </>
//   )
// }

// export default InterviewerFeedback
